import React, { useState, useEffect } from 'react';
import './App.css';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';

const API_URL = process.env.REACT_APP_API_URL || '';

// Translations object for i18n
const translations = {
  fr: {
    // Header
    appTitle: 'ALTAIR',
    // Common
    add: 'Ajouter',
    calculate: 'Calculer',
    // Credit section
    creditTitle: 'Crédit d\'Investissement',
    creditAmount: 'Montant du Crédit',
    over: 'Durée (années)',
    years: 'ans',
    creditType: 'Type de Crédit',
    interestRate: 'Taux d\'Intérêt (annuel) (%)',
    paymentFrequency: 'Fréquence de Paiement',
    monthlyPayment: 'Mensualité',
    quarterlyPayment: 'Paiement Trimestriel',
    annualPayment: 'Paiement Annuel',
    totalCreditCost: 'Coût Total du Crédit',
    interest: 'Intérêts',
    loanPaymentSchedule: 'Échéancier de Remboursement',
    amortizationTable: 'Tableau d\'Amortissement',
    month: 'Mois',
    payment: 'Paiement',
    principal: 'Capital',
    remainingBalance: 'Solde Restant',
    bullet: 'In Fine',
    amortizing: 'Amortissable',
    interestOnly: 'Intérêts Seuls',
    crescendo: 'Crescendo',
    interestOnlyPeriod: 'Période d\'Intérêts Seuls (mois)',
    crescendoRate: 'Taux de Croissance Annuel (%)',
    paymentEuro: 'Paiement (€)',
    yearsAxis: 'Années',
    interestOnlyPayment: 'Mensualité (Intérêts Seuls)',
    interestOnlyPaymentQuarterly: 'Paiement Trimestriel (Intérêts Seuls)',
    interestOnlyPaymentAnnually: 'Paiement Annuel (Intérêts Seuls)',
    amortizingPayment: 'Mensualité (Intérêts + Capital)',
    amortizingPaymentQuarterly: 'Paiement Trimestriel (Intérêts + Capital)',
    amortizingPaymentAnnually: 'Paiement Annuel (Intérêts + Capital)',
    totalInterestOnlyPeriod: 'Total Intérêts (Période Intérêts Seuls)',
    initialPayment: 'Mensualité Initiale',
    initialPaymentQuarterly: 'Paiement Trimestriel Initial',
    initialPaymentAnnually: 'Paiement Annuel Initial',
    finalPayment: 'Mensualité Finale',
    finalPaymentQuarterly: 'Paiement Trimestriel Final',
    finalPaymentAnnually: 'Paiement Annuel Final',
    // Investment section
    recurrentFeesIncome: 'Charges Récurrentes & Revenus',
    recurrentFees: 'Charges Récurrentes',
    income: 'Revenus',
    feeName: 'Nom de la Charge',
    amount: 'Montant',
    frequency: 'Fréquence',
    monthly: 'Mensuel',
    quarterly: 'Trimestriel',
    annually: 'Annuel',
    addFee: 'Ajouter une Charge',
    rent: 'Loyer',
    occupancyRate: 'Taux d\'Occupation (%)',
    indexation: 'Indexation (%)',
    addRevenue: 'Ajouter un Revenu',
    revenueName: 'Nom du Revenu',
    propertyTax: 'Taxe Foncière',
    coOwnershipFees: 'Charges de Copropriété',
    insurance: 'Assurance',
    maintenance: 'Entretien',
    monthlyCashFlow: 'Cash-Flow Mensuel Moyen',
    rentToValueRatio: 'Ratio Loyer/Valeur',
    summaryTable: 'Tableau Récapitulatif',
    year: 'Année',
    rentalIncome: 'Revenus Locatifs',
    annualFees: 'Charges Annuelles',
    loanPayment: 'Paiement Crédit',
    netCashFlow: 'Cash-Flow Net',
    loanBalance: 'Solde Crédit',
    totalYears: 'Total (10 ans)',
    monthlyBreakdown: 'Détail Mensuel',
    effectiveMonthlyRent: 'Loyer Mensuel Effectif (avec occupation)',
    monthlyFees: 'Charges Mensuelles',
    monthlyLoanPayment: 'Mensualité Crédit',
    netMonthlyCashFlow: 'Cash-Flow Mensuel Net',
    // Performance tab
    performance: 'Performance',
    internalRateOfReturn: 'Taux de Rentabilité Interne (TRI)',
    returnOnEquity: 'Rentabilité des Fonds Propres (ROE)',
    debtServiceCoverageRatio: 'Ratio Loyer / Prix avec Travaux',
    netPresentValue: 'Valeur Actuelle Nette (VAN)',
    monthlyCashFlow: 'Cash-Flow Mensuel Moyen',
    netCashFlowNCF: 'Cash-Flow Net (NCF)',
    netProfitability: 'Rentabilité Nette Prévisionnelle',
    renovationPerformance: 'Performance après travaux',
    addedValueAfterRenovation: 'Plus-value après travaux (brut, hors charges)',
    pricePerSqmBeforeRenovation: 'Prix au m² avant travaux',
    pricePerSqmAfterRenovation: 'Prix au m² après travaux',
    pricePerSqmWithRenovation: 'Prix au m² avec travaux (sans plus-value)',
    rentPerSqm: 'Loyer au m²',
    rentalPerformance: 'Performance Locative',
    totalPerformance: 'Performance Totale (à la revente)',
    cashFlow: 'Cash Flow',
    annualCashFlow: 'Cash-Flow Annuel',
    cumulativeCashFlow: 'Cash-Flow Cumulatif',
    cashFlowSummary: 'Résumé du Cash-Flow',
    showMonthly: 'Afficher Mensuel',
    showYearly: 'Afficher Annuel',
    cashFlowFund: 'Fonds Apportés',
    totalCredit: 'Crédit Total',
    monthlyRent: 'Loyer Mensuel',
    totalAnnualCost: 'Coûts Annuels Totaux',
    totalCashFlow: 'Cash-Flow Total',
    balance: 'Solde',
    homeValue: 'Valeur du Bien',
    difference: 'Différence',
    investmentHorizon: 'Horizon d\'Investissement (mois)',
    housePriceInflation: 'Inflation du Prix du Bien (%)',
    saleProceeds: 'Produit de la Vente',
    capitalGain: 'Plus-Value à la Revente',
    viewMode: 'Mode d\'Affichage',
    annual: 'Annuel',
    quarterly: 'Trimestriel',
    period: 'Période',
    sensitivity: 'Sensibilité',
    sensitivityParameter: 'Paramètre (X)',
    sensitivityYAxis: 'Métrique (Y)',
    interestRateParam: 'Taux d\'intérêt',
    equityPercentParam: '% Apport',
    occupancyRateParam: 'Taux d\'occupation',
    durationParam: 'Horizon d\'Investissement (mois)',
    irrMetric: 'Taux de Rendement Interne (TRI)',
    ncfMetric: 'Cash-Flow Net (NCF)',
    npvMetric: 'Valeur Actuelle Nette (VAN)',
    roeMetric: 'Rentabilité des Fonds Propres (ROE)',
    irrAxis: 'Taux de Rendement Interne (%)'
  },
  en: {
    // Header
    appTitle: 'ALTAIR',
    // Common
    add: 'Add',
    calculate: 'Calculate',
    // Credit section
    creditTitle: 'Investment Credit',
    creditAmount: 'Credit Amount',
    over: 'Duration (years)',
    years: 'years',
    creditType: 'Credit Type',
    interestRate: 'Interest Rate (annual) (%)',
    paymentFrequency: 'Payment Frequency',
    monthlyPayment: 'Monthly Payment',
    quarterlyPayment: 'Quarterly Payment',
    annualPayment: 'Annual Payment',
    totalCreditCost: 'Total Credit Cost',
    interest: 'Interest',
    loanPaymentSchedule: 'Loan Payment Schedule',
    amortizationTable: 'Amortization Table',
    month: 'Month',
    payment: 'Payment',
    principal: 'Principal',
    remainingBalance: 'Remaining Balance',
    bullet: 'Bullet',
    amortizing: 'Amortizing',
    interestOnly: 'Interest Only',
    crescendo: 'Crescendo',
    interestOnlyPeriod: 'Interest Only Period (months)',
    crescendoRate: 'Annual Growth Rate (%)',
    paymentEuro: 'Payment (€)',
    yearsAxis: 'Years',
    interestOnlyPayment: 'Monthly Payment (Interest Only)',
    interestOnlyPaymentQuarterly: 'Quarterly Payment (Interest Only)',
    interestOnlyPaymentAnnually: 'Annual Payment (Interest Only)',
    amortizingPayment: 'Monthly Payment (Interest + Principal)',
    amortizingPaymentQuarterly: 'Quarterly Payment (Interest + Principal)',
    amortizingPaymentAnnually: 'Annual Payment (Interest + Principal)',
    totalInterestOnlyPeriod: 'Total Interest (Interest Only Period)',
    initialPayment: 'Initial Payment',
    initialPaymentQuarterly: 'Initial Quarterly Payment',
    initialPaymentAnnually: 'Initial Annual Payment',
    finalPayment: 'Final Payment',
    finalPaymentQuarterly: 'Final Quarterly Payment',
    finalPaymentAnnually: 'Final Annual Payment',
    // Investment section
    recurrentFeesIncome: 'Recurrent Fees & Income',
    recurrentFees: 'Recurrent Fees',
    income: 'Income',
    feeName: 'Fee Name',
    amount: 'Amount',
    frequency: 'Frequency',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    annually: 'Annually',
    addFee: 'Add Fee',
    rent: 'Rent',
    occupancyRate: 'Occupancy Rate (%)',
    indexation: 'Indexation (%)',
    addRevenue: 'Add Revenue',
    revenueName: 'Revenue Name',
    propertyTax: 'Property Tax',
    coOwnershipFees: 'Co-ownership Fees',
    insurance: 'Insurance',
    maintenance: 'Maintenance',
    monthlyCashFlow: 'Average Monthly Cash Flow',
    rentToValueRatio: 'Rent-to-Value Ratio',
    summaryTable: 'Summary Table',
    year: 'Year',
    rentalIncome: 'Rental Income',
    annualFees: 'Annual Fees',
    loanPayment: 'Loan Payment',
    netCashFlow: 'Net Cash Flow',
    loanBalance: 'Loan Balance',
    totalYears: 'Total (10 yrs)',
    monthlyBreakdown: 'Monthly Breakdown',
    effectiveMonthlyRent: 'Effective Monthly Rent (with occupancy)',
    monthlyFees: 'Monthly Fees',
    monthlyLoanPayment: 'Monthly Loan Payment',
    netMonthlyCashFlow: 'Net Monthly Cash Flow',
    // Performance tab
    performance: 'Performance',
    internalRateOfReturn: 'Internal Rate of Return (IRR)',
    returnOnEquity: 'Return on Equity (ROE)',
    debtServiceCoverageRatio: 'Rent / Purchase Price with Works Ratio',
    annualCashFlow: 'Annual Cash Flow',
    cumulativeCashFlow: 'Cumulative Cash Flow',
    cashFlowSummary: 'Cash Flow Summary',
    showMonthly: 'Show Monthly',
    showYearly: 'Show Yearly',
    cashFlowFund: 'Cash Fund',
    totalCredit: 'Total Credit',
    monthlyRent: 'Monthly Rent',
    totalAnnualCost: 'Total Annual Cost',
    totalCashFlow: 'Total Cash Flow',
    balance: 'Balance',
    homeValue: 'Home Value',
    difference: 'Difference',
    investmentHorizon: 'Investment Horizon (months)',
    housePriceInflation: 'House Price Inflation (%)',
    saleProceeds: 'Sale Proceeds',
    capitalGain: 'Capital Gain on Sale',
    viewMode: 'View Mode',
    annual: 'Annual',
    quarterly: 'Quarterly',
    period: 'Period',
    sensitivity: 'Sensitivity',
    sensitivityParameter: 'Parameter (X)',
    sensitivityYAxis: 'Metric (Y)',
    interestRateParam: 'Interest Rate',
    equityPercentParam: '% Equity',
    occupancyRateParam: 'Occupancy Rate',
    durationParam: 'Investment Horizon (months)',
    irrMetric: 'Internal Rate of Return (IRR)',
    ncfMetric: 'Net Cash Flow (NCF)',
    npvMetric: 'Net Present Value (NPV)',
    roeMetric: 'Return on Equity (ROE)',
    irrAxis: 'Internal Rate of Return (%)'
  }
};

// Helper function to format numbers with thousand separators
const formatNumber = (num, decimals = 2) => {
  if (num === undefined || num === null) return '0';
  const numValue = parseFloat(num);

  // For numbers >= 1000, use only 1 decimal place
  if (Math.abs(numValue) >= 1000 && decimals === 2) {
    decimals = 1;
  }

  const rounded = typeof decimals === 'number' ? numValue.toFixed(decimals) : num.toString();
  const parts = rounded.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join('.');
};

function App() {
  // Helper function to calculate borrowing capacity at a given debt ratio
  const calculateCapacityAtRatio = (debtRatio) => {
    const futureRevenuesTotal = totalRevenues + totalFutureRevenues;
    const futureChargesTotal = totalCharges + totalFutureCharges;

    if (futureRevenuesTotal === 0) return 0;

    // Calculate max monthly payment based on debt ratio
    const maxMonthlyPayment = (futureRevenuesTotal * (debtRatio / 100)) - futureChargesTotal;

    if (maxMonthlyPayment <= 0) return 0;

    // Calculate borrowing capacity using loan formula
    const monthlyRate = parseFloat(loanParams.annualRate) / 100 / 12;
    const numMonths = parseFloat(loanParams.durationYears) * 12;

    if (monthlyRate === 0) {
      return maxMonthlyPayment * numMonths;
    }

    const capacity = maxMonthlyPayment * ((Math.pow(1 + monthlyRate, numMonths) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, numMonths)));

    return capacity;
  };

  // Helper function to calculate borrowing capacity with custom rate and duration
  const calculateCapacityWithRateAndDuration = (annualRate, durationYears) => {
    const futureRevenuesTotal = totalRevenues + totalFutureRevenues;
    const futureChargesTotal = totalCharges + totalFutureCharges;
    const debtRatio = parseFloat(loanParams.maxDebtRatio);

    if (futureRevenuesTotal === 0) return 0;

    // Calculate max monthly payment based on current debt ratio
    const maxMonthlyPayment = (futureRevenuesTotal * (debtRatio / 100)) - futureChargesTotal;

    if (maxMonthlyPayment <= 0) return 0;

    // Calculate borrowing capacity using loan formula with custom rate and duration
    const monthlyRate = annualRate / 100 / 12;
    const numMonths = durationYears * 12;

    if (monthlyRate === 0) {
      return maxMonthlyPayment * numMonths;
    }

    const capacity = maxMonthlyPayment * ((Math.pow(1 + monthlyRate, numMonths) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, numMonths)));

    return capacity;
  };

  // Helper function to calculate required apport for a given LTV
  const calculateRequiredApportAtLTV = (ltvPercent, borrowingCapacity) => {
    if (!borrowingCapacity || ltvPercent >= 100) return 0;

    // LTV = Loan / Purchase Price
    // Purchase Price = Loan + Apport
    // So: LTV = Loan / (Loan + Apport)
    // Solving for Apport:
    // LTV * (Loan + Apport) = Loan
    // LTV * Loan + LTV * Apport = Loan
    // LTV * Apport = Loan - LTV * Loan
    // LTV * Apport = Loan * (1 - LTV)
    // Apport = Loan * (1 - LTV) / LTV

    const ltvDecimal = ltvPercent / 100;
    const requiredApport = borrowingCapacity * (1 - ltvDecimal) / ltvDecimal;

    return requiredApport;
  };

  // Situation Financière state - for the new Situation Financière tab
  const [currentFinanceRevenues, setCurrentFinanceRevenues] = useState([
    { id: 1, name: 'Salaire Net', amount: 5000, percentage: 100 }
  ]);

  const [currentFinanceCharges, setCurrentFinanceCharges] = useState([
    { id: 1, name: 'Loyer', amount: 1200, percentage: 100 }
  ]);

  const [currentFinanceApport, setCurrentFinanceApport] = useState('');

  // Dynamic revenue items with coefficient
  const [revenues, setRevenues] = useState([
    { id: 1, label: 'Salaire Net', amount: '5000', coefficient: 100 }
  ]);

  // Dynamic future revenue items with coefficient
  const [futureRevenues, setFutureRevenues] = useState([]);

  // Dynamic charge items with coefficient
  const [charges, setCharges] = useState([
    { id: 1, label: 'Loyer', amount: '1200', coefficient: 100 }
  ]);

  // Dynamic future charge items with coefficient
  const [futureCharges, setFutureCharges] = useState([]);

  // Pondération for future revenues (from Investment tab)
  const [futureRevenuesPonderation, setFutureRevenuesPonderation] = useState(100);

  // Loan parameters
  const [loanParams, setLoanParams] = useState({
    annualRate: '3.5',
    durationYears: '20',
    maxDebtRatio: 33
  });

  // Quotité parameters
  const [quotiteParams, setQuotiteParams] = useState({
    maxContribution: '200000'
  });

  const [totalRevenues, setTotalRevenues] = useState(0);
  const [totalCharges, setTotalCharges] = useState(0);
  const [totalFutureRevenues, setTotalFutureRevenues] = useState(0);
  const [totalFutureCharges, setTotalFutureCharges] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('home'); // 'home', 'reader', 'summary', 'debt-ratio', 'borrowing', 'quotite', 'acquisition-financement', 'credit', 'recurrent-fees-income'
  const [aideSubTab, setAideSubTab] = useState('timeline'); // 'timeline', 'checklist-achat', 'documents'
  const [checkedDocs, setCheckedDocs] = useState({});
  const [checklistData, setChecklistData] = useState({ risks: {}, comments: {}, materiality: {}, actions: {} });
  const [checklistCollapsed, setChecklistCollapsed] = useState({});
  const [showInfoSections, setShowInfoSections] = useState(false); // false = hidden by default
  const checklistSections = [
    { id: 'prixAchat', number: 1, title: "Prix d'achat trop élevé", category: 'Risque Revente/Marché, Financier', note: null, criteria: [
      { id: 'prixM2', label: 'Prix au m²', low: '-10% vs marché', medium: 'Aligné marché', high: '+10% vs marché' },
      { id: 'localisation', label: 'Localisation', low: 'Quartier recherché', medium: 'Zone moyenne', high: 'Zone défavorisée' },
      { id: 'commodites', label: 'Commodités', low: 'Proche transports/écoles', medium: 'Accès correct', high: 'Éloigné services' },
      { id: 'demandeSecteur', label: 'Demande secteur', low: 'Forte demande', medium: 'Stabilité', high: 'Demande faible' },
    ]},
    { id: 'fraisNotaire', number: 2, title: 'Frais de notaire sous-estimés', category: 'Risque Financier', note: null, criteria: [
      { id: 'typeBien', label: 'Type de bien', low: 'Neuf (<5%)', medium: 'Ancien standard', high: 'Ancien de prestige' },
      { id: 'budgetPrevu', label: 'Budget prévu', low: '+10% de marge', medium: 'Budget juste', high: 'Pas de marge' },
      { id: 'fraisAnnexes', label: 'Frais annexes', low: 'Tous identifiés', medium: 'Partiellement', high: 'Non estimés' },
      { id: 'droitsMutation', label: 'Droits de mutation', low: 'Calcul précis', medium: 'Estimation', high: 'Inconnus' },
    ]},
    { id: 'vicesCaches', number: 3, title: 'Vices Cachés', category: 'Risque Opérationnel, Juridique, Financier', note: "Impacte indirecte car protégé par la loi. Impactes Administratif et temps perdu.", criteria: [
      { id: 'ageBatiment', label: 'Âge du bâtiment', low: '<10 ans', medium: '10-30 ans', high: '>30 ans' },
      { id: 'diagnostics', label: 'Diagnostics', low: 'Complets/récent', medium: 'Partiels', high: 'Absents/périmés' },
      { id: 'expertiseTechnique', label: 'Expertise technique', low: 'Expert accompagné', medium: 'Visite seule', high: 'Achat sans visite' },
      { id: 'historiqueTravaux', label: 'Historique travaux', low: 'Documenté', medium: 'Partiel', high: 'Inconnu' },
      { id: 'humiditeFissures', label: 'Humidité/fissures', low: 'Aucun signe', medium: 'Légers signes', high: 'Problèmes visibles' },
    ]},
    { id: 'problemesJuridiques', number: 4, title: 'Problèmes juridiques', category: 'Risque Juridique/Fiscal', note: "Impacte indirecte car protégé par la loi. Impactes Administratif et temps perdu.", criteria: [
      { id: 'reglesCopropriete', label: 'Règles copropriété', low: 'Souples', medium: 'Standard', high: 'Très restrictives' },
      { id: 'litigesEnCours', label: 'Litiges en cours', low: 'Aucun', medium: 'Mineurs', high: 'Litiges importants' },
      { id: 'servitudes', label: 'Servitudes', low: 'Aucune', medium: 'Connues', high: 'Non documentées' },
      { id: 'urbanisme', label: 'Urbanisme', low: 'Conforme', medium: 'Dérogation', high: 'Non conforme' },
      { id: 'qualiteSyndic', label: 'Qualité syndic', low: 'Syndic professionnel', medium: 'Gestion correcte', high: 'Syndic défaillant' },
    ]},
    { id: 'retardPret', number: 5, title: 'Retard/annulation prêt', category: 'Risque de Délai, Financier', note: null, criteria: [
      { id: 'apportPersonnel', label: 'Apport personnel', low: '>30%', medium: '10-30%', high: '<10%' },
      { id: 'stabiliteRevenus', label: 'Stabilité revenus', low: 'CDI >2 ans', medium: 'CDI <1 an', high: 'Revenus variables' },
      { id: 'endettement', label: 'Endettement', low: '<25%', medium: '25-35%', high: '>35%' },
      { id: 'noteBanque', label: 'Note banque', low: 'Excellente', medium: 'Moyenne', high: 'Faible' },
      { id: 'preAccord', label: 'Pré-accord', low: 'Obtenu', medium: 'En cours', high: 'Non demandé' },
    ]},
    { id: 'retardSignature', number: 6, title: "Retard signature de l'acte authentique", category: 'Risque de Délai', note: null, criteria: [
      { id: 'retardSignature', label: "Retard dans la signature", low: 'Délai respecté', medium: 'Léger retard', high: 'Retard important' },
    ]},
    { id: 'environnemental', number: 7, title: 'Risques Environnementaux', category: 'Risque Environnemental', note: null, criteria: [
      { id: 'pollution', label: 'Pollution sols', low: 'Zone saine', medium: 'Risque modéré', high: 'Site pollué' },
      { id: 'inondation', label: 'Zone inondable', low: 'Hors zone', medium: 'Zone modérée', high: 'Zone rouge' },
      { id: 'nuisances', label: 'Nuisances sonores', low: 'Zone calme', medium: 'Bruit modéré', high: 'Forte nuisance' },
      { id: 'dpe', label: 'Performance énergétique', low: 'DPE A-C', medium: 'DPE D-E', high: 'DPE F-G' },
    ]},
    { id: 'locatif', number: 8, title: 'Risques Locatifs', category: 'Risque Locatif', note: "Applicable principalement pour un investissement locatif.", criteria: [
      { id: 'vacanceLocative', label: 'Vacance locative', low: '<1 mois/an', medium: '1-3 mois/an', high: '>3 mois/an' },
      { id: 'encadrementLoyers', label: 'Encadrement loyers', low: 'Zone libre', medium: 'Zone tendue', high: 'Loyer plafonné' },
      { id: 'impayes', label: 'Risque impayés', low: 'Garantie GLI', medium: 'Caution solide', high: 'Sans garantie' },
      { id: 'turnover', label: 'Rotation locataire', low: 'Bail long terme', medium: 'Rotation modérée', high: 'Forte rotation' },
    ]},
    { id: 'fiscal', number: 9, title: 'Risques Fiscaux', category: 'Risque Fiscal', note: null, criteria: [
      { id: 'plusValueTaxe', label: 'Taxe plus-value', low: 'Exonération applicable', medium: 'Réduction partielle', high: 'Imposition maximale' },
      { id: 'regimeFiscal', label: 'Régime fiscal', low: 'Structure optimisée', medium: 'Régime standard', high: 'Non optimisé' },
      { id: 'taxeFonciere', label: 'Taxe foncière', low: 'Faible (<1% valeur)', medium: 'Modérée (1-2%)', high: 'Élevée (>2%)' },
      { id: 'evolutionFiscale', label: 'Évolution fiscale', low: 'Stable/favorable', medium: 'Incertaine', high: 'Défavorable' },
    ]},
  ];
  const updateChecklistRisk = (key, value) => setChecklistData(prev => ({ ...prev, risks: { ...prev.risks, [key]: prev.risks[key] === value ? null : value } }));
  const updateChecklistComment = (key, value) => setChecklistData(prev => ({ ...prev, comments: { ...prev.comments, [key]: value } }));
  const updateChecklistMateriality = (sectionId, value) => setChecklistData(prev => ({ ...prev, materiality: { ...prev.materiality, [sectionId]: prev.materiality[sectionId] === value ? null : value } }));
  const getChecklistAvg = (sectionId, criteria) => { const r = criteria.map(c => checklistData.risks[`${sectionId}_${c.id}`]).filter(v => v != null); return r.length > 0 ? r.reduce((a, b) => a + b, 0) / r.length : null; };
  const getRiskColor = (risk) => { if (risk == null) return 'rgba(156, 163, 175, 0.5)'; if (risk <= 2) return '#10b981'; if (risk <= 3.5) return '#f59e0b'; return '#ef4444'; };
  const getRiskBg = (risk) => { if (risk == null) return 'transparent'; if (risk <= 2) return 'rgba(16, 185, 129, 0.15)'; if (risk <= 3.5) return 'rgba(245, 158, 11, 0.15)'; return 'rgba(239, 68, 68, 0.15)'; };
  const getRiskLabel = (risk) => { if (risk == null) return '-'; if (risk <= 2) return 'Faible'; if (risk <= 3.5) return 'Moyen'; return 'Élevé'; };
  const updateChecklistAction = (key, value) => setChecklistData(prev => ({ ...prev, actions: { ...prev.actions, [key]: value } }));
  const toggleChecklistSection = (sectionId) => setChecklistCollapsed(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  const autoFillChecklist = () => {
    const newRisks = { ...checklistData.risks };
    const apportPct = totalAcquisition > 0 ? (financementData.apport / totalAcquisition * 100) : 0;
    if (apportPct > 30) newRisks['retardPret_apportPersonnel'] = 1;
    else if (apportPct >= 10) newRisks['retardPret_apportPersonnel'] = 3;
    else if (totalAcquisition > 0) newRisks['retardPret_apportPersonnel'] = 5;
    const totalIncome = revenues.reduce((s, r) => s + r.amount, 0) + futureRevenues.reduce((s, r) => s + r.amount, 0);
    const totalDebt = charges.reduce((s, c) => s + c.amount, 0) + futureCharges.reduce((s, c) => s + c.amount, 0);
    if (totalIncome > 0) {
      const dr = (totalDebt / totalIncome) * 100;
      if (dr < 25) newRisks['retardPret_endettement'] = 1;
      else if (dr <= 35) newRisks['retardPret_endettement'] = 3;
      else newRisks['retardPret_endettement'] = 5;
    }
    setChecklistData(prev => ({ ...prev, risks: newRisks }));
  };
  const checklistTemplates = [
    { id: 'ancien-centre', label: 'Ancien centre-ville', risks: {
      prixAchat_prixM2: 3, prixAchat_localisation: 1, prixAchat_commodites: 1, prixAchat_demandeSecteur: 2,
      fraisNotaire_typeBien: 4, fraisNotaire_budgetPrevu: 3, fraisNotaire_fraisAnnexes: 3, fraisNotaire_droitsMutation: 3,
      vicesCaches_ageBatiment: 5, vicesCaches_diagnostics: 3, vicesCaches_expertiseTechnique: 3, vicesCaches_historiqueTravaux: 3, vicesCaches_humiditeFissures: 3,
      environnemental_dpe: 4, environnemental_nuisances: 3
    }, materiality: { prixAchat: 4, fraisNotaire: 3, vicesCaches: 4, problemesJuridiques: 3, retardPret: 2, retardSignature: 2, environnemental: 3, locatif: 3, fiscal: 2 }},
    { id: 'neuf-peripherie', label: 'Neuf périphérie', risks: {
      prixAchat_prixM2: 2, prixAchat_localisation: 3, prixAchat_commodites: 4, prixAchat_demandeSecteur: 3,
      fraisNotaire_typeBien: 1, fraisNotaire_budgetPrevu: 2, fraisNotaire_fraisAnnexes: 2, fraisNotaire_droitsMutation: 1,
      vicesCaches_ageBatiment: 1, vicesCaches_diagnostics: 1, vicesCaches_expertiseTechnique: 2, vicesCaches_historiqueTravaux: 1, vicesCaches_humiditeFissures: 1,
      environnemental_dpe: 1, environnemental_nuisances: 2
    }, materiality: { prixAchat: 3, fraisNotaire: 2, vicesCaches: 1, problemesJuridiques: 2, retardPret: 3, retardSignature: 2, environnemental: 2, locatif: 3, fiscal: 2 }},
    { id: 'investissement-locatif', label: 'Investissement locatif', risks: {
      prixAchat_prixM2: 3, prixAchat_localisation: 2, prixAchat_demandeSecteur: 2,
      locatif_vacanceLocative: 3, locatif_encadrementLoyers: 3, locatif_impayes: 3, locatif_turnover: 3,
      fiscal_plusValueTaxe: 3, fiscal_regimeFiscal: 2, fiscal_taxeFonciere: 3, fiscal_evolutionFiscale: 3
    }, materiality: { prixAchat: 4, fraisNotaire: 2, vicesCaches: 3, problemesJuridiques: 3, retardPret: 3, retardSignature: 2, environnemental: 2, locatif: 5, fiscal: 4 }},
  ];
  const applyChecklistTemplate = (template) => {
    setChecklistData(prev => ({ ...prev, risks: { ...prev.risks, ...template.risks }, materiality: { ...prev.materiality, ...template.materiality } }));
  };
  const exportChecklistPDF = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    let html = '<html><head><title>Checklist Achat - Rapport de Risques</title><style>';
    html += 'body{font-family:Arial,sans-serif;padding:20px;color:#333}h1{color:#1e3a8a;border-bottom:2px solid #1e3a8a;padding-bottom:8px}';
    html += 'h2{color:#374151;margin-top:24px}table{width:100%;border-collapse:collapse;margin:12px 0}';
    html += 'th{background:#1e3a8a;color:#fff;padding:8px 12px;text-align:left;font-size:0.85em}';
    html += 'td{border:1px solid #ddd;padding:6px 10px;font-size:0.85em}';
    html += '.risk-low{background:#d1fae5;color:#065f46}.risk-med{background:#fef3c7;color:#92400e}.risk-high{background:#fee2e2;color:#991b1b}';
    html += '.score{font-weight:bold;text-align:center}';
    html += '@media print{body{padding:0}}</style></head><body>';
    html += '<h1>Checklist Achat - Rapport de Risques</h1>';
    html += '<p>Date: ' + new Date().toLocaleDateString('fr-FR') + '</p>';
    html += '<h2>Résumé</h2><table><tr><th>Phase</th><th>Événement</th><th>Catégorie</th><th>Probabilité</th><th>Matérialité</th><th>Score (P×M)</th></tr>';
    checklistSections.forEach(s => {
      const avg = getChecklistAvg(s.id, s.criteria);
      const mat = checklistData.materiality[s.id];
      const score = (avg && mat) ? (avg * mat).toFixed(1) : '-';
      const cls = avg == null ? '' : avg <= 2 ? 'risk-low' : avg <= 3.5 ? 'risk-med' : 'risk-high';
      html += '<tr><td>Achat</td><td>' + s.title + '</td><td>' + s.category + '</td><td class="score ' + cls + '">' + (avg != null ? avg.toFixed(1) : '-') + '</td><td class="score">' + (mat || '-') + '</td><td class="score">' + score + '</td></tr>';
    });
    html += '</table>';
    checklistSections.forEach(s => {
      html += '<h2>' + s.number + '. ' + s.title + '</h2>';
      if (s.note) html += '<p><em>' + s.note + '</em></p>';
      html += '<table><tr><th>Critère</th><th>Faible</th><th>Moyen</th><th>Élevé</th><th>Risque</th><th>Action</th><th>Commentaire</th></tr>';
      s.criteria.forEach(c => {
        const key = s.id + '_' + c.id;
        const risk = checklistData.risks[key];
        const cls = risk == null ? '' : risk <= 2 ? 'risk-low' : risk <= 3.5 ? 'risk-med' : 'risk-high';
        html += '<tr><td>' + c.label + '</td><td>' + c.low + '</td><td>' + c.medium + '</td><td>' + c.high + '</td>';
        html += '<td class="score ' + cls + '">' + (risk || '-') + '</td>';
        html += '<td>' + (checklistData.actions[key] || '-') + '</td>';
        html += '<td>' + (checklistData.comments[key] || '-') + '</td></tr>';
      });
      html += '</table>';
    });
    html += '</body></html>';
    w.document.write(html);
    w.document.close();
    w.print();
  };
  const [expandedCategories, setExpandedCategories] = useState({
    home: false,
    reader: false,
    endettement: false,
    investment: false,
    portefeuille: false
  });

  // Reader tab state
  const [propertyUrl, setPropertyUrl] = useState('');
  const [extractedData, setExtractedData] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState('');
  const [expandedDescriptions, setExpandedDescriptions] = useState({
    restantVivre: false,
    tauxEndettement: false,
    quotite: false
  });

  // Language state
  const [language, setLanguage] = useState('fr'); // 'fr' or 'en'

  // Sidebar collapsed state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Saved investments state
  const [savedInvestments, setSavedInvestments] = useState([]);

  // Current project state (for Home tab)
  const [currentProject, setCurrentProject] = useState(null); // null = show project list, object = show project detail
  const [savedProjects, setSavedProjects] = useState([]);
  const [portfolioProjects, setPortfolioProjects] = useState([]); // Portfolio projects with {key, startDate}
  const [showPortfolioAnalysis, setShowPortfolioAnalysis] = useState(false); // Show portfolio analysis view
  const [portfolioAnalysisTab, setPortfolioAnalysisTab] = useState('summary'); // 'summary' or 'cashflow'
  const [portfolioAddModal, setPortfolioAddModal] = useState({ show: false, projectKey: null }); // Modal for adding to portfolio
  const [portfolioStartDate, setPortfolioStartDate] = useState(''); // Start date input for portfolio modal
  const [portfolioCashFlowView, setPortfolioCashFlowView] = useState('annual'); // 'monthly', 'quarterly', 'annual'
  const [currentInvestmentName, setCurrentInvestmentName] = useState('');
  const [previewModal, setPreviewModal] = useState({ show: false, project: null }); // Preview modal state

  // Chart hover state
  const [hoveredSegment, setHoveredSegment] = useState(null);

  // Frais sub-tab state
  const [fraisSubTab, setFraisSubTab] = useState('acte-achat'); // 'acte-achat', 'hypothecaire', 'summary'

  // Recurrent Fees state
  const [recurrentFees, setRecurrentFees] = useState([
    { id: 1, name: 'propertyTax', amount: 1500, frequency: 'annually' },
    { id: 2, name: 'coOwnershipFees', amount: 1000, frequency: 'quarterly' },
    { id: 3, name: 'insurance', amount: 500, frequency: 'annually' },
    { id: 4, name: 'maintenance', amount: 1000, frequency: 'annually' }
  ]);

  // Property Revenues state
  const [propertyRevenues, setPropertyRevenues] = useState([
    { id: 1, name: 'rent', amount: 1500, frequency: 'monthly', occupancyRate: 100, indexation: 0, startLagMonths: 0 }
  ]);

  // Performance parameters
  const [performanceParams, setPerformanceParams] = useState({
    investmentHorizonMonths: 120, // 10 years in months
    housePriceInflation: 2
  });

  // Calculated Performance Totale values (updated by Performance tab)
  const [calculatedPerformanceTotale, setCalculatedPerformanceTotale] = useState({
    ncf: 0,
    tri: 0
  });

  // Estimated home value after renovation (defaults to house price + renovation)
  const [estimatedHomeValue, setEstimatedHomeValue] = useState(0);

  // Performance view mode
  const [performanceViewMode, setPerformanceViewMode] = useState('annual'); // 'monthly', 'quarterly', 'annual'

  // Amortization table view mode
  const [amortizationViewMode, setAmortizationViewMode] = useState('monthly'); // 'monthly', 'quarterly', 'annually'

  // Sensitivity analysis parameter
  const [sensitivityParam, setSensitivityParam] = useState('interestRate'); // 'interestRate', 'equityPercent', 'occupancyRate', 'duration'
  const [sensitivityYMetric, setSensitivityYMetric] = useState('irr'); // 'irr', 'ncf', 'npv', 'roe'
  const [sensitivityHoveredPoint, setSensitivityHoveredPoint] = useState(null); // { index, x, y, screenX, screenY }
  const [borrowingHoveredPoint, setBorrowingHoveredPoint] = useState(null); // { index, x, y, screenX, screenY }
  const [borrowingSensitivityParam, setBorrowingSensitivityParam] = useState('debtRatio'); // 'debtRatio', 'duration', 'annualRate', 'revenues'
  const [interestTableHovered, setInterestTableHovered] = useState(null); // { rate, duration, interest, monthlyPayment, screenX, screenY }
  const [interestTableMetric, setInterestTableMetric] = useState('interest'); // 'interest' or 'monthlyPayment'

  // Track unsaved modifications
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedChangesPopup, setShowUnsavedChangesPopup] = useState(false);
  const [originalProjectData, setOriginalProjectData] = useState(null); // Store original data when project is loaded

  // Collapsible sections state
  const [collapsedSections, setCollapsedSections] = useState({
    performanceAfterRenovation: false,
    performanceLocative: false,
    performanceTotale: false,
    cashFlow: false,
    sensitivity: false,
    inputsExogenes: false,
    inputsEndogenes: false,
    ncfDetail: true
  });

  // Tooltip state for performance charts
  const [chartTooltip, setChartTooltip] = useState(null); // { x, y, data, type }

  // Info modal state for performance metrics
  const [infoModal, setInfoModal] = useState(null); // { metric, title, description, formula, interpretation }

  // Notary Fees Calculator state
  const [notaryFeesCalc, setNotaryFeesCalc] = useState({
    purchasePrice: 500000,
    region: 'brussels', // 'brussels', 'wallonia', 'flanders'
    propertyType: 'house', // 'house' (maison/appartement) or 'land' (terrain à bâtir)
    isOnlyHome: true, // Propre et unique habitation
    registrationType: 'registration', // 'registration' (droits d'enregistrement), 'vat' (TVA), 'land-registration' (terrain avec droits+TVA)
    firstHome: true,
    brusselsAbatement: true
  });

  // Mortgage Fees Calculator state
  const [mortgageFeesCalc, setMortgageFeesCalc] = useState({
    loanAmount: 800000,
    mortgagePrincipal: 760000,
    mortgageAccessories: 80000,
    isPrimaryResidence: true,
    isMainDeed: true
  });

  // Chart hover state
  const [chartHoverIndex, setChartHoverIndex] = useState(null);

  // Acquisition & Financement state
  const [acquisitionData, setAcquisitionData] = useState({
    prixAchat: 500000,
    fraisNotaire: 60000,  // 12% du prix d'achat (frais d'acquisition)
    fraisAgence: 10000,   // 2% du prix d'achat (frais d'hypothèque)
    travaux: 50000,       // 10% du prix d'achat
    autresFrais: 0
  });

  const [financementData, setFinancementData] = useState({
    apport: 124000,  // 20% du coût total (620 000 €)
    emprunt: 496000  // 80% du coût total
  });

  // Credit state
  const [creditData, setCreditData] = useState({
    amount: 496000,  // Montant de l'emprunt (80% du coût total)
    durationYears: 20,
    type: 'amortizing',
    interestRate: 3.5,
    interestOnlyMonths: 60, // Months before amortization starts (for interest-only credits)
    crescendoRate: 2, // Annual growth rate for crescendo credits (%)
    paymentFrequency: 'monthly' // Payment frequency: 'monthly', 'quarterly', 'annually'
  });

  // Credit Travaux state
  const [creditTravauxData, setCreditTravauxData] = useState({
    amount: 0,
    durationYears: 10,
    type: 'amortizing',
    interestRate: 3.5,
    interestOnlyMonths: 12,
    crescendoRate: 2,
    paymentFrequency: 'monthly'
  });
  const [amortizationCreditView, setAmortizationCreditView] = useState('immobilier'); // 'immobilier' | 'travaux'
  const [hasCustomCreditSplit, setHasCustomCreditSplit] = useState(false);
  const [customApportSplit, setCustomApportSplit] = useState(null); // null = auto, {achat: n, travaux: n} = manual

  // Calculate Belgian Notary Fees - EXACT formulas from notaire.be
  const calculateNotaryFees = (purchasePrice, region, propertyType, isOnlyHome, registrationType, brusselsAbatement = false) => {
    let registrationFees = 0; // Droits d'enregistrement
    const annexFees = 100; // Droit pour les annexes - FIXED
    let notaryFees = 0; // Honoraires
    const adminFees = 855; // Frais administratifs - FIXED
    const disbursements = 304; // Débours - FIXED
    const mortgageTranscription = 285; // Transcription hypothécaire - FIXED
    const writingFees = 100; // Droit d'écriture - FIXED
    let vat = 0; // TVA

    // Calculate registration fees using EXACT formulas from notaire.be
    if (registrationType === 'registration' || registrationType === 'land-registration') {
      if (region === 'brussels') {
        if (isOnlyHome && purchasePrice <= 600000 && brusselsAbatement) {
          // BXL-E-A-Z: Own home with 25,000 EUR abatement (only if user selected it)
          registrationFees = Math.max(50, (purchasePrice * 0.125) - 25000);
        } else {
          // BXL-E-X-or-B or BXL-E-high: Standard 12.5%
          registrationFees = Math.max(50, purchasePrice * 0.125);
        }
      } else if (region === 'wallonia') {
        if (isOnlyHome) {
          // WL-E-A: 3% with rounding
          registrationFees = Math.max(50, Math.round((((purchasePrice * 0.03) + 0.005) * 100)) / 100);
        } else {
          // WL-E-B: 12.5% with rounding
          registrationFees = Math.max(50, Math.round((((purchasePrice * 0.125) + 0.005) * 100)) / 100);
        }
      } else { // flanders
        if (isOnlyHome) {
          // VL-E-A: 2% with 1,867 EUR reduction if below threshold
          // Using 220,000 threshold (non-core city default)
          if (purchasePrice > 220000) {
            registrationFees = Math.max(50, purchasePrice * 0.02);
          } else {
            registrationFees = Math.max(50, (purchasePrice * 0.02) - 1867);
          }
        } else {
          // VL-E-B: 12%
          registrationFees = Math.max(50, purchasePrice * 0.12);
        }
      }
    }

    // Calculate notary fees using EXACT progressive scales from notaire.be
    const calculateProgressiveNotaryFees = (amount, isOwnHome) => {
      let fees = 0;
      const fixedAmount = isOwnHome ? 257 : 285;

      // Tiers from Jbis (own home) or Jnew (not own home)
      const tiers = isOwnHome ? [
        { from: 0, to: 7500, rate: 0.025 },
        { from: 7500, to: 17500, rate: 0.025 },
        { from: 17500, to: 30000, rate: 0.02 },
        { from: 30000, to: 45495, rate: 0.015 },
        { from: 45495, to: 64090, rate: 0.005 },
        { from: 64090, to: 250095, rate: 0.00485 },
        { from: 250095, to: 500000, rate: 0.003 },
        { from: 500000, to: Infinity, rate: 0.002 }
      ] : [
        { from: 0, to: 7500, rate: 0.025 },
        { from: 7500, to: 17500, rate: 0.025 },
        { from: 17500, to: 30000, rate: 0.0175 },
        { from: 30000, to: 45495, rate: 0.0171 },
        { from: 45495, to: 64090, rate: 0.0114 },
        { from: 64090, to: 250095, rate: 0.0057 },
        { from: 250095, to: 500000, rate: 0.002 },
        { from: 500000, to: Infinity, rate: 0.002 }
      ];

      for (const tier of tiers) {
        if (amount > tier.from) {
          const taxableInTier = Math.min(amount, tier.to) - tier.from;
          fees += taxableInTier * tier.rate;
        }
        if (amount <= tier.to) break;
      }

      fees += fixedAmount;

      // Adjustment for small amounts
      if (amount < 20001) {
        fees -= isOwnHome ? 86 : 85;
      }

      return Math.max(8.48, fees);
    };

    notaryFees = calculateProgressiveNotaryFees(purchasePrice, isOnlyHome);

    // Calculate TVA
    // If "Achat avec TVA", add 21% on purchase price + 21% on services
    // If "Achat avec droits d'enregistrement", only 21% on services
    if (registrationType === 'vat') {
      // VAT on purchase (new construction) + VAT on services
      vat = (purchasePrice * 0.21) + ((notaryFees + adminFees + disbursements + writingFees) * 0.21);
    } else {
      // Only VAT on services
      vat = (notaryFees + adminFees + disbursements + writingFees) * 0.21;
    }

    const total = registrationFees + annexFees + notaryFees + adminFees +
                  disbursements + mortgageTranscription + writingFees + vat;

    return {
      registrationFees: Math.round(registrationFees * 100) / 100,
      annexFees: Math.round(annexFees * 100) / 100,
      notaryFees: Math.round(notaryFees * 100) / 100,
      adminFees: Math.round(adminFees * 100) / 100,
      disbursements: Math.round(disbursements * 100) / 100,
      mortgageTranscription: Math.round(mortgageTranscription * 100) / 100,
      writingFees: Math.round(writingFees * 100) / 100,
      vat: Math.round(vat * 100) / 100,
      total: Math.round(total * 100) / 100
    };
  };

  // Calculate Mortgage Fees - EXACT formulas from notaire.be
  const calculateMortgageFees = (loanAmount, mortgagePrincipal, mortgageAccessories, isPrimaryResidence, isMainDeed) => {
    const totalMortgage = mortgagePrincipal + mortgageAccessories;

    // 1. Registration Tax - 1% of total mortgage
    const registrationTax = totalMortgage * 0.01;

    // 2. Registration Appendix Fee - Fixed
    const appendixFee = 100;

    // 3. Mortgage Right - 0.3% of total mortgage
    const mortgageRight = totalMortgage * 0.003;

    // 4. Mortgage Remuneration - Based on threshold
    const mortgageRemuneration = totalMortgage <= 300000 ? 270 : 1160;

    // 5. Notary Fees - Complex tiered calculation
    const calculateNotaryFeesHypotheque = (amount, isPrimary) => {
      let fees = 0;

      if (isPrimary) {
        // Gbis schedule for primary residence
        const tiers = [
          { from: 0, to: 7500, rate: 0.01368 },
          { from: 7500, to: 17500, rate: 0.01094 },
          { from: 17500, to: 30000, rate: 0.0073 },
          { from: 30000, to: 45495, rate: 0.00547 },
          { from: 45495, to: 64090, rate: 0.00365 },
          { from: 64090, to: 250095, rate: 0.00182 },
          { from: 250095, to: 500000, rate: 0.00036 },
          { from: 500000, to: Infinity, rate: 0.00036 }
        ];

        for (const tier of tiers) {
          if (amount > tier.from) {
            const taxableInTier = Math.min(amount, tier.to) - tier.from;
            fees += taxableInTier * tier.rate;
          }
          if (amount <= tier.to) break;
        }
      } else {
        // Gnew schedule for non-primary residence
        const fixedAmount = 285;
        const tiers = [
          { from: 0, to: 7500, rate: 0.0075 },
          { from: 7500, to: 17500, rate: 0.0065 },
          { from: 17500, to: 30000, rate: 0.005 },
          { from: 30000, to: 45495, rate: 0.004 },
          { from: 45495, to: 64095, rate: 0.004 },
          { from: 64095, to: 250095, rate: 0.00228 },
          { from: 250095, to: 500000, rate: 0.00046 },
          { from: 500000, to: Infinity, rate: 0.00046 }
        ];

        fees = fixedAmount;
        for (const tier of tiers) {
          if (amount > tier.from) {
            const taxableInTier = Math.min(amount, tier.to) - tier.from;
            fees += taxableInTier * tier.rate;
          }
          if (amount <= tier.to) break;
        }

        // Apply reductions for small amounts
        if (amount <= 10000) {
          fees -= 200;
        } else if (amount <= 20000) {
          fees -= 86;
        }
      }

      return Math.max(8.55, fees);
    };

    const notaryFees = calculateNotaryFeesHypotheque(loanAmount, isPrimaryResidence);

    // 6. Administrative Fees - Based on main deed status
    const adminFees = isMainDeed ? 855 : 627;

    // 7. Disbursements - Based on main deed status
    const disbursements = isMainDeed ? 304 : 50;

    // 8. Writing Fee - Fixed
    const writingFee = 100;

    // 9. VAT - 21% on notary fees + admin + disbursements + writing
    const vat = (notaryFees + adminFees + disbursements + writingFee) * 0.21;

    const total = registrationTax + appendixFee + mortgageRight + mortgageRemuneration +
                  notaryFees + adminFees + disbursements + writingFee + vat;

    return {
      registrationTax: Math.round(registrationTax * 100) / 100,
      appendixFee: Math.round(appendixFee * 100) / 100,
      mortgageRight: Math.round(mortgageRight * 100) / 100,
      mortgageRemuneration: Math.round(mortgageRemuneration * 100) / 100,
      notaryFees: Math.round(notaryFees * 100) / 100,
      adminFees: Math.round(adminFees * 100) / 100,
      disbursements: Math.round(disbursements * 100) / 100,
      writingFee: Math.round(writingFee * 100) / 100,
      vat: Math.round(vat * 100) / 100,
      total: Math.round(total * 100) / 100
    };
  };

  // Calculate notary fees breakdown
  const notaryFeesBreakdown = calculateNotaryFees(
    notaryFeesCalc.purchasePrice,
    notaryFeesCalc.region,
    notaryFeesCalc.propertyType,
    notaryFeesCalc.isOnlyHome,
    notaryFeesCalc.registrationType,
    notaryFeesCalc.brusselsAbatement
  );

  // Calculate mortgage fees breakdown
  const mortgageFeesBreakdown = calculateMortgageFees(
    mortgageFeesCalc.loanAmount,
    mortgageFeesCalc.mortgagePrincipal,
    mortgageFeesCalc.mortgageAccessories,
    mortgageFeesCalc.isPrimaryResidence,
    mortgageFeesCalc.isMainDeed
  );

  // Calculate credit amortization
  const calculateAmortization = (creditDataParam = creditData) => {
    const { amount, durationYears, type, interestRate, interestOnlyMonths: interestOnlyMonthsParam, crescendoRate, paymentFrequency } = creditDataParam;

    if (!amount || !durationYears || !interestRate || amount <= 0 || interestRate <= 0) {
      return {
        schedule: [],
        monthlyPayment: 0,
        totalCost: 0,
        totalInterest: 0,
        interestOnlyPayment: 0,
        amortizingPayment: 0,
        totalInterestOnlyPeriodCost: 0,
        initialPayment: 0,
        finalPayment: 0,
        paymentFrequency: paymentFrequency || 'monthly',
        periodicPayment: 0,
        monthlyEquivalentPayment: 0
      };
    }

    // Determine payment frequency multiplier
    const frequencyMultiplier = paymentFrequency === 'annually' ? 12 : paymentFrequency === 'quarterly' ? 3 : 1;
    const paymentsPerYear = paymentFrequency === 'annually' ? 1 : paymentFrequency === 'quarterly' ? 4 : 12;

    // Calculate the periodic rate based on payment frequency
    const annualRate = interestRate / 100;
    const monthlyRate = annualRate / 12;

    // For quarterly and annual, we compound the monthly rate
    const periodicRate = paymentFrequency === 'annually'
      ? Math.pow(1 + monthlyRate, 12) - 1  // Compound 12 months
      : paymentFrequency === 'quarterly'
        ? Math.pow(1 + monthlyRate, 3) - 1  // Compound 3 months
        : monthlyRate;
    const numPayments = durationYears * paymentsPerYear; // Number of periodic payments
    const totalMonths = durationYears * 12; // Total months for the loan
    let schedule = [];
    let monthlyPayment = 0;
    let totalCost = 0;

    if (type === 'amortizing') {
      // Standard amortizing loan (constant periodic payment)
      const periodicPayment = (amount * periodicRate * Math.pow(1 + periodicRate, numPayments)) /
                              (Math.pow(1 + periodicRate, numPayments) - 1);

      let remainingPrincipal = amount;

      for (let i = 1; i <= numPayments; i++) {
        const interestPayment = remainingPrincipal * periodicRate;
        const principalPayment = periodicPayment - interestPayment;
        remainingPrincipal -= principalPayment;

        schedule.push({
          month: i,
          payment: periodicPayment,
          principal: principalPayment,
          interest: interestPayment,
          remainingBalance: Math.max(0, remainingPrincipal)
        });
      }

      totalCost = periodicPayment * numPayments;
      monthlyPayment = periodicPayment / frequencyMultiplier; // Monthly equivalent

    } else if (type === 'interest-only') {
      // Interest-only loan with lag period
      // Phase 1: Interest only for interestOnlyPeriods
      // Phase 2: Amortizing for remaining periods

      const interestOnlyPeriods = Math.min(Math.ceil((interestOnlyMonthsParam || 60) / frequencyMultiplier), numPayments);
      const amortizingPeriods = numPayments - interestOnlyPeriods;

      const periodicInterestOnlyPayment = amount * periodicRate;
      const totalInterestOnlyPeriodCost = periodicInterestOnlyPayment * interestOnlyPeriods;

      // Phase 1: Interest only period
      for (let i = 1; i <= interestOnlyPeriods; i++) {
        schedule.push({
          month: i,
          payment: periodicInterestOnlyPayment,
          principal: 0,
          interest: periodicInterestOnlyPayment,
          remainingBalance: amount
        });
      }

      // Phase 2: Amortizing period
      if (amortizingPeriods > 0) {
        const periodicAmortizingPayment = (amount * periodicRate * Math.pow(1 + periodicRate, amortizingPeriods)) /
                                          (Math.pow(1 + periodicRate, amortizingPeriods) - 1);

        let remainingPrincipal = amount;

        for (let i = interestOnlyPeriods + 1; i <= numPayments; i++) {
          const interestPayment = remainingPrincipal * periodicRate;
          const principalPayment = periodicAmortizingPayment - interestPayment;
          remainingPrincipal -= principalPayment;

          schedule.push({
            month: i,
            payment: periodicAmortizingPayment,
            principal: principalPayment,
            interest: interestPayment,
            remainingBalance: Math.max(0, remainingPrincipal)
          });
        }

        monthlyPayment = periodicAmortizingPayment / frequencyMultiplier; // Monthly equivalent
        totalCost = (periodicInterestOnlyPayment * interestOnlyPeriods) + (periodicAmortizingPayment * amortizingPeriods);

        // Return additional metrics for interest-only with both phases
        const totalInterest = schedule.reduce((sum, item) => sum + item.interest, 0);
        const periodicPayment = periodicAmortizingPayment;
        const monthlyEquivalentPayment = monthlyPayment;

        return {
          schedule,
          monthlyPayment,
          totalCost,
          totalInterest,
          interestOnlyPayment: periodicInterestOnlyPayment,
          amortizingPayment: periodicAmortizingPayment,
          totalInterestOnlyPeriodCost,
          paymentFrequency: paymentFrequency || 'monthly',
          periodicPayment,
          monthlyEquivalentPayment
        };
      } else {
        // If all period is interest only
        monthlyPayment = periodicInterestOnlyPayment / frequencyMultiplier;
        totalCost = periodicInterestOnlyPayment * numPayments + amount;

        // Add final payment with principal
        schedule[schedule.length - 1] = {
          ...schedule[schedule.length - 1],
          payment: periodicInterestOnlyPayment + amount,
          principal: amount,
          remainingBalance: 0
        };
      }

    } else if (type === 'bullet') {
      // Bullet loan (pay only interest, principal at end)
      const periodicPayment = amount * periodicRate;

      for (let i = 1; i <= numPayments; i++) {
        const isLastPayment = i === numPayments;
        const payment = isLastPayment ? periodicPayment + amount : periodicPayment;

        schedule.push({
          month: i,
          payment: payment,
          principal: isLastPayment ? amount : 0,
          interest: periodicPayment,
          remainingBalance: isLastPayment ? 0 : amount
        });
      }

      totalCost = (periodicPayment * numPayments) + amount;
      monthlyPayment = periodicPayment / frequencyMultiplier; // Monthly equivalent

    } else if (type === 'crescendo') {
      // Crescendo loan (payments increase annually by crescendoRate%)
      // Calculate initial payment using iterative method

      const periodicGrowthRate = Math.pow(1 + (crescendoRate / 100), 1/paymentsPerYear) - 1;
      let remainingPrincipal = amount;
      let totalPaid = 0;

      // Start with an estimated initial payment and iterate to find the correct value
      let initialPeriodicPayment = (amount * periodicRate * Math.pow(1 + periodicRate, numPayments)) /
                                   (Math.pow(1 + periodicRate, numPayments) - crescendoRate/100);

      // Build the schedule with increasing payments
      for (let i = 1; i <= numPayments; i++) {
        const currentPayment = initialPeriodicPayment * Math.pow(1 + periodicGrowthRate, i - 1);
        const interestPayment = remainingPrincipal * periodicRate;
        const principalPayment = Math.max(0, currentPayment - interestPayment);

        remainingPrincipal = Math.max(0, remainingPrincipal - principalPayment);
        totalPaid += currentPayment;

        schedule.push({
          month: i,
          payment: currentPayment,
          principal: principalPayment,
          interest: interestPayment,
          remainingBalance: remainingPrincipal
        });
      }

      // Adjust to ensure principal is fully paid
      if (remainingPrincipal > 0.01) {
        // Add remaining principal to last payment
        const lastMonth = schedule[schedule.length - 1];
        lastMonth.principal += remainingPrincipal;
        lastMonth.payment += remainingPrincipal;
        totalPaid += remainingPrincipal;
        lastMonth.remainingBalance = 0;
      }

      monthlyPayment = initialPeriodicPayment / frequencyMultiplier; // Monthly equivalent
      totalCost = totalPaid;

      // Return with initial and final payment info
      const finalPeriodicPayment = schedule[schedule.length - 1].payment;
      const periodicPaymentValue = initialPeriodicPayment;
      const monthlyEquivalentPayment = monthlyPayment;

      return {
        schedule,
        monthlyPayment,
        totalCost,
        totalInterest: totalPaid - amount,
        interestOnlyPayment: 0,
        amortizingPayment: 0,
        totalInterestOnlyPeriodCost: 0,
        initialPayment: initialPeriodicPayment,
        finalPayment: finalPeriodicPayment,
        paymentFrequency: paymentFrequency || 'monthly',
        periodicPayment: periodicPaymentValue,
        monthlyEquivalentPayment
      };
    }

    const totalInterest = totalCost - amount;
    const periodicPaymentValue = monthlyPayment * frequencyMultiplier;
    const monthlyEquivalentPayment = monthlyPayment;

    return {
      schedule,
      monthlyPayment,
      totalCost,
      totalInterest,
      interestOnlyPayment: 0,
      amortizingPayment: 0,
      totalInterestOnlyPeriodCost: 0,
      initialPayment: 0,
      finalPayment: 0,
      paymentFrequency: paymentFrequency || 'monthly',
      periodicPayment: periodicPaymentValue,
      monthlyEquivalentPayment
    };
  };

  const creditAmortization = calculateAmortization(creditData);
  const creditTravauxAmortization = calculateAmortization(creditTravauxData);

  // Calculate acquisition total
  const totalAcquisition = acquisitionData.prixAchat + acquisitionData.fraisNotaire +
                           acquisitionData.fraisAgence + acquisitionData.travaux +
                           acquisitionData.autresFrais;

  // Calculate financing total (actual Equity + Debt from Plan de Financement)
  const actualEquity = customApportSplit
    ? (customApportSplit.achat || 0) + (customApportSplit.travaux || 0)
    : (quotiteParams.maxContribution || 0);
  const actualDebt = (creditData.amount || 0) + (creditTravauxData.amount || 0);
  const totalFinancement = actualEquity + actualDebt;

  // Calculate financing ratios
  const ltv = totalAcquisition > 0 ? (actualDebt / totalAcquisition * 100) : 0;
  const equityRatio = totalAcquisition > 0 ? (actualEquity / totalAcquisition * 100) : 0;
  const difference = totalFinancement - totalAcquisition;

  // Calculate totals with coefficients
  useEffect(() => {
    const total = revenues.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0;
      const coeff = parseFloat(item.coefficient) || 0;
      return sum + (amount * coeff / 100);
    }, 0);
    setTotalRevenues(total);
  }, [revenues]);

  useEffect(() => {
    const total = charges.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0;
      const coeff = parseFloat(item.coefficient) || 0;
      return sum + (amount * coeff / 100);
    }, 0);
    setTotalCharges(total);
  }, [charges]);

  // Calculate totalFutureRevenues from propertyRevenues (Investment/Charges récurrentes & Revenus)
  // Uses futureRevenuesPonderation instead of individual occupancy rates
  useEffect(() => {
    const calculateMonthlyAmount = (amount, frequency) => {
      if (frequency === 'monthly') return amount;
      if (frequency === 'quarterly') return amount / 3;
      if (frequency === 'annually') return amount / 12;
      return 0;
    };

    const total = propertyRevenues.reduce((sum, rev) => {
      const monthlyAmount = calculateMonthlyAmount(parseFloat(rev.amount) || 0, rev.frequency);
      // Apply global futureRevenuesPonderation instead of individual occupancy rate
      const effectiveMonthly = monthlyAmount * (futureRevenuesPonderation / 100);
      return sum + effectiveMonthly;
    }, 0);
    setTotalFutureRevenues(total);
  }, [propertyRevenues, futureRevenuesPonderation]);

  useEffect(() => {
    const total = futureCharges.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0;
      const coeff = parseFloat(item.coefficient) || 0;
      return sum + (amount * coeff / 100);
    }, 0);
    setTotalFutureCharges(total);
  }, [futureCharges]);

  // Sync Situation Financière Apport Disponible to quotiteParams.maxContribution
  useEffect(() => {
    const apportValue = parseFloat(currentFinanceApport) || 0;
    setQuotiteParams(prev => ({...prev, maxContribution: apportValue}));
  }, [currentFinanceApport]);

  // Sync Apport from Inputs Exogènes to Inputs Endogènes
  useEffect(() => {
    const apportPersonnel = parseFloat(quotiteParams.maxContribution) || 0;
    setFinancementData(prev => ({...prev, apport: apportPersonnel}));
  }, [quotiteParams.maxContribution]);

  // Calculate Emprunt as difference between Total Acquisition and Apport
  useEffect(() => {
    const totalAcq = acquisitionData.prixAchat + acquisitionData.fraisNotaire +
                     acquisitionData.fraisAgence + acquisitionData.travaux +
                     acquisitionData.autresFrais;
    const apport = financementData.apport || 0;
    const calculatedEmprunt = Math.max(0, totalAcq - apport);

    setFinancementData(prev => ({...prev, emprunt: calculatedEmprunt}));
    if (!hasCustomCreditSplit) {
      const travauxCreditAmount = Math.round((acquisitionData.travaux || 0) / 2);
      setCreditData(prev => ({...prev, amount: Math.max(0, calculatedEmprunt - travauxCreditAmount)}));
    } else {
      // Respect custom split: adjust creditData.amount = emprunt - creditTravaux
      setCreditData(prev => ({...prev, amount: Math.max(0, calculatedEmprunt - (creditTravauxData.amount || 0))}));
    }
  }, [acquisitionData.prixAchat, acquisitionData.fraisNotaire, acquisitionData.fraisAgence,
      acquisitionData.travaux, acquisitionData.autresFrais, financementData.apport, hasCustomCreditSplit, creditTravauxData.amount]);

  // Sync creditTravauxData defaults from creditData (same rate, type, frequency) with amount = half of travaux
  useEffect(() => {
    setCreditTravauxData(prev => ({
      ...prev,
      ...(hasCustomCreditSplit ? {} : { amount: Math.round((acquisitionData.travaux || 0) / 2) }),
      durationYears: creditData.durationYears,
      type: creditData.type,
      interestRate: creditData.interestRate,
      interestOnlyMonths: creditData.interestOnlyMonths,
      crescendoRate: creditData.crescendoRate,
      paymentFrequency: creditData.paymentFrequency
    }));
  }, [acquisitionData.travaux, creditData.durationYears, creditData.type, creditData.interestRate,
      creditData.interestOnlyMonths, creditData.crescendoRate, creditData.paymentFrequency, hasCustomCreditSplit]);

  // Track if user has manually set a custom estimated home value
  const [hasCustomEstimatedValue, setHasCustomEstimatedValue] = useState(false);

  // Update estimated home value to default (house price + renovation costs) only if user hasn't set a custom value
  useEffect(() => {
    // Only auto-update if user hasn't set a custom value
    if (!hasCustomEstimatedValue) {
      const defaultValue = acquisitionData.prixAchat + acquisitionData.travaux;
      setEstimatedHomeValue(defaultValue);
    }
  }, [acquisitionData.prixAchat, acquisitionData.travaux, hasCustomEstimatedValue]);

  // Detect unsaved changes when project data is modified
  useEffect(() => {
    if (currentProject && originalProjectData) {
      const currentData = JSON.stringify({
        acquisitionData,
        financementData,
        creditData,
        creditTravauxData,
        recurrentFees,
        propertyRevenues,
        currentFinanceRevenues,
        currentFinanceCharges,
        currentFinanceApport,
        revenues,
        futureRevenues,
        charges,
        futureCharges,
        loanParams,
        quotiteParams,
        performanceParams,
        estimatedHomeValue,
        hasCustomEstimatedValue,
        hasCustomCreditSplit,
        customApportSplit
      });
      setHasUnsavedChanges(currentData !== originalProjectData);
    }
  }, [currentProject, originalProjectData, acquisitionData, financementData, creditData, creditTravauxData,
      recurrentFees, propertyRevenues, currentFinanceRevenues, currentFinanceCharges,
      currentFinanceApport, revenues, futureRevenues, charges, futureCharges,
      loanParams, quotiteParams, performanceParams, estimatedHomeValue, hasCustomEstimatedValue, hasCustomCreditSplit, customApportSplit]);

  // Automatically calculate borrowing capacity when inputs change
  useEffect(() => {
    const calculateCapacity = async () => {
      const futureRevenuesTotal = totalRevenues + totalFutureRevenues;
      const futureChargesTotal = totalCharges + totalFutureCharges;

      // Only calculate if we have revenues and valid loan parameters
      if (futureRevenuesTotal > 0 &&
          loanParams.annualRate > 0 &&
          loanParams.durationYears > 0) {

        try {
          const response = await fetch(`${API_URL}/api/borrowing-capacity`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              total_revenues: futureRevenuesTotal,
              total_charges: futureChargesTotal,
              annual_rate: parseFloat(loanParams.annualRate),
              duration_years: parseInt(loanParams.durationYears),
              max_debt_ratio: parseFloat(loanParams.maxDebtRatio)
            })
          });

          if (response.ok) {
            const data = await response.json();
            setResults(data);
            setError(null);
          }
        } catch (err) {
          console.error('Auto-calculation error:', err);
          // Silently fail for auto-calculation
        }
      }
    };

    // Debounce the calculation to avoid too many API calls
    const timeoutId = setTimeout(calculateCapacity, 500);
    return () => clearTimeout(timeoutId);
  }, [totalRevenues, totalFutureRevenues, totalCharges, totalFutureCharges,
      loanParams.annualRate, loanParams.durationYears, loanParams.maxDebtRatio]);

  // Add new items
  const addRevenue = () => {
    setRevenues([...revenues, { id: Date.now(), label: '', amount: '', coefficient: 100 }]);
  };

  const addFutureRevenue = () => {
    setFutureRevenues([...futureRevenues, { id: Date.now(), label: '', amount: '', coefficient: 70 }]);
  };

  const addCharge = () => {
    setCharges([...charges, { id: Date.now(), label: '', amount: '', coefficient: 100 }]);
  };

  const addFutureCharge = () => {
    setFutureCharges([...futureCharges, { id: Date.now(), label: '', amount: '', coefficient: 100 }]);
  };

  // Remove items
  const removeRevenue = (id) => {
    setRevenues(revenues.filter(item => item.id !== id));
  };

  const removeFutureRevenue = (id) => {
    setFutureRevenues(futureRevenues.filter(item => item.id !== id));
  };

  const removeCharge = (id) => {
    setCharges(charges.filter(item => item.id !== id));
  };

  const removeFutureCharge = (id) => {
    setFutureCharges(futureCharges.filter(item => item.id !== id));
  };

  // Update items
  const updateRevenue = (id, field, value) => {
    setRevenues(revenues.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
    setError('');
  };

  const updateFutureRevenue = (id, field, value) => {
    setFutureRevenues(futureRevenues.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
    setError('');
  };

  const updateCharge = (id, field, value) => {
    setCharges(charges.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
    setError('');
  };

  const updateFutureCharge = (id, field, value) => {
    setFutureCharges(futureCharges.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
    setError('');
  };

  const handleLoanParamChange = (e) => {
    const { name, value } = e.target;
    setLoanParams(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleQuotiteParamChange = (e) => {
    const { name, value } = e.target;
    setQuotiteParams(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const calculateDebtRatio = async () => {
    if (totalRevenues === 0) {
      setError('Veuillez renseigner au moins un revenu');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/debt-ratio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          total_revenues: totalRevenues,
          total_charges: totalCharges
        })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError(`Erreur de connexion au serveur: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const toggleDescription = (cardName) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [cardName]: !prev[cardName]
    }));
  };

  const calculateBorrowingCapacity = async () => {
    const futureRevenuesTotal = totalRevenues + totalFutureRevenues;
    const futureChargesTotal = totalCharges + totalFutureCharges;

    if (futureRevenuesTotal === 0) {
      setError('Veuillez renseigner au moins un revenu');
      return;
    }

    if (!loanParams.annualRate || !loanParams.durationYears) {
      setError('Veuillez remplir le taux et la durée du crédit');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Sending borrowing capacity request:', {
        total_revenues: futureRevenuesTotal,
        total_charges: futureChargesTotal,
        annual_rate: parseFloat(loanParams.annualRate),
        duration_years: parseInt(loanParams.durationYears),
        max_debt_ratio: parseFloat(loanParams.maxDebtRatio)
      });

      const response = await fetch(`${API_URL}/api/borrowing-capacity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          total_revenues: futureRevenuesTotal,
          total_charges: futureChargesTotal,
          annual_rate: parseFloat(loanParams.annualRate),
          duration_years: parseInt(loanParams.durationYears),
          max_debt_ratio: parseFloat(loanParams.maxDebtRatio)
        })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      console.log('Borrowing capacity results:', data);
      setResults(data);
    } catch (err) {
      console.error('Error calculating borrowing capacity:', err);
      setError(`Erreur de connexion au serveur: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Save current investment (Investment tab data only)
  const saveInvestment = () => {
    if (!currentInvestmentName.trim()) {
      alert('Veuillez entrer un nom pour l\'investissement');
      return;
    }

    const investment = {
      id: Date.now(),
      name: currentInvestmentName,
      date: new Date().toISOString(),
      data: {
        // Only save Investment tab data
        recurrentFees,
        propertyRevenues,
        creditData,
        creditTravauxData,
        acquisitionData,
        financementData,
        performanceParams
      }
    };

    setSavedInvestments([...savedInvestments, investment]);
    setCurrentInvestmentName('');
    alert(`Investissement "${currentInvestmentName}" sauvegardé avec succès!`);
  };

  // Save entire project with auto-generated key from address and surface
  const saveProject = () => {
    // Extract street name from address (first part before comma)
    let streetName = 'unknown';
    if (extractedData && extractedData.address) {
      const addressParts = extractedData.address.split(',');
      streetName = addressParts[0].trim().replace(/\s+/g, '_').toLowerCase();
    }

    // Get surface area
    const surface = (extractedData && extractedData.surface) ? Math.round(extractedData.surface) : 0;

    if (surface === 0) {
      alert('Aucune surface disponible. Veuillez extraire les données d\'un bien immobilier d\'abord.');
      return;
    }

    // Generate key: streetname_surfaceM2
    const projectKey = `${streetName}_${surface}m2`;

    const projectData = {
      key: projectKey,
      savedAt: new Date().toISOString(),
      // Property data
      extractedData,
      propertyUrl,
      // Investment data
      acquisitionData,
      financementData,
      creditData,
      creditTravauxData,
      recurrentFees,
      propertyRevenues,
      // Saved investments
      savedInvestments,
      selectedInvestments,
      currentInvestmentName,
      // Current finance data
      currentFinanceRevenues,
      currentFinanceCharges,
      currentFinanceApport,
      estimatedHomeValue,
      hasCustomEstimatedValue,
      hasCustomCreditSplit,
      customApportSplit,
      // Borrowing capacity data
      revenues,
      futureRevenues,
      charges,
      futureCharges,
      // Parameters
      loanParams,
      quotiteParams,
      performanceParams,
      // Calculated credit data
      creditAmortization,
      creditTravauxAmortization,
      // Pre-calculated Performance Totale metrics (from Performance tab state)
      calculatedNCF: calculatedPerformanceTotale.ncf,
      calculatedTRI: calculatedPerformanceTotale.tri,
      checklistData
    };

    // Save to localStorage
    try {
      localStorage.setItem(`project_${projectKey}`, JSON.stringify(projectData));
      setCurrentProject(projectData); // Set as current project
      loadSavedProjects(); // Refresh the project list
      // Update original data and reset unsaved changes flag
      setOriginalProjectData(JSON.stringify({
        acquisitionData: projectData.acquisitionData,
        financementData: projectData.financementData,
        creditData: projectData.creditData,
        creditTravauxData: projectData.creditTravauxData,
        recurrentFees: projectData.recurrentFees,
        propertyRevenues: projectData.propertyRevenues,
        currentFinanceRevenues: projectData.currentFinanceRevenues,
        currentFinanceCharges: projectData.currentFinanceCharges,
        currentFinanceApport: projectData.currentFinanceApport,
        revenues: projectData.revenues,
        futureRevenues: projectData.futureRevenues,
        charges: projectData.charges,
        futureCharges: projectData.futureCharges,
        loanParams: projectData.loanParams,
        quotiteParams: projectData.quotiteParams,
        performanceParams: projectData.performanceParams,
        estimatedHomeValue: projectData.estimatedHomeValue,
        hasCustomEstimatedValue: projectData.hasCustomEstimatedValue,
        hasCustomCreditSplit: projectData.hasCustomCreditSplit,
        customApportSplit: projectData.customApportSplit,
        checklistData: projectData.checklistData
      }));
      setHasUnsavedChanges(false);
      alert(`Projet sauvegardé: ${projectKey}`);
    } catch (error) {
      alert(`Erreur lors de la sauvegarde: ${error.message}`);
    }
  };

  // Generate PDF Report - Stratego Finance Style (Teal Theme)
  const generatePDFReport = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = 0;

    // Theme colors (Teal/Professional)
    const teal = { r: 45, g: 85, b: 95 };        // Primary teal
    const tealLight = { r: 65, g: 115, b: 125 }; // Lighter teal
    const tealDark = { r: 35, g: 65, b: 75 };    // Darker teal
    const white = { r: 255, g: 255, b: 255 };
    const lightGray = { r: 245, g: 245, b: 245 };
    const darkGray = { r: 60, g: 60, b: 60 };
    const medGray = { r: 120, g: 120, b: 120 };
    const green = { r: 34, g: 197, b: 94 };
    const red = { r: 239, g: 68, b: 68 };

    // Helper function to format numbers (ASCII-safe for PDF)
    const fmt = (num, decimals = 0) => {
      if (num === undefined || num === null || isNaN(num)) return '0';
      const number = Number(num);
      const fixed = Math.abs(number).toFixed(decimals);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      const result = parts.join(',');
      return number < 0 ? '-' + result : result;
    };

    // Calculate key metrics
    const totalAcquisition = acquisitionData.prixAchat + acquisitionData.fraisNotaire +
                             acquisitionData.fraisAgence + acquisitionData.travaux + acquisitionData.autresFrais;
    const ltv = totalAcquisition > 0 ? (financementData.emprunt / totalAcquisition) * 100 : 0;
    const equityPercent = 100 - ltv;
    const monthlyRate = creditData.annualRate / 100 / 12;
    const numPayments = creditData.duration * 12;
    const monthlyPayment = monthlyRate > 0 ?
      (creditData.amount * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1) : 0;
    const totalInterest = (monthlyPayment * numPayments) - creditData.amount;
    const totalCreditCost = creditData.amount + totalInterest;

    // Calculate rental metrics
    const annualRent = propertyRevenues.reduce((sum, rev) => {
      const amount = parseFloat(rev.amount) || 0;
      const occupancy = (parseFloat(rev.occupancyRate) || 100) / 100;
      if (rev.frequency === 'monthly') return sum + (amount * 12 * occupancy);
      if (rev.frequency === 'quarterly') return sum + (amount * 4 * occupancy);
      return sum + (amount * occupancy);
    }, 0);

    const annualCharges = recurrentFees.reduce((sum, fee) => {
      const amount = parseFloat(fee.amount) || 0;
      if (fee.frequency === 'monthly') return sum + (amount * 12);
      if (fee.frequency === 'quarterly') return sum + (amount * 4);
      return sum + amount;
    }, 0);

    const netRent = annualRent - annualCharges;
    const grossYield = totalAcquisition > 0 ? (annualRent / totalAcquisition) * 100 : 0;
    const netYield = totalAcquisition > 0 ? (netRent / totalAcquisition) * 100 : 0;
    const monthlyCashFlow = (netRent / 12) - monthlyPayment;
    const annualCashFlow = monthlyCashFlow * 12;

    // Performance metrics - Ratio Loyer / Prix avec Travaux
    const totalAcqWithWorks = acquisitionData.prixAchat + acquisitionData.travaux;
    const dscr = totalAcqWithWorks > 0 ? annualRent / totalAcqWithWorks : 0;
    const roe = financementData.apport > 0 ? (annualCashFlow / financementData.apport) * 100 : 0;
    const pricePerSqm = extractedData?.surface > 0 ? acquisitionData.prixAchat / extractedData.surface : 0;
    const rentPerSqm = extractedData?.surface > 0 ? (annualRent / 12) / extractedData.surface : 0;

    // Estimated value after renovation
    const estimatedValue = estimatedHomeValue || (acquisitionData.prixAchat + acquisitionData.travaux);
    const addedValue = estimatedValue - (acquisitionData.prixAchat + acquisitionData.travaux);

    // Sidebar helper
    const sidebarWidth = 55;
    const contentX = sidebarWidth + 10;
    const contentWidth = pageWidth - contentX - margin;

    const drawSidebar = (pageNum, title, subtitle = '') => {
      doc.setFillColor(teal.r, teal.g, teal.b);
      doc.rect(0, 0, sidebarWidth, pageHeight, 'F');

      doc.setTextColor(white.r, white.g, white.b);
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.text(pageNum.toString().padStart(2, '0'), 15, 40);

      doc.setFontSize(16);
      doc.text(title, 10, 60);
      if (subtitle) {
        doc.setFontSize(11);
        doc.text(subtitle, 10, 72);
      }
    };

    const drawBrandLogo = () => {
      doc.setFillColor(tealLight.r, tealLight.g, tealLight.b);
      doc.rect(pageWidth - 35, 15, 8, 8, 'F');
      doc.rect(pageWidth - 25, 15, 8, 8, 'F');
      doc.rect(pageWidth - 35, 25, 8, 8, 'F');
      doc.setFillColor(teal.r, teal.g, teal.b);
      doc.rect(pageWidth - 25, 25, 8, 8, 'F');
    };

    const drawSectionTitle = (title, y) => {
      doc.setTextColor(teal.r, teal.g, teal.b);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(title, contentX, y);
      doc.setFillColor(tealLight.r, tealLight.g, tealLight.b);
      doc.rect(contentX, y + 2, 8, 2, 'F');
      return y + 12;
    };

    // ===== PAGE 1: SYNTHESE EXECUTIVE =====
    drawSidebar(1, 'Synthese', 'Executive');
    drawBrandLogo();

    // Property info box on sidebar
    doc.setFillColor(tealDark.r, tealDark.g, tealDark.b);
    doc.roundedRect(5, 85, sidebarWidth - 10, 70, 3, 3, 'F');
    doc.setTextColor(white.r, white.g, white.b);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Bien Immobilier', 10, 97);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    let sideY = 108;
    if (extractedData?.surface) {
      doc.text(`Surface: ${fmt(extractedData.surface)} m2`, 10, sideY);
      sideY += 9;
    }
    if (extractedData?.rooms) {
      doc.text(`Pieces: ${extractedData.rooms}`, 10, sideY);
      sideY += 9;
    }
    if (pricePerSqm > 0) {
      doc.text(`${fmt(pricePerSqm)} EUR/m2`, 10, sideY);
      sideY += 9;
    }

    // Main content
    yPos = 50;
    doc.setTextColor(teal.r, teal.g, teal.b);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('RAPPORT D\'INVESTISSEMENT', contentX, yPos);
    yPos += 8;

    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const address = extractedData?.address || 'Projet Immobilier';
    const addressLines = doc.splitTextToSize(address, contentWidth);
    doc.text(addressLines, contentX, yPos);
    yPos += addressLines.length * 5 + 3;

    doc.setTextColor(medGray.r, medGray.g, medGray.b);
    doc.setFontSize(9);
    doc.text(`Genere le ${new Date().toLocaleDateString('fr-FR')}`, contentX, yPos);
    yPos += 15;

    // Key metrics highlight box
    doc.setFillColor(teal.r, teal.g, teal.b);
    doc.roundedRect(contentX, yPos, contentWidth, 50, 3, 3, 'F');
    doc.setTextColor(white.r, white.g, white.b);

    // Calculate column positions for 3 equal columns
    const colWidth = contentWidth / 3;
    const kpi1X = contentX + 5;
    const kpi2X = contentX + colWidth + 5;
    const kpi3X = contentX + colWidth * 2 + 5;

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('INVESTISSEMENT', kpi1X, yPos + 10);
    doc.text('RENDEMENT NET', kpi2X, yPos + 10);
    doc.text('CASH-FLOW', kpi3X, yPos + 10);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`${fmt(totalAcquisition)}`, kpi1X, yPos + 22);
    doc.text(`${fmt(netYield, 2)}%`, kpi2X, yPos + 22);
    const cfColor = monthlyCashFlow >= 0 ? green : red;
    doc.setTextColor(cfColor.r, cfColor.g, cfColor.b);
    doc.text(`${fmt(monthlyCashFlow, 0)} EUR`, kpi3X, yPos + 22);

    doc.setTextColor(white.r, white.g, white.b);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`EUR | LTV: ${fmt(ltv, 1)}%`, kpi1X, yPos + 30);
    doc.text(`Brut: ${fmt(grossYield, 2)}%`, kpi2X, yPos + 30);
    doc.text(`/mois`, kpi3X, yPos + 30);

    doc.setFontSize(6);
    doc.text(`Annuel: ${fmt(annualCashFlow, 0)} EUR`, kpi3X, yPos + 38);
    yPos += 68; // More space before acquisition section

    // Acquisition section with pie chart
    yPos = drawSectionTitle('COUTS D\'ACQUISITION', yPos);

    // Pie chart for acquisition costs
    const pieX = contentX + contentWidth - 30;
    const pieY = yPos + 25;
    const pieRadius = 22;

    // Calculate percentages for pie chart - use distinct colors
    const pieColors = [
      { r: 45, g: 85, b: 95 },    // Dark teal - Prix d'achat
      { r: 52, g: 152, b: 219 },  // Blue - Notaire
      { r: 155, g: 89, b: 182 },  // Purple - Agence
      { r: 230, g: 126, b: 34 },  // Orange - Travaux
      { r: 149, g: 165, b: 166 }  // Gray - Autres
    ];
    const acqData = [
      { label: 'Achat', value: acquisitionData.prixAchat, color: pieColors[0] },
      { label: 'Notaire', value: acquisitionData.fraisNotaire, color: pieColors[1] },
      { label: 'Agence', value: acquisitionData.fraisAgence, color: pieColors[2] },
      { label: 'Travaux', value: acquisitionData.travaux, color: pieColors[3] },
      { label: 'Autres', value: acquisitionData.autresFrais, color: pieColors[4] }
    ].filter(d => d.value > 0);

    // Draw pie chart
    let startAngle = -Math.PI / 2;
    acqData.forEach(segment => {
      const angle = (segment.value / totalAcquisition) * 2 * Math.PI;
      const endAngle = startAngle + angle;

      // Draw pie segment using lines (approximate arc)
      doc.setFillColor(segment.color.r, segment.color.g, segment.color.b);
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.5);

      // Create pie slice path
      const steps = 20;
      const points = [[pieX, pieY]];
      for (let i = 0; i <= steps; i++) {
        const a = startAngle + (angle * i / steps);
        points.push([pieX + pieRadius * Math.cos(a), pieY + pieRadius * Math.sin(a)]);
      }

      // Draw filled triangle segments
      for (let i = 1; i < points.length - 1; i++) {
        doc.triangle(points[0][0], points[0][1], points[i][0], points[i][1], points[i+1][0], points[i+1][1], 'F');
      }

      startAngle = endAngle;
    });

    // Draw pie border
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(1);
    doc.circle(pieX, pieY, pieRadius, 'S');

    doc.setFontSize(9);
    const acqItems = [
      ['Prix d\'achat', fmt(acquisitionData.prixAchat), `${((acquisitionData.prixAchat/totalAcquisition)*100).toFixed(0)}%`],
      ['Frais de notaire', fmt(acquisitionData.fraisNotaire), `${((acquisitionData.fraisNotaire/totalAcquisition)*100).toFixed(0)}%`],
      ['Frais d\'agence', fmt(acquisitionData.fraisAgence), `${((acquisitionData.fraisAgence/totalAcquisition)*100).toFixed(0)}%`],
      ['Travaux', fmt(acquisitionData.travaux), `${((acquisitionData.travaux/totalAcquisition)*100).toFixed(0)}%`],
      ['Autres frais', fmt(acquisitionData.autresFrais), `${((acquisitionData.autresFrais/totalAcquisition)*100).toFixed(0)}%`]
    ];

    acqItems.forEach(([label, value, pct], index) => {
      // Color indicator matching pie chart
      doc.setFillColor(pieColors[index].r, pieColors[index].g, pieColors[index].b);
      doc.rect(contentX, yPos - 2, 3, 3, 'F');

      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.setFont('helvetica', 'normal');
      doc.text(label, contentX + 6, yPos);
      doc.setTextColor(medGray.r, medGray.g, medGray.b);
      doc.setFontSize(7);
      doc.text(pct, contentX + 6, yPos + 4);
      doc.setFontSize(9);
      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.setFont('helvetica', 'bold');
      doc.text(value + ' EUR', contentX + contentWidth - 65, yPos, { align: 'right' });
      yPos += 10;
    });

    doc.setDrawColor(teal.r, teal.g, teal.b);
    doc.setLineWidth(0.5);
    doc.line(contentX, yPos, contentX + contentWidth - 65, yPos);
    yPos += 5;
    doc.setTextColor(teal.r, teal.g, teal.b);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL', contentX, yPos);
    doc.text(fmt(totalAcquisition) + ' EUR', contentX + contentWidth - 65, yPos, { align: 'right' });

    // ===== PAGE 2: PLAN DE FINANCEMENT =====
    doc.addPage();
    drawSidebar(2, 'Plan de', 'Financement');
    drawBrandLogo();
    yPos = margin + 15;

    // Financing section with amortization chart
    yPos = drawSectionTitle('PLAN DE FINANCEMENT', yPos);

    // Use safe values for credit data
    const safeDuration = parseInt(creditData.duration) || 20;
    const safeNumPayments = safeDuration * 12;
    const safeRate = parseFloat(creditData.annualRate) || 3.5;
    const safeMonthlyRate = safeRate / 100 / 12;
    const safeAmount = parseFloat(creditData.amount) || financementData.emprunt || 0;
    const safeMonthlyPayment = safeMonthlyRate > 0 && safeAmount > 0 ?
      (safeAmount * safeMonthlyRate * Math.pow(1 + safeMonthlyRate, safeNumPayments)) / (Math.pow(1 + safeMonthlyRate, safeNumPayments) - 1) : 0;

    // Credit type box
    doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
    doc.roundedRect(contentX, yPos, contentWidth, 18, 2, 2, 'F');
    doc.setTextColor(teal.r, teal.g, teal.b);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('CREDIT AMORTISSABLE', contentX + 5, yPos + 7);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFont('helvetica', 'normal');
    doc.text(`Taux: ${fmt(safeRate, 2)}% | Duree: ${safeNumPayments} mois (${safeDuration} ans) | Mensualite: ${fmt(safeMonthlyPayment, 0)} EUR`, contentX + 5, yPos + 14);
    yPos += 25;

    // Stacked area chart showing Interest vs Capital breakdown over time
    const chartLeftMargin = 18; // Less space on left for Y-axis labels
    const chartRightMargin = 10; // Keep margin on right
    const chartStartX = contentX + chartLeftMargin;
    const chartStartY = yPos + 12;
    const chartW = contentWidth - chartLeftMargin - chartRightMargin; // Proper margins
    const chartH = 48; // Shorter, wider aspect ratio

    // Colors for stacked chart - more distinct colors
    const interestColor = { r: 134, g: 239, b: 172 };  // Light mint green for interest
    const capitalColor = { r: 22, g: 163, b: 74 };     // Strong green for capital

    const chartYears = Math.max(1, Math.min(safeDuration, 30));

    // Chart title
    doc.setTextColor(teal.r, teal.g, teal.b);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Repartition des paiements mensuels', contentX, chartStartY - 5);

    // Y-axis values (before chart background so they're outside)
    doc.setTextColor(medGray.r, medGray.g, medGray.b);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(fmt(safeMonthlyPayment, 0), chartStartX - 3, chartStartY + 3, { align: 'right' });
    doc.text(fmt(safeMonthlyPayment/2, 0), chartStartX - 3, chartStartY + chartH/2 + 2, { align: 'right' });
    doc.text('0', chartStartX - 3, chartStartY + chartH, { align: 'right' });

    // Chart background
    doc.setFillColor(250, 250, 250);
    doc.rect(chartStartX, chartStartY, chartW, chartH, 'F');
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.2);
    doc.rect(chartStartX, chartStartY, chartW, chartH, 'S');

    // Draw horizontal grid lines
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.1);
    doc.line(chartStartX, chartStartY + chartH/4, chartStartX + chartW, chartStartY + chartH/4);
    doc.line(chartStartX, chartStartY + chartH/2, chartStartX + chartW, chartStartY + chartH/2);
    doc.line(chartStartX, chartStartY + 3*chartH/4, chartStartX + chartW, chartStartY + 3*chartH/4);

    if (safeAmount > 0 && safeMonthlyPayment > 0) {
      // Calculate payment breakdown for each year
      const paymentData = [];
      let remainingBalance = safeAmount;

      for (let year = 0; year <= chartYears; year++) {
        if (year === 0) {
          // First point - full payment is interest-heavy
          const interestPart = remainingBalance * safeMonthlyRate;
          const capitalPart = safeMonthlyPayment - interestPart;
          paymentData.push({ year, interest: interestPart, capital: capitalPart, total: safeMonthlyPayment });
        } else {
          // Calculate average for this year
          let yearInterest = 0;
          let yearCapital = 0;
          for (let month = 0; month < 12; month++) {
            const interestPayment = remainingBalance * safeMonthlyRate;
            const principalPayment = safeMonthlyPayment - interestPayment;
            yearInterest += interestPayment;
            yearCapital += principalPayment;
            remainingBalance = Math.max(0, remainingBalance - principalPayment);
          }
          // Average monthly for this year
          paymentData.push({
            year,
            interest: yearInterest / 12,
            capital: yearCapital / 12,
            total: safeMonthlyPayment
          });
        }
      }

      // Draw stacked areas
      // First draw interest area (bottom - light green)
      doc.setFillColor(interestColor.r, interestColor.g, interestColor.b);
      for (let i = 0; i < paymentData.length - 1; i++) {
        const x1 = chartStartX + (paymentData[i].year / chartYears) * chartW;
        const x2 = chartStartX + (paymentData[i+1].year / chartYears) * chartW;
        const interestPct1 = paymentData[i].interest / safeMonthlyPayment;
        const interestPct2 = paymentData[i+1].interest / safeMonthlyPayment;
        const y1 = chartStartY + chartH - (interestPct1 * chartH);
        const y2 = chartStartY + chartH - (interestPct2 * chartH);

        // Fill interest area from bottom
        doc.triangle(x1, chartStartY + chartH, x1, y1, x2, y2, 'F');
        doc.triangle(x1, chartStartY + chartH, x2, y2, x2, chartStartY + chartH, 'F');
      }

      // Then draw capital area (top - darker green)
      doc.setFillColor(capitalColor.r, capitalColor.g, capitalColor.b);
      for (let i = 0; i < paymentData.length - 1; i++) {
        const x1 = chartStartX + (paymentData[i].year / chartYears) * chartW;
        const x2 = chartStartX + (paymentData[i+1].year / chartYears) * chartW;
        const interestPct1 = paymentData[i].interest / safeMonthlyPayment;
        const interestPct2 = paymentData[i+1].interest / safeMonthlyPayment;
        const y1Bottom = chartStartY + chartH - (interestPct1 * chartH);
        const y2Bottom = chartStartY + chartH - (interestPct2 * chartH);
        const y1Top = chartStartY; // Top of chart (100% = full payment)
        const y2Top = chartStartY;

        // Fill capital area from interest line to top
        doc.triangle(x1, y1Bottom, x1, y1Top, x2, y2Top, 'F');
        doc.triangle(x1, y1Bottom, x2, y2Top, x2, y2Bottom, 'F');
      }

      // Draw dividing line between interest and capital (thicker white line)
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(2.5);
      for (let i = 0; i < paymentData.length - 1; i++) {
        const x1 = chartStartX + (paymentData[i].year / chartYears) * chartW;
        const x2 = chartStartX + (paymentData[i+1].year / chartYears) * chartW;
        const interestPct1 = paymentData[i].interest / safeMonthlyPayment;
        const interestPct2 = paymentData[i+1].interest / safeMonthlyPayment;
        const y1 = chartStartY + chartH - (interestPct1 * chartH);
        const y2 = chartStartY + chartH - (interestPct2 * chartH);
        doc.line(x1, y1, x2, y2);
      }

    } else {
      // No loan data - show message
      doc.setTextColor(medGray.r, medGray.g, medGray.b);
      doc.setFontSize(8);
      doc.text('Aucun emprunt configure', chartStartX + chartW/2, chartStartY + chartH/2, { align: 'center' });
    }

    // X-axis labels
    doc.setTextColor(medGray.r, medGray.g, medGray.b);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('0y', chartStartX, chartStartY + chartH + 8);
    // Add intermediate year labels
    const quarterYear = Math.floor(chartYears / 4);
    const midYear = Math.floor(chartYears / 2);
    const threeQuarterYear = Math.floor(3 * chartYears / 4);
    doc.text(`${quarterYear}y`, chartStartX + chartW/4, chartStartY + chartH + 8, { align: 'center' });
    doc.text(`${midYear}y`, chartStartX + chartW/2, chartStartY + chartH + 8, { align: 'center' });
    doc.text(`${threeQuarterYear}y`, chartStartX + 3*chartW/4, chartStartY + chartH + 8, { align: 'center' });
    doc.text(`${chartYears}y`, chartStartX + chartW, chartStartY + chartH + 8, { align: 'right' });
    doc.text('Annees', chartStartX + chartW/2, chartStartY + chartH + 16, { align: 'center' });

    // Legend (centered below X-axis)
    const legendY = chartStartY + chartH + 22;
    const legendX = chartStartX + chartW/2 - 50;

    // Interest legend
    doc.setFillColor(interestColor.r, interestColor.g, interestColor.b);
    doc.rect(legendX, legendY - 3, 10, 10, 'F');
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(8);
    doc.text('Interets', legendX + 14, legendY + 4);

    // Capital legend
    doc.setFillColor(capitalColor.r, capitalColor.g, capitalColor.b);
    doc.rect(legendX + 60, legendY - 3, 10, 10, 'F');
    doc.text('Capital', legendX + 74, legendY + 4);

    // Calculate safe total interest
    const safeTotalInterest = (safeMonthlyPayment * safeNumPayments) - safeAmount;

    // Financing details - horizontal layout below the chart
    yPos = chartStartY + chartH + 38;

    // Draw a box for financing summary
    doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
    doc.roundedRect(contentX, yPos, contentWidth, 25, 2, 2, 'F');

    const finColWidth = contentWidth / 3;

    // Emprunt
    doc.setFontSize(8);
    doc.setTextColor(medGray.r, medGray.g, medGray.b);
    doc.setFont('helvetica', 'normal');
    doc.text('Emprunt', contentX + finColWidth/2, yPos + 8, { align: 'center' });
    doc.setFontSize(11);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFont('helvetica', 'bold');
    doc.text(fmt(financementData.emprunt) + ' EUR', contentX + finColWidth/2, yPos + 18, { align: 'center' });

    // Apport
    doc.setFontSize(8);
    doc.setTextColor(medGray.r, medGray.g, medGray.b);
    doc.setFont('helvetica', 'normal');
    doc.text('Apport', contentX + finColWidth + finColWidth/2, yPos + 8, { align: 'center' });
    doc.setFontSize(11);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFont('helvetica', 'bold');
    doc.text(fmt(financementData.apport) + ' EUR', contentX + finColWidth + finColWidth/2, yPos + 18, { align: 'center' });

    // Interets totaux
    doc.setFontSize(8);
    doc.setTextColor(medGray.r, medGray.g, medGray.b);
    doc.setFont('helvetica', 'normal');
    doc.text('Interets totaux', contentX + 2*finColWidth + finColWidth/2, yPos + 8, { align: 'center' });
    doc.setFontSize(11);
    doc.setTextColor(red.r, red.g, red.b);
    doc.setFont('helvetica', 'bold');
    doc.text(fmt(safeTotalInterest, 0) + ' EUR', contentX + 2*finColWidth + finColWidth/2, yPos + 18, { align: 'center' });

    yPos += 32;

    doc.setDrawColor(teal.r, teal.g, teal.b);
    doc.setLineWidth(0.5);
    doc.line(contentX, yPos, contentX + contentWidth, yPos);
    yPos += 5;
    doc.setTextColor(teal.r, teal.g, teal.b);
    doc.setFont('helvetica', 'bold');
    doc.text('COUT TOTAL CREDIT', contentX, yPos);
    doc.text(fmt(totalCreditCost, 0) + ' EUR', contentX + contentWidth, yPos, { align: 'right' });
    doc.setTextColor(medGray.r, medGray.g, medGray.b);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`dont ${fmt(totalInterest, 0)} EUR d'interets`, contentX + contentWidth, yPos + 5, { align: 'right' });

    // ===== PAGE 3: REVENUS ET CHARGES =====
    doc.addPage();
    drawSidebar(3, 'Revenus', '& Charges');
    drawBrandLogo();

    yPos = 35;

    // Rental income section
    yPos = drawSectionTitle('REVENUS LOCATIFS', yPos);

    doc.setFontSize(8);
    doc.setTextColor(medGray.r, medGray.g, medGray.b);
    doc.setFont('helvetica', 'italic');
    doc.text('Les revenus locatifs representent les loyers percus, ajustes selon le taux d\'occupation.', contentX, yPos);
    yPos += 10;

    propertyRevenues.forEach((rev, index) => {
      const amount = parseFloat(rev.amount) || 0;
      const occupancy = parseFloat(rev.occupancyRate) || 100;
      const freq = rev.frequency === 'monthly' ? 'mensuel' : rev.frequency === 'quarterly' ? 'trimestriel' : 'annuel';
      const annualAmount = rev.frequency === 'monthly' ? amount * 12 : rev.frequency === 'quarterly' ? amount * 4 : amount;
      const effectiveAnnual = annualAmount * (occupancy / 100);

      doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
      doc.roundedRect(contentX, yPos, contentWidth, 22, 2, 2, 'F');

      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`Revenu ${index + 1}`, contentX + 5, yPos + 8);

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`${fmt(amount)} EUR ${freq} | Occupation: ${occupancy}%`, contentX + 5, yPos + 16);

      doc.setTextColor(teal.r, teal.g, teal.b);
      doc.setFont('helvetica', 'bold');
      doc.text(`${fmt(effectiveAnnual)} EUR/an`, contentX + contentWidth - 5, yPos + 12, { align: 'right' });
      yPos += 28;
    });

    // Annual rent summary
    doc.setFillColor(teal.r, teal.g, teal.b);
    doc.roundedRect(contentX, yPos, contentWidth, 18, 2, 2, 'F');
    doc.setTextColor(white.r, white.g, white.b);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL REVENUS ANNUELS', contentX + 5, yPos + 12);
    doc.text(fmt(annualRent, 0) + ' EUR', contentX + contentWidth - 5, yPos + 12, { align: 'right' });
    yPos += 30;

    // Charges section
    yPos = drawSectionTitle('CHARGES RECURRENTES', yPos);

    doc.setFontSize(8);
    doc.setTextColor(medGray.r, medGray.g, medGray.b);
    doc.setFont('helvetica', 'italic');
    doc.text('Les charges comprennent toutes les depenses regulieres liees a la propriete.', contentX, yPos);
    yPos += 10;

    const feeNames = {
      propertyTax: 'Taxe fonciere',
      coOwnershipFees: 'Charges copropriete',
      insurance: 'Assurance PNO',
      maintenance: 'Provision entretien'
    };

    const feeDescriptions = {
      propertyTax: 'Impot local annuel sur la propriete',
      coOwnershipFees: 'Charges communes de copropriete',
      insurance: 'Assurance proprietaire non occupant',
      maintenance: 'Reserve pour travaux et reparations'
    };

    recurrentFees.forEach(fee => {
      const amount = parseFloat(fee.amount) || 0;
      const name = feeNames[fee.name] || fee.name;
      const desc = feeDescriptions[fee.name] || '';
      const freq = fee.frequency === 'monthly' ? 'mensuel' : fee.frequency === 'quarterly' ? 'trimestriel' : 'annuel';
      const annualAmount = fee.frequency === 'monthly' ? amount * 12 : fee.frequency === 'quarterly' ? amount * 4 : amount;

      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`${name}`, contentX, yPos);
      doc.setTextColor(medGray.r, medGray.g, medGray.b);
      doc.setFontSize(7);
      doc.text(`${desc} - ${fmt(amount)} EUR ${freq}`, contentX, yPos + 4);
      doc.setTextColor(red.r, red.g, red.b);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`-${fmt(annualAmount)} EUR/an`, contentX + contentWidth, yPos, { align: 'right' });
      yPos += 12;
    });

    // Total charges
    doc.setDrawColor(red.r, red.g, red.b);
    doc.setLineWidth(0.5);
    doc.line(contentX, yPos, contentX + contentWidth, yPos);
    yPos += 5;
    doc.setTextColor(red.r, red.g, red.b);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL CHARGES ANNUELLES', contentX, yPos);
    doc.text('-' + fmt(annualCharges, 0) + ' EUR', contentX + contentWidth, yPos, { align: 'right' });
    yPos += 15;

    // Net result
    doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
    doc.roundedRect(contentX, yPos, contentWidth, 35, 3, 3, 'F');

    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('RESULTAT NET LOCATIF', contentX + 5, yPos + 10);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Revenus: ${fmt(annualRent)} EUR - Charges: ${fmt(annualCharges)} EUR`, contentX + 5, yPos + 18);

    const netColor = netRent >= 0 ? green : red;
    doc.setTextColor(netColor.r, netColor.g, netColor.b);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${fmt(netRent, 0)} EUR/an`, contentX + contentWidth - 5, yPos + 22, { align: 'right' });

    // ===== PAGE 4: ANALYSE DE RENTABILITE =====
    doc.addPage();
    drawSidebar(4, 'Rentabilite', '& Performance');
    drawBrandLogo();

    yPos = 35;

    // Yield analysis
    yPos = drawSectionTitle('ANALYSE DES RENDEMENTS', yPos);

    doc.setFontSize(8);
    doc.setTextColor(medGray.r, medGray.g, medGray.b);
    doc.setFont('helvetica', 'italic');
    const yieldText = doc.splitTextToSize('Le rendement mesure la performance de votre investissement. Le rendement brut ne tient compte que des loyers, tandis que le rendement net integre toutes les charges.', contentWidth);
    doc.text(yieldText, contentX, yPos);
    yPos += yieldText.length * 4 + 8;

    // Yield boxes
    const boxWidth = (contentWidth - 10) / 2;

    // Gross yield box
    doc.setFillColor(tealLight.r, tealLight.g, tealLight.b);
    doc.roundedRect(contentX, yPos, boxWidth, 40, 3, 3, 'F');
    doc.setTextColor(white.r, white.g, white.b);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('RENDEMENT BRUT', contentX + 5, yPos + 12);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`${fmt(grossYield, 2)}%`, contentX + 5, yPos + 28);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`${fmt(annualRent)} / ${fmt(totalAcquisition)}`, contentX + 5, yPos + 35);

    // Net yield box
    doc.setFillColor(teal.r, teal.g, teal.b);
    doc.roundedRect(contentX + boxWidth + 10, yPos, boxWidth, 40, 3, 3, 'F');
    doc.setTextColor(white.r, white.g, white.b);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('RENDEMENT NET', contentX + boxWidth + 15, yPos + 12);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`${fmt(netYield, 2)}%`, contentX + boxWidth + 15, yPos + 28);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`${fmt(netRent)} / ${fmt(totalAcquisition)}`, contentX + boxWidth + 15, yPos + 35);
    yPos += 50;

    // Cash-flow analysis
    yPos = drawSectionTitle('ANALYSE DU CASH-FLOW', yPos);

    doc.setFontSize(8);
    doc.setTextColor(medGray.r, medGray.g, medGray.b);
    doc.setFont('helvetica', 'italic');
    const cfText = doc.splitTextToSize('Le cash-flow represente l\'argent qui reste apres paiement de toutes les charges et de la mensualite du credit. Un cash-flow positif signifie que l\'investissement s\'autofinance.', contentWidth);
    doc.text(cfText, contentX, yPos);
    yPos += cfText.length * 4 + 8;

    // Cash-flow calculation breakdown
    doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
    doc.roundedRect(contentX, yPos, contentWidth, 55, 3, 3, 'F');

    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Loyer net mensuel', contentX + 10, yPos + 12);
    doc.text(`${fmt(netRent / 12, 0)} EUR`, contentX + contentWidth - 10, yPos + 12, { align: 'right' });

    doc.text('Mensualite credit', contentX + 10, yPos + 24);
    doc.setTextColor(red.r, red.g, red.b);
    doc.text(`-${fmt(monthlyPayment, 0)} EUR`, contentX + contentWidth - 10, yPos + 24, { align: 'right' });

    doc.setDrawColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setLineWidth(0.3);
    doc.line(contentX + 10, yPos + 32, contentX + contentWidth - 10, yPos + 32);

    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFont('helvetica', 'bold');
    doc.text('Cash-flow mensuel', contentX + 10, yPos + 42);
    const cfResultColor = monthlyCashFlow >= 0 ? green : red;
    doc.setTextColor(cfResultColor.r, cfResultColor.g, cfResultColor.b);
    doc.setFontSize(12);
    doc.text(`${fmt(monthlyCashFlow, 0)} EUR`, contentX + contentWidth - 10, yPos + 42, { align: 'right' });
    yPos += 65;

    // Performance indicators
    yPos = drawSectionTitle('INDICATEURS DE PERFORMANCE', yPos);

    const indicators = [
      ['Ratio Loyer/Prix+Travaux', fmt(dscr * 100, 2) + '%', dscr >= 0.06 ? 'Excellent' : dscr >= 0.04 ? 'Bon' : 'Faible', 'Loyer annuel / Prix achat avec travaux. Ideal > 6%'],
      ['ROE (Return on Equity)', fmt(roe, 2) + '%', roe > 10 ? 'Excellent' : roe > 5 ? 'Bon' : 'Faible', 'Rendement sur fonds propres investis'],
      ['Effort d\'epargne', fmt(-monthlyCashFlow, 0) + ' EUR/mois', monthlyCashFlow >= 0 ? 'Aucun' : 'Requis', 'Apport mensuel necessaire si cash-flow negatif']
    ];

    indicators.forEach(([label, value, status, desc]) => {
      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(label, contentX, yPos);

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(medGray.r, medGray.g, medGray.b);
      doc.text(desc, contentX, yPos + 5);

      doc.setTextColor(teal.r, teal.g, teal.b);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(value, contentX + contentWidth - 40, yPos, { align: 'right' });

      const statusColor = status === 'Excellent' ? green : status === 'Bon' || status === 'Acceptable' || status === 'Aucun' ? teal : red;
      doc.setTextColor(statusColor.r, statusColor.g, statusColor.b);
      doc.setFontSize(8);
      doc.text(status, contentX + contentWidth, yPos, { align: 'right' });
      yPos += 16;
    });

    // ===== PAGE 5: PLUS-VALUE ET HORIZON =====
    doc.addPage();
    drawSidebar(5, 'Plus-Value', '& Projection');
    drawBrandLogo();

    yPos = 35;

    // Renovation value
    yPos = drawSectionTitle('CREATION DE VALEUR', yPos);

    doc.setFontSize(8);
    doc.setTextColor(medGray.r, medGray.g, medGray.b);
    doc.setFont('helvetica', 'italic');
    const valueText = doc.splitTextToSize('L\'analyse de creation de valeur evalue le potentiel de plus-value immediate apres travaux de renovation.', contentWidth);
    doc.text(valueText, contentX, yPos);
    yPos += valueText.length * 4 + 8;

    // Value boxes
    doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
    doc.roundedRect(contentX, yPos, contentWidth, 50, 3, 3, 'F');

    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Prix d\'achat + Travaux', contentX + 10, yPos + 12);
    doc.text(`${fmt(acquisitionData.prixAchat + acquisitionData.travaux)} EUR`, contentX + contentWidth - 10, yPos + 12, { align: 'right' });

    doc.text('Valeur estimee apres travaux', contentX + 10, yPos + 24);
    doc.text(`${fmt(estimatedValue)} EUR`, contentX + contentWidth - 10, yPos + 24, { align: 'right' });

    doc.setDrawColor(teal.r, teal.g, teal.b);
    doc.setLineWidth(0.5);
    doc.line(contentX + 10, yPos + 32, contentX + contentWidth - 10, yPos + 32);

    doc.setFont('helvetica', 'bold');
    doc.text('Plus-value potentielle', contentX + 10, yPos + 42);
    const valueColor = addedValue >= 0 ? green : red;
    doc.setTextColor(valueColor.r, valueColor.g, valueColor.b);
    doc.setFontSize(12);
    doc.text(`${addedValue >= 0 ? '+' : ''}${fmt(addedValue)} EUR`, contentX + contentWidth - 10, yPos + 42, { align: 'right' });
    yPos += 60;

    // Investment horizon
    yPos = drawSectionTitle('HORIZON D\'INVESTISSEMENT', yPos);

    const horizonYears = performanceParams.investmentHorizonMonths / 12;
    const totalPaid = monthlyPayment * performanceParams.investmentHorizonMonths;
    const appreciatedValue = estimatedValue * Math.pow(1 + performanceParams.housePriceInflation / 100, horizonYears);

    doc.setFontSize(8);
    doc.setTextColor(medGray.r, medGray.g, medGray.b);
    doc.setFont('helvetica', 'italic');
    doc.text(`Projection sur ${horizonYears} ans avec une appreciation de ${performanceParams.housePriceInflation}% par an.`, contentX, yPos);
    yPos += 10;

    const projItems = [
      ['Duree de detention', `${horizonYears} ans`, `${performanceParams.investmentHorizonMonths} mois`],
      ['Total rembourse', fmt(totalPaid, 0) + ' EUR', `${numPayments > performanceParams.investmentHorizonMonths ? performanceParams.investmentHorizonMonths : numPayments} mensualites`],
      ['Appreciation immobiliere', performanceParams.housePriceInflation + '%/an', 'Hypothese de croissance'],
      ['Valeur projetee', fmt(appreciatedValue, 0) + ' EUR', `A ${horizonYears} ans`]
    ];

    projItems.forEach(([label, value, desc]) => {
      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(label, contentX, yPos);
      doc.setTextColor(medGray.r, medGray.g, medGray.b);
      doc.setFontSize(7);
      doc.text(desc, contentX, yPos + 4);
      doc.setTextColor(teal.r, teal.g, teal.b);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(value, contentX + contentWidth, yPos, { align: 'right' });
      yPos += 14;
    });

    yPos += 5;

    // Summary recommendation box
    doc.setFillColor(teal.r, teal.g, teal.b);
    doc.roundedRect(contentX, yPos, contentWidth, 45, 3, 3, 'F');

    doc.setTextColor(white.r, white.g, white.b);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('SYNTHESE DE L\'INVESTISSEMENT', contentX + 5, yPos + 12);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    let synthText = '';
    if (netYield >= 5 && monthlyCashFlow >= 0) {
      synthText = 'Investissement attractif avec un bon rendement et un cash-flow positif.';
    } else if (netYield >= 3) {
      synthText = 'Investissement correct. Le rendement est dans la moyenne du marche.';
    } else {
      synthText = 'Rendement faible. Analyser les possibilites d\'optimisation.';
    }
    const synthLines = doc.splitTextToSize(synthText, contentWidth - 10);
    doc.text(synthLines, contentX + 5, yPos + 24);

    doc.setFontSize(9);
    doc.text(`Cash-flow cumule sur ${horizonYears} ans: ${fmt(annualCashFlow * horizonYears, 0)} EUR`, contentX + 5, yPos + 38);

    // ===== FOOTER on all pages =====
    const addFooter = (pageNum, totalPages) => {
      doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
      doc.rect(sidebarWidth, pageHeight - 12, pageWidth - sidebarWidth, 12, 'F');
      doc.setTextColor(medGray.r, medGray.g, medGray.b);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('ALTAIR - Rapport d\'Investissement Immobilier', sidebarWidth + 10, pageHeight - 5);
      doc.text(`${pageNum} / ${totalPages}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
    };

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      addFooter(i, totalPages);
    }

    // Save the PDF
    const fileName = extractedData?.address
      ? `Rapport_${extractedData.address.split(',')[0].replace(/\s+/g, '_')}.pdf`
      : 'Rapport_Investissement.pdf';
    doc.save(fileName);
  };

  // Generate Endettement PDF Report
  const generateEndettementPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const topMargin = 20;
    const bottomMargin = 25;
    let yPos = topMargin;

    // Theme colors
    const red = { r: 239, g: 68, b: 68 };
    const darkRed = { r: 180, g: 50, b: 50 };
    const white = { r: 255, g: 255, b: 255 };
    const lightGray = { r: 245, g: 245, b: 245 };
    const darkGray = { r: 60, g: 60, b: 60 };
    const medGray = { r: 120, g: 120, b: 120 };
    const green = { r: 34, g: 197, b: 94 };

    // Helper function to check page break and add new page if needed
    const checkPageBreak = (neededSpace = 30) => {
      if (yPos + neededSpace > pageHeight - bottomMargin) {
        doc.addPage();
        yPos = topMargin;
        return true;
      }
      return false;
    };

    // Helper function to format numbers
    const fmt = (num, decimals = 0) => {
      if (num === undefined || num === null || isNaN(num)) return '0';
      const number = Number(num);
      const fixed = Math.abs(number).toFixed(decimals);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      const result = parts.join(',');
      return number < 0 ? '-' + result : result;
    };

    // Header
    doc.setFillColor(red.r, red.g, red.b);
    doc.rect(0, 0, pageWidth, 45, 'F');

    doc.setTextColor(white.r, white.g, white.b);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Rapport Endettement', margin, 28);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Genere le ${new Date().toLocaleDateString('fr-FR')}`, margin, 38);

    yPos = 60;

    // Section: Situation Financiere Actuelle
    doc.setTextColor(red.r, red.g, red.b);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Situation Financiere Actuelle', margin, yPos);
    doc.setFillColor(red.r, red.g, red.b);
    doc.rect(margin, yPos + 2, 40, 2, 'F');
    yPos += 15;

    // Revenus Mensuels - Table header
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Revenus Mensuels', margin, yPos);
    yPos += 10;

    // Table header for revenues
    doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
    doc.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('Type', margin + 3, yPos);
    doc.text('Montant', margin + 75, yPos);
    doc.text('Pond.', margin + 105, yPos);
    doc.text('Montant Pondere', pageWidth - margin - 5, yPos, { align: 'right' });
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    revenues.forEach(rev => {
      checkPageBreak(10);
      const amount = parseFloat(rev.amount) || 0;
      const coefPercent = parseFloat(rev.coefficient) || 100;
      const coef = coefPercent / 100;
      const weighted = amount * coef;
      doc.text(`${rev.label || 'Revenu'}`, margin + 3, yPos);
      doc.text(`${fmt(amount, 2)} EUR`, margin + 75, yPos);
      doc.text(`${fmt(coefPercent, 0)}%`, margin + 105, yPos);
      doc.setTextColor(green.r, green.g, green.b);
      doc.text(`${fmt(weighted, 2)} EUR`, pageWidth - margin - 5, yPos, { align: 'right' });
      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      yPos += 6;
    });

    // Total line
    checkPageBreak(20);
    doc.setDrawColor(darkGray.r, darkGray.g, darkGray.b);
    doc.line(margin, yPos - 2, pageWidth - margin, yPos - 2);
    doc.setFont('helvetica', 'bold');
    doc.text('Total Revenus Actuels:', margin + 3, yPos + 4);
    doc.setTextColor(green.r, green.g, green.b);
    doc.text(`${fmt(totalRevenues, 2)} EUR`, pageWidth - margin - 5, yPos + 4, { align: 'right' });
    yPos += 15;

    // Charges Mensuelles - Table header
    checkPageBreak(40);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Charges Mensuelles', margin, yPos);
    yPos += 10;

    // Table header for charges
    doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
    doc.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('Type', margin + 3, yPos);
    doc.text('Montant', margin + 75, yPos);
    doc.text('Pond.', margin + 105, yPos);
    doc.text('Montant Pondere', pageWidth - margin - 5, yPos, { align: 'right' });
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    charges.forEach(charge => {
      checkPageBreak(10);
      const amount = parseFloat(charge.amount) || 0;
      const coefPercent = parseFloat(charge.coefficient) || 100;
      const coef = coefPercent / 100;
      const weighted = amount * coef;
      doc.text(`${charge.label || 'Charge'}`, margin + 3, yPos);
      doc.text(`${fmt(amount, 2)} EUR`, margin + 75, yPos);
      doc.text(`${fmt(coefPercent, 0)}%`, margin + 105, yPos);
      doc.setTextColor(red.r, red.g, red.b);
      doc.text(`${fmt(weighted, 2)} EUR`, pageWidth - margin - 5, yPos, { align: 'right' });
      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      yPos += 6;
    });

    // Total line
    checkPageBreak(25);
    doc.setDrawColor(darkGray.r, darkGray.g, darkGray.b);
    doc.line(margin, yPos - 2, pageWidth - margin, yPos - 2);
    doc.setFont('helvetica', 'bold');
    doc.text('Total Charges Actuelles:', margin + 3, yPos + 4);
    doc.setTextColor(red.r, red.g, red.b);
    doc.text(`${fmt(totalCharges, 2)} EUR`, pageWidth - margin - 5, yPos + 4, { align: 'right' });
    yPos += 18;

    // Revenus de l'Investissement (from Investment tab)
    if (propertyRevenues.length > 0) {
      checkPageBreak(50);
      doc.setTextColor(red.r, red.g, red.b);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Revenus de l\'Investissement', margin, yPos);
      yPos += 10;

      // Table header for property revenues
      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
      doc.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Revenu Investissement', margin + 3, yPos);
      doc.text('Montant', margin + 55, yPos);
      doc.text('Pond.', margin + 95, yPos);
      doc.text('Montant Pondere', pageWidth - margin - 5, yPos, { align: 'right' });
      yPos += 8;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      let totalPondere = 0;
      propertyRevenues.forEach(rev => {
        checkPageBreak(10);
        const amount = parseFloat(rev.amount) || 0;
        let monthlyBrut = 0;
        if (rev.frequency === 'monthly') monthlyBrut = amount;
        else if (rev.frequency === 'quarterly') monthlyBrut = amount / 3;
        else if (rev.frequency === 'annually') monthlyBrut = amount / 12;
        const monthlyPondere = monthlyBrut * (futureRevenuesPonderation / 100);
        totalPondere += monthlyPondere;

        doc.text(`${rev.label || 'Revenu'}`, margin + 3, yPos);
        doc.text(`${fmt(monthlyBrut, 2)} EUR`, margin + 55, yPos);
        doc.text(`${fmt(futureRevenuesPonderation, 0)}%`, margin + 95, yPos);
        doc.setTextColor(green.r, green.g, green.b);
        doc.text(`+ ${fmt(monthlyPondere, 2)} EUR`, pageWidth - margin - 5, yPos, { align: 'right' });
        doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
        yPos += 6;
      });

      // Total line
      checkPageBreak(20);
      doc.setDrawColor(darkGray.r, darkGray.g, darkGray.b);
      doc.line(margin, yPos - 2, pageWidth - margin, yPos - 2);
      doc.setFont('helvetica', 'bold');
      doc.text('Total Revenus Futurs:', margin + 3, yPos + 4);
      doc.setTextColor(green.r, green.g, green.b);
      doc.text(`+ ${fmt(totalPondere, 2)} EUR`, pageWidth - margin - 5, yPos + 4, { align: 'right' });
      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      yPos += 18;
    }

    // Section: Capacite d'Emprunt
    checkPageBreak(100); // Need space for section header + capacity box
    doc.setTextColor(red.r, red.g, red.b);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text("Capacite d'Emprunt", margin, yPos);
    doc.setFillColor(red.r, red.g, red.b);
    doc.rect(margin, yPos + 2, 40, 2, 'F');
    yPos += 15;

    // Box for capacity
    doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
    doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 60, 3, 3, 'F');

    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const boxY = yPos + 12;
    doc.text(`Taux d'endettement maximum: ${loanParams.maxDebtRatio}%`, margin + 10, boxY);
    doc.text(`Duree du pret: ${loanParams.durationYears} ans`, margin + 10, boxY + 8);
    doc.text(`Taux annuel: ${loanParams.annualRate}%`, margin + 10, boxY + 16);

    if (results && results.max_borrowing_capacity !== undefined) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Capacite d\'Emprunt Maximum:', margin + 10, boxY + 30);
      doc.setTextColor(green.r, green.g, green.b);
      doc.setFontSize(18);
      doc.text(`${fmt(results.max_borrowing_capacity, 0)} EUR`, margin + 10, boxY + 42);

      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Mensualite maximum: ${fmt(results.max_monthly_payment, 2)} EUR/mois`, pageWidth / 2 + 10, boxY + 30);

      const restantVivre = totalRevenues + totalFutureRevenues - totalCharges - results.max_monthly_payment - totalFutureCharges;
      doc.text(`Restant a vivre: ${fmt(restantVivre, 2)} EUR/mois`, pageWidth / 2 + 10, boxY + 38);

      const tauxEndettement = (totalRevenues + totalFutureRevenues) > 0
        ? ((totalCharges + results.max_monthly_payment + totalFutureCharges) / (totalRevenues + totalFutureRevenues)) * 100
        : 0;
      doc.text(`Taux d'endettement: ${fmt(tauxEndettement, 2)}%`, pageWidth / 2 + 10, boxY + 46);
    } else {
      doc.setFontSize(11);
      doc.text('Calculez votre capacite d\'emprunt dans l\'onglet "Capacite d\'Emprunt"', margin + 10, boxY + 35);
    }

    yPos += 75;

    // Box showing debt ratio calculation
    if (results && results.max_monthly_payment !== undefined) {
      checkPageBreak(45);
      doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
      doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 35, 3, 3, 'F');

      const totalChargesWithCredit = totalCharges + totalFutureCharges + results.max_monthly_payment;
      const totalRevenusAll = totalRevenues + totalFutureRevenues;
      const tauxCalcule = totalRevenusAll > 0 ? (totalChargesWithCredit / totalRevenusAll) * 100 : 0;

      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Calcul du Taux d\'Endettement:', margin + 10, yPos + 12);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Total Charges (avec credit): ${fmt(totalChargesWithCredit, 2)} EUR`, margin + 10, yPos + 22);
      doc.text(`Total Revenus: ${fmt(totalRevenusAll, 2)} EUR`, margin + 10, yPos + 30);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(`${fmt(totalChargesWithCredit, 2)} / ${fmt(totalRevenusAll, 2)} = ${fmt(tauxCalcule, 2)}%`, pageWidth / 2 + 10, yPos + 22);

      yPos += 45;
    }

    // Footer on each page
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setTextColor(medGray.r, medGray.g, medGray.b);
      doc.setFontSize(8);
      doc.text('Document genere automatiquement - Calculs indicatifs', pageWidth / 2, pageHeight - 10, { align: 'center' });
    }

    // Save PDF
    doc.save('Rapport_Endettement.pdf');
  };

  // Generate Bien Immobilier PDF Report - Professional Format
  // Generate Bien Immobilier PDF Report - Professional Format with all sections
  const generateBienImmobilierPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15; // Reduced margin for more content space
    const topMargin = 20;
    const bottomMargin = 20;
    let yPos = topMargin;

    // Theme colors - Goldman Sachs inspired palette
    const primaryBlue = { r: 96, g: 125, b: 139 }; // Steel blue-gray
    const darkBlue = { r: 33, g: 47, b: 61 }; // Dark slate
    const lightBlue = { r: 158, g: 178, b: 196 }; // Light steel blue
    const white = { r: 255, g: 255, b: 255 };
    const lightGray = { r: 250, g: 250, b: 250 };
    const mediumGray = { r: 120, g: 120, b: 120 };
    const darkGray = { r: 60, g: 60, b: 60 };
    const accentGreen = { r: 34, g: 139, b: 34 }; // Forest green
    const accentRed = { r: 178, g: 34, b: 34 }; // Brick red

    // Helper function to check page break
    const checkPageBreak = (neededSpace = 30) => {
      if (yPos + neededSpace > pageHeight - bottomMargin) {
        doc.addPage();
        yPos = topMargin;
        return true;
      }
      return false;
    };

    // Helper function to format numbers
    const fmt = (num, decimals = 0) => {
      if (num === undefined || num === null || isNaN(num)) return '0';
      const number = Number(num);
      const fixed = Math.abs(number).toFixed(decimals);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      const result = parts.join(',');
      return number < 0 ? '-' + result : result;
    };

    // Helper function to draw donut chart
    const drawDonutChart = (centerX, centerY, radius, percentage, colorFill, colorBg) => {
      const innerRadius = radius * 0.65;
      doc.setFillColor(colorBg.r, colorBg.g, colorBg.b);
      doc.circle(centerX, centerY, radius, 'F');

      if (percentage > 0) {
        const endAngle = (percentage / 100) * 360;
        doc.setFillColor(colorFill.r, colorFill.g, colorFill.b);
        const segments = Math.ceil(endAngle / 5);
        for (let i = 0; i < segments; i++) {
          const startA = (i * 5) * (Math.PI / 180) - Math.PI / 2;
          const endA = Math.min(((i + 1) * 5), endAngle) * (Math.PI / 180) - Math.PI / 2;
          const x1 = centerX + radius * Math.cos(startA);
          const y1 = centerY + radius * Math.sin(startA);
          const x2 = centerX + radius * Math.cos(endA);
          const y2 = centerY + radius * Math.sin(endA);
          doc.triangle(x1, y1, x2, y2, centerX, centerY, 'F');
        }
      }

      doc.setFillColor(white.r, white.g, white.b);
      doc.circle(centerX, centerY, innerRadius, 'F');
    };

    const drawDonutSegment = (doc, centerX, centerY, outerRadius, innerRadius, startAngleDeg, sweepAngleDeg) => {
      if (sweepAngleDeg <= 0) return;

      const segments = Math.ceil(sweepAngleDeg / 5);
      for (let i = 0; i < segments; i++) {
        const currentAngleDeg = startAngleDeg + (i * 5);
        const nextAngleDeg = startAngleDeg + Math.min(((i + 1) * 5), sweepAngleDeg);

        const startA = currentAngleDeg * (Math.PI / 180);
        const endA = nextAngleDeg * (Math.PI / 180);

        const x1Outer = centerX + outerRadius * Math.cos(startA);
        const y1Outer = centerY + outerRadius * Math.sin(startA);
        const x2Outer = centerX + outerRadius * Math.cos(endA);
        const y2Outer = centerY + outerRadius * Math.sin(endA);
        const x1Inner = centerX + innerRadius * Math.cos(startA);
        const y1Inner = centerY + innerRadius * Math.sin(startA);
        const x2Inner = centerX + innerRadius * Math.cos(endA);
        const y2Inner = centerY + innerRadius * Math.sin(endA);

        // Draw quad as two triangles
        doc.triangle(x1Outer, y1Outer, x2Outer, y2Outer, x1Inner, y1Inner, 'F');
        doc.triangle(x2Outer, y2Outer, x2Inner, y2Inner, x1Inner, y1Inner, 'F');
      }
    };

    // Calculate metrics
    const totalAcq = acquisitionData.prixAchat + acquisitionData.fraisNotaire +
                     acquisitionData.fraisAgence + acquisitionData.travaux + acquisitionData.autresFrais;
    const ltv = totalAcq > 0 ? ((financementData.emprunt || 0) / totalAcq * 100) : 0;

    // === HEADER ===
    const headerHeight = 40;
    doc.setFillColor(darkBlue.r, darkBlue.g, darkBlue.b);
    doc.rect(0, yPos, pageWidth, headerHeight, 'F');
    doc.setTextColor(white.r, white.g, white.b);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('Analyse Investissement Immobilier', margin, yPos + 12);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const currentDate = new Date().toLocaleDateString('fr-FR', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
    doc.text(`Généré le ${currentDate}`, margin, yPos + 22);
    doc.setFontSize(8);
    doc.setTextColor(lightBlue.r, lightBlue.g, lightBlue.b);
    if (extractedData && extractedData.address) {
      doc.text(extractedData.address, margin, yPos + 29);
    }

    yPos = yPos + headerHeight + 10;

    // === 1. INFORMATIONS SUR LE BIEN ===
    doc.setTextColor(darkBlue.r, darkBlue.g, darkBlue.b);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('1. Informations sur le Bien', margin, yPos);
    doc.setDrawColor(primaryBlue.r, primaryBlue.g, primaryBlue.b);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos + 2, margin + 55, yPos + 2);
    yPos += 10;

    if (extractedData && extractedData.address) {
      doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
      doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 30, 2, 2, 'F');
      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.setFontSize(8);
      let infoY = yPos + 7;

      // Row 1: Adresse (spans full width)
      if (extractedData.address) {
        doc.setFont('helvetica', 'bold');
        doc.text('Adresse:', margin + 5, infoY);
        doc.setFont('helvetica', 'normal');
        doc.text(String(extractedData.address), margin + 25, infoY);
        infoY += 6;
      }

      // Row 2: Surface and Chambres (two columns)
      const col1X = margin + 5;
      const col2X = margin + 95;

      doc.setFont('helvetica', 'bold');
      doc.text('Surface:', col1X, infoY);
      doc.setFont('helvetica', 'normal');
      const surfaceText = extractedData.surface ? `${extractedData.surface} m²` : 'N/A';
      doc.text(surfaceText, col1X + 20, infoY);

      doc.setFont('helvetica', 'bold');
      doc.text('Chambres:', col2X, infoY);
      doc.setFont('helvetica', 'normal');
      const roomsText = extractedData.rooms ? String(extractedData.rooms) : 'N/A';
      doc.text(roomsText, col2X + 23, infoY);
      infoY += 6;

      // Row 3: Prix affiché and Type (two columns)
      if (extractedData.price) {
        doc.setFont('helvetica', 'bold');
        doc.text('Prix affiché:', col1X, infoY);
        doc.setFont('helvetica', 'normal');
        doc.text(`${fmt(extractedData.price, 0)} €`, col1X + 27, infoY);
      }

      doc.setFont('helvetica', 'bold');
      doc.text('Type:', col2X, infoY);
      doc.setFont('helvetica', 'normal');
      const typeText = extractedData.type || 'Appartement';
      doc.text(typeText, col2X + 13, infoY);

      yPos += 35;
    } else {
      yPos += 5;
    }

    // Add extra spacing between section 1 and section 2
    yPos += 10;

    // === 2. ACQUISITION ET FINANCEMENT (CÔTE À CÔTE) ===
    checkPageBreak(120);

    doc.setTextColor(darkBlue.r, darkBlue.g, darkBlue.b);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('2. Acquisition et Financement', margin, yPos);
    doc.setDrawColor(primaryBlue.r, primaryBlue.g, primaryBlue.b);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos + 2, margin + 65, yPos + 2);
    yPos += 10;

    const columnWidth = (pageWidth - 2 * margin - 10) / 2;
    const startY = yPos;

    // LEFT COLUMN: Acquisition with bar chart
    let leftYPos = startY;
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Détail de l\'Acquisition', margin, leftYPos);
    leftYPos += 8;

    // Define color mapping for acquisition components
    const allComponents = [
      { label: 'Prix d\'achat', amount: acquisitionData.prixAchat || 0, color: { r: 70, g: 130, b: 180 } },      // Steel Blue
      { label: 'Frais de notaire', amount: acquisitionData.fraisNotaire || 0, color: { r: 255, g: 165, b: 0 } },    // Orange
      { label: 'Frais d\'agence', amount: acquisitionData.fraisAgence || 0, color: { r: 46, g: 184, b: 46 } },      // Light Green
      { label: 'Travaux', amount: acquisitionData.travaux || 0, color: { r: 186, g: 85, b: 211 } },              // Medium Orchid
      { label: 'Autres frais', amount: acquisitionData.autresFrais || 0, color: { r: 255, g: 99, b: 71 } }       // Tomato Red
    ];

    doc.setFontSize(8);
    const tableStartY = leftYPos;
    allComponents.forEach((component, idx) => {
      if (idx % 2 === 0) {
        doc.setFillColor(lightGray.r - 3, lightGray.g - 3, lightGray.b - 3);
        doc.rect(margin, leftYPos - 3, columnWidth, 6, 'F');
      }

      // Color dot on the left
      doc.setFillColor(component.color.r, component.color.g, component.color.b);
      doc.circle(margin + 1.5, leftYPos - 1, 1.2, 'F');

      // Label
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.text(component.label, margin + 5, leftYPos);

      // Amount
      doc.setFont('helvetica', 'bold');
      doc.text(`${fmt(component.amount, 0)} €`, margin + columnWidth - 2, leftYPos, { align: 'right' });
      leftYPos += 6;
    });

    // Store the Y position before the total line for alignment
    const beforeTotalY = leftYPos;
    doc.setDrawColor(darkBlue.r, darkBlue.g, darkBlue.b);
    doc.setLineWidth(0.6);
    doc.line(margin, leftYPos, margin + columnWidth, leftYPos);
    leftYPos += 4;
    const totalLineY = leftYPos; // This is the Y position of the TOTAL text
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(darkBlue.r, darkBlue.g, darkBlue.b);
    doc.text('TOTAL', margin + 2, totalLineY);
    doc.text(`${fmt(totalAcq, 0)} €`, margin + columnWidth - 2, totalLineY, { align: 'right' });
    leftYPos += 15;  // Increased spacing before donut chart

    // Donut chart for full acquisition breakdown (all components)
    const acqDonutCenterX = margin + columnWidth / 2;
    const acqDonutCenterY = leftYPos + 15;
    const acqDonutRadius = 16;

    // Draw multi-segment donut for ALL acquisition components (matching financing thickness)
    if (totalAcq > 0) {
      let startAngle = -90; // Start at top
      allComponents.forEach(component => {
        if (component.amount > 0) {
          const percentage = (component.amount / totalAcq) * 100;
          const sweepAngle = (percentage / 100) * 360;

          // Draw segment with same thickness as financing donut (innerRadius = radius * 0.65)
          const innerRadius = acqDonutRadius * 0.65;
          doc.setFillColor(component.color.r, component.color.g, component.color.b);
          drawDonutSegment(doc, acqDonutCenterX, acqDonutCenterY, acqDonutRadius, innerRadius, startAngle, sweepAngle);

          startAngle += sweepAngle;
        }
      });

      // White center circle
      doc.setFillColor(255, 255, 255);
      doc.circle(acqDonutCenterX, acqDonutCenterY, acqDonutRadius * 0.65, 'F');
    }

    // Display total amount in center
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(darkBlue.r, darkBlue.g, darkBlue.b);
    doc.text(`${fmt(totalAcq / 1000, 0)}k€`, acqDonutCenterX, acqDonutCenterY + 1.5, { align: 'center' });

    leftYPos = acqDonutCenterY + acqDonutRadius + 2;

    // RIGHT COLUMN: Financement avec graphique EN DESSOUS
    let rightYPos = startY;
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Plan de Financement', margin + columnWidth + 10, rightYPos);
    rightYPos += 20;

    // Legend FIRST (above chart)
    const legendX = margin + columnWidth + 10 + 5;
    const legendY = rightYPos;

    doc.setFillColor(accentGreen.r, accentGreen.g, accentGreen.b);
    doc.circle(legendX, legendY, 1.5, 'F');
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Apport', legendX + 4, legendY + 1);
    doc.setFont('helvetica', 'bold');
    doc.text(`${fmt(financementData.apport || 0, 0)} €`, margin + columnWidth + 10 + columnWidth - 2, legendY + 1, { align: 'right' });

    doc.setFillColor(230, 230, 230);
    doc.circle(legendX, legendY + 7, 1.5, 'F');
    doc.setFont('helvetica', 'normal');
    doc.text('Emprunt', legendX + 4, legendY + 8);
    doc.setFont('helvetica', 'bold');
    doc.text(`${fmt(financementData.emprunt || 0, 0)} €`, margin + columnWidth + 10 + columnWidth - 2, legendY + 8, { align: 'right' });

    // Calculate where the Total line should be to align with left column
    const rightTotalY = totalLineY;

    doc.setDrawColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.setLineWidth(0.6);
    doc.line(legendX, rightTotalY - 4, margin + columnWidth + 10 + columnWidth - 2, rightTotalY - 4);
    doc.setTextColor(darkBlue.r, darkBlue.g, darkBlue.b);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Total', legendX, rightTotalY);
    doc.text(`${fmt(totalAcq, 0)} €`, margin + columnWidth + 10 + columnWidth - 2, rightTotalY, { align: 'right' });

    rightYPos = rightTotalY + 15;  // Increased spacing before donut chart

    // Donut chart BELOW total line
    const apportPct = totalAcq > 0 ? ((financementData.apport || 0) / totalAcq * 100) : 0;
    const donutCenterX = margin + columnWidth + 10 + columnWidth / 2;
    const donutCenterY = rightYPos + 15;
    const donutRadius = 16;

    drawDonutChart(donutCenterX, donutCenterY, donutRadius, apportPct, accentGreen, { r: 230, g: 230, b: 230 });

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(darkBlue.r, darkBlue.g, darkBlue.b);
    doc.text(`${fmt(ltv, 0)}%`, donutCenterX, donutCenterY + 2, { align: 'center' });
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.text('LTV', donutCenterX, donutCenterY + 6, { align: 'center' });

    yPos = Math.max(leftYPos, donutCenterY + donutRadius + 5) + 10;

    // === 3. CRÉDIT - ÉCHÉANCIER ET COMPARAISONS ===
    checkPageBreak(150);

    doc.setTextColor(darkBlue.r, darkBlue.g, darkBlue.b);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('3. Conditions du Crédit Immobilier', margin, yPos);
    doc.setDrawColor(primaryBlue.r, primaryBlue.g, primaryBlue.b);
    doc.setLineWidth(1);
    doc.line(margin, yPos + 3, margin + 90, yPos + 3);
    yPos += 15;

    // Caractéristiques du crédit
    const creditBoxWidth = (pageWidth - 2 * margin - 10) / 2;
    const creditBoxHeight = 45;

    doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
    doc.roundedRect(margin, yPos, creditBoxWidth, creditBoxHeight, 3, 3, 'F');

    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('CARACTÉRISTIQUES', margin + 5, yPos + 8);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    let creditYPos = yPos + 16;

    doc.text(`Montant emprunté:`, margin + 5, creditYPos);
    doc.setFont('helvetica', 'bold');
    doc.text(`${fmt(creditData.amount || 0, 0)} €`, margin + creditBoxWidth - 5, creditYPos, { align: 'right' });
    creditYPos += 7;

    doc.setFont('helvetica', 'normal');
    doc.text(`Taux d'intérêt:`, margin + 5, creditYPos);
    doc.setFont('helvetica', 'bold');
    doc.text(`${creditData.interestRate || 0}%`, margin + creditBoxWidth - 5, creditYPos, { align: 'right' });
    creditYPos += 7;

    doc.setFont('helvetica', 'normal');
    doc.text(`Durée:`, margin + 5, creditYPos);
    doc.setFont('helvetica', 'bold');
    doc.text(`${creditData.durationYears || 0} ans`, margin + creditBoxWidth - 5, creditYPos, { align: 'right' });

    // Mensualité box
    doc.setFillColor(primaryBlue.r, primaryBlue.g, primaryBlue.b);
    doc.roundedRect(margin + creditBoxWidth + 10, yPos, creditBoxWidth, creditBoxHeight, 3, 3, 'F');

    doc.setTextColor(white.r, white.g, white.b);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('MENSUALITÉ', margin + creditBoxWidth + 10 + creditBoxWidth / 2, yPos + 12, { align: 'center' });

    if (creditAmortization && creditAmortization.monthlyPayment) {
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text(`${fmt(creditAmortization.monthlyPayment, 0)} €`, margin + creditBoxWidth + 10 + creditBoxWidth / 2, yPos + 28, { align: 'center' });

      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total intérêts: ${fmt(creditAmortization.totalInterest, 0)} €`, margin + creditBoxWidth + 10 + creditBoxWidth / 2, yPos + 38, { align: 'center' });
    }

    yPos += creditBoxHeight + 15;

    // === ÉCHÉANCIER DE REMBOURSEMENT (Graphique) ===
    checkPageBreak(80);

    const graphStartY = yPos;
    const graphWidth = pageWidth - 2 * margin;
    const graphHeight = 50;

    // Title
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Échéancier de Remboursement', margin, yPos);
    yPos += 10;

    // Draw axes
    const chartX = margin + 20;
    const chartY = yPos;
    const chartWidth = graphWidth - 25;
    const chartHeight = 35;

    // Axes
    doc.setDrawColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setLineWidth(0.5);
    doc.line(chartX, chartY + chartHeight, chartX + chartWidth, chartY + chartHeight); // X-axis
    doc.line(chartX, chartY, chartX, chartY + chartHeight); // Y-axis

    // Calculate amortization data for graph
    const loanAmount = creditData.amount || 0;
    const monthlyRate = (creditData.interestRate || 0) / 100 / 12;
    const totalMonths = (creditData.durationYears || 20) * 12;
    const monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / (Math.pow(1 + monthlyRate, totalMonths) - 1);

    // Sample points across the loan duration
    const numPoints = 20;
    const stepMonths = Math.floor(totalMonths / numPoints);
    let remainingBalance = loanAmount;

    doc.setLineWidth(0.6);

    // Draw principal and interest areas
    for (let i = 0; i < numPoints; i++) {
      const month = i * stepMonths;
      const x = chartX + (month / totalMonths) * chartWidth;

      // Calculate remaining balance at this point
      let balance = loanAmount;
      for (let m = 0; m < month; m++) {
        const interest = balance * monthlyRate;
        const principal = monthlyPayment - interest;
        balance -= principal;
      }

      const interestPayment = balance * monthlyRate;
      const principalPayment = monthlyPayment - interestPayment;

      const maxPayment = monthlyPayment * 1.1;
      const principalHeight = (principalPayment / maxPayment) * chartHeight;
      const interestHeight = (interestPayment / maxPayment) * chartHeight;

      // Draw stacked bars
      // Principal (blue)
      doc.setFillColor(primaryBlue.r, primaryBlue.g, primaryBlue.b);
      doc.rect(x, chartY + chartHeight - principalHeight, chartWidth / numPoints * 0.8, principalHeight, 'F');

      // Interest (orange)
      doc.setFillColor(255, 165, 0);
      doc.rect(x, chartY + chartHeight - principalHeight - interestHeight, chartWidth / numPoints * 0.8, interestHeight, 'F');
    }

    // Axis labels
    doc.setFontSize(7);
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.text('Années', chartX + chartWidth / 2, chartY + chartHeight + 5, { align: 'center' });
    doc.text('0', chartX - 2, chartY + chartHeight + 3, { align: 'right' });
    doc.text(`${creditData.durationYears || 20}`, chartX + chartWidth, chartY + chartHeight + 3, { align: 'center' });

    // Y-axis label at top
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.text(`${fmt(monthlyPayment, 0)}€`, chartX - 3, chartY - 2, { align: 'right' });
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('/mois', chartX - 3, chartY + 2, { align: 'right' });

    yPos += chartHeight + 20;

    // Legend - positioned BELOW the chart with more spacing
    doc.setFontSize(8);
    const legendStartX = chartX + chartWidth / 2 - 25;
    const legendStartY = yPos - 8;

    doc.setFillColor(255, 165, 0);
    doc.rect(legendStartX, legendStartY, 5, 3.5, 'F');
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFont('helvetica', 'normal');
    doc.text('Intérêts', legendStartX + 7, legendStartY + 2.5);

    doc.setFillColor(primaryBlue.r, primaryBlue.g, primaryBlue.b);
    doc.rect(legendStartX + 30, legendStartY, 5, 3.5, 'F');
    doc.text('Principal', legendStartX + 37, legendStartY + 2.5);

    yPos += 8;

    // === TABLEAU COMPARATIF ÉTENDU (Intérêts totaux) ===
    checkPageBreak(80);

    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Comparaison Intérêts Totaux (en k€)', margin, yPos);
    yPos += 10;

    // Define comparison scenarios - FULL TABLE WITH SMALLER FONTS
    const baseRate = creditData.interestRate || 4;
    const baseDuration = creditData.durationYears || 20;

    // Create 5 rates (full range)
    const rates = [
      baseRate - 1.0,
      baseRate - 0.5,
      baseRate,
      baseRate + 0.5,
      baseRate + 1.0
    ];

    // Create 6 durations (full range)
    const durations = [
      Math.max(10, baseDuration - 10),
      Math.max(10, baseDuration - 5),
      baseDuration,
      baseDuration + 5,
      baseDuration + 10,
      baseDuration + 15
    ];

    // Table dimensions - smaller cells for fitting on one page
    const cellWidth = 25;
    const cellHeight = 6.5;
    const compTableStartX = margin;
    const compTableStartY = yPos;

    // Header row (durations)
    doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
    for (let i = 0; i <= durations.length; i++) {
      doc.rect(compTableStartX + i * cellWidth, compTableStartY, cellWidth, cellHeight, 'F');
    }

    doc.setFontSize(6);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(darkBlue.r, darkBlue.g, darkBlue.b);
    doc.text('Taux/Durée', compTableStartX + cellWidth / 2, compTableStartY + 4.5, { align: 'center' });

    durations.forEach((duration, i) => {
      doc.text(`${duration}a`, compTableStartX + (i + 1) * cellWidth + cellWidth / 2, compTableStartY + 4.5, { align: 'center' });
    });

    // Draw grid
    doc.setDrawColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.setLineWidth(0.4);

    const totalRows = rates.length + 1;
    const totalCols = durations.length + 1;

    // Draw internal lines with thinner line
    doc.setLineWidth(0.3);

    // Internal Vertical lines (skip first and last as they're part of border)
    for (let i = 1; i < totalCols; i++) {
      doc.line(compTableStartX + i * cellWidth, compTableStartY, compTableStartX + i * cellWidth, compTableStartY + cellHeight * totalRows);
    }

    // Internal Horizontal lines (skip first and last as they're part of border)
    for (let i = 1; i < totalRows; i++) {
      doc.line(compTableStartX, compTableStartY + i * cellHeight, compTableStartX + cellWidth * totalCols, compTableStartY + i * cellHeight);
    }

    // Data rows
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.5);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);

    rates.forEach((rate, i) => {
      const rowY = compTableStartY + cellHeight * (i + 1);

      // Rate label
      doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
      doc.rect(compTableStartX, rowY, cellWidth, cellHeight, 'F');
      doc.setFont('helvetica', 'bold');
      doc.text(`${rate.toFixed(1)}%`, compTableStartX + cellWidth / 2, rowY + 4.5, { align: 'center' });

      // Calculate interest for each duration
      durations.forEach((duration, j) => {
        const r = rate / 100 / 12;
        const n = duration * 12;
        const P = loanAmount;

        if (r === 0) {
          // No interest
          doc.setFont('helvetica', 'normal');
          doc.text('0k', compTableStartX + (j + 1) * cellWidth + cellWidth / 2, rowY + 4.5, { align: 'center' });
        } else {
          const M = P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
          const totalInt = M * n - P;

          const cellX = compTableStartX + (j + 1) * cellWidth;

          // Highlight current scenario
          if (Math.abs(rate - baseRate) < 0.01 && duration === baseDuration) {
            doc.setFillColor(200, 230, 201);  // Light green
            doc.rect(cellX, rowY, cellWidth, cellHeight, 'F');
            doc.setFont('helvetica', 'bold');
          } else {
            doc.setFont('helvetica', 'normal');
          }

          doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
          doc.text(`${fmt(totalInt / 1000, 0)}k`, cellX + cellWidth / 2, rowY + 4.5, { align: 'center' });
        }
      });
    });

    // Draw outer border with thicker line (after data to ensure it's on top)
    doc.setDrawColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.setLineWidth(0.5);
    doc.rect(compTableStartX, compTableStartY, cellWidth * totalCols, cellHeight * totalRows);

    yPos += cellHeight * totalRows + 10;

    // === TABLEAU COMPARATIF MENSUALITÉS ===
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Comparaison Mensualités (en €)', margin, yPos);
    yPos += 8;

    // Use same rates and durations as previous table
    const monthTableStartX = margin;
    const monthTableStartY = yPos;

    // Header row (durations)
    doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
    for (let i = 0; i <= durations.length; i++) {
      doc.rect(monthTableStartX + i * cellWidth, monthTableStartY, cellWidth, cellHeight, 'F');
    }

    doc.setFontSize(6);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(darkBlue.r, darkBlue.g, darkBlue.b);
    doc.text('Taux/Durée', monthTableStartX + cellWidth / 2, monthTableStartY + 4.5, { align: 'center' });

    durations.forEach((duration, i) => {
      doc.text(`${duration}a`, monthTableStartX + (i + 1) * cellWidth + cellWidth / 2, monthTableStartY + 4.5, { align: 'center' });
    });

    // Draw grid
    doc.setDrawColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.setLineWidth(0.4);

    // Draw internal lines with thinner line
    doc.setLineWidth(0.3);

    // Internal Vertical lines (skip first and last as they're part of border)
    for (let i = 1; i < totalCols; i++) {
      doc.line(monthTableStartX + i * cellWidth, monthTableStartY, monthTableStartX + i * cellWidth, monthTableStartY + cellHeight * totalRows);
    }

    // Internal Horizontal lines (skip first and last as they're part of border)
    for (let i = 1; i < totalRows; i++) {
      doc.line(monthTableStartX, monthTableStartY + i * cellHeight, monthTableStartX + cellWidth * totalCols, monthTableStartY + i * cellHeight);
    }

    // Data rows
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.5);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);

    rates.forEach((rate, i) => {
      const rowY = monthTableStartY + cellHeight * (i + 1);

      // Rate label
      doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
      doc.rect(monthTableStartX, rowY, cellWidth, cellHeight, 'F');
      doc.setFont('helvetica', 'bold');
      doc.text(`${rate.toFixed(1)}%`, monthTableStartX + cellWidth / 2, rowY + 4.5, { align: 'center' });

      // Calculate monthly payment for each duration
      durations.forEach((duration, j) => {
        const r = rate / 100 / 12;
        const n = duration * 12;
        const P = loanAmount;

        if (r === 0) {
          // No interest - simple division
          const monthlyPmt = P / n;
          doc.setFont('helvetica', 'normal');
          doc.text(`${fmt(monthlyPmt, 0)}`, monthTableStartX + (j + 1) * cellWidth + cellWidth / 2, rowY + 4.5, { align: 'center' });
        } else {
          const M = P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);

          const cellX = monthTableStartX + (j + 1) * cellWidth;

          // Highlight current scenario
          if (Math.abs(rate - baseRate) < 0.01 && duration === baseDuration) {
            doc.setFillColor(200, 230, 201);  // Light green
            doc.rect(cellX, rowY, cellWidth, cellHeight, 'F');
            doc.setFont('helvetica', 'bold');
          } else {
            doc.setFont('helvetica', 'normal');
          }

          doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
          doc.text(`${fmt(M, 0)}`, cellX + cellWidth / 2, rowY + 4.5, { align: 'center' });
        }
      });
    });

    // Draw outer border with thicker line (after data to ensure it's on top)
    doc.setDrawColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.setLineWidth(0.5);
    doc.rect(monthTableStartX, monthTableStartY, cellWidth * totalCols, cellHeight * totalRows);

    yPos += cellHeight * totalRows + 10;

    // === 4. PERFORMANCE - HYPOTHÈSES ET MÉTRIQUES ===
    checkPageBreak(200);

    doc.setTextColor(darkBlue.r, darkBlue.g, darkBlue.b);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('4. Analyse de Performance', margin, yPos);
    doc.setDrawColor(primaryBlue.r, primaryBlue.g, primaryBlue.b);
    doc.setLineWidth(1);
    doc.line(margin, yPos + 3, margin + 70, yPos + 3);
    yPos += 15;

    // HYPOTHÈSES
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Hypothèses', margin, yPos);
    yPos += 10;

    doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
    doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 35, 3, 3, 'F');

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    let hypY = yPos + 10;

    doc.text('Horizon de revente:', margin + 5, hypY);
    doc.setFont('helvetica', 'bold');
    const horizonYears = (performanceParams.investmentHorizonMonths || 120) / 12;
    doc.text(`${horizonYears.toFixed(2)} ans`, margin + 60, hypY);

    doc.setFont('helvetica', 'normal');
    doc.text('Prix de revente estimé:', margin + 5, hypY + 8);
    doc.setFont('helvetica', 'bold');
    doc.text(`${fmt(estimatedHomeValue || totalAcq, 0)} €`, margin + 60, hypY + 8);

    doc.setFont('helvetica', 'normal');
    doc.text('Taux d\'inflation:', margin + 5, hypY + 16);
    doc.setFont('helvetica', 'bold');
    doc.text(`${performanceParams.housePriceInflation || 2}%`, margin + 60, hypY + 16);

    doc.setFont('helvetica', 'normal');
    doc.text('Taux d\'actualisation:', margin + 95, hypY);
    doc.setFont('helvetica', 'bold');
    const discountRatePercent = creditData.interestRate || 4;
    doc.text(`${discountRatePercent}%`, margin + 155, hypY);

    yPos += 45;

    // === PERFORMANCE APRÈS TRAVAUX (4 KPIs) ===
    checkPageBreak(50);

    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Performance après travaux', margin, yPos);
    yPos += 10;

    // Calculate Performance Après Travaux metrics
    const purchasePrice = acquisitionData.prixAchat || 0;
    const renovationCost = acquisitionData.travaux || 0;
    const estimatedValue = estimatedHomeValue || (purchasePrice + renovationCost);
    const addedValue = estimatedValue - (purchasePrice + renovationCost);
    const surfaceArea = extractedData?.surface || 0;
    const pricePerSqmBefore = surfaceArea > 0 ? purchasePrice / surfaceArea : 0;
    const pricePerSqmAfter = surfaceArea > 0 ? estimatedValue / surfaceArea : 0;
    const pricePerSqmWithRenovation = surfaceArea > 0 ? (purchasePrice + renovationCost) / surfaceArea : 0;
    const addedValuePercent = (purchasePrice + renovationCost) > 0
      ? (addedValue / (purchasePrice + renovationCost)) * 100
      : 0;

    // 4-column grid for Performance Après Travaux
    const kpiBoxWidth = (pageWidth - 2 * margin - 15) / 4;
    const kpiBoxHeight = 22;
    const kpiStartY = yPos;

    // Box 1: Plus-value après travaux (hors charges)
    doc.setFillColor(240, 240, 240);
    doc.roundedRect(margin, kpiStartY, kpiBoxWidth, kpiBoxHeight, 2, 2, 'F');
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    // Split label into two lines
    doc.text('Plus-value après travaux', margin + kpiBoxWidth / 2, kpiStartY + 4, { align: 'center' });
    doc.text('(hors charges)', margin + kpiBoxWidth / 2, kpiStartY + 8, { align: 'center' });
    const pvColor = addedValue >= 0 ? accentGreen : accentRed;
    doc.setTextColor(pvColor.r, pvColor.g, pvColor.b);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${fmt(addedValue, 0)} €`, margin + kpiBoxWidth / 2, kpiStartY + 16, { align: 'center' });
    doc.setFontSize(7);
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.text(`${addedValue >= 0 ? '+' : ''}${fmt(addedValuePercent, 2)}%`, margin + kpiBoxWidth / 2, kpiStartY + 20, { align: 'center' });

    // Box 2: Prix au m² avant travaux
    doc.setFillColor(240, 240, 240);
    doc.roundedRect(margin + kpiBoxWidth + 5, kpiStartY, kpiBoxWidth, kpiBoxHeight, 2, 2, 'F');
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Prix au m² avant travaux', margin + kpiBoxWidth + 5 + kpiBoxWidth / 2, kpiStartY + 6, { align: 'center' });
    doc.setTextColor(primaryBlue.r, primaryBlue.g, primaryBlue.b);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    if (surfaceArea > 0) {
      doc.text(`${fmt(pricePerSqmBefore, 0)} €/m²`, margin + kpiBoxWidth + 5 + kpiBoxWidth / 2, kpiStartY + 17, { align: 'center' });
    } else {
      doc.setFontSize(10);
      doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
      doc.text('N/A', margin + kpiBoxWidth + 5 + kpiBoxWidth / 2, kpiStartY + 17, { align: 'center' });
    }

    // Box 3: Prix au m² avec travaux (sans plus-value)
    doc.setFillColor(240, 240, 240);
    doc.roundedRect(margin + 2 * (kpiBoxWidth + 5), kpiStartY, kpiBoxWidth, kpiBoxHeight, 2, 2, 'F');
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    // Split label into two lines
    doc.text('Prix au m² avec travaux', margin + 2 * (kpiBoxWidth + 5) + kpiBoxWidth / 2, kpiStartY + 4, { align: 'center' });
    doc.text('(sans plus-value)', margin + 2 * (kpiBoxWidth + 5) + kpiBoxWidth / 2, kpiStartY + 8, { align: 'center' });
    doc.setTextColor(255, 191, 36);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    if (surfaceArea > 0) {
      doc.text(`${fmt(pricePerSqmWithRenovation, 0)} €/m²`, margin + 2 * (kpiBoxWidth + 5) + kpiBoxWidth / 2, kpiStartY + 17, { align: 'center' });
    } else {
      doc.setFontSize(10);
      doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
      doc.text('N/A', margin + 2 * (kpiBoxWidth + 5) + kpiBoxWidth / 2, kpiStartY + 17, { align: 'center' });
    }

    // Box 4: Prix au m² après travaux
    doc.setFillColor(240, 240, 240);
    doc.roundedRect(margin + 3 * (kpiBoxWidth + 5), kpiStartY, kpiBoxWidth, kpiBoxHeight, 2, 2, 'F');
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Prix au m² après travaux', margin + 3 * (kpiBoxWidth + 5) + kpiBoxWidth / 2, kpiStartY + 6, { align: 'center' });
    doc.setTextColor(52, 211, 153);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    if (surfaceArea > 0) {
      doc.text(`${fmt(pricePerSqmAfter, 0)} €/m²`, margin + 3 * (kpiBoxWidth + 5) + kpiBoxWidth / 2, kpiStartY + 16, { align: 'center' });
      doc.setFontSize(7);
      const diffColor = pricePerSqmAfter - pricePerSqmWithRenovation > 0 ? accentGreen : (pricePerSqmAfter - pricePerSqmWithRenovation < 0 ? accentRed : mediumGray);
      doc.setTextColor(diffColor.r, diffColor.g, diffColor.b);
      doc.text(`${pricePerSqmAfter - pricePerSqmWithRenovation > 0 ? '+' : ''}${fmt(pricePerSqmAfter - pricePerSqmWithRenovation, 0)} €/m²`, margin + 3 * (kpiBoxWidth + 5) + kpiBoxWidth / 2, kpiStartY + 20, { align: 'center' });
    } else {
      doc.setFontSize(10);
      doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
      doc.text('N/A', margin + 3 * (kpiBoxWidth + 5) + kpiBoxWidth / 2, kpiStartY + 17, { align: 'center' });
    }

    yPos = kpiStartY + kpiBoxHeight + 8;

    // === PERFORMANCE LOCATIVE (4 KPIs) ===
    checkPageBreak(50);

    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Performance Locative', margin, yPos);
    yPos += 10;

    // Calculate Performance Locative metrics (EXACT same calculations as in UI)
    const perfTotalMonths = performanceParams.investmentHorizonMonths || 120;
    const yearsExact = perfTotalMonths / 12;
    const totalInvestment = financementData.apport || totalAcq;
    const perfMonthlyPayment = creditAmortization.monthlyPayment || 0;

    // Helper function to calculate monthly amount from frequency
    const calculateMonthlyAmount = (amount, frequency) => {
      if (frequency === 'monthly') return amount;
      if (frequency === 'quarterly') return amount / 3;
      if (frequency === 'annually' || frequency === 'yearly') return amount / 12;
      return 0;
    };

    // Build monthlyData array for exact calculations (matching UI)
    const monthlyData = Array.from({length: perfTotalMonths + 1}, (_, monthIndex) => {
      if (monthIndex === 0) {
        return { month: 0, netCashFlow: -totalInvestment, operationalCashFlow: 0 };
      }

      const yearOfMonth = Math.floor((monthIndex - 1) / 12);
      const monthInYear = ((monthIndex - 1) % 12) + 1;

      // Monthly revenue with startLag and indexation
      const monthRevenue = propertyRevenues.reduce((sum, rev) => {
        const startLag = rev.startLagMonths || 0;
        if (monthIndex <= startLag) return sum;

        const revAmount = parseFloat(rev.amount) || 0;
        const occupancy = (parseFloat(rev.occupancyRate) || 100) / 100;
        const freq = rev.frequency || 'monthly';
        const indexation = parseFloat(rev.indexation) || 0;

        let amount = 0;
        if (freq === 'monthly') {
          amount = revAmount * occupancy;
        } else if (freq === 'quarterly' && monthInYear % 3 === 0) {
          amount = revAmount * occupancy;
        } else if ((freq === 'annually' || freq === 'yearly') && monthInYear === 12) {
          amount = revAmount * occupancy;
        }
        return sum + (amount * Math.pow(1 + indexation / 100, yearOfMonth));
      }, 0);

      // Monthly fees (with pro-rata for final month)
      const isLastMonth = monthIndex === perfTotalMonths;
      const monthFees = recurrentFees.reduce((sum, fee) => {
        const feeAmount = parseFloat(fee.amount) || 0;
        const freq = fee.frequency || 'monthly';
        let amount = 0;
        if (freq === 'monthly') {
          amount = feeAmount;
        } else if (freq === 'quarterly') {
          if (monthInYear % 3 === 0) {
            amount = feeAmount;
          } else if (isLastMonth) {
            const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
            amount = feeAmount * (monthsIntoQuarter / 3);
          }
        } else if (freq === 'annually' || freq === 'yearly') {
          if (monthInYear === 12) {
            amount = feeAmount;
          } else if (isLastMonth) {
            amount = feeAmount * (monthInYear / 12);
          }
        }
        return sum + amount;
      }, 0);

      // Get actual credit payment from schedule (principal + interest)
      const scheduleIndex = monthIndex - 1;
      const scheduleEntry = creditAmortization.schedule?.[scheduleIndex];
      const actualCreditPayment = scheduleEntry ? (scheduleEntry.principal + scheduleEntry.interest) : 0;

      const operationalCashFlow = monthRevenue - monthFees - actualCreditPayment;
      let netCashFlow = operationalCashFlow;

      // Add capital gain in last month (matching UI calculation)
      if (monthIndex === perfTotalMonths) {
        const totalCreditAmount = creditData.amount || 0;
        const initialHomeValue = estimatedHomeValue || (totalInvestment + totalCreditAmount);
        const finalHomeValue = initialHomeValue * Math.pow(1 + performanceParams.housePriceInflation / 100, perfTotalMonths / 12);
        const scheduleLength = creditAmortization.schedule?.length || 0;
        const finalBalance = scheduleLength > 0
          ? creditAmortization.schedule[Math.min(perfTotalMonths - 1, scheduleLength - 1)]?.remainingBalance || 0
          : 0;
        netCashFlow += (finalHomeValue - finalBalance);
      }

      return { month: monthIndex, netCashFlow, operationalCashFlow };
    });

    // Cash-Flow Mensuel Moyen: Sum of all operational Cash-Flow (except month 0) / number of months
    const totalOperationalCashFlow = monthlyData
      .filter((data, idx) => idx > 0)
      .reduce((sum, data) => sum + data.operationalCashFlow, 0);
    const averageMonthlyCashFlow = perfTotalMonths > 0 ? totalOperationalCashFlow / perfTotalMonths : 0;

    // For netProfitability calculation
    const monthlyRevenue = propertyRevenues.reduce((sum, rev) =>
      sum + (calculateMonthlyAmount(rev.amount, rev.frequency) * (rev.occupancyRate / 100)), 0);
    const monthlyCharges = recurrentFees.reduce((sum, fee) =>
      sum + calculateMonthlyAmount(fee.amount, fee.frequency), 0);

    const totalRent = monthlyRevenue * perfTotalMonths;
    const totalCharges = monthlyCharges * perfTotalMonths;
    const totalCredit = perfMonthlyPayment * perfTotalMonths;
    const netProfitability = totalInvestment > 0 ? ((totalRent - totalCharges - totalCredit) / totalInvestment) * 100 : 0;
    const totalInterest = creditAmortization?.schedule?.slice(0, perfTotalMonths).reduce((sum, month) => sum + month.interest, 0) || 0;
    // Ratio Loyer / Prix avec Travaux
    const totalAcqWithWorksPerf = (acquisitionData.prixAchat || 0) + (acquisitionData.travaux || 0);
    const dscr = totalAcqWithWorksPerf > 0 ? (monthlyRevenue * 12) / totalAcqWithWorksPerf : 0;
    const rentPerSqm = surfaceArea > 0 ? monthlyRevenue / surfaceArea : 0;

    // Performance Totale calculations (matching UI exactly)
    // Total cash flows (already includes capital gain in last month from monthlyData calculation above)
    const totalCashFlows = monthlyData.reduce((sum, d) => sum + d.netCashFlow, 0);

    // ROE - Return on Equity (total net revenues / equity invested)
    const perfROE = totalInvestment > 0 ? (totalCashFlows / totalInvestment) * 100 : 0;

    // IRR calculation - totalCashFlows already includes the capital gain, so we DON'T add it again
    const totalCreditAmount = creditData.amount || 0;
    const initialHomeValue = estimatedHomeValue || (totalInvestment + totalCreditAmount);
    const finalHomeValue = initialHomeValue * Math.pow(1 + performanceParams.housePriceInflation / 100, yearsExact);
    const scheduleLength = creditAmortization.schedule?.length || 0;
    const finalBalance = scheduleLength > 0
      ? creditAmortization.schedule[Math.min(perfTotalMonths - 1, scheduleLength - 1)]?.remainingBalance || 0
      : 0;
    // TRI calculation - matching UI exactly (UI adds capital gain again even though it's already in monthlyData)
    const perfTRI = totalInvestment > 0 && yearsExact > 0
      ? ((totalCashFlows + (finalHomeValue - finalBalance) - totalInvestment) / totalInvestment / yearsExact) * 100
      : 0;

    // VAN (NPV) - Net Present Value using monthly discounting
    const monthlyDiscountRate = (creditData.interestRate || 0) / 100 / 12;
    const perfVAN = monthlyData.reduce((sum, data) => {
      const month = data.month;
      if (month === 0) {
        return sum + data.netCashFlow;
      }
      const discountFactor = Math.pow(1 + monthlyDiscountRate, month);
      return sum + (data.netCashFlow / discountFactor);
    }, 0);

    // NCF - Total Net Cash Flow
    const perfNCF = totalCashFlows;

    const kpiLocStartY = yPos;

    // Box 1: Cash-Flow Mensuel Moyen (t) avec split labels
    doc.setFillColor(240, 240, 240);
    doc.roundedRect(margin, kpiLocStartY, kpiBoxWidth, kpiBoxHeight, 2, 2, 'F');
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Cash-Flow Mensuel Moyen', margin + kpiBoxWidth / 2, kpiLocStartY + 4, { align: 'center' });
    doc.text('(t)', margin + kpiBoxWidth / 2, kpiLocStartY + 8, { align: 'center' });
    const cfColor = averageMonthlyCashFlow >= 0 ? accentGreen : accentRed;
    doc.setTextColor(cfColor.r, cfColor.g, cfColor.b);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${fmt(averageMonthlyCashFlow, 1)}`, margin + kpiBoxWidth / 2, kpiLocStartY + 17, { align: 'center' });

    // Box 2: Rentabilité Nette Prévisionnelle avec split labels
    doc.setFillColor(240, 240, 240);
    doc.roundedRect(margin + kpiBoxWidth + 5, kpiLocStartY, kpiBoxWidth, kpiBoxHeight, 2, 2, 'F');
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Rentabilité Nette', margin + kpiBoxWidth + 5 + kpiBoxWidth / 2, kpiLocStartY + 4, { align: 'center' });
    doc.text('Prévisionnelle', margin + kpiBoxWidth + 5 + kpiBoxWidth / 2, kpiLocStartY + 8, { align: 'center' });
    const npColor = netProfitability >= 0 ? accentGreen : accentRed;
    doc.setTextColor(npColor.r, npColor.g, npColor.b);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${fmt(netProfitability, 2)}%`, margin + kpiBoxWidth + 5 + kpiBoxWidth / 2, kpiLocStartY + 17, { align: 'center' });

    // Box 3: Ratio Loyer / Prix avec Travaux
    doc.setFillColor(240, 240, 240);
    doc.roundedRect(margin + 2 * (kpiBoxWidth + 5), kpiLocStartY, kpiBoxWidth, kpiBoxHeight, 2, 2, 'F');
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Ratio Loyer / Prix', margin + 2 * (kpiBoxWidth + 5) + kpiBoxWidth / 2, kpiLocStartY + 4, { align: 'center' });
    doc.text('avec Travaux', margin + 2 * (kpiBoxWidth + 5) + kpiBoxWidth / 2, kpiLocStartY + 8, { align: 'center' });
    const dscrColorCalc = dscr >= 0.06 ? accentGreen : (dscr >= 0.04 ? { r: 251, g: 191, b: 36 } : accentRed);
    doc.setTextColor(dscrColorCalc.r, dscrColorCalc.g, dscrColorCalc.b);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${fmt(dscr * 100, 2)}%`, margin + 2 * (kpiBoxWidth + 5) + kpiBoxWidth / 2, kpiLocStartY + 17, { align: 'center' });

    // Box 4: Loyer au m²
    doc.setFillColor(240, 240, 240);
    doc.roundedRect(margin + 3 * (kpiBoxWidth + 5), kpiLocStartY, kpiBoxWidth, kpiBoxHeight, 2, 2, 'F');
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Loyer au m²', margin + 3 * (kpiBoxWidth + 5) + kpiBoxWidth / 2, kpiLocStartY + 6, { align: 'center' });
    doc.setTextColor(primaryBlue.r, primaryBlue.g, primaryBlue.b);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    if (surfaceArea > 0) {
      doc.text(`${fmt(rentPerSqm, 2)} €/m²`, margin + 3 * (kpiBoxWidth + 5) + kpiBoxWidth / 2, kpiLocStartY + 17, { align: 'center' });
    } else {
      doc.setFontSize(10);
      doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
      doc.text('N/A', margin + 3 * (kpiBoxWidth + 5) + kpiBoxWidth / 2, kpiLocStartY + 17, { align: 'center' });
    }

    yPos = kpiLocStartY + kpiBoxHeight + 8;

    // MÉTRIQUES DE PERFORMANCE TOTALE (4 KPIs)
    checkPageBreak(90);

    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Performance Totale (à la revente)', margin, yPos);
    yPos += 10;

    // Use calculated values instead of state
    const ncf = perfNCF;
    const tri = perfTRI;
    const van = perfVAN;
    const roe = perfROE;

    if (true) { // Always show performance totale

      const perfTotKpiWidth = (pageWidth - 2 * margin - 15) / 4;
      const perfTotKpiHeight = 22;
      const perfTotStartY = yPos;

      // Box 1: TRI (RENDEMENT)
      doc.setFillColor(240, 240, 240);
      doc.roundedRect(margin, perfTotStartY, perfTotKpiWidth, perfTotKpiHeight, 2, 2, 'F');

      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      // Split label into two lines
      doc.text('Taux de Rentabilité Interne', margin + perfTotKpiWidth / 2, perfTotStartY + 4, { align: 'center' });
      doc.text('(TRI)', margin + perfTotKpiWidth / 2, perfTotStartY + 8, { align: 'center' });

      const triColor = tri >= 8 ? accentGreen : (tri >= 5 ? primaryBlue : accentRed);
      doc.setTextColor(triColor.r, triColor.g, triColor.b);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`${fmt(tri, 2)}%`, margin + perfTotKpiWidth / 2, perfTotStartY + 17, { align: 'center' });

      // Box 2: ROE (FONDS PROPRES)
      doc.setFillColor(240, 240, 240);
      doc.roundedRect(margin + perfTotKpiWidth + 5, perfTotStartY, perfTotKpiWidth, perfTotKpiHeight, 2, 2, 'F');

      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      // Split label into two lines
      doc.text('Rentabilité des Fonds', margin + perfTotKpiWidth + 5 + perfTotKpiWidth / 2, perfTotStartY + 4, { align: 'center' });
      doc.text('Propres (ROE)', margin + perfTotKpiWidth + 5 + perfTotKpiWidth / 2, perfTotStartY + 8, { align: 'center' });

      const roeColor = roe >= 10 ? accentGreen : (roe >= 5 ? primaryBlue : accentRed);
      doc.setTextColor(roeColor.r, roeColor.g, roeColor.b);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`${fmt(roe, 2)}%`, margin + perfTotKpiWidth + 5 + perfTotKpiWidth / 2, perfTotStartY + 17, { align: 'center' });

      // Box 3: VAN (Valeur Actuelle Nette)
      doc.setFillColor(240, 240, 240);
      doc.roundedRect(margin + 2 * (perfTotKpiWidth + 5), perfTotStartY, perfTotKpiWidth, perfTotKpiHeight, 2, 2, 'F');

      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      // Split label into two lines
      doc.text('Valeur Actuelle Nette', margin + 2 * (perfTotKpiWidth + 5) + perfTotKpiWidth / 2, perfTotStartY + 4, { align: 'center' });
      doc.text('(VAN)', margin + 2 * (perfTotKpiWidth + 5) + perfTotKpiWidth / 2, perfTotStartY + 8, { align: 'center' });

      const vanColor = van >= 0 ? accentGreen : accentRed;
      doc.setTextColor(vanColor.r, vanColor.g, vanColor.b);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`${fmt(van, 0)}`, margin + 2 * (perfTotKpiWidth + 5) + perfTotKpiWidth / 2, perfTotStartY + 17, { align: 'center' });

      // Box 4: NCF (CASH-FLOW NET)
      doc.setFillColor(240, 240, 240);
      doc.roundedRect(margin + 3 * (perfTotKpiWidth + 5), perfTotStartY, perfTotKpiWidth, perfTotKpiHeight, 2, 2, 'F');

      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      doc.text('Cash-Flow Net (NCF)', margin + 3 * (perfTotKpiWidth + 5) + perfTotKpiWidth / 2, perfTotStartY + 6, { align: 'center' });

      const ncfColor = ncf >= 0 ? accentGreen : accentRed;
      doc.setTextColor(ncfColor.r, ncfColor.g, ncfColor.b);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`${fmt(ncf, 0)}`, margin + 3 * (perfTotKpiWidth + 5) + perfTotKpiWidth / 2, perfTotStartY + 17, { align: 'center' });

      yPos = perfTotStartY + perfTotKpiHeight + 10;

    }

    // === CASH FLOW CHARTS (part of Performance section) ===
    // Don't force page break - let it flow naturally on same page

    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Cash Flow', margin, yPos);
    yPos += 6; // Reduced from 8 to 6

    // MATCH APP UI: Calculate cash flow data with Year 0 and capital gain
    const cfHorizonMonths = performanceParams.investmentHorizonMonths || 120;
    const cfHorizonYears = Math.ceil(cfHorizonMonths / 12);
    const cfLoanDurationMonths = perfTotalMonths;
    // totalInvestment is already defined above at line 3369

    // Calculate annual cash flows (including Year 0)
    const yearlyData = [];
    let runningTotal = 0;

    // Year 0: Initial investment (negative)
    const year0CF = -totalInvestment;
    runningTotal += year0CF;
    yearlyData.push({
      year: 0,
      annual: year0CF,
      cumulative: runningTotal
    });

    // Debug monthly values
    console.log('=== MONTHLY VALUES DEBUG ===');
    console.log('Monthly revenue:', monthlyRevenue);
    console.log('Monthly charges:', monthlyCharges);
    console.log('Monthly loan payment:', perfMonthlyPayment);
    console.log('Monthly net CF:', monthlyRevenue - monthlyCharges - perfMonthlyPayment);
    console.log('========================');

    // Years 1 to N: Operational cash flows
    for (let y = 0; y < cfHorizonYears; y++) {
      const yearNumber = y + 1;
      const monthStart = y * 12;
      const monthEnd = Math.min((y + 1) * 12, cfHorizonMonths);
      const monthCount = monthEnd - monthStart;
      const isLastYear = (y === cfHorizonYears - 1);

      // Revenue for this year
      const yearRevenue = monthlyRevenue * monthCount;

      // Charges for this year
      const yearCharges = monthlyCharges * monthCount;

      // Loan payments for this year (only count months within loan duration)
      let yearLoanPayments = 0;
      for (let m = monthStart; m < monthEnd; m++) {
        if (m < cfLoanDurationMonths) {
          yearLoanPayments += perfMonthlyPayment;
        }
      }

      // Annual cash flow (operational)
      let annualCF = yearRevenue - yearCharges - yearLoanPayments;

      if (y === 0) {
        console.log('=== YEAR 1 DETAIL ===');
        console.log('Month count:', monthCount);
        console.log('Year revenue:', yearRevenue);
        console.log('Year charges:', yearCharges);
        console.log('Year loan payments:', yearLoanPayments);
        console.log('Annual CF:', annualCF);
        console.log('==================');
      }

      // Add capital gain in the last year (property sale)
      if (isLastYear) {
        const yearsElapsed = cfHorizonMonths / 12;
        const appreciatedValue = estimatedValue * Math.pow(1 + performanceParams.housePriceInflation / 100, yearsElapsed);

        // Calculate remaining loan balance at end of investment horizon
        let remainingBalance = 0;
        if (cfHorizonMonths < cfLoanDurationMonths && creditAmortization?.schedule) {
          const scheduleEntry = creditAmortization.schedule[cfHorizonMonths - 1];
          remainingBalance = scheduleEntry ? scheduleEntry.remainingBalance : 0;
        }

        const capitalGain = appreciatedValue - remainingBalance;

        console.log('=== LAST YEAR DEBUG ===');
        console.log('Year:', yearNumber);
        console.log('Estimated value:', estimatedValue);
        console.log('Years elapsed:', yearsElapsed);
        console.log('House inflation:', performanceParams.housePriceInflation);
        console.log('Appreciated value:', appreciatedValue);
        console.log('Loan duration months:', cfLoanDurationMonths);
        console.log('Horizon months:', cfHorizonMonths);
        console.log('Remaining balance:', remainingBalance);
        console.log('Capital gain:', capitalGain);
        console.log('Operational CF:', annualCF);
        console.log('After adding capital gain:', annualCF + capitalGain);

        annualCF += capitalGain;
      }

      runningTotal += annualCF;

      yearlyData.push({
        year: yearNumber,
        annual: annualCF,
        cumulative: runningTotal
      });
    }

    console.log('=== FINAL YEARLY DATA ===');
    console.log('Total years:', yearlyData.length);
    yearlyData.forEach(d => console.log(`Year ${d.year}: annual=${d.annual}, cumulative=${d.cumulative}`));

    // SIMPLE CHART RENDERING - Side by side, very compact for single page
    const chartW = (pageWidth - 2 * margin - 10) / 2;
    const chartH = 30; // Reduced from 35 to 30 for better fit
    const yAxisW = 12;
    const chartStartY = yPos;

    // Helper function to draw one chart
    const drawChart = (x, title, dataField) => {
      // Title
      doc.setFontSize(9); // Reduced from 10 to 9
      doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
      doc.setFont('helvetica', 'bold');
      doc.text(title, x, chartStartY);

      // Get min/max values
      const values = yearlyData.map(d => d[dataField]);
      const maxVal = Math.max(...values, 0);
      const minVal = Math.min(...values, 0);
      const range = (maxVal - minVal) || 1;
      const padding = range * 0.1;

      // Chart area
      const plotX = x + yAxisW;
      const plotW = chartW - yAxisW;
      const plotY = chartStartY + 5; // Reduced from 6 to 5

      // Background
      doc.setFillColor(250, 250, 250);
      doc.rect(plotX, plotY, plotW, chartH, 'F');

      // Zero line position
      const zeroY = plotY + ((maxVal + padding) / (range + 2 * padding)) * chartH;

      // Zero line
      doc.setDrawColor(100, 100, 100);
      doc.setLineWidth(0.2);
      doc.line(plotX, zeroY, plotX + plotW, zeroY);

      // Y-axis labels
      doc.setFontSize(5);
      doc.setTextColor(100, 100, 100);
      for (let i = 0; i <= 4; i++) {
        const val = (maxVal + padding) - (i / 4) * (range + 2 * padding);
        const y = plotY + (i / 4) * chartH;
        doc.text(fmt(val, 0), x + yAxisW - 1, y + 1, { align: 'right' });
      }

      // Bars - sized to fit all years while being visible
      const numBars = yearlyData.length;
      const barW = (plotW / numBars) * 0.5; // Wider bars (50% of space)
      const barGap = plotW / numBars;

      yearlyData.forEach((d, i) => {
        const val = d[dataField];
        const barH = (Math.abs(val) / (range + 2 * padding)) * chartH;
        const barX = plotX + (i * barGap) + (barGap / 2) - (barW / 2);

        if (val >= 0) {
          doc.setFillColor(76, 175, 80); // Green
          doc.rect(barX, zeroY - barH, barW, barH, 'F');
        } else {
          doc.setFillColor(220, 53, 69); // Red
          doc.rect(barX, zeroY, barW, barH, 'F');
        }

        // X label - positioned at center of bar space
        doc.setFontSize(5);
        doc.setTextColor(100, 100, 100);
        const labelX = plotX + (i * barGap) + (barGap / 2);
        doc.text(d.year.toString(), labelX, plotY + chartH + 3, { align: 'center' });
      });
    };

    // Draw both charts
    drawChart(margin, 'Cash-Flow Annuel', 'annual');
    drawChart(margin + chartW + 10, 'Cash-Flow Cumulatif', 'cumulative');

    yPos = chartStartY + chartH + 8; // Reduced spacing to 8 for compact fit

    // === ANNEXE - DÉTAILS DES CALCULS ===
    checkPageBreak(200);

    doc.setTextColor(darkBlue.r, darkBlue.g, darkBlue.b);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Annexe - Détails des Calculs', margin, yPos);
    doc.setDrawColor(primaryBlue.r, primaryBlue.g, primaryBlue.b);
    doc.setLineWidth(1);
    doc.line(margin, yPos + 3, margin + 85, yPos + 3);
    yPos += 15;

    // Get performance totale values for annexe (use PDF-calculated values)
    const annexeNcf = perfNCF;
    const annexeTri = perfTRI;
    const annexeVan = perfVAN;
    const annexeRoe = perfROE;

    // SECTION 1: Performance Après Travaux
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('1. Performance Après Travaux', margin, yPos);
    yPos += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    // Plus-value après travaux
    doc.text('Plus-value après travaux (hors charges)', margin + 3, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.text(`= Valeur estimée - (Prix d'achat + Travaux)`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= ${fmt(estimatedValue, 0)} - (${fmt(purchasePrice, 0)} + ${fmt(renovationCost, 0)}) = ${fmt(addedValue, 0)} €`, margin + 5, yPos);
    yPos += 7;

    // Valeur après travaux
    doc.setFontSize(9);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.text('Valeur après travaux', margin + 3, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.text(`= Prix d'achat + Travaux`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= ${fmt(purchasePrice, 0)} + ${fmt(renovationCost, 0)} = ${fmt(purchasePrice + renovationCost, 0)} €`, margin + 5, yPos);
    yPos += 7;

    // Prix au m² avant travaux
    doc.setFontSize(9);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.text('Prix au m² avant travaux', margin + 3, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.text(`= Prix d'achat / Surface`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= ${fmt(purchasePrice, 0)} / ${surfaceArea} = ${fmt(pricePerSqmBefore, 0)} €/m²`, margin + 5, yPos);
    yPos += 7;

    // Prix au m² avec travaux
    doc.setFontSize(9);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.text('Prix au m² avec travaux (sans plus-value)', margin + 3, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.text(`= (Prix d'achat + Travaux) / Surface`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= (${fmt(purchasePrice, 0)} + ${fmt(renovationCost, 0)}) / ${surfaceArea} = ${fmt(pricePerSqmWithRenovation, 0)} €/m²`, margin + 5, yPos);
    yPos += 12;

    // SECTION 2: Performance Locative
    checkPageBreak(80);
    doc.setFontSize(12);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFont('helvetica', 'bold');
    doc.text('2. Performance Locative', margin, yPos);
    yPos += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    // Cash-Flow Mensuel Moyen
    doc.text('Cash-Flow Mensuel Moyen (t)', margin + 3, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.text(`= (Revenus totaux - Charges totales - Crédit total) / Nombre de mois`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= (${fmt(totalRent, 0)} - ${fmt(totalCharges, 0)} - ${fmt(totalCredit, 0)}) / ${perfTotalMonths}`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= ${fmt(averageMonthlyCashFlow, 1)} €`, margin + 5, yPos);
    yPos += 7;

    // Rentabilité Nette Prévisionnelle
    doc.setFontSize(9);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.text('Rentabilité Nette Prévisionnelle', margin + 3, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.text(`= ((Revenus - Charges - Crédit) / Investissement total) × 100`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= ((${fmt(totalRent, 0)} - ${fmt(totalCharges, 0)} - ${fmt(totalCredit, 0)}) / ${fmt(totalInvestment, 0)}) × 100`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= ${fmt(netProfitability, 2)}%`, margin + 5, yPos);
    yPos += 7;

    // Ratio Loyer / Prix avec Travaux
    doc.setFontSize(9);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.text('Ratio Loyer / Prix avec Travaux', margin + 3, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.text(`= Loyer annuel / (Prix d'achat + Travaux)`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= (${fmt(monthlyRevenue, 0)} × 12) / (${fmt(purchasePrice, 0)} + ${fmt(renovationCost, 0)})`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= ${fmt(dscr * 100, 2)}%`, margin + 5, yPos);
    yPos += 7;

    // Loyer au m²
    doc.setFontSize(9);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.text('Loyer au m²', margin + 3, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    if (surfaceArea > 0) {
      doc.text(`= Revenu locatif mensuel / Surface`, margin + 5, yPos);
      yPos += 4;
      doc.text(`= ${fmt(monthlyRevenue, 0)} / ${surfaceArea} = ${fmt(rentPerSqm, 2)} €/m²`, margin + 5, yPos);
    } else {
      doc.text(`= N/A (surface non définie)`, margin + 5, yPos);
    }
    yPos += 12;

    // SECTION 3: Performance Totale
    checkPageBreak(80);
    doc.setFontSize(12);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFont('helvetica', 'bold');
    doc.text('3. Performance Totale (à la revente)', margin, yPos);
    yPos += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    // TRI
    doc.text('Taux de Rentabilité Interne (TRI)', margin + 3, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.text(`= ((Total cash flows + Capital gain - Investissement) / Investissement / Années) × 100`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= ((${fmt(totalCashFlows, 0)} + ${fmt(finalHomeValue - finalBalance, 0)} - ${fmt(totalInvestment, 0)}) / ${fmt(totalInvestment, 0)} / ${fmt(yearsExact, 2)}) × 100`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= ${fmt(annexeTri, 2)}%`, margin + 5, yPos);
    yPos += 7;

    // ROE
    doc.setFontSize(9);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.text('Rentabilité des Fonds Propres (ROE)', margin + 3, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.text(`= (Total cash flows / Investissement total) × 100`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= (${fmt(totalCashFlows, 0)} / ${fmt(totalInvestment, 0)}) × 100`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= ${fmt(annexeRoe, 2)}%`, margin + 5, yPos);
    yPos += 7;

    // VAN
    const discountRate = (creditData.interestRate || 4) / 100;
    doc.setFontSize(9);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.text('Valeur Actuelle Nette (VAN)', margin + 3, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.text(`= Somme des flux de tresorerie actualises au taux de ${fmt(discountRate * 100, 2)}%`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= Somme (Flux_t / (1 + taux)^t) - Investissement initial`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= ${fmt(annexeVan, 0)} EUR`, margin + 5, yPos);
    yPos += 7;

    // NCF
    doc.setFontSize(9);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.text('Cash-Flow Net (NCF)', margin + 3, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.text(`= Somme de tous les flux de tresorerie (investissement + operations + revente)`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= -${fmt(totalInvestment, 0)} (invest.) + ${fmt(totalOperationalCashFlow, 0)} (ops.) + ${fmt(finalHomeValue - finalBalance, 0)} (revente)`, margin + 5, yPos);
    yPos += 4;
    doc.text(`= ${fmt(annexeNcf, 0)} EUR`, margin + 5, yPos);
    yPos += 12;

    // SECTION 4: Détail du calcul du Cash-Flow Net (NCF)
    checkPageBreak(150);
    doc.setFontSize(12);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFont('helvetica', 'bold');
    doc.text('4. Détail du calcul du Cash-Flow Net (NCF)', margin, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.text('(basé sur le tableau Résumé du Cash-Flow)', margin + 95, yPos);
    yPos += 10;

    // 1. Fonds Apportés
    doc.setFontSize(10);
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFont('helvetica', 'bold');
    doc.text('1. Fonds Apportés (Apport initial)', margin + 3, yPos);
    yPos += 6;

    const apportInitialAnnexe = -(totalAcq - (creditData.amount || 0));
    const apportColorAnnexe = apportInitialAnnexe >= 0 ? accentGreen : accentRed;
    doc.setTextColor(apportColorAnnexe.r, apportColorAnnexe.g, apportColorAnnexe.b);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`${fmt(apportInitialAnnexe, 0)} €`, pageWidth - margin - 10, yPos, { align: 'right' });
    yPos += 10;

    // 2. Cash-Flow Opérationnel
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`2. Cash-Flow Opérationnel sur ${fmt(yearsExact, 1)} ans (${perfTotalMonths} mois):`, margin + 3, yPos);
    yPos += 6;

    // + Revenus locatifs totaux
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('+ Revenus locatifs totaux', margin + 5, yPos);
    const totalRentAmount = totalRent || 0;
    const rentColorAnnexe = totalRentAmount >= 0 ? accentGreen : accentRed;
    doc.setTextColor(rentColorAnnexe.r, rentColorAnnexe.g, rentColorAnnexe.b);
    doc.setFont('helvetica', 'bold');
    doc.text(`${totalRentAmount >= 0 ? '+' : ''} ${fmt(totalRentAmount, 0)} €`, pageWidth - margin - 10, yPos, { align: 'right' });
    yPos += 5;

    // - Remboursement capital crédit
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFont('helvetica', 'normal');
    doc.text('- Remboursement capital crédit', margin + 5, yPos);
    const capitalRepaymentAnnexe = totalCredit - totalInterest;
    const capitalColorAnnexe = capitalRepaymentAnnexe <= 0 ? accentGreen : accentRed;
    doc.setTextColor(capitalColorAnnexe.r, capitalColorAnnexe.g, capitalColorAnnexe.b);
    doc.setFont('helvetica', 'bold');
    doc.text(`${capitalRepaymentAnnexe >= 0 ? '+' : ''} ${fmt(capitalRepaymentAnnexe, 0)} €`, pageWidth - margin - 10, yPos, { align: 'right' });
    yPos += 5;

    // - Intérêts crédit
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFont('helvetica', 'normal');
    doc.text('- Intérêts crédit', margin + 5, yPos);
    doc.setTextColor(accentRed.r, accentRed.g, accentRed.b);
    doc.setFont('helvetica', 'bold');
    doc.text(`- ${fmt(Math.abs(totalInterest), 0)} €`, pageWidth - margin - 10, yPos, { align: 'right' });
    yPos += 5;

    // - Charges et frais récurrents
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFont('helvetica', 'normal');
    doc.text('- Charges et frais récurrents', margin + 5, yPos);
    doc.setTextColor(accentRed.r, accentRed.g, accentRed.b);
    doc.setFont('helvetica', 'bold');
    doc.text(`- ${fmt(Math.abs(totalCharges), 0)} €`, pageWidth - margin - 10, yPos, { align: 'right' });
    yPos += 5;

    // = Cash-Flow Opérationnel Total
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('= Cash-Flow Opérationnel Total', margin + 5, yPos);
    const cfOpAnnexe = totalOperationalCashFlow || 0;
    const cfOpColorAnnexe = cfOpAnnexe >= 0 ? accentGreen : accentRed;
    doc.setTextColor(cfOpColorAnnexe.r, cfOpColorAnnexe.g, cfOpColorAnnexe.b);
    doc.text(`${fmt(cfOpAnnexe, 0)} €`, pageWidth - margin - 10, yPos, { align: 'right' });
    yPos += 10;

    // 3. Plus-value à la revente
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('3. Plus-value à la revente (et au paiement)', margin + 3, yPos);
    yPos += 6;

    // Valeur du bien
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const appreciationRateAnnexe = performanceParams.housePriceInflation || 0;
    doc.text(`Valeur du bien (avec appréciation ${fmt(appreciationRateAnnexe, 1)}%/an sur ${fmt(yearsExact, 1)} ans)`, margin + 5, yPos);
    doc.setTextColor(primaryBlue.r, primaryBlue.g, primaryBlue.b);
    doc.setFont('helvetica', 'bold');
    const propertyValueAnnexe = finalHomeValue;
    doc.text(`${fmt(propertyValueAnnexe, 0)} €`, pageWidth - margin - 10, yPos, { align: 'right' });
    yPos += 5;

    // - Solde restant du crédit
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFont('helvetica', 'normal');
    doc.text('- Solde restant du crédit', margin + 5, yPos);
    const remainingDebtAnnexe = finalBalance;
    doc.setTextColor(accentRed.r, accentRed.g, accentRed.b);
    doc.setFont('helvetica', 'bold');
    doc.text(`- ${fmt(Math.abs(remainingDebtAnnexe), 0)} €`, pageWidth - margin - 10, yPos, { align: 'right' });
    yPos += 5;

    // = Plus-value nette à la revente
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('= Plus-value nette à la revente', margin + 5, yPos);
    const netResaleValueAnnexe = propertyValueAnnexe - remainingDebtAnnexe;
    const resaleColorAnnexe = netResaleValueAnnexe >= 0 ? accentGreen : accentRed;
    doc.setTextColor(resaleColorAnnexe.r, resaleColorAnnexe.g, resaleColorAnnexe.b);
    doc.text(`${netResaleValueAnnexe >= 0 ? '+' : ''} ${fmt(netResaleValueAnnexe, 0)} €`, pageWidth - margin - 10, yPos, { align: 'right' });
    yPos += 10;

    // Final NCF calculation
    doc.setFillColor(240, 240, 240);
    doc.roundedRect(margin, yPos - 2, pageWidth - 2 * margin, 12, 2, 2, 'F');
    doc.setTextColor(darkGray.r, darkGray.g, darkGray.b);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('NCF = Apport + CF Opérationnel + Plus-value', margin + 5, yPos + 6);
    const ncfFinalColorAnnexe = annexeNcf >= 0 ? accentGreen : accentRed;
    doc.setTextColor(ncfFinalColorAnnexe.r, ncfFinalColorAnnexe.g, ncfFinalColorAnnexe.b);
    doc.setFontSize(13);
    doc.text(`${fmt(annexeNcf, 0)} €`, pageWidth - margin - 10, yPos + 6, { align: 'right' });
    yPos += 16;

    // Formula
    doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    const ncfFormulaAnnexe = `= ${fmt(apportInitialAnnexe, 0)} + ${fmt(cfOpAnnexe, 0)} + ${fmt(netResaleValueAnnexe, 0)}`;
    doc.text(ncfFormulaAnnexe, margin + 5, yPos);
    yPos += 15;

    // === FOOTER WITH PAGE NUMBERS ===
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      doc.setDrawColor(lightGray.r, lightGray.g, lightGray.b);
      doc.setLineWidth(0.5);
      doc.line(margin, pageHeight - 18, pageWidth - margin, pageHeight - 18);

      doc.setFontSize(8);
      doc.setTextColor(mediumGray.r, mediumGray.g, mediumGray.b);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${i} sur ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

      doc.setFontSize(7);
      doc.text('Rapport généré par Analyse Investissement Immobilier', margin, pageHeight - 10);
      doc.text(new Date().toLocaleDateString('fr-FR'), pageWidth - margin, pageHeight - 10, { align: 'right' });
    }

    // Save PDF
    const fileName = extractedData && extractedData.address
      ? `Analyse_Investissement_${extractedData.address.split(',')[0].replace(/\s+/g, '_')}_${new Date().getFullYear()}.pdf`
      : `Analyse_Investissement_${new Date().toLocaleDateString('fr-FR').replace(/\//g, '-')}.pdf`;
    doc.save(fileName);
  };

  // Load all saved projects from localStorage
  const loadSavedProjects = () => {
    const projects = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('project_')) {
        try {
          const projectData = JSON.parse(localStorage.getItem(key));
          projects.push(projectData);
        } catch (error) {
          console.error(`Error loading project ${key}:`, error);
        }
      }
    }
    // Sort by savedAt date (most recent first)
    projects.sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));
    setSavedProjects(projects);

    // Load portfolio projects
    try {
      const savedPortfolio = JSON.parse(localStorage.getItem('portfolioProjects') || '[]');
      setPortfolioProjects(savedPortfolio);
    } catch (error) {
      console.error('Error loading portfolio projects:', error);
      setPortfolioProjects([]);
    }
  };

  // Open modal to add project to portfolio
  const openAddToPortfolioModal = (projectKey) => {
    // Set default date to today
    const today = new Date().toISOString().split('T')[0];
    setPortfolioStartDate(today);
    setPortfolioAddModal({ show: true, projectKey });
  };

  // Confirm adding project to portfolio with start date
  const confirmAddToPortfolio = () => {
    const projectKey = portfolioAddModal.projectKey;
    if (projectKey && !portfolioProjects.find(p => p.key === projectKey)) {
      const newPortfolio = [...portfolioProjects, { key: projectKey, startDate: portfolioStartDate }];
      setPortfolioProjects(newPortfolio);
      localStorage.setItem('portfolioProjects', JSON.stringify(newPortfolio));
    }
    setPortfolioAddModal({ show: false, projectKey: null });
    setPortfolioStartDate('');
  };

  // Remove project from portfolio
  const removeFromPortfolio = (projectKey) => {
    const newPortfolio = portfolioProjects.filter(p => p.key !== projectKey);
    setPortfolioProjects(newPortfolio);
    localStorage.setItem('portfolioProjects', JSON.stringify(newPortfolio));
  };

  // Get portfolio start date for a project
  const getPortfolioStartDate = (projectKey) => {
    const portfolioProject = portfolioProjects.find(p => p.key === projectKey);
    return portfolioProject ? portfolioProject.startDate : null;
  };

  // Load a specific project and set it as current
  const loadProject = (project) => {
    // Property data
    setExtractedData(project.extractedData);
    if (project.propertyUrl) setPropertyUrl(project.propertyUrl);

    // Investment data
    setAcquisitionData(project.acquisitionData);
    setFinancementData(project.financementData);
    setCreditData(project.creditData);
    if (project.creditTravauxData) setCreditTravauxData(project.creditTravauxData);
    setRecurrentFees(project.recurrentFees);
    setPropertyRevenues(project.propertyRevenues);

    // Saved investments
    if (project.savedInvestments) setSavedInvestments(project.savedInvestments);
    if (project.selectedInvestments) setSelectedInvestments(project.selectedInvestments);
    if (project.currentInvestmentName) setCurrentInvestmentName(project.currentInvestmentName);

    // Current finance data
    setCurrentFinanceRevenues(project.currentFinanceRevenues);
    setCurrentFinanceCharges(project.currentFinanceCharges);
    setCurrentFinanceApport(project.currentFinanceApport);
    if (project.hasCustomEstimatedValue !== undefined) setHasCustomEstimatedValue(project.hasCustomEstimatedValue);
    if (project.hasCustomCreditSplit !== undefined) setHasCustomCreditSplit(project.hasCustomCreditSplit);
    if (project.customApportSplit !== undefined) setCustomApportSplit(project.customApportSplit);
    if (project.estimatedHomeValue) setEstimatedHomeValue(project.estimatedHomeValue);

    // Borrowing capacity data
    setRevenues(project.revenues);
    setFutureRevenues(project.futureRevenues);
    setCharges(project.charges);
    setFutureCharges(project.futureCharges);

    // Parameters
    setLoanParams(project.loanParams);
    setQuotiteParams(project.quotiteParams);
    setPerformanceParams(project.performanceParams);

    // Restore calculated Performance Totale values if available
    if (project.calculatedNCF !== undefined && project.calculatedTRI !== undefined) {
      setCalculatedPerformanceTotale({
        ncf: project.calculatedNCF,
        tri: project.calculatedTRI
      });
    }

    // Restore checklist data
    if (project.checklistData) setChecklistData(project.checklistData);

    setCurrentProject(project);

    // Store original data for change detection
    setOriginalProjectData(JSON.stringify({
      acquisitionData: project.acquisitionData,
      financementData: project.financementData,
      creditData: project.creditData,
      creditTravauxData: project.creditTravauxData,
      recurrentFees: project.recurrentFees,
      propertyRevenues: project.propertyRevenues,
      currentFinanceRevenues: project.currentFinanceRevenues,
      currentFinanceCharges: project.currentFinanceCharges,
      currentFinanceApport: project.currentFinanceApport,
      revenues: project.revenues,
      futureRevenues: project.futureRevenues,
      charges: project.charges,
      futureCharges: project.futureCharges,
      loanParams: project.loanParams,
      quotiteParams: project.quotiteParams,
      performanceParams: project.performanceParams,
      estimatedHomeValue: project.estimatedHomeValue,
      hasCustomEstimatedValue: project.hasCustomEstimatedValue,
      checklistData: project.checklistData
    }));
    setHasUnsavedChanges(false);
  };

  // Delete a project
  const deleteProject = (projectKey) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le projet "${projectKey}"?`)) {
      try {
        localStorage.removeItem(`project_${projectKey}`);
        // Also remove from portfolio if present
        removeFromPortfolio(projectKey);
        loadSavedProjects();
        if (currentProject && currentProject.key === projectKey) {
          setCurrentProject(null);
        }
        alert(`Projet "${projectKey}" supprimé avec succès!`);
      } catch (error) {
        alert(`Erreur lors de la suppression: ${error.message}`);
      }
    }
  };

  // Load saved projects on component mount
  useEffect(() => {
    loadSavedProjects();
  }, []);

  // Load investment
  const loadInvestment = (investment) => {
    const data = investment.data;
    setRecurrentFees(data.recurrentFees);
    setPropertyRevenues(data.propertyRevenues);
    setCreditData(data.creditData);
    if (data.creditTravauxData) setCreditTravauxData(data.creditTravauxData);
    setAcquisitionData(data.acquisitionData);
    setFinancementData(data.financementData);
    setPerformanceParams(data.performanceParams);
    setCurrentInvestmentName(investment.name);
    setActiveTab('acquisition-financement');
    setExpandedCategories({...expandedCategories, investment: true});
  };

  // Delete investment
  const deleteInvestment = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet investissement?')) {
      setSavedInvestments(savedInvestments.filter(inv => inv.id !== id));
    }
  };

  // Selected investments for comparison
  const [selectedInvestments, setSelectedInvestments] = useState([]);

  // Toggle investment selection
  const toggleInvestmentSelection = (id) => {
    if (selectedInvestments.includes(id)) {
      setSelectedInvestments(selectedInvestments.filter(invId => invId !== id));
    } else {
      setSelectedInvestments([...selectedInvestments, id]);
    }
  };

  // Extract property data from URL
  const extractPropertyData = async () => {
    if (!propertyUrl.trim()) {
      setExtractError('Veuillez entrer une URL');
      return;
    }

    setExtracting(true);
    setExtractError('');
    setExtractedData(null);

    try {
      const response = await fetch(`${API_URL}/api/extract-property`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: propertyUrl })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'extraction des données');
      }

      const data = await response.json();
      setExtractedData(data);

      // Auto-populate acquisition data if extraction succeeded
      if (data.price) {
        setAcquisitionData({
          ...acquisitionData,
          prixAchat: data.price || acquisitionData.prixAchat
        });
      }

      // Auto-populate property fees if available
      if (data.monthlyCharges) {
        const existingFee = recurrentFees.find(f => f.name === 'coOwnershipFees');
        if (existingFee) {
          setRecurrentFees(recurrentFees.map(f =>
            f.name === 'coOwnershipFees' ? {...f, amount: data.monthlyCharges} : f
          ));
        }
      }

    } catch (err) {
      console.error('Error extracting property data:', err);
      setExtractError(err.message);
    } finally {
      setExtracting(false);
    }
  };

  // Apply extracted data to investment
  const applyExtractedData = () => {
    if (!extractedData) return;

    // Update acquisition data
    const newAcquisitionData = {
      ...acquisitionData,
      prixAchat: extractedData.price || acquisitionData.prixAchat
    };
    setAcquisitionData(newAcquisitionData);

    // Update recurrent fees
    let newRecurrentFees = [...recurrentFees];
    if (extractedData.monthlyCharges) {
      const feeExists = recurrentFees.find(f => f.name === 'coOwnershipFees');
      if (feeExists) {
        newRecurrentFees = recurrentFees.map(f =>
          f.name === 'coOwnershipFees'
            ? {...f, amount: extractedData.monthlyCharges}
            : f
        );
      } else {
        newRecurrentFees = [...recurrentFees, {
          id: Date.now(),
          name: 'coOwnershipFees',
          amount: extractedData.monthlyCharges,
          frequency: 'monthly'
        }];
      }
      setRecurrentFees(newRecurrentFees);
    }

    // Create a new project to make all tabs accessible
    const newProject = {
      id: Date.now(),
      name: extractedData.address || 'Nouveau Projet',
      extractedData: extractedData,
      acquisitionData: newAcquisitionData,
      financementData: financementData,
      creditData: creditData,
      revenues: revenues,
      charges: charges,
      futureRevenues: futureRevenues,
      futureCharges: futureCharges,
      recurrentFees: newRecurrentFees,
      propertyRevenues: propertyRevenues,
      loanParams: loanParams,
      quotiteParams: quotiteParams,
      performanceParams: performanceParams,
      createdAt: new Date().toISOString()
    };

    setCurrentProject(newProject);
    setActiveTab('home');
    setExpandedCategories({...expandedCategories, investment: true});
  };

  // Get translation helper
  const t = translations[language];

  return (
    <div className="App">
      <div className="container">
        <div className="header">
          <div className="header-left">
            <h1>{t.appTitle}</h1>
          </div>
          <div className="header-right" style={{display: 'flex', alignItems: 'flex-end', gap: '12px', flexDirection: 'column'}}>
            <button
              onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
              style={{
                padding: '6px 12px',
                background: '#10b981',
                backdropFilter: 'blur(20px)',
                border: '1px solid #2a2a2a',
                borderRadius: '6px',
                color: '#ffffff',
                fontSize: '0.7em',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
              }}
            >
              {language === 'fr' ? 'EN' : 'FR'}
            </button>

            {/* Only show Save Project button when inside a project */}
            {currentProject && (
              <button
                onClick={saveProject}
                style={{
                  padding: '6px 10px',
                  background: 'rgba(209, 178, 135, 0.15)',
                  border: '1px solid rgba(209, 178, 135, 0.3)',
                  borderRadius: '5px',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.6em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.15)';
                }}
              >
                Sauver
              </button>
            )}
          </div>
        </div>

        <div className="main-layout">
          <div className={`tab-selector ${sidebarCollapsed ? 'collapsed' : ''}`}>
            <button
              className="sidebar-toggle"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <line x1="9" y1="3" x2="9" y2="21"/>
              </svg>
            </button>

            <div className="tab-category tab-category-home">
              <button
                className={`category-header ${activeTab === 'home' && !showPortfolioAnalysis ? 'active' : ''}`}
                onClick={() => { setActiveTab('home'); setError(''); setShowPortfolioAnalysis(false); }}
                title="Home"
              >
                <span>{sidebarCollapsed ? '' : 'Home'}</span>
              </button>
            </div>

            <div className="tab-category tab-category-aide">
              <button
                className={`category-header ${activeTab === 'aide' ? 'active' : ''}`}
                onClick={() => { setActiveTab('aide'); setError(''); }}
                title="Aide"
              >
                <span>{sidebarCollapsed ? '' : 'Aide'}</span>
              </button>
            </div>

            {/* Portefeuille category - shown when in portfolio analysis view */}
            {showPortfolioAnalysis && (
              <div className="tab-category tab-category-portefeuille">
                <button
                  className="category-header active"
                  title="Portefeuille"
                >
                  <span>{sidebarCollapsed ? '' : 'Portefeuille'}</span>
                </button>
              </div>
            )}

            {(activeTab !== 'home' || currentProject) && (
              <div className="tab-category tab-category-endettement">
              <button
                className={`category-header ${(activeTab === 'summary' || activeTab === 'borrowing' || activeTab === 'current-finance') ? 'active' : ''}`}
                onClick={() => {
                  if (!currentProject) {
                    alert('Veuillez ouvrir ou créer un projet d\'abord');
                    return;
                  }
                  setActiveTab('summary');
                  setError('');
                }}
                title="Endettement"
                style={{
                  opacity: !currentProject ? 0.4 : 1,
                  cursor: !currentProject ? 'not-allowed' : 'pointer'
                }}
              >
                <span>{sidebarCollapsed ? '' : 'Endettement'}</span>
              </button>
            </div>
            )}

            {(activeTab !== 'home' || currentProject) && (
              <div className="tab-category tab-category-investment">
                <button
                  className={`category-header ${(activeTab === 'extraction-charac' || activeTab === 'acquisition-financement' || activeTab === 'credit' || activeTab === 'recurrent-fees-income' || activeTab === 'performance') ? 'active' : ''}`}
                  onClick={() => {
                    if (!currentProject) {
                      alert('Veuillez ouvrir ou créer un projet d\'abord');
                      return;
                    }
                    setActiveTab('extraction-charac');
                    setError('');
                  }}
                  title="Rentabilité"
                  style={{
                    opacity: !currentProject ? 0.4 : 1,
                    cursor: !currentProject ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span>{sidebarCollapsed ? '' : 'Rentabilité'}</span>
                </button>
              </div>
            )}

            {(activeTab !== 'home' || currentProject) && (
              <div className="tab-category tab-category-frais">
                <button
                  className={`category-header ${activeTab === 'frais' ? 'active' : ''}`}
                  onClick={() => {
                    if (!currentProject) {
                      alert('Veuillez ouvrir ou créer un projet d\'abord');
                      return;
                    }
                    setActiveTab('frais');
                    setError('');
                  }}
                  title="Frais"
                  style={{
                    opacity: !currentProject ? 0.4 : 1,
                    cursor: !currentProject ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span>{sidebarCollapsed ? '' : 'Frais'}</span>
                </button>
              </div>
            )}
          </div>

          <div className="tab-content">

        {/* Aide Tab */}
        {activeTab === 'aide' && (
          <div className="aide-section" style={{ padding: '20px' }}>
            <h2 className="section-title" style={{ marginBottom: '24px', fontSize: '1.8em', color: '#ffffff' }}>
              Aide
            </h2>

            {/* Sub-tab navigation */}
            <div style={{
              display: 'flex',
              gap: '0',
              marginBottom: '32px',
              borderBottom: '1px solid rgba(58, 58, 58, 0.5)'
            }}>
              {[
                { id: 'timeline', label: 'Timeline' },
                { id: 'checklist-achat', label: 'Checklist Risques' },
                { id: 'documents', label: 'Documents' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setAideSubTab(tab.id)}
                  style={{
                    padding: '12px 24px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: aideSubTab === tab.id ? '2px solid #10b981' : '2px solid transparent',
                    color: aideSubTab === tab.id ? '#ffffff' : 'rgba(156, 163, 175, 0.7)',
                    fontSize: '0.95em',
                    fontWeight: aideSubTab === tab.id ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Checklist Achat sub-tab */}
            {aideSubTab === 'checklist-achat' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

              <p style={{ color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.9em', lineHeight: '1.7' }}>
                Notez chaque crit&egrave;re de 1 (faible) &agrave; 5 (&eacute;lev&eacute;). Le score combin&eacute; Probabilit&eacute; &times; Mat&eacute;rialit&eacute; identifie les risques prioritaires.
              </p>

              {/* Toolbar: Templates, Auto-fill, Export */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                {checklistTemplates.map(t => (
                  <button key={t.id || t.name} onClick={() => applyChecklistTemplate(t)} style={{
                    background: 'rgba(30, 58, 138, 0.3)', border: '1px solid rgba(59, 130, 246, 0.4)', borderRadius: '6px',
                    padding: '6px 14px', color: '#93c5fd', fontSize: '0.8em', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s'
                  }}>{t.name || t.label}</button>
                ))}
                <button onClick={() => autoFillChecklist()} style={{
                  background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.4)', borderRadius: '6px',
                  padding: '6px 14px', color: '#10b981', fontSize: '0.8em', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s'
                }}>Auto-fill</button>
                <button onClick={() => exportChecklistPDF()} style={{
                  background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.4)', borderRadius: '6px',
                  padding: '6px 14px', color: '#f59e0b', fontSize: '0.8em', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s'
                }}>Export PDF</button>
              </div>

              {/* Radar Chart */}
              <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(58, 58, 58, 0.5)', display: 'flex', justifyContent: 'center' }}>
                {(() => {
                  const numAxes = checklistSections.length;
                  const cx = 160, cy = 160, radius = 110;
                  const labels = ['Prix', 'Frais', 'Vices', 'Juridique', 'Pr\u00eat', 'Signature', 'Environ.', 'Locatif', 'Fiscal'];
                  const angles = checklistSections.map((_, i) => i * (2 * Math.PI / numAxes) - Math.PI / 2);
                  const values = checklistSections.map(s => getChecklistAvg(s.id, s.criteria) || 0);
                  const dataPoints = values.map((v, i) => ({
                    x: cx + (v / 5) * radius * Math.cos(angles[i]),
                    y: cy + (v / 5) * radius * Math.sin(angles[i]),
                    val: v
                  }));
                  const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ') + ' Z';
                  return (
                    <svg width="320" height="320" viewBox="0 0 320 320">
                      {/* Concentric grid polygons */}
                      {[1, 2, 3, 4, 5].map(level => {
                        const pts = angles.map(a => `${(cx + (level / 5) * radius * Math.cos(a)).toFixed(1)},${(cy + (level / 5) * radius * Math.sin(a)).toFixed(1)}`).join(' ');
                        return <polygon key={level} points={pts} fill="none" stroke="rgba(58,58,58,0.5)" strokeWidth="0.5" />;
                      })}
                      {/* Axis lines */}
                      {angles.map((a, i) => (
                        <line key={i} x1={cx} y1={cy} x2={cx + radius * Math.cos(a)} y2={cy + radius * Math.sin(a)} stroke="rgba(58,58,58,0.5)" strokeWidth="0.5" />
                      ))}
                      {/* Data polygon */}
                      <polygon points={dataPoints.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')} fill="rgba(59, 130, 246, 0.15)" stroke="rgba(59, 130, 246, 0.7)" strokeWidth="1.5" />
                      {/* Data dots */}
                      {dataPoints.map((p, i) => (
                        <circle key={i} cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r="4" fill={getRiskColor(p.val) || 'rgba(156,163,175,0.5)'} />
                      ))}
                      {/* Labels */}
                      {angles.map((a, i) => {
                        const lx = cx + (radius + 30) * Math.cos(a);
                        const ly = cy + (radius + 30) * Math.sin(a);
                        return <text key={i} x={lx.toFixed(1)} y={ly.toFixed(1)} fill="rgba(156,163,175,0.95)" fontSize="12" fontWeight="500" textAnchor="middle" dominantBaseline="middle">{labels[i] || checklistSections[i].title.substring(0, 6)}</text>;
                      })}
                    </svg>
                  );
                })()}
              </div>

              {/* Enhanced Summary Table with P x M column */}
              <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(58, 58, 58, 0.5)', overflowX: 'auto' }}>
                <h3 style={{ color: '#ffffff', fontSize: '1.1em', fontWeight: '700', marginBottom: '16px' }}>Summary Achat</h3>
                <div style={{ minWidth: '800px' }}>
                  {/* Header */}
                  <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr 1fr 90px 90px 90px', gap: '1px', marginBottom: '2px' }}>
                    {['Phase', '\u00c9v\u00e9nement', 'Cat\u00e9gorie', 'Prob. /5', 'Mat. /5', 'Score P\u00d7M'].map(h => (
                      <div key={h} style={{ background: 'rgba(30, 58, 138, 0.4)', padding: '8px 10px', fontSize: '0.75em', fontWeight: '600', color: '#ffffff' }}>{h}</div>
                    ))}
                  </div>
                  {/* Rows */}
                  {checklistSections.map(section => {
                    const avg = getChecklistAvg(section.id, section.criteria);
                    const mat = checklistData.materiality[section.id];
                    const score = (avg && mat) ? avg * mat : null;
                    return (
                      <div key={section.id} style={{ display: 'grid', gridTemplateColumns: '60px 1fr 1fr 90px 90px 90px', gap: '1px', marginBottom: '1px' }}>
                        <div style={{ background: 'rgba(255,255,255,0.04)', padding: '6px 10px', fontSize: '0.8em', color: 'rgba(156,163,175,0.9)' }}>Achat</div>
                        <div style={{ background: 'rgba(255,255,255,0.04)', padding: '6px 10px', fontSize: '0.8em', color: '#ffffff', fontWeight: '500' }}>{section.title}</div>
                        <div style={{ background: 'rgba(255,255,255,0.04)', padding: '6px 10px', fontSize: '0.75em', color: 'rgba(156,163,175,0.7)' }}>{section.category}</div>
                        <div style={{ background: getRiskBg(avg), padding: '6px 10px', fontSize: '0.85em', fontWeight: '700', color: getRiskColor(avg), textAlign: 'center' }}>{avg != null ? avg.toFixed(1) : '-'}</div>
                        <div style={{ background: 'rgba(255,255,255,0.04)', padding: '4px 6px', display: 'flex', gap: '3px', justifyContent: 'center', alignItems: 'center' }}>
                          {[1,2,3,4,5].map(v => (
                            <div key={v} onClick={() => updateChecklistMateriality(section.id, v)} style={{
                              width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.7em', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s',
                              background: mat === v ? getRiskColor(v) : 'rgba(255,255,255,0.06)',
                              color: mat === v ? '#ffffff' : 'rgba(156,163,175,0.5)',
                              border: mat === v ? 'none' : '1px solid rgba(58,58,58,0.5)'
                            }}>{v}</div>
                          ))}
                        </div>
                        <div style={{ background: getRiskBg(score != null ? score / 5 : null), padding: '6px 10px', fontSize: '0.85em', fontWeight: '700', color: getRiskColor(score != null ? score / 5 : null), textAlign: 'center' }}>{score != null ? score.toFixed(1) : '-'}</div>
                      </div>
                    );
                  })}
                  {/* Totals */}
                  {(() => {
                    const allAvgs = checklistSections.map(s => getChecklistAvg(s.id, s.criteria)).filter(v => v != null);
                    const allMats = checklistSections.map(s => checklistData.materiality[s.id]).filter(v => v != null);
                    const totalProb = allAvgs.length > 0 ? allAvgs.reduce((a,b) => a+b, 0) / allAvgs.length : null;
                    const totalMat = allMats.length > 0 ? allMats.reduce((a,b) => a+b, 0) / allMats.length : null;
                    const allScores = checklistSections.map(s => {
                      const a = getChecklistAvg(s.id, s.criteria);
                      const m = checklistData.materiality[s.id];
                      return (a && m) ? a * m : null;
                    }).filter(v => v != null);
                    const totalScore = allScores.length > 0 ? allScores.reduce((a,b) => a+b, 0) / allScores.length : null;
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr 1fr 90px 90px 90px', gap: '1px', marginTop: '4px', borderTop: '2px solid rgba(58,58,58,0.5)' }}>
                        <div style={{ gridColumn: '1 / 4' }}></div>
                        <div style={{ background: getRiskBg(totalProb), padding: '8px 10px', fontSize: '0.85em', fontWeight: '700', color: getRiskColor(totalProb), textAlign: 'center' }}>{totalProb != null ? totalProb.toFixed(2) : '-'}</div>
                        <div style={{ background: getRiskBg(totalMat), padding: '8px 10px', fontSize: '0.85em', fontWeight: '700', color: getRiskColor(totalMat), textAlign: 'center' }}>{totalMat != null ? totalMat.toFixed(2) : '-'}</div>
                        <div style={{ background: getRiskBg(totalScore != null ? totalScore / 5 : null), padding: '8px 10px', fontSize: '0.85em', fontWeight: '700', color: getRiskColor(totalScore != null ? totalScore / 5 : null), textAlign: 'center' }}>{totalScore != null ? totalScore.toFixed(2) : '-'}</div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Detail Sections (Collapsible) */}
              {checklistSections.map(section => {
                const sectionAvg = getChecklistAvg(section.id, section.criteria);
                return (
                  <div key={section.id} style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(58, 58, 58, 0.5)' }}>
                    {/* Section header (clickable to collapse) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: !checklistCollapsed[section.id] ? (section.note ? '12px' : '16px') : '0px', cursor: 'pointer' }} onClick={() => toggleChecklistSection(section.id)}>
                      <span style={{ color: 'rgba(156,163,175,0.7)', fontSize: '0.85em', userSelect: 'none', width: '16px', textAlign: 'center' }}>
                        {checklistCollapsed[section.id] ? '\u25B6' : '\u25BC'}
                      </span>
                      <div style={{
                        background: 'rgba(156, 163, 175, 0.3)', color: '#ffffff', width: '32px', height: '32px', borderRadius: '8px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.95em', fontWeight: '700'
                      }}>{section.number}</div>
                      <div style={{ color: '#ffffff', fontSize: '1.05em', fontWeight: '700', flex: 1 }}>{section.title}</div>
                      {sectionAvg != null && (
                        <div style={{ background: getRiskBg(sectionAvg), color: getRiskColor(sectionAvg), padding: '4px 12px', borderRadius: '6px', fontSize: '0.85em', fontWeight: '700' }}>
                          {sectionAvg.toFixed(1)} / 5
                        </div>
                      )}
                    </div>

                    {/* Collapsible content */}
                    {!checklistCollapsed[section.id] && (
                    <>
                    {section.note && (
                      <div style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px dashed rgba(245, 158, 11, 0.3)', borderRadius: '6px', padding: '8px 12px', marginBottom: '16px' }}>
                        <p style={{ color: 'rgba(245, 158, 11, 0.9)', fontSize: '0.8em', margin: 0, fontStyle: 'italic' }}>{section.note}</p>
                      </div>
                    )}

                    {/* Criteria table */}
                    <div style={{ overflowX: 'auto' }}>
                      <div style={{ minWidth: '850px' }}>
                        {/* Table header */}
                        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 1fr 110px 1fr 1fr', gap: '1px', marginBottom: '2px' }}>
                          <div style={{ background: 'rgba(30, 58, 138, 0.3)', padding: '8px 10px', fontSize: '0.75em', fontWeight: '600', color: 'rgba(156,163,175,0.9)' }}>Crit&egrave;re</div>
                          <div style={{ background: 'rgba(16, 185, 129, 0.1)', padding: '8px 10px', fontSize: '0.75em', fontWeight: '600', color: '#10b981', textAlign: 'center' }}>Faible Risque (1-2)</div>
                          <div style={{ background: 'rgba(245, 158, 11, 0.1)', padding: '8px 10px', fontSize: '0.75em', fontWeight: '600', color: '#f59e0b', textAlign: 'center' }}>Risque Moyen (3)</div>
                          <div style={{ background: 'rgba(239, 68, 68, 0.1)', padding: '8px 10px', fontSize: '0.75em', fontWeight: '600', color: '#ef4444', textAlign: 'center' }}>Risque &Eacute;lev&eacute; (4-5)</div>
                          <div style={{ background: 'rgba(30, 58, 138, 0.3)', padding: '8px 10px', fontSize: '0.75em', fontWeight: '600', color: 'rgba(156,163,175,0.9)', textAlign: 'center' }}>Risque 1-5</div>
                          <div style={{ background: 'rgba(30, 58, 138, 0.3)', padding: '8px 10px', fontSize: '0.75em', fontWeight: '600', color: 'rgba(156,163,175,0.9)' }}>Action</div>
                          <div style={{ background: 'rgba(30, 58, 138, 0.3)', padding: '8px 10px', fontSize: '0.75em', fontWeight: '600', color: 'rgba(156,163,175,0.9)' }}>Commentaire</div>
                        </div>

                        {/* Criteria rows */}
                        {section.criteria.map(criterion => {
                          const key = `${section.id}_${criterion.id}`;
                          const risk = checklistData.risks[key];
                          const cellBase = { padding: '8px 10px', fontSize: '0.8em', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' };
                          return (
                            <div key={criterion.id} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 1fr 110px 1fr 1fr', gap: '1px', marginBottom: '1px' }}>
                              <div style={{ background: 'rgba(255,255,255,0.04)', padding: '8px 10px', fontSize: '0.8em', color: '#ffffff', fontWeight: '500', display: 'flex', alignItems: 'center' }}>{criterion.label}</div>
                              <div style={{ ...cellBase, background: risk >= 1 && risk <= 2 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255,255,255,0.02)', color: 'rgba(156,163,175,0.7)', fontWeight: '400' }}>
                                {criterion.low}
                              </div>
                              <div style={{ ...cellBase, background: risk === 3 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(255,255,255,0.02)', color: 'rgba(156,163,175,0.7)', fontWeight: '400' }}>
                                {criterion.medium}
                              </div>
                              <div style={{ ...cellBase, background: risk >= 4 && risk <= 5 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255,255,255,0.02)', color: 'rgba(156,163,175,0.7)', fontWeight: '400' }}>
                                {criterion.high}
                              </div>
                              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '4px 6px', display: 'flex', gap: '3px', justifyContent: 'center', alignItems: 'center' }}>
                                {[1,2,3,4,5].map(v => (
                                  <div key={v} onClick={() => updateChecklistRisk(key, v)} style={{
                                    width: '18px', height: '18px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.65em', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s',
                                    background: risk === v ? getRiskColor(v) : 'rgba(255,255,255,0.06)',
                                    color: risk === v ? '#ffffff' : 'rgba(156,163,175,0.5)',
                                    border: risk === v ? 'none' : '1px solid rgba(58,58,58,0.5)'
                                  }}>{v}</div>
                                ))}
                              </div>
                              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '4px' }}>
                                <input
                                  value={checklistData.actions[key] || ''}
                                  onChange={(e) => updateChecklistAction(key, e.target.value)}
                                  placeholder="Mitigation..."
                                  style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(58,58,58,0.3)', borderRadius: '4px', padding: '5px 8px', color: '#ffffff', fontSize: '0.8em', outline: 'none' }}
                                />
                              </div>
                              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '4px' }}>
                                <input
                                  value={checklistData.comments[key] || ''}
                                  onChange={(e) => updateChecklistComment(key, e.target.value)}
                                  placeholder="..."
                                  style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(58,58,58,0.3)', borderRadius: '4px', padding: '5px 8px', color: '#ffffff', fontSize: '0.8em', outline: 'none' }}
                                />
                              </div>
                            </div>
                          );
                        })}

                        {/* Average row */}
                        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 1fr 110px 1fr 1fr', gap: '1px', marginTop: '2px', borderTop: '2px solid rgba(58,58,58,0.4)' }}>
                          <div style={{ gridColumn: '1 / 5' }}></div>
                          <div style={{ background: getRiskBg(sectionAvg), padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.9em', color: getRiskColor(sectionAvg) }}>
                            {sectionAvg != null ? sectionAvg.toFixed(1) : '-'}
                          </div>
                          <div></div>
                          <div></div>
                        </div>
                      </div>
                    </div>
                    </>
                    )}
                  </div>
                );
              })}
            </div>
            )}

            {/* Documents sub-tab */}
            {aideSubTab === 'documents' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <p style={{ color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.9em', lineHeight: '1.7' }}>
                Liste des documents à rassembler et vérifier lors d'un achat immobilier. Cochez chaque document au fur et à mesure de votre avancement.
              </p>

              {/* Avant l'offre */}
              <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(58, 58, 58, 0.5)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ background: 'rgba(59, 130, 246, 0.3)', color: '#3b82f6', width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9em', fontWeight: '700' }}>1</div>
                  <h3 style={{ color: '#ffffff', fontSize: '1.05em', fontWeight: '700', margin: 0 }}>Avant l'offre d'achat</h3>
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {[
                    { doc: 'Annonce immobilière complète (prix, surface, charges)', type: 'vérifier' },
                    { doc: 'Photos et plans du bien', type: 'vérifier' },
                    { doc: 'Diagnostics techniques (DPE, amiante, plomb, électricité, gaz)', type: 'vérifier' },
                    { doc: 'Taxe foncière des dernières années', type: 'vérifier' },
                    { doc: 'Montant des charges de copropriété', type: 'vérifier' },
                    { doc: 'Procès-verbaux des 3 dernières AG (copropriété)', type: 'vérifier' },
                    { doc: 'Règlement de copropriété', type: 'vérifier' },
                    { doc: 'Carnet d\'entretien de l\'immeuble', type: 'vérifier' },
                    { doc: 'État des lieux des parties communes', type: 'vérifier' }
                  ].map((item, i) => (
                    <div key={i} onClick={() => setCheckedDocs(prev => ({ ...prev, [item.doc]: !prev[item.doc] }))} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: checkedDocs[item.doc] ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.02)', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                      <div style={{ width: '18px', height: '18px', borderRadius: '4px', border: checkedDocs[item.doc] ? '2px solid #10b981' : '2px solid rgba(156,163,175,0.4)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: checkedDocs[item.doc] ? '#10b981' : 'transparent', transition: 'all 0.2s ease' }}>{checkedDocs[item.doc] && <span style={{ color: '#fff', fontSize: '0.7em', fontWeight: '700' }}>✓</span>}</div>
                      <span style={{ color: item.type === 'fournir' ? '#60a5fa' : '#f59e0b', fontSize: '0.7em', fontWeight: '600', padding: '2px 6px', borderRadius: '4px', background: item.type === 'fournir' ? 'rgba(96,165,250,0.15)' : 'rgba(245,158,11,0.15)' }}>{item.type === 'fournir' ? 'À fournir' : 'À vérifier'}</span>
                      <span style={{ color: checkedDocs[item.doc] ? 'rgba(156,163,175,0.5)' : 'rgba(255,255,255,0.85)', fontSize: '0.85em', textDecoration: checkedDocs[item.doc] ? 'line-through' : 'none', transition: 'all 0.2s ease' }}>{item.doc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Compromis */}
              <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(58, 58, 58, 0.5)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ background: 'rgba(245, 158, 11, 0.3)', color: '#f59e0b', width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9em', fontWeight: '700' }}>2</div>
                  <h3 style={{ color: '#ffffff', fontSize: '1.05em', fontWeight: '700', margin: 0 }}>Pour le compromis de vente</h3>
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {[
                    { doc: 'Pièce d\'identité en cours de validité', type: 'fournir' },
                    { doc: 'Justificatif de domicile récent', type: 'fournir' },
                    { doc: 'Justificatifs de revenus (3 derniers bulletins de salaire)', type: 'fournir' },
                    { doc: 'Avis d\'imposition (2 dernières années)', type: 'fournir' },
                    { doc: 'Relevés bancaires (3 derniers mois)', type: 'fournir' },
                    { doc: 'Attestation d\'emploi ou contrat de travail', type: 'fournir' },
                    { doc: 'Simulation de prêt ou accord de principe', type: 'fournir' },
                    { doc: 'Titre de propriété du vendeur', type: 'vérifier' },
                    { doc: 'État hypothécaire du bien', type: 'vérifier' }
                  ].map((item, i) => (
                    <div key={i} onClick={() => setCheckedDocs(prev => ({ ...prev, [item.doc]: !prev[item.doc] }))} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: checkedDocs[item.doc] ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.02)', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                      <div style={{ width: '18px', height: '18px', borderRadius: '4px', border: checkedDocs[item.doc] ? '2px solid #10b981' : '2px solid rgba(156,163,175,0.4)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: checkedDocs[item.doc] ? '#10b981' : 'transparent', transition: 'all 0.2s ease' }}>{checkedDocs[item.doc] && <span style={{ color: '#fff', fontSize: '0.7em', fontWeight: '700' }}>✓</span>}</div>
                      <span style={{ color: item.type === 'fournir' ? '#60a5fa' : '#f59e0b', fontSize: '0.7em', fontWeight: '600', padding: '2px 6px', borderRadius: '4px', background: item.type === 'fournir' ? 'rgba(96,165,250,0.15)' : 'rgba(245,158,11,0.15)' }}>{item.type === 'fournir' ? 'À fournir' : 'À vérifier'}</span>
                      <span style={{ color: checkedDocs[item.doc] ? 'rgba(156,163,175,0.5)' : 'rgba(255,255,255,0.85)', fontSize: '0.85em', textDecoration: checkedDocs[item.doc] ? 'line-through' : 'none', transition: 'all 0.2s ease' }}>{item.doc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Demande de prêt */}
              <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(58, 58, 58, 0.5)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ background: 'rgba(16, 185, 129, 0.3)', color: '#10b981', width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9em', fontWeight: '700' }}>3</div>
                  <h3 style={{ color: '#ffffff', fontSize: '1.05em', fontWeight: '700', margin: 0 }}>Pour la demande de prêt</h3>
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {[
                    { doc: 'Compromis de vente signé', type: 'fournir' },
                    { doc: 'Justificatif d\'apport personnel (épargne, donation)', type: 'fournir' },
                    { doc: 'Tableau d\'amortissement des crédits en cours', type: 'fournir' },
                    { doc: 'Justificatif de situation familiale', type: 'fournir' },
                    { doc: 'Questionnaire de santé (assurance emprunteur)', type: 'fournir' },
                    { doc: 'Devis travaux si applicable', type: 'fournir' },
                    { doc: 'Estimation locative si investissement', type: 'fournir' }
                  ].map((item, i) => (
                    <div key={i} onClick={() => setCheckedDocs(prev => ({ ...prev, [item.doc]: !prev[item.doc] }))} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: checkedDocs[item.doc] ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.02)', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                      <div style={{ width: '18px', height: '18px', borderRadius: '4px', border: checkedDocs[item.doc] ? '2px solid #10b981' : '2px solid rgba(156,163,175,0.4)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: checkedDocs[item.doc] ? '#10b981' : 'transparent', transition: 'all 0.2s ease' }}>{checkedDocs[item.doc] && <span style={{ color: '#fff', fontSize: '0.7em', fontWeight: '700' }}>✓</span>}</div>
                      <span style={{ color: item.type === 'fournir' ? '#60a5fa' : '#f59e0b', fontSize: '0.7em', fontWeight: '600', padding: '2px 6px', borderRadius: '4px', background: item.type === 'fournir' ? 'rgba(96,165,250,0.15)' : 'rgba(245,158,11,0.15)' }}>{item.type === 'fournir' ? 'À fournir' : 'À vérifier'}</span>
                      <span style={{ color: checkedDocs[item.doc] ? 'rgba(156,163,175,0.5)' : 'rgba(255,255,255,0.85)', fontSize: '0.85em', textDecoration: checkedDocs[item.doc] ? 'line-through' : 'none', transition: 'all 0.2s ease' }}>{item.doc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Acte authentique */}
              <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(58, 58, 58, 0.5)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ background: 'rgba(139, 92, 246, 0.3)', color: '#8b5cf6', width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9em', fontWeight: '700' }}>4</div>
                  <h3 style={{ color: '#ffffff', fontSize: '1.05em', fontWeight: '700', margin: 0 }}>Pour l'acte authentique</h3>
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {[
                    { doc: 'Offre de prêt signée (après délai de réflexion)', type: 'fournir' },
                    { doc: 'Attestation d\'assurance habitation', type: 'fournir' },
                    { doc: 'Virement des fonds chez le notaire', type: 'fournir' },
                    { doc: 'RIB pour le déblocage du prêt', type: 'fournir' },
                    { doc: 'Procuration si impossibilité d\'être présent', type: 'fournir' }
                  ].map((item, i) => (
                    <div key={i} onClick={() => setCheckedDocs(prev => ({ ...prev, [item.doc]: !prev[item.doc] }))} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: checkedDocs[item.doc] ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.02)', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                      <div style={{ width: '18px', height: '18px', borderRadius: '4px', border: checkedDocs[item.doc] ? '2px solid #10b981' : '2px solid rgba(156,163,175,0.4)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: checkedDocs[item.doc] ? '#10b981' : 'transparent', transition: 'all 0.2s ease' }}>{checkedDocs[item.doc] && <span style={{ color: '#fff', fontSize: '0.7em', fontWeight: '700' }}>✓</span>}</div>
                      <span style={{ color: item.type === 'fournir' ? '#60a5fa' : '#f59e0b', fontSize: '0.7em', fontWeight: '600', padding: '2px 6px', borderRadius: '4px', background: item.type === 'fournir' ? 'rgba(96,165,250,0.15)' : 'rgba(245,158,11,0.15)' }}>{item.type === 'fournir' ? 'À fournir' : 'À vérifier'}</span>
                      <span style={{ color: checkedDocs[item.doc] ? 'rgba(156,163,175,0.5)' : 'rgba(255,255,255,0.85)', fontSize: '0.85em', textDecoration: checkedDocs[item.doc] ? 'line-through' : 'none', transition: 'all 0.2s ease' }}>{item.doc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            )}

            {/* Timeline sub-tab */}
            {aideSubTab === 'timeline' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <p style={{ color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.9em', lineHeight: '1.7' }}>
                Chronologie type d'un achat immobilier, de la recherche à la remise des clés. Les délais peuvent varier selon les situations.
              </p>

              {/* Two timelines side by side */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>

                {/* Timeline Étapes */}
                <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '24px', borderRadius: '12px', border: '1px solid rgba(58, 58, 58, 0.5)' }}>
                  <h3 style={{ color: '#ffffff', fontSize: '1.2em', fontWeight: '700', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ background: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', padding: '8px 12px', borderRadius: '6px', fontSize: '0.9em' }}>Étapes</span>
                    Processus d'achat
                  </h3>
                  <div style={{ position: 'relative', paddingLeft: '130px' }}>
                    {/* Vertical line */}
                    <div style={{ position: 'absolute', left: '110px', top: '10px', bottom: '10px', width: '3px', background: '#3b82f6', borderRadius: '2px' }}></div>

                    {[
                      { phase: 'Recherche & Visites', duration: '1-6 mois', items: [
                        { text: 'Définir ses critères et budget', linkTo: 'Endettement', linkTab: 'summary' },
                        { text: 'Visiter les biens' },
                        { text: 'Analyser la rentabilité', linkTo: 'Bien immobilier', linkTab: 'extraction-charac' },
                        { text: 'Négocier le prix' }
                      ] },
                      { phase: 'Offre d\'achat', duration: '1-2 sem.', items: [
                        { text: 'Rédiger l\'offre écrite' },
                        { text: 'Négociation éventuelle' },
                        { text: 'Acceptation du vendeur' }
                      ] },
                      { phase: 'Compromis de vente', duration: '2-4 sem.', items: [
                        { text: 'Signature chez le notaire' },
                        { text: 'Versement du dépôt de garantie (5-10%)' },
                        { text: 'Délai de rétractation de 10 jours' },
                        { text: 'Conditions suspensives (prêt, etc.)' }
                      ] },
                      { phase: 'Recherche de financement', duration: '4-8 sem.', items: [
                        { text: 'Démarcher plusieurs banques' },
                        { text: 'Comparer les offres' },
                        { text: 'Obtenir l\'accord de prêt' },
                        { text: 'Délai de réflexion de 10 jours' }
                      ] },
                      { phase: 'Acte authentique', duration: '2-3 mois', items: [
                        { text: 'Signature chez le notaire' },
                        { text: 'Déblocage des fonds' },
                        { text: 'Paiement des frais de notaire' },
                        { text: 'Remise des clés' }
                      ] },
                      { phase: 'Après l\'achat', duration: 'Immédiat', items: [
                        { text: 'Souscrire assurance habitation' },
                        { text: 'Déclarer aux impôts' },
                        { text: 'Mettre en location si investissement' },
                        { text: 'Démarrer les travaux si prévus' }
                      ] }
                    ].map((step, idx) => (
                      <div key={idx} style={{ position: 'relative', marginBottom: '32px' }}>
                        {/* Duration label on the left */}
                        <div style={{ position: 'absolute', left: '-128px', top: '2px', width: '95px', textAlign: 'right', color: '#3b82f6', fontSize: '0.85em', fontWeight: '600' }}>{step.duration}</div>
                        {/* Circle marker */}
                        <div style={{ position: 'absolute', left: '-28px', top: '4px', width: '18px', height: '18px', borderRadius: '50%', background: '#3b82f6', border: '3px solid #222222' }}></div>

                        <div style={{ marginBottom: '10px' }}>
                          <span style={{ color: '#ffffff', fontSize: '1.1em', fontWeight: '700' }}>{step.phase}</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {step.items.map((item, i) => (
                            <div key={i} style={{ color: 'rgba(156,163,175,0.9)', fontSize: '0.95em', paddingLeft: '12px', borderLeft: '2px solid rgba(59,130,246,0.3)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>{item.text}</span>
                              {item.linkTo && (
                                <span
                                  onClick={() => setActiveTab(item.linkTab)}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    color: '#60a5fa',
                                    fontSize: '0.85em',
                                    cursor: 'pointer',
                                    padding: '2px 8px',
                                    background: 'rgba(96, 165, 250, 0.15)',
                                    borderRadius: '4px',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  → {item.linkTo}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Timeline Paiements */}
                <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '24px', borderRadius: '12px', border: '1px solid rgba(58, 58, 58, 0.5)' }}>
                  <h3 style={{ color: '#ffffff', fontSize: '1.2em', fontWeight: '700', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ background: 'rgba(16, 185, 129, 0.2)', color: '#10b981', padding: '8px 12px', borderRadius: '6px', fontSize: '0.9em' }}>Paiements</span>
                    Flux financiers
                  </h3>
                  <div style={{ position: 'relative', paddingLeft: '130px' }}>
                    {/* Vertical line */}
                    <div style={{ position: 'absolute', left: '110px', top: '10px', bottom: '10px', width: '3px', background: '#10b981', borderRadius: '2px' }}></div>

                    {[
                      { phase: 'Compromis', timing: 'J0', items: [
                        { label: 'Dépôt de garantie (séquestre)', amount: '5-10% du prix', icon: '↑' }
                      ]},
                      { phase: 'Avant l\'acte', timing: 'J+30 à J+60', items: [
                        { label: 'Frais de dossier bancaire', amount: '500-1 500€', icon: '↑' },
                        { label: 'Expertise du bien (si demandée)', amount: '200-500€', icon: '↑' },
                        { label: 'Assurance emprunteur (1ère prime)', amount: 'Variable', icon: '↑' }
                      ]},
                      { phase: 'Acte authentique', timing: 'J+90', items: [
                        { label: 'Apport personnel', amount: 'Fonds propres', icon: '↑' },
                        { label: 'Frais de notaire', amount: '7-8% (ancien) / 2-3% (neuf)', icon: '↑' },
                        { label: 'Frais d\'agence (si charge acheteur)', amount: '3-8% du prix', icon: '↑' },
                        { label: 'Déblocage du prêt', amount: 'Montant emprunté', icon: '↓' }
                      ]},
                      { phase: 'Travaux (si prévus)', timing: 'J+90 à J+180', items: [
                        { label: 'Paiement artisans / entreprises', amount: 'Selon devis', icon: '↑' },
                        { label: 'Déblocage progressif du prêt travaux', amount: 'Par tranches', icon: '↓' }
                      ]},
                      { phase: 'Exploitation locative', timing: 'Mensuel', items: [
                        { label: 'Loyers perçus', amount: 'Revenus locatifs', icon: '↓' },
                        { label: 'Mensualité crédit', amount: 'Capital + Intérêts', icon: '↑' },
                        { label: 'Charges copropriété', amount: 'Trimestriel/Mensuel', icon: '↑' },
                        { label: 'Taxe foncière', amount: 'Annuel', icon: '↑' },
                        { label: 'Assurance PNO', amount: 'Annuel', icon: '↑' },
                        { label: 'Frais de gestion (si agence)', amount: '6-10% des loyers', icon: '↑' }
                      ]},
                      { phase: 'Revente', timing: 'Année N', items: [
                        { label: 'Prix de vente encaissé', amount: 'Plus-value potentielle', icon: '↓' },
                        { label: 'Solde du crédit remboursé', amount: 'Capital restant dû', icon: '↑' },
                        { label: 'Frais d\'agence (si vente)', amount: '3-6% du prix', icon: '↑' },
                        { label: 'Impôt sur plus-value', amount: 'Si applicable', icon: '↑' }
                      ]}
                    ].map((step, idx) => (
                      <div key={idx} style={{ position: 'relative', marginBottom: '32px' }}>
                        {/* Timing label on the left */}
                        <div style={{ position: 'absolute', left: '-128px', top: '2px', width: '95px', textAlign: 'right', color: '#10b981', fontSize: '0.85em', fontWeight: '600' }}>{step.timing}</div>
                        {/* Circle marker */}
                        <div style={{ position: 'absolute', left: '-28px', top: '4px', width: '18px', height: '18px', borderRadius: '50%', background: '#10b981', border: '3px solid #222222' }}></div>

                        <div style={{ marginBottom: '10px' }}>
                          <span style={{ color: '#ffffff', fontSize: '1.1em', fontWeight: '700' }}>{step.phase}</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {step.items.map((item, i) => (
                            <div key={i} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              color: 'rgba(156,163,175,0.9)',
                              fontSize: '0.95em',
                              paddingLeft: '12px',
                              borderLeft: `2px solid ${item.icon === '↓' ? 'rgba(74, 222, 128, 0.5)' : 'rgba(248, 113, 113, 0.5)'}`
                            }}>
                              <span style={{
                                color: item.icon === '↓' ? '#4ade80' : '#f87171',
                                fontWeight: '700',
                                fontSize: '1.1em'
                              }}>{item.icon}</span>
                              <span>{item.label}</span>
                              <span style={{ color: 'rgba(156,163,175,0.6)', marginLeft: 'auto', fontSize: '0.95em' }}>{item.amount}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Légende */}
              <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#f87171', fontWeight: '700' }}>↑</span>
                  <span style={{ color: 'rgba(156,163,175,0.85)', fontSize: '0.85em' }}>Décaissement (vous payez)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#4ade80', fontWeight: '700' }}>↓</span>
                  <span style={{ color: 'rgba(156,163,175,0.85)', fontSize: '0.85em' }}>Encaissement (vous recevez)</span>
                </div>
              </div>
            </div>
            )}

          </div>
        )}

        {/* Home Tab */}
        {activeTab === 'home' && (
          <div className="home-section">

            {/* Project List View - shown when no project is selected and not in portfolio analysis */}
            {!currentProject && !showPortfolioAnalysis && (
              <div>
                {/* Mon Portefeuille Section */}
                <div style={{ marginBottom: '48px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h2 className="section-title" style={{ marginBottom: 0 }}>Mon Portefeuille</h2>
                    {portfolioProjects.length > 0 && (
                      <button
                        onClick={() => setShowPortfolioAnalysis(true)}
                        style={{
                          padding: '10px 20px',
                          background: 'rgba(59, 130, 246, 0.15)',
                          border: '1px solid rgba(59, 130, 246, 0.3)',
                          borderRadius: '8px',
                          color: '#60a5fa',
                          fontSize: '0.95em',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(59, 130, 246, 0.25)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(59, 130, 246, 0.15)';
                        }}
                      >
                        Analyse du Portefeuille
                        <span style={{ fontSize: '1.2em' }}>→</span>
                      </button>
                    )}
                  </div>

                  {(() => {
                    const portfolioProjectsList = savedProjects.filter(p => portfolioProjects.find(pp => pp.key === p.key));

                    if (portfolioProjectsList.length === 0) {
                      return (
                        <div style={{
                          background: 'rgba(255, 255, 255, 0.02)',
                          padding: '32px',
                          borderRadius: '12px',
                          border: '1px solid rgba(58, 58, 58, 0.3)',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '1em', color: 'rgba(156, 163, 175, 0.6)' }}>
                            Votre portefeuille est vide. Ajoutez des projets depuis "Projets analysés".
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {portfolioProjectsList.map((project) => {
                          const address = project.extractedData?.address || 'Adresse inconnue';
                          const surface = project.extractedData?.surface || 0;
                          const price = project.acquisitionData?.prixAchat || 0;
                          const savedDate = new Date(project.savedAt);
                          const investmentStartDate = getPortfolioStartDate(project.key);
                          const totalMonths = project.performanceParams?.investmentHorizonMonths || 120;

                          // Get saved NCF and TRI
                          const projNCF = project.calculatedNCF || 0;
                          const projTRI = project.calculatedTRI || 0;

                          // Calculate monthly cash flow
                          const projPropertyRevenues = project.propertyRevenues || [];
                          const projRecurrentFees = project.recurrentFees || [];
                          let projMonthlyRent = 0;
                          projPropertyRevenues.forEach(rev => {
                            const amount = parseFloat(rev.amount) || 0;
                            const occupancy = (parseFloat(rev.occupancyRate) || 100) / 100;
                            if (rev.frequency === 'monthly') projMonthlyRent += amount * occupancy;
                            else if (rev.frequency === 'quarterly') projMonthlyRent += (amount * occupancy) / 3;
                            else if (rev.frequency === 'annually') projMonthlyRent += (amount * occupancy) / 12;
                          });
                          let projMonthlyCharges = 0;
                          projRecurrentFees.forEach(fee => {
                            const amount = parseFloat(fee.amount) || 0;
                            if (fee.frequency === 'monthly') projMonthlyCharges += amount;
                            else if (fee.frequency === 'quarterly') projMonthlyCharges += amount / 3;
                            else if (fee.frequency === 'annually') projMonthlyCharges += amount / 12;
                          });
                          const projMonthlyPayment = project.creditAmortization?.monthlyPayment || 0;
                          const cashFlow = projMonthlyRent - projMonthlyCharges - projMonthlyPayment;

                          return (
                            <div
                              key={project.key}
                              style={{
                                background: '#222222',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                border: '1px solid #2a2a2a',
                                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.3)',
                                position: 'relative'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.4)';
                                e.currentTarget.style.borderColor = '#3a3a3a';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.3)';
                                e.currentTarget.style.borderColor = '#2a2a2a';
                              }}
                              onClick={() => loadProject(project)}
                            >
                              {/* Favorite Button - Top Right */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeFromPortfolio(project.key);
                                }}
                                style={{
                                  position: 'absolute',
                                  top: '12px',
                                  right: '12px',
                                  background: 'rgba(255, 255, 255, 0.9)',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '36px',
                                  height: '36px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  fontSize: '1.2em',
                                  color: '#ef4444',
                                  transition: 'all 0.2s ease',
                                  zIndex: 10
                                }}
                                onMouseEnter={(e) => { e.target.style.background = 'rgba(239, 68, 68, 0.1)'; }}
                                onMouseLeave={(e) => { e.target.style.background = 'rgba(255, 255, 255, 0.9)'; }}
                                title="Retirer du portefeuille"
                              >
                                ♥
                              </button>

                              {/* Date Badge - Top Left */}
                              {investmentStartDate && (
                                <div style={{
                                  position: 'absolute',
                                  top: '12px',
                                  left: '12px',
                                  background: 'rgba(0, 0, 0, 0.7)',
                                  color: '#ffffff',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontSize: '0.75em',
                                  fontWeight: '600',
                                  zIndex: 10
                                }}>
                                  {new Date(investmentStartDate).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                </div>
                              )}

                              {/* Main Content Flex Container */}
                              <div style={{ display: 'flex', alignItems: 'stretch' }}>
                                {/* Property Image */}
                                {project.extractedData?.imageUrl && (
                                  <div style={{ width: '240px', flexShrink: 0 }}>
                                    <img
                                      src={project.extractedData.imageUrl}
                                      alt={project.extractedData?.address || 'Property'}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                      }}
                                      onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.display = 'none'; }}
                                    />
                                  </div>
                                )}

                                {/* Content Area */}
                                <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                  <div>
                                    {/* Price and Title */}
                                    <div style={{ marginBottom: '8px' }}>
                                      <div style={{ fontSize: '1.6em', fontWeight: '700', color: '#ffffff', marginBottom: '2px' }}>
                                        {formatNumber(price, 0)} €
                                      </div>
                                      <div style={{ fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)', fontWeight: '500' }}>
                                        {project.key}
                                      </div>
                                    </div>

                                    {/* Address */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px' }}>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(156, 163, 175, 0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                        <circle cx="12" cy="10" r="3"></circle>
                                      </svg>
                                      <span style={{ fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.8)' }}>{address}</span>
                                    </div>
                                  </div>

                                  {/* Metrics Badges */}
                                  <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                                    <div style={{
                                      flex: '1',
                                      background: 'rgba(42, 42, 42, 0.6)',
                                      border: '1px solid rgba(75, 75, 75, 0.8)',
                                      borderRadius: '8px',
                                      padding: '12px 16px'
                                    }}>
                                      <div style={{ fontSize: '0.7em', color: 'rgba(156, 163, 175, 0.6)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Budget</div>
                                      <div style={{ fontSize: '1.1em', fontWeight: '700', color: '#fbbf24' }}>{formatNumber(price, 0)} €</div>
                                    </div>
                                    <div style={{
                                      flex: '1',
                                      background: 'rgba(42, 42, 42, 0.6)',
                                      border: '1px solid rgba(75, 75, 75, 0.8)',
                                      borderRadius: '8px',
                                      padding: '12px 16px'
                                    }}>
                                      <div style={{ fontSize: '0.7em', color: 'rgba(156, 163, 175, 0.6)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cash-flow</div>
                                      <div style={{ fontSize: '1.1em', fontWeight: '700', color: cashFlow >= 0 ? '#10b981' : '#ef4444' }}>{formatNumber(cashFlow, 0)} €/mois</div>
                                    </div>
                                    <div style={{
                                      flex: '1',
                                      background: 'rgba(42, 42, 42, 0.6)',
                                      border: '1px solid rgba(75, 75, 75, 0.8)',
                                      borderRadius: '8px',
                                      padding: '12px 16px'
                                    }}>
                                      <div style={{ fontSize: '0.7em', color: 'rgba(156, 163, 175, 0.6)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Rendement net</div>
                                      <div style={{ fontSize: '1.1em', fontWeight: '700', color: projTRI >= 0 ? '#10b981' : '#ef4444' }}>{formatNumber(projTRI, 2)}%</div>
                                    </div>
                                  </div>

                                  {/* Description */}
                                  <div style={{ fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.6)', lineHeight: '1.6' }}>
                                    Surface: {formatNumber(surface, 0)} m² • NCF: {formatNumber(projNCF, 0)} €
                                  </div>
                                </div>

                                {/* Action Buttons - Right Side */}
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '12px',
                                  padding: '20px 24px',
                                  justifyContent: 'center',
                                  minWidth: '200px'
                                }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPreviewModal({ show: true, project: project });
                                    }}
                                    style={{
                                      background: '#1a1a1a',
                                      color: '#ffffff',
                                      border: '1px solid rgba(75, 75, 75, 0.8)',
                                      borderRadius: '8px',
                                      padding: '12px 20px',
                                      fontSize: '0.9em',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.background = '#2a2a2a';
                                      e.target.style.borderColor = 'rgba(100, 100, 100, 1)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.background = '#1a1a1a';
                                      e.target.style.borderColor = 'rgba(75, 75, 75, 0.8)';
                                    }}
                                  >
                                    Aperçu
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      loadProject(project);
                                    }}
                                    style={{
                                      background: '#1a1a1a',
                                      color: '#ffffff',
                                      border: '1px solid rgba(75, 75, 75, 0.8)',
                                      borderRadius: '8px',
                                      padding: '12px 20px',
                                      fontSize: '0.9em',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.background = '#2a2a2a';
                                      e.target.style.borderColor = 'rgba(100, 100, 100, 1)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.background = '#1a1a1a';
                                      e.target.style.borderColor = 'rgba(75, 75, 75, 0.8)';
                                    }}
                                  >
                                    Afficher
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeFromPortfolio(project.key);
                                    }}
                                    style={{
                                      background: '#dc2626',
                                      color: '#ffffff',
                                      border: '1px solid #dc2626',
                                      borderRadius: '8px',
                                      padding: '12px 20px',
                                      fontSize: '0.9em',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.background = '#b91c1c';
                                      e.target.style.borderColor = '#b91c1c';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.background = '#dc2626';
                                      e.target.style.borderColor = '#dc2626';
                                    }}
                                  >
                                    Supprimer
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Projets analysés Section */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '32px'
                }}>
                  <h2 className="section-title" style={{marginBottom: 0}}>Projets analysés</h2>
                  <button
                    onClick={() => {
                      // Clear all extracted data for a fresh new project
                      setExtractedData(null);
                      setPropertyUrl('');
                      setExtractError('');
                      // Reset acquisition data to defaults
                      const freshAcquisitionData = {
                        prixAchat: 0,
                        fraisNotaire: 0,
                        fraisAgence: 0,
                        travaux: 0,
                        autresFrais: 0
                      };
                      setAcquisitionData(freshAcquisitionData);
                      // Reset financement data
                      const freshFinancementData = { apport: 0, emprunt: 0 };
                      setFinancementData(freshFinancementData);
                      // Reset credit data
                      setHasCustomCreditSplit(false);
                      setCustomApportSplit(null);
                      // Create a new project so tabs are accessible
                      const newProject = {
                        id: Date.now(),
                        name: 'Nouveau Projet',
                        key: `project_${Date.now()}`,
                        acquisitionData: freshAcquisitionData,
                        financementData: freshFinancementData,
                        creditData: creditData,
                        createdAt: new Date().toISOString()
                      };
                      setCurrentProject(newProject);
                      // Navigate to Rentabilité first tab
                      setActiveTab('extraction-charac');
                      setExpandedCategories({...expandedCategories, investment: true});
                    }}
                    style={{
                      padding: '10px 20px',
                      background: 'rgba(209, 178, 135, 0.15)',
                      border: '1px solid rgba(209, 178, 135, 0.3)',
                      borderRadius: '8px',
                      color: 'rgba(209, 178, 135, 0.95)',
                      fontSize: '0.95em',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(209, 178, 135, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(209, 178, 135, 0.15)';
                    }}
                  >
                    + Nouveau Projet
                  </button>
                </div>

                {(() => {
                  const nonPortfolioProjects = savedProjects.filter(project => !portfolioProjects.find(pp => pp.key === project.key));

                  if (nonPortfolioProjects.length === 0) {
                    return (
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.02)',
                        padding: '48px',
                        borderRadius: '12px',
                        border: '1px solid rgba(58, 58, 58, 0.3)',
                        textAlign: 'center'
                      }}>
                        <div style={{fontSize: '3em', marginBottom: '16px', opacity: 0.3}}>📁</div>
                        <div style={{fontSize: '1.1em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '12px'}}>
                          {savedProjects.length === 0 ? 'Aucun projet analysé' : 'Tous les projets sont dans le portefeuille'}
                        </div>
                        <div style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.5)'}}>
                          {savedProjects.length === 0 ? 'Créez votre premier projet en extrayant les données d\'un bien immobilier' : 'Créez un nouveau projet pour l\'analyser'}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="summary-grid">
                      {nonPortfolioProjects.map((project) => {
                      const address = project.extractedData?.address || 'Adresse inconnue';
                      const surface = project.extractedData?.surface || 0;
                      const price = project.acquisitionData?.prixAchat || 0;
                      const savedDate = new Date(project.savedAt);

                      // Calculate total revenues and charges for this project
                      const projRevenues = (project.revenues || []).reduce((sum, r) => sum + (parseFloat(r.amount) || 0) * (parseFloat(r.coefficient) || 100) / 100, 0);
                      const projFutureRevenues = (project.futureRevenues || []).reduce((sum, r) => sum + (parseFloat(r.amount) || 0) * (parseFloat(r.coefficient) || 100) / 100, 0);
                      const projCharges = (project.charges || []).reduce((sum, c) => sum + (parseFloat(c.amount) || 0) * (parseFloat(c.coefficient) || 100) / 100, 0);
                      const projFutureCharges = (project.futureCharges || []).reduce((sum, c) => sum + (parseFloat(c.amount) || 0) * (parseFloat(c.coefficient) || 100) / 100, 0);

                      // Use the saved creditAmortization.monthlyPayment (same as Performance tab)
                      // For older saved projects, calculate it from creditData as fallback
                      let monthlyPayment = project.creditAmortization?.monthlyPayment || 0;
                      if (monthlyPayment === 0 && project.creditData) {
                        const projCreditData = project.creditData;
                        const projAmount = parseFloat(projCreditData.amount) || 0;
                        const projDurationYears = parseFloat(projCreditData.durationYears) || 20;
                        const projInterestRate = parseFloat(projCreditData.interestRate) || 0;
                        const monthlyRate = projInterestRate / 100 / 12;
                        const numPayments = projDurationYears * 12;
                        if (projAmount > 0 && monthlyRate > 0 && numPayments > 0) {
                          monthlyPayment = (projAmount * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
                        }
                      }

                      // Calculate borrowing capacity for status
                      const futureRevenuesTotal = projRevenues + projFutureRevenues;
                      const futureChargesTotal = projCharges + projFutureCharges;
                      const maxDebtRatio = project.loanParams?.maxDebtRatio || 33;
                      const maxMonthlyPayment = (futureRevenuesTotal * (maxDebtRatio / 100)) - futureChargesTotal;
                      const projMonthlyRate = (project.loanParams?.annualRate || 3.5) / 100 / 12;
                      const projNumMonths = (project.loanParams?.durationYears || 20) * 12;
                      let maxBorrowingCapacity = 0;
                      if (projMonthlyRate > 0 && maxMonthlyPayment > 0) {
                        maxBorrowingCapacity = maxMonthlyPayment * ((Math.pow(1 + projMonthlyRate, projNumMonths) - 1) / (projMonthlyRate * Math.pow(1 + projMonthlyRate, projNumMonths)));
                      }

                      // Calculate debt status
                      const emprunt = project.financementData?.emprunt || 0;
                      const capaciteStatus = maxBorrowingCapacity > 0 && emprunt > 0
                        ? (emprunt <= maxBorrowingCapacity * 0.8 ? 'green' : emprunt <= maxBorrowingCapacity ? 'amber' : 'red')
                        : 'gray';

                      const restantVivre = futureRevenuesTotal - futureChargesTotal - monthlyPayment;
                      const restantStatus = monthlyPayment > 0
                        ? (restantVivre >= 1000 ? 'green' : restantVivre >= 800 ? 'amber' : 'red')
                        : 'gray';

                      const totalAcq = (project.acquisitionData?.prixAchat || 0) + (project.acquisitionData?.fraisNotaire || 0) +
                                       (project.acquisitionData?.fraisAgence || 0) + (project.acquisitionData?.travaux || 0) +
                                       (project.acquisitionData?.autresFrais || 0);
                      const ltv = totalAcq > 0 ? (emprunt / totalAcq) * 100 : 0;
                      const ltvStatus = ltv > 0
                        ? (Math.floor(ltv) <= 80 ? 'green' : Math.floor(ltv) <= 90 ? 'amber' : 'red')
                        : 'gray';

                      // Determine overall debt status (worst of the three)
                      let debtStatus = 'green';
                      if (capaciteStatus === 'red' || restantStatus === 'red' || ltvStatus === 'red') {
                        debtStatus = 'red';
                      } else if (capaciteStatus === 'amber' || restantStatus === 'amber' || ltvStatus === 'amber') {
                        debtStatus = 'amber';
                      }

                      const statusColor = debtStatus === 'red' ? '#ef4444' : debtStatus === 'amber' ? '#f59e0b' : debtStatus === 'green' ? '#10b981' : 'rgba(156, 163, 175, 0.5)';

                      // Calculate performance metrics using exact months (matching Performance tab)
                      const projPropertyRevenues = project.propertyRevenues || [];
                      const projRecurrentFees = project.recurrentFees || [];
                      // Get exact months (support both old and new format)
                      const totalMonths = project.performanceParams?.investmentHorizonMonths || (project.performanceParams?.investmentHorizon * 12) || 120;
                      const projTotalInvestment = project.financementData?.apport || 0;

                      // Helper function to calculate monthly amount from different frequencies
                      const calcMonthlyAmount = (amount, frequency) => {
                        if (frequency === 'monthly') return amount;
                        if (frequency === 'quarterly') return amount / 3;
                        if (frequency === 'annually') return amount / 12;
                        return 0;
                      };

                      // Build monthlyData to match Performance tab calculation
                      const projCreditSchedule = project.creditAmortization?.schedule || [];

                      // Calculate operational cash flow for each month (matching Performance tab)
                      let totalOperationalCashFlow = 0;
                      for (let monthIndex = 1; monthIndex <= totalMonths; monthIndex++) {
                        const yearOfMonth = Math.floor((monthIndex - 1) / 12);
                        const monthInYear = ((monthIndex - 1) % 12) + 1;
                        const isLastMonth = monthIndex === totalMonths;

                        // Monthly revenue with startLag and indexation
                        const monthRevenue = projPropertyRevenues.reduce((sum, rev) => {
                          const startLag = rev.startLagMonths || 0;
                          if (monthIndex <= startLag) return sum;

                          const revAmount = parseFloat(rev.amount) || 0;
                          const occupancy = (parseFloat(rev.occupancyRate) || 100) / 100;
                          const freq = rev.frequency || 'monthly';
                          const indexation = parseFloat(rev.indexation) || 0;

                          let amount = 0;
                          if (freq === 'monthly') {
                            amount = revAmount * occupancy;
                          } else if (freq === 'quarterly' && monthInYear % 3 === 0) {
                            amount = revAmount * occupancy;
                          } else if (freq === 'annually' && monthInYear === 12) {
                            amount = revAmount * occupancy;
                          }
                          return sum + (amount * Math.pow(1 + indexation / 100, yearOfMonth));
                        }, 0);

                        // Monthly fees (with pro-rata for final month)
                        const monthFees = projRecurrentFees.reduce((sum, fee) => {
                          const feeAmount = parseFloat(fee.amount) || 0;
                          const freq = fee.frequency || 'monthly';
                          let amount = 0;
                          if (freq === 'monthly') {
                            amount = feeAmount;
                          } else if (freq === 'quarterly') {
                            if (monthInYear % 3 === 0) {
                              amount = feeAmount;
                            } else if (isLastMonth) {
                              const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
                              amount = feeAmount * (monthsIntoQuarter / 3);
                            }
                          } else if (freq === 'annually') {
                            if (monthInYear === 12) {
                              amount = feeAmount;
                            } else if (isLastMonth) {
                              amount = feeAmount * (monthInYear / 12);
                            }
                          }
                          return sum + amount;
                        }, 0);

                        // Get actual credit payment from schedule
                        const scheduleIndex = monthIndex - 1;
                        const scheduleEntry = projCreditSchedule[scheduleIndex];
                        const actualCreditPayment = scheduleEntry ? (scheduleEntry.principal + scheduleEntry.interest) : monthlyPayment;

                        const operationalCashFlow = monthRevenue - monthFees - actualCreditPayment;
                        totalOperationalCashFlow += operationalCashFlow;
                      }

                      // Cash-Flow Mensuel Moyen: Sum of operational cash flows / number of months
                      const cashFlow = totalMonths > 0 ? totalOperationalCashFlow / totalMonths : 0;

                      // Rentabilité nette prévisionnelle using exact months (matching Performance tab)
                      const projMonthlyRevenue = projPropertyRevenues.reduce((sum, rev) => {
                        const amount = parseFloat(rev.amount) || 0;
                        const freq = rev.frequency || 'monthly';
                        const occupancy = (parseFloat(rev.occupancyRate) || 100) / 100;
                        return sum + (calcMonthlyAmount(amount, freq) * occupancy);
                      }, 0);
                      const projMonthlyCharges = projRecurrentFees.reduce((sum, fee) => {
                        const amount = parseFloat(fee.amount) || 0;
                        const freq = fee.frequency || 'monthly';
                        return sum + calcMonthlyAmount(amount, freq);
                      }, 0);
                      const totalRent = projMonthlyRevenue * totalMonths;
                      const totalChargesSum = projMonthlyCharges * totalMonths;
                      const totalCreditSum = monthlyPayment * totalMonths;
                      const netProfitability = projTotalInvestment > 0 ? ((totalRent - totalChargesSum - totalCreditSum) / projTotalInvestment) * 100 : 0;

                      // Use pre-calculated Performance Totale metrics from saved project
                      // If not available (old projects), calculate them using the same logic as Performance tab
                      let projNCF = project.calculatedNCF;
                      let projTRI = project.calculatedTRI;

                      if (projNCF === undefined || projTRI === undefined) {
                        // Fallback calculation for projects saved before this feature
                        const projHousePriceInflation = project.performanceParams?.housePriceInflation || 2;
                        const projCreditAmount = project.creditData?.amount || 0;
                        const projInitialHomeValue = project.estimatedHomeValue || (projTotalInvestment + projCreditAmount);
                        const yearsExact = totalMonths / 12;
                        const projFinalHomeValue = projInitialHomeValue * Math.pow(1 + projHousePriceInflation / 100, yearsExact);
                        const projFinalBalance = projCreditSchedule && projCreditSchedule.length > 0
                          ? projCreditSchedule[Math.min(totalMonths - 1, projCreditSchedule.length - 1)]?.remainingBalance || 0
                          : 0;

                        // Build monthlyData and calculate NCF
                        let calcNCF = -projTotalInvestment;
                        for (let monthIndex = 1; monthIndex <= totalMonths; monthIndex++) {
                          const yearOfMonth = Math.floor((monthIndex - 1) / 12);
                          const monthInYear = ((monthIndex - 1) % 12) + 1;
                          const isLastMonth = monthIndex === totalMonths;

                          const monthRevenue = projPropertyRevenues.reduce((sum, rev) => {
                            const startLag = rev.startLagMonths || 0;
                            if (monthIndex <= startLag) return sum;
                            let amount = 0;
                            if (rev.frequency === 'monthly') amount = rev.amount * (rev.occupancyRate / 100);
                            else if (rev.frequency === 'quarterly' && monthInYear % 3 === 0) amount = rev.amount * (rev.occupancyRate / 100);
                            else if (rev.frequency === 'annually' && monthInYear === 12) amount = rev.amount * (rev.occupancyRate / 100);
                            return sum + (amount * Math.pow(1 + (rev.indexation || 0) / 100, yearOfMonth));
                          }, 0);

                          const monthFees = projRecurrentFees.reduce((sum, fee) => {
                            let amount = 0;
                            if (fee.frequency === 'monthly') amount = fee.amount;
                            else if (fee.frequency === 'quarterly') {
                              if (monthInYear % 3 === 0) amount = fee.amount;
                              else if (isLastMonth) amount = fee.amount * (((monthInYear - 1) % 3) + 1) / 3;
                            } else if (fee.frequency === 'annually') {
                              if (monthInYear === 12) amount = fee.amount;
                              else if (isLastMonth) amount = fee.amount * (monthInYear / 12);
                            }
                            return sum + amount;
                          }, 0);

                          const scheduleEntry = projCreditSchedule?.[monthIndex - 1];
                          const monthCreditPayment = scheduleEntry ? (scheduleEntry.principal + scheduleEntry.interest) : 0;

                          let netCashFlow = monthRevenue - monthFees - monthCreditPayment;
                          if (isLastMonth) {
                            netCashFlow += (projFinalHomeValue - projFinalBalance);
                          }
                          calcNCF += netCashFlow;
                        }

                        projNCF = calcNCF;
                        projTRI = projTotalInvestment > 0 && yearsExact > 0
                          ? ((calcNCF + (projFinalHomeValue - projFinalBalance) - projTotalInvestment) / projTotalInvestment / yearsExact) * 100
                          : 0;
                      }

                      return (
                        <div
                          key={project.key}
                          className="summary-card"
                          style={{
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          onClick={() => loadProject(project)}
                        >
                          <div className="summary-card-header" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start'
                          }}>
                            <div style={{flex: 1, paddingRight: '12px', display: 'flex', alignItems: 'center', gap: '12px'}}>
                              {/* Debt Status Indicator */}
                              <div style={{
                                width: '14px',
                                height: '14px',
                                borderRadius: '50%',
                                background: statusColor,
                                flexShrink: 0,
                                boxShadow: debtStatus === 'red' ? '0 0 10px rgba(239, 68, 68, 0.6)' : debtStatus === 'amber' ? '0 0 10px rgba(245, 158, 11, 0.6)' : debtStatus === 'green' ? '0 0 10px rgba(16, 185, 129, 0.6)' : 'none'
                              }}></div>
                              <div style={{flex: 1}}>
                                <div style={{fontSize: '0.9em', fontWeight: '700', marginBottom: '4px'}}>
                                  {project.key}
                                </div>
                                <div style={{
                                  fontSize: '0.75em',
                                  fontWeight: '400',
                                  color: 'rgba(156, 163, 175, 0.6)',
                                  marginTop: '4px'
                                }}>
                                  Sauvegardé le {savedDate.toLocaleDateString('fr-FR')}
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAddToPortfolioModal(project.key);
                                }}
                                style={{
                                  padding: '6px 10px',
                                  background: 'rgba(16, 185, 129, 0.1)',
                                  border: '1px solid rgba(16, 185, 129, 0.3)',
                                  borderRadius: '6px',
                                  color: '#4ade80',
                                  fontSize: '0.75em',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = 'rgba(16, 185, 129, 0.2)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'rgba(16, 185, 129, 0.1)';
                                }}
                                title="Ajouter au portefeuille"
                              >
                                + Portefeuille
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteProject(project.key);
                                }}
                                style={{
                                  padding: '6px 10px',
                                  background: 'rgba(239, 68, 68, 0.1)',
                                  border: '1px solid rgba(239, 68, 68, 0.3)',
                                  borderRadius: '6px',
                                  color: '#f87171',
                                  fontSize: '0.75em',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                                }}
                              >
                                Supprimer
                              </button>
                            </div>
                          </div>
                          {/* Content with Image on left */}
                          <div style={{ display: 'flex', gap: '16px', marginTop: '12px' }}>
                            {/* Property Image */}
                            {project.extractedData?.imageUrl && (
                              <div style={{ flexShrink: 0 }}>
                                <img
                                  src={project.extractedData.imageUrl}
                                  alt={project.extractedData?.address || 'Property'}
                                  style={{
                                    width: '150px',
                                    height: '120px',
                                    borderRadius: '6px',
                                    objectFit: 'cover',
                                    border: '1px solid rgba(58, 58, 58, 0.4)'
                                  }}
                                  onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                                />
                              </div>
                            )}
                            {/* Basic Info */}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                paddingBottom: '6px',
                                borderBottom: '1px solid rgba(58, 58, 58, 0.3)'
                              }}>
                                <span style={{fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.7)'}}>Adresse</span>
                                <span style={{fontSize: '0.8em', fontWeight: '600', color: '#ffffff', textAlign: 'right', maxWidth: '55%'}}>
                                  {address.length > 30 ? address.substring(0, 30) + '...' : address}
                                </span>
                              </div>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                paddingBottom: '6px',
                                borderBottom: '1px solid rgba(58, 58, 58, 0.3)'
                              }}>
                                <span style={{fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.7)'}}>Surface</span>
                                <span style={{fontSize: '0.85em', fontWeight: '600', color: '#60a5fa'}}>
                                  {formatNumber(surface, 0)} m²
                                </span>
                              </div>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                paddingBottom: '6px',
                                borderBottom: '1px solid rgba(58, 58, 58, 0.3)'
                              }}>
                                <span style={{fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.7)'}}>Prix d'achat</span>
                                <span style={{fontSize: '0.85em', fontWeight: '600', color: '#fbbf24'}}>
                                  {formatNumber(price, 0)} €
                                </span>
                              </div>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between'
                              }}>
                                <span style={{fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.7)'}}>Horizon</span>
                                <span style={{fontSize: '0.85em', fontWeight: '600', color: '#a78bfa'}}>
                                  {Math.floor(totalMonths / 12)} ans ({totalMonths} mois)
                                </span>
                              </div>
                            </div>
                          </div>

                          <div style={{marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px'}}>
                            {/* Performance Locative Section */}
                            <div style={{
                              marginTop: '12px',
                              paddingTop: '12px',
                              borderTop: '1px solid rgba(58, 58, 58, 0.5)'
                            }}>
                              <div style={{
                                fontSize: '0.8em',
                                fontWeight: '600',
                                color: 'rgba(209, 178, 135, 0.8)',
                                marginBottom: '10px'
                              }}>
                                Performance Locative
                              </div>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '8px'
                              }}>
                                {/* Cash-Flow Mensuel Moyen Box */}
                                <div style={{
                                  background: 'rgba(255, 255, 255, 0.03)',
                                  padding: '8px 10px',
                                  borderRadius: '6px',
                                  border: `1px solid ${cashFlow >= 0 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                                }}>
                                  <div style={{fontSize: '0.7em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '4px'}}>
                                    Cash-Flow Mensuel Moyen
                                  </div>
                                  <div style={{
                                    fontSize: '0.85em',
                                    fontWeight: '700',
                                    color: cashFlow >= 0 ? '#10b981' : '#ef4444'
                                  }}>
                                    {formatNumber(cashFlow, 0)} €/mois
                                  </div>
                                </div>

                                {/* Rentabilité Nette Prévisionnelle Box */}
                                <div style={{
                                  background: 'rgba(255, 255, 255, 0.03)',
                                  padding: '8px 10px',
                                  borderRadius: '6px',
                                  border: `1px solid ${netProfitability >= 0 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                                }}>
                                  <div style={{fontSize: '0.7em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '4px'}}>
                                    Rentabilité Nette Prév.
                                  </div>
                                  <div style={{
                                    fontSize: '0.85em',
                                    fontWeight: '700',
                                    color: netProfitability >= 0 ? '#10b981' : '#ef4444'
                                  }}>
                                    {formatNumber(netProfitability, 2)}%
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Performance Totale Section */}
                            <div style={{
                              marginTop: '12px',
                              paddingTop: '12px',
                              borderTop: '1px solid rgba(58, 58, 58, 0.5)'
                            }}>
                              <div style={{
                                fontSize: '0.8em',
                                fontWeight: '600',
                                color: 'rgba(209, 178, 135, 0.8)',
                                marginBottom: '10px'
                              }}>
                                Performance Totale (à la revente)
                              </div>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '8px'
                              }}>
                                {/* NCF Box */}
                                <div style={{
                                  background: 'rgba(255, 255, 255, 0.03)',
                                  padding: '8px 10px',
                                  borderRadius: '6px',
                                  border: `1px solid ${projNCF >= 0 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                                }}>
                                  <div style={{fontSize: '0.7em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '4px'}}>
                                    Cash-Flow Net (NCF)
                                  </div>
                                  <div style={{
                                    fontSize: '0.85em',
                                    fontWeight: '700',
                                    color: projNCF >= 0 ? '#10b981' : '#ef4444'
                                  }}>
                                    {formatNumber(projNCF, 0)} €
                                  </div>
                                </div>

                                {/* TRI Box */}
                                <div style={{
                                  background: 'rgba(255, 255, 255, 0.03)',
                                  padding: '8px 10px',
                                  borderRadius: '6px',
                                  border: `1px solid ${projTRI >= 0 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                                }}>
                                  <div style={{fontSize: '0.7em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '4px'}}>
                                    TRI (Taux Rentabilité)
                                  </div>
                                  <div style={{
                                    fontSize: '0.85em',
                                    fontWeight: '700',
                                    color: projTRI >= 0 ? '#10b981' : '#ef4444'
                                  }}>
                                    {formatNumber(projTRI, 2)}%
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Portfolio Analysis View */}
            {!currentProject && showPortfolioAnalysis && (
              <div>
                {/* Header with back button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <button
                    onClick={() => setShowPortfolioAnalysis(false)}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(156, 163, 175, 0.1)',
                      border: '1px solid rgba(156, 163, 175, 0.2)',
                      borderRadius: '6px',
                      color: 'rgba(156, 163, 175, 0.8)',
                      fontSize: '0.9em',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(156, 163, 175, 0.2)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(156, 163, 175, 0.1)'; }}
                  >
                    <span>←</span> Retour
                  </button>
                  <h2 className="section-title" style={{ marginBottom: 0 }}>
                    Analyse du Portefeuille
                  </h2>
                </div>

                {/* Sub-tabs navigation - Horizontal */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '24px',
                  borderBottom: '1px solid rgba(58, 58, 58, 0.4)',
                  paddingBottom: '0'
                }}>
                  <button
                    onClick={() => setPortfolioAnalysisTab('summary')}
                    style={{
                      padding: '12px 20px',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: portfolioAnalysisTab === 'summary' ? '2px solid rgba(209, 178, 135, 0.9)' : '2px solid transparent',
                      color: portfolioAnalysisTab === 'summary' ? 'rgba(209, 178, 135, 0.95)' : 'rgba(156, 163, 175, 0.7)',
                      fontSize: '0.95em',
                      fontWeight: portfolioAnalysisTab === 'summary' ? '600' : '500',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      marginBottom: '-1px'
                    }}
                    onMouseEnter={(e) => {
                      if (portfolioAnalysisTab !== 'summary') {
                        e.target.style.color = 'rgba(209, 178, 135, 0.8)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (portfolioAnalysisTab !== 'summary') {
                        e.target.style.color = 'rgba(156, 163, 175, 0.7)';
                      }
                    }}
                  >
                    Summary
                  </button>
                  <button
                    onClick={() => setPortfolioAnalysisTab('cashflow')}
                    style={{
                      padding: '12px 20px',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: portfolioAnalysisTab === 'cashflow' ? '2px solid rgba(209, 178, 135, 0.9)' : '2px solid transparent',
                      color: portfolioAnalysisTab === 'cashflow' ? 'rgba(209, 178, 135, 0.95)' : 'rgba(156, 163, 175, 0.7)',
                      fontSize: '0.95em',
                      fontWeight: portfolioAnalysisTab === 'cashflow' ? '600' : '500',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      marginBottom: '-1px'
                    }}
                    onMouseEnter={(e) => {
                      if (portfolioAnalysisTab !== 'cashflow') {
                        e.target.style.color = 'rgba(209, 178, 135, 0.8)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (portfolioAnalysisTab !== 'cashflow') {
                        e.target.style.color = 'rgba(156, 163, 175, 0.7)';
                      }
                    }}
                  >
                    Cash Flow
                  </button>
                </div>

                {/* Portfolio Analysis Content */}
                {(() => {
                  const portfolioProjectsList = savedProjects.filter(p => portfolioProjects.find(pp => pp.key === p.key));

                  // Calculate portfolio totals
                  let totalInvestment = 0;
                  let totalApport = 0;
                  let totalCredit = 0;
                  let totalSurface = 0;
                  let totalMonthlyRent = 0;
                  let totalMonthlyCharges = 0;
                  let totalMonthlyLoanPayment = 0;
                  let totalNCF = 0;
                  let weightedTRI = 0;

                  portfolioProjectsList.forEach(project => {
                    const projAcquisition = project.acquisitionData || {};
                    const projFinancement = project.financementData || {};
                    const projExtracted = project.extractedData || {};

                    const projTotalCost = (projAcquisition.prixAchat || 0) +
                                         (projAcquisition.fraisNotaire || 0) +
                                         (projAcquisition.fraisAgence || 0) +
                                         (projAcquisition.travaux || 0) +
                                         (projAcquisition.autresFrais || 0);
                    totalInvestment += projTotalCost;
                    totalApport += projFinancement.apport || 0;
                    totalCredit += projFinancement.emprunt || 0;
                    totalSurface += projExtracted.surface || 0;

                    const projPropertyRevenues = project.propertyRevenues || [];
                    const projRecurrentFees = project.recurrentFees || [];

                    let projMonthlyRent = 0;
                    projPropertyRevenues.forEach(rev => {
                      const amount = parseFloat(rev.amount) || 0;
                      const occupancy = (parseFloat(rev.occupancyRate) || 100) / 100;
                      if (rev.frequency === 'monthly') projMonthlyRent += amount * occupancy;
                      else if (rev.frequency === 'quarterly') projMonthlyRent += (amount * occupancy) / 3;
                      else if (rev.frequency === 'annually') projMonthlyRent += (amount * occupancy) / 12;
                    });
                    totalMonthlyRent += projMonthlyRent;

                    let projMonthlyCharges = 0;
                    projRecurrentFees.forEach(fee => {
                      const amount = parseFloat(fee.amount) || 0;
                      if (fee.frequency === 'monthly') projMonthlyCharges += amount;
                      else if (fee.frequency === 'quarterly') projMonthlyCharges += amount / 3;
                      else if (fee.frequency === 'annually') projMonthlyCharges += amount / 12;
                    });
                    totalMonthlyCharges += projMonthlyCharges;

                    const projMonthlyPayment = project.creditAmortization?.monthlyPayment || 0;
                    totalMonthlyLoanPayment += projMonthlyPayment;

                    const projNCF = project.calculatedNCF || 0;
                    const projTRI = project.calculatedTRI || 0;
                    totalNCF += projNCF;

                    if (projTotalCost > 0) {
                      weightedTRI += projTRI * projTotalCost;
                    }
                  });

                  const totalMonthlyCashFlow = totalMonthlyRent - totalMonthlyCharges - totalMonthlyLoanPayment;
                  const avgTRI = totalInvestment > 0 ? weightedTRI / totalInvestment : 0;

                  // Find earliest start date for cash flow reference
                  let earliestStartDate = new Date();
                  portfolioProjectsList.forEach(project => {
                    const investmentStartDate = project.acquisitionData?.investmentStartDate;
                    if (investmentStartDate) {
                      const startDate = new Date(investmentStartDate);
                      if (startDate < earliestStartDate) {
                        earliestStartDate = startDate;
                      }
                    }
                  });

                  return (
                    <div>
                      {/* Summary Tab */}
                      {portfolioAnalysisTab === 'summary' && (
                        <div>
                          {/* Key Metrics Grid */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '32px' }}>
                            <div style={{
                              background: 'rgba(59, 130, 246, 0.08)',
                              border: '1px solid rgba(59, 130, 246, 0.2)',
                              borderRadius: '12px',
                              padding: '20px'
                            }}>
                              <div style={{ fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.8)', marginBottom: '8px' }}>Investissement Total</div>
                              <div style={{ fontSize: '1.8em', fontWeight: '700', color: '#60a5fa' }}>{formatNumber(totalInvestment, 0)} €</div>
                              <div style={{ fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.6)', marginTop: '4px' }}>{portfolioProjectsList.length} projet{portfolioProjectsList.length > 1 ? 's' : ''}</div>
                            </div>

                            <div style={{
                              background: 'rgba(209, 178, 135, 0.08)',
                              border: '1px solid rgba(209, 178, 135, 0.2)',
                              borderRadius: '12px',
                              padding: '20px'
                            }}>
                              <div style={{ fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.8)', marginBottom: '8px' }}>Apport Total</div>
                              <div style={{ fontSize: '1.8em', fontWeight: '700', color: 'rgba(209, 178, 135, 0.95)' }}>{formatNumber(totalApport, 0)} €</div>
                              <div style={{ fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.6)', marginTop: '4px' }}>{totalInvestment > 0 ? ((totalApport / totalInvestment) * 100).toFixed(1) : 0}% du total</div>
                            </div>

                            <div style={{
                              background: 'rgba(139, 92, 246, 0.08)',
                              border: '1px solid rgba(139, 92, 246, 0.2)',
                              borderRadius: '12px',
                              padding: '20px'
                            }}>
                              <div style={{ fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.8)', marginBottom: '8px' }}>Crédit Total</div>
                              <div style={{ fontSize: '1.8em', fontWeight: '700', color: '#a78bfa' }}>{formatNumber(totalCredit, 0)} €</div>
                              <div style={{ fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.6)', marginTop: '4px' }}>Mensualités: {formatNumber(totalMonthlyLoanPayment, 0)} €/mois</div>
                            </div>

                            <div style={{
                              background: 'rgba(34, 197, 94, 0.08)',
                              border: '1px solid rgba(34, 197, 94, 0.2)',
                              borderRadius: '12px',
                              padding: '20px'
                            }}>
                              <div style={{ fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.8)', marginBottom: '8px' }}>Surface Totale</div>
                              <div style={{ fontSize: '1.8em', fontWeight: '700', color: '#4ade80' }}>{formatNumber(totalSurface, 0)} m²</div>
                              <div style={{ fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.6)', marginTop: '4px' }}>Prix moyen: {totalSurface > 0 ? formatNumber(totalInvestment / totalSurface, 0) : 0} €/m²</div>
                            </div>

                            <div style={{
                              background: totalMonthlyCashFlow >= 0 ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                              border: `1px solid ${totalMonthlyCashFlow >= 0 ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                              borderRadius: '12px',
                              padding: '20px'
                            }}>
                              <div style={{ fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.8)', marginBottom: '8px' }}>Cash-Flow Mensuel</div>
                              <div style={{ fontSize: '1.8em', fontWeight: '700', color: totalMonthlyCashFlow >= 0 ? '#4ade80' : '#f87171' }}>{totalMonthlyCashFlow >= 0 ? '+' : ''}{formatNumber(totalMonthlyCashFlow, 0)} €</div>
                              <div style={{ fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.6)', marginTop: '4px' }}>Loyers - Charges - Crédits</div>
                              <div style={{ fontSize: '0.7em', color: 'rgba(156, 163, 175, 0.5)', marginTop: '4px', fontStyle: 'italic' }}>
                                au {earliestStartDate.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                              </div>
                            </div>

                            <div style={{
                              background: totalNCF >= 0 ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                              border: `1px solid ${totalNCF >= 0 ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                              borderRadius: '12px',
                              padding: '20px'
                            }}>
                              <div style={{ fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.8)', marginBottom: '8px' }}>NCF Total (revente)</div>
                              <div style={{ fontSize: '1.8em', fontWeight: '700', color: totalNCF >= 0 ? '#4ade80' : '#f87171' }}>{totalNCF >= 0 ? '+' : ''}{formatNumber(totalNCF, 0)} €</div>
                              <div style={{ fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.6)', marginTop: '4px' }}>Somme des projets</div>
                            </div>

                            <div style={{
                              background: avgTRI >= 0 ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                              border: `1px solid ${avgTRI >= 0 ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                              borderRadius: '12px',
                              padding: '20px'
                            }}>
                              <div style={{ fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.8)', marginBottom: '8px' }}>TRI Moyen Pondéré</div>
                              <div style={{ fontSize: '1.8em', fontWeight: '700', color: avgTRI >= 0 ? '#4ade80' : '#f87171' }}>{formatNumber(avgTRI, 2)}%</div>
                              <div style={{ fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.6)', marginTop: '4px' }}>Pondéré par investissement</div>
                            </div>
                          </div>

                          {/* Cash Flow Chart */}
                          <div style={{ marginTop: '32px', marginBottom: '32px', width: '100%' }}>
                            <h3 style={{ fontSize: '1.1em', fontWeight: '600', color: '#ffffff', marginBottom: '16px' }}>Évolution du Cash-Flow Mensuel</h3>
                            <div style={{
                              background: 'rgba(255, 255, 255, 0.02)',
                              borderRadius: '12px',
                              border: '1px solid rgba(58, 58, 58, 0.3)',
                              padding: '24px',
                              position: 'relative',
                              width: '100%',
                              height: 'auto',
                              overflow: 'visible'
                            }}>
                              {(() => {
                                // Calculate monthly cash flow at each time point
                                const maxMonths = Math.max(...portfolioProjectsList.map(p => p.performanceParams?.investmentHorizonMonths || 120));

                                // Find earliest start date
                                let earliestStartDate = new Date();
                                portfolioProjectsList.forEach(project => {
                                  const startDateStr = getPortfolioStartDate(project.key);
                                  if (startDateStr) {
                                    const startDate = new Date(startDateStr);
                                    if (startDate < earliestStartDate) {
                                      earliestStartDate = startDate;
                                    }
                                  }
                                });

                                const monthlyCF = [];

                                // Start from month 1 (first operational month, excluding apport month 0)
                                for (let month = 1; month <= maxMonths; month++) {
                                  let totalCF = 0;

                                  // Calculate the current calendar date for this month
                                  const currentDate = new Date(earliestStartDate);
                                  currentDate.setMonth(currentDate.getMonth() + month);

                                  portfolioProjectsList.forEach(project => {
                                    const projectStartDateStr = getPortfolioStartDate(project.key);
                                    if (!projectStartDateStr) return;

                                    const projectStartDate = new Date(projectStartDateStr);

                                    // Calculate how many months have passed since THIS project's start using calendar months
                                    // This is 'i' in the table logic (0 = apport month, 1 = first operational month, etc.)
                                    const i = (currentDate.getFullYear() - projectStartDate.getFullYear()) * 12 +
                                              (currentDate.getMonth() - projectStartDate.getMonth());

                                    // Only include this project if it has started (i >= 0)
                                    // and if we're in an operational month (i >= 1, since i=0 is apport)
                                    if (i < 1) return;

                                    const projHorizon = project.performanceParams?.investmentHorizonMonths || 120;
                                    if (i > projHorizon) return;

                                    // Month 1+: Regular cash flows (same logic as table)
                                    const monthIndex = i - 1; // Amortization index (0-based from month 1)

                                    // Monthly cash flow for this project
                                    const projPropertyRevenues = project.propertyRevenues || [];
                                    const projRecurrentFees = project.recurrentFees || [];
                                    let projMonthlyRent = 0;
                                    // Use monthIndex for indexation (same as table)
                                    const yearOfMonth = monthIndex / 12;

                                    projPropertyRevenues.forEach(rev => {
                                      const amount = parseFloat(rev.amount) || 0;
                                      const occupancy = (parseFloat(rev.occupancyRate) || 100) / 100;
                                      const indexation = parseFloat(rev.indexation) || 0;
                                      const startLagMonths = parseFloat(rev.startLagMonths) || 0;

                                      // Only count revenue if we're past the start lag (same as table logic)
                                      if (monthIndex >= startLagMonths) {
                                        // Apply indexation: amount * (1 + indexation%)^years
                                        const indexedAmount = amount * Math.pow(1 + indexation / 100, yearOfMonth);

                                        if (rev.frequency === 'monthly') projMonthlyRent += indexedAmount * occupancy;
                                        else if (rev.frequency === 'quarterly') projMonthlyRent += (indexedAmount * occupancy) / 3;
                                        else if (rev.frequency === 'annually') projMonthlyRent += (indexedAmount * occupancy) / 12;
                                      }
                                    });

                                    let projMonthlyCharges = 0;
                                    projRecurrentFees.forEach(fee => {
                                      const amount = parseFloat(fee.amount) || 0;
                                      if (fee.frequency === 'monthly') projMonthlyCharges += amount;
                                      else if (fee.frequency === 'quarterly') projMonthlyCharges += amount / 3;
                                      else if (fee.frequency === 'annually') projMonthlyCharges += amount / 12;
                                    });
                                    const projMonthlyPayment = project.creditAmortization?.monthlyPayment || 0;
                                    const projMonthlyCF = projMonthlyRent - projMonthlyCharges - projMonthlyPayment;

                                    totalCF += projMonthlyCF;
                                  });

                                  monthlyCF.push({
                                    month: month,
                                    cf: totalCF
                                  });
                                }

                                const maxCF = Math.max(...monthlyCF.map(d => d.cf), 0);
                                const minCF = Math.min(...monthlyCF.map(d => d.cf), 0);
                                const range = maxCF - minCF || 1;

                                return (
                                  <svg
                                    width="100%"
                                    style={{
                                      display: 'block',
                                      height: 'auto',
                                      minHeight: '400px',
                                      maxHeight: '600px'
                                    }}
                                    viewBox="0 0 1400 500"
                                    preserveAspectRatio="xMidYMid meet"
                                  >
                                    {/* Grid lines */}
                                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
                                      const y = 450 - (ratio * 400);
                                      const value = minCF + (ratio * range);
                                      return (
                                        <g key={i}>
                                          <line x1="80" y1={y} x2="1360" y2={y} stroke="rgba(156, 163, 175, 0.1)" strokeWidth="1" />
                                          <text x="70" y={y + 5} fill="rgba(156, 163, 175, 0.6)" fontSize="12" textAnchor="end">
                                            {formatNumber(value, 0)} €
                                          </text>
                                        </g>
                                      );
                                    })}

                                    {/* Zero line */}
                                    {minCF < 0 && (
                                      <line
                                        x1="80"
                                        y1={450 - ((0 - minCF) / range * 400)}
                                        x2="1360"
                                        y2={450 - ((0 - minCF) / range * 400)}
                                        stroke="rgba(156, 163, 175, 0.4)"
                                        strokeWidth="2"
                                        strokeDasharray="5,5"
                                      />
                                    )}

                                    {/* X-axis labels */}
                                    {[1, maxMonths / 4, maxMonths / 2, (3 * maxMonths) / 4, maxMonths].map((month, i) => {
                                      const x = 80 + ((month / maxMonths) * 1280);
                                      const date = new Date(earliestStartDate);
                                      // Add month to get correct date (month 1 = first operational month after apport)
                                      date.setMonth(date.getMonth() + Math.round(month));
                                      return (
                                        <text key={i} x={x} y="475" fill="rgba(156, 163, 175, 0.6)" fontSize="11" textAnchor="middle">
                                          {date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                                        </text>
                                      );
                                    })}

                                    {/* Chart line */}
                                    <polyline
                                      points={monthlyCF.map((d, i) => {
                                        const x = 80 + ((d.month / maxMonths) * 1280);
                                        const y = 450 - ((d.cf - minCF) / range * 400);
                                        return `${x},${y}`;
                                      }).join(' ')}
                                      fill="none"
                                      stroke="rgba(59, 130, 246, 0.8)"
                                      strokeWidth="3"
                                      strokeLinejoin="round"
                                    />

                                    {/* Area under curve */}
                                    <polygon
                                      points={
                                        `80,${450 - ((0 - minCF) / range * 400)} ` +
                                        monthlyCF.map((d, i) => {
                                          const x = 80 + ((d.month / maxMonths) * 1280);
                                          const y = 450 - ((d.cf - minCF) / range * 400);
                                          return `${x},${y}`;
                                        }).join(' ') +
                                        ` 1360,${450 - ((0 - minCF) / range * 400)}`
                                      }
                                      fill="rgba(59, 130, 246, 0.1)"
                                    />

                                    {/* Axes */}
                                    <line x1="80" y1="50" x2="80" y2="450" stroke="rgba(156, 163, 175, 0.4)" strokeWidth="2" />
                                    <line x1="80" y1="450" x2="1360" y2="450" stroke="rgba(156, 163, 175, 0.4)" strokeWidth="2" />

                                    {/* Interactive data points */}
                                    {monthlyCF.map((d, i) => {
                                      const x = 80 + ((d.month / maxMonths) * 1280);
                                      const y = 450 - ((d.cf - minCF) / range * 400);
                                      const date = new Date(earliestStartDate);
                                      // d.month starts at 1, so we add d.month to get the correct date (month 1 = Feb 2026 if Jan 2026 is month 0)
                                      date.setMonth(date.getMonth() + d.month);
                                      const isHovered = chartHoverIndex === i;

                                      return (
                                        <g key={i}>
                                          {/* Hover area */}
                                          <circle
                                            cx={x}
                                            cy={y}
                                            r="20"
                                            fill="transparent"
                                            style={{ cursor: 'pointer' }}
                                            onMouseEnter={() => setChartHoverIndex(i)}
                                            onMouseLeave={() => setChartHoverIndex(null)}
                                          />
                                          {/* Visible dot */}
                                          <circle
                                            cx={x}
                                            cy={y}
                                            r={isHovered ? "6" : "3"}
                                            fill={isHovered ? "rgba(59, 130, 246, 1)" : "rgba(59, 130, 246, 0.6)"}
                                            stroke="#ffffff"
                                            strokeWidth={isHovered ? "2" : "1"}
                                            style={{
                                              cursor: 'pointer',
                                              transition: 'all 0.2s ease',
                                              pointerEvents: 'none'
                                            }}
                                          />
                                          {/* Tooltip */}
                                          {isHovered && (
                                            <g>
                                              {/* Tooltip background */}
                                              <rect
                                                x={x - 80}
                                                y={y - 65}
                                                width="160"
                                                height="55"
                                                rx="6"
                                                fill="rgba(30, 30, 30, 0.95)"
                                                stroke="rgba(59, 130, 246, 0.5)"
                                                strokeWidth="1.5"
                                              />
                                              {/* Date text */}
                                              <text
                                                x={x}
                                                y={y - 40}
                                                fill="#ffffff"
                                                fontSize="13"
                                                fontWeight="600"
                                                textAnchor="middle"
                                              >
                                                {date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                                              </text>
                                              {/* Cash flow text */}
                                              <text
                                                x={x}
                                                y={y - 20}
                                                fill="rgba(59, 130, 246, 1)"
                                                fontSize="14"
                                                fontWeight="700"
                                                textAnchor="middle"
                                              >
                                                {formatNumber(d.cf, 0)} €
                                              </text>
                                            </g>
                                          )}
                                        </g>
                                      );
                                    })}

                                    {/* Axis labels */}
                                    <text x="720" y="492" fill="rgba(156, 163, 175, 0.8)" fontSize="14" textAnchor="middle" fontWeight="600">
                                      Date
                                    </text>
                                    <text x="30" y="250" fill="rgba(156, 163, 175, 0.8)" fontSize="14" textAnchor="middle" fontWeight="600" transform="rotate(-90, 30, 250)">
                                      Cash-Flow Mensuel (€)
                                    </text>
                                  </svg>
                                );
                              })()}
                            </div>
                          </div>

                          {/* Projects Table */}
                          <div style={{ marginTop: '32px' }}>
                            <h3 style={{ fontSize: '1.1em', fontWeight: '600', color: '#ffffff', marginBottom: '16px' }}>Détail par Projet</h3>
                            <div style={{
                              background: 'rgba(255, 255, 255, 0.02)',
                              borderRadius: '12px',
                              border: '1px solid rgba(58, 58, 58, 0.3)',
                              overflow: 'hidden'
                            }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ background: 'rgba(255, 255, 255, 0.03)' }}>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.8)', fontWeight: '600', borderBottom: '1px solid rgba(58, 58, 58, 0.3)' }}>Projet</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.8)', fontWeight: '600', borderBottom: '1px solid rgba(58, 58, 58, 0.3)' }}>Investissement</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.8)', fontWeight: '600', borderBottom: '1px solid rgba(58, 58, 58, 0.3)' }}>Cash-Flow/mois</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.8)', fontWeight: '600', borderBottom: '1px solid rgba(58, 58, 58, 0.3)' }}>NCF</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.8)', fontWeight: '600', borderBottom: '1px solid rgba(58, 58, 58, 0.3)' }}>TRI</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {portfolioProjectsList.map((project, index) => {
                                    const projAcq = project.acquisitionData || {};
                                    const projInvestment = (projAcq.prixAchat || 0) + (projAcq.fraisNotaire || 0) + (projAcq.fraisAgence || 0) + (projAcq.travaux || 0) + (projAcq.autresFrais || 0);

                                    const projPropertyRevenues = project.propertyRevenues || [];
                                    const projRecurrentFees = project.recurrentFees || [];
                                    let projMonthlyRent = 0;
                                    projPropertyRevenues.forEach(rev => {
                                      const amount = parseFloat(rev.amount) || 0;
                                      const occupancy = (parseFloat(rev.occupancyRate) || 100) / 100;
                                      if (rev.frequency === 'monthly') projMonthlyRent += amount * occupancy;
                                      else if (rev.frequency === 'quarterly') projMonthlyRent += (amount * occupancy) / 3;
                                      else if (rev.frequency === 'annually') projMonthlyRent += (amount * occupancy) / 12;
                                    });
                                    let projMonthlyCharges = 0;
                                    projRecurrentFees.forEach(fee => {
                                      const amount = parseFloat(fee.amount) || 0;
                                      if (fee.frequency === 'monthly') projMonthlyCharges += amount;
                                      else if (fee.frequency === 'quarterly') projMonthlyCharges += amount / 3;
                                      else if (fee.frequency === 'annually') projMonthlyCharges += amount / 12;
                                    });
                                    const projMonthlyPayment = project.creditAmortization?.monthlyPayment || 0;
                                    const projCashFlow = projMonthlyRent - projMonthlyCharges - projMonthlyPayment;

                                    const projNCF = project.calculatedNCF || 0;
                                    const projTRI = project.calculatedTRI || 0;

                                    return (
                                      <tr key={project.key} style={{ borderBottom: index < portfolioProjectsList.length - 1 ? '1px solid rgba(58, 58, 58, 0.2)' : 'none' }}>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85em', color: '#ffffff' }}>{project.key}</td>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85em', color: '#fbbf24', textAlign: 'right' }}>{formatNumber(projInvestment, 0)} €</td>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85em', color: projCashFlow >= 0 ? '#4ade80' : '#f87171', textAlign: 'right' }}>{formatNumber(projCashFlow, 0)} €</td>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85em', color: projNCF >= 0 ? '#4ade80' : '#f87171', textAlign: 'right' }}>{formatNumber(projNCF, 0)} €</td>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85em', color: projTRI >= 0 ? '#4ade80' : '#f87171', textAlign: 'right' }}>{formatNumber(projTRI, 2)}%</td>
                                      </tr>
                                    );
                                  })}
                                  {/* Total Row */}
                                  <tr style={{ background: 'rgba(255, 255, 255, 0.03)', fontWeight: '600' }}>
                                    <td style={{ padding: '12px 16px', fontSize: '0.85em', color: '#ffffff' }}>TOTAL</td>
                                    <td style={{ padding: '12px 16px', fontSize: '0.85em', color: '#fbbf24', textAlign: 'right' }}>{formatNumber(totalInvestment, 0)} €</td>
                                    <td style={{ padding: '12px 16px', fontSize: '0.85em', color: totalMonthlyCashFlow >= 0 ? '#4ade80' : '#f87171', textAlign: 'right' }}>{formatNumber(totalMonthlyCashFlow, 0)} €</td>
                                    <td style={{ padding: '12px 16px', fontSize: '0.85em', color: totalNCF >= 0 ? '#4ade80' : '#f87171', textAlign: 'right' }}>{formatNumber(totalNCF, 0)} €</td>
                                    <td style={{ padding: '12px 16px', fontSize: '0.85em', color: avgTRI >= 0 ? '#4ade80' : '#f87171', textAlign: 'right' }}>{formatNumber(avgTRI, 2)}%</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Cash Flow Tab */}
                      {portfolioAnalysisTab === 'cashflow' && (
                        <div>
                          {/* Monthly Cash Flow Breakdown */}
                          <div style={{ marginBottom: '32px' }}>
                            <h3 style={{ fontSize: '1.1em', fontWeight: '600', color: '#ffffff', marginBottom: '16px' }}>Cash-Flow Mensuel du Portefeuille</h3>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                              {/* Revenus */}
                              <div style={{
                                background: 'rgba(34, 197, 94, 0.08)',
                                border: '1px solid rgba(34, 197, 94, 0.2)',
                                borderRadius: '12px',
                                padding: '20px'
                              }}>
                                <div style={{ fontSize: '0.9em', fontWeight: '600', color: '#4ade80', marginBottom: '16px' }}>Revenus Locatifs</div>
                                <div style={{ fontSize: '2em', fontWeight: '700', color: '#4ade80', marginBottom: '8px' }}>+{formatNumber(totalMonthlyRent, 0)} €</div>
                                <div style={{ fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.6)' }}>par mois</div>
                              </div>

                              {/* Charges */}
                              <div style={{
                                background: 'rgba(239, 68, 68, 0.08)',
                                border: '1px solid rgba(239, 68, 68, 0.2)',
                                borderRadius: '12px',
                                padding: '20px'
                              }}>
                                <div style={{ fontSize: '0.9em', fontWeight: '600', color: '#f87171', marginBottom: '16px' }}>Charges & Frais</div>
                                <div style={{ fontSize: '2em', fontWeight: '700', color: '#f87171', marginBottom: '8px' }}>-{formatNumber(totalMonthlyCharges, 0)} €</div>
                                <div style={{ fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.6)' }}>par mois</div>
                              </div>

                              {/* Crédits */}
                              <div style={{
                                background: 'rgba(139, 92, 246, 0.08)',
                                border: '1px solid rgba(139, 92, 246, 0.2)',
                                borderRadius: '12px',
                                padding: '20px'
                              }}>
                                <div style={{ fontSize: '0.9em', fontWeight: '600', color: '#a78bfa', marginBottom: '16px' }}>Remboursements Crédits</div>
                                <div style={{ fontSize: '2em', fontWeight: '700', color: '#a78bfa', marginBottom: '8px' }}>-{formatNumber(totalMonthlyLoanPayment, 0)} €</div>
                                <div style={{ fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.6)' }}>par mois</div>
                              </div>

                              {/* Net */}
                              <div style={{
                                background: totalMonthlyCashFlow >= 0 ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                border: `2px solid ${totalMonthlyCashFlow >= 0 ? 'rgba(34, 197, 94, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
                                borderRadius: '12px',
                                padding: '20px'
                              }}>
                                <div style={{ fontSize: '0.9em', fontWeight: '600', color: totalMonthlyCashFlow >= 0 ? '#4ade80' : '#f87171', marginBottom: '16px' }}>Cash-Flow Net</div>
                                <div style={{ fontSize: '2em', fontWeight: '700', color: totalMonthlyCashFlow >= 0 ? '#4ade80' : '#f87171', marginBottom: '8px' }}>{totalMonthlyCashFlow >= 0 ? '+' : ''}{formatNumber(totalMonthlyCashFlow, 0)} €</div>
                                <div style={{ fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.6)' }}>par mois</div>
                              </div>
                            </div>
                          </div>

                          {/* Annual Projections */}
                          <div style={{ marginBottom: '32px' }}>
                            <h3 style={{ fontSize: '1.1em', fontWeight: '600', color: '#ffffff', marginBottom: '16px' }}>Projections Annuelles</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                              <div style={{
                                background: 'rgba(255, 255, 255, 0.03)',
                                borderRadius: '12px',
                                border: '1px solid rgba(58, 58, 58, 0.3)',
                                padding: '20px'
                              }}>
                                <div style={{ fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.8)', marginBottom: '8px' }}>Revenus Annuels</div>
                                <div style={{ fontSize: '1.5em', fontWeight: '700', color: '#4ade80' }}>+{formatNumber(totalMonthlyRent * 12, 0)} €</div>
                              </div>
                              <div style={{
                                background: 'rgba(255, 255, 255, 0.03)',
                                borderRadius: '12px',
                                border: '1px solid rgba(58, 58, 58, 0.3)',
                                padding: '20px'
                              }}>
                                <div style={{ fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.8)', marginBottom: '8px' }}>Charges Annuelles</div>
                                <div style={{ fontSize: '1.5em', fontWeight: '700', color: '#f87171' }}>-{formatNumber(totalMonthlyCharges * 12, 0)} €</div>
                              </div>
                              <div style={{
                                background: 'rgba(255, 255, 255, 0.03)',
                                borderRadius: '12px',
                                border: '1px solid rgba(58, 58, 58, 0.3)',
                                padding: '20px'
                              }}>
                                <div style={{ fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.8)', marginBottom: '8px' }}>Crédits Annuels</div>
                                <div style={{ fontSize: '1.5em', fontWeight: '700', color: '#a78bfa' }}>-{formatNumber(totalMonthlyLoanPayment * 12, 0)} €</div>
                              </div>
                              <div style={{
                                background: 'rgba(255, 255, 255, 0.03)',
                                borderRadius: '12px',
                                border: '1px solid rgba(58, 58, 58, 0.3)',
                                padding: '20px'
                              }}>
                                <div style={{ fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.8)', marginBottom: '8px' }}>Cash-Flow Annuel Net</div>
                                <div style={{ fontSize: '1.5em', fontWeight: '700', color: totalMonthlyCashFlow >= 0 ? '#4ade80' : '#f87171' }}>{totalMonthlyCashFlow >= 0 ? '+' : ''}{formatNumber(totalMonthlyCashFlow * 12, 0)} €</div>
                              </div>
                            </div>
                          </div>

                          {/* Cash Flow by Project */}
                          <div>
                            <h3 style={{ fontSize: '1.1em', fontWeight: '600', color: '#ffffff', marginBottom: '16px' }}>Détail par Projet</h3>
                            <div style={{
                              background: 'rgba(255, 255, 255, 0.02)',
                              borderRadius: '12px',
                              border: '1px solid rgba(58, 58, 58, 0.3)',
                              overflow: 'hidden'
                            }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ background: 'rgba(255, 255, 255, 0.03)' }}>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.8)', fontWeight: '600', borderBottom: '1px solid rgba(58, 58, 58, 0.3)' }}>Projet</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.8)', fontWeight: '600', borderBottom: '1px solid rgba(58, 58, 58, 0.3)' }}>Loyers</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.8)', fontWeight: '600', borderBottom: '1px solid rgba(58, 58, 58, 0.3)' }}>Charges</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.8)', fontWeight: '600', borderBottom: '1px solid rgba(58, 58, 58, 0.3)' }}>Crédit</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.8)', fontWeight: '600', borderBottom: '1px solid rgba(58, 58, 58, 0.3)' }}>Cash-Flow Net</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {portfolioProjectsList.map((project, index) => {
                                    const projPropertyRevenues = project.propertyRevenues || [];
                                    const projRecurrentFees = project.recurrentFees || [];
                                    let projMonthlyRent = 0;
                                    projPropertyRevenues.forEach(rev => {
                                      const amount = parseFloat(rev.amount) || 0;
                                      const occupancy = (parseFloat(rev.occupancyRate) || 100) / 100;
                                      if (rev.frequency === 'monthly') projMonthlyRent += amount * occupancy;
                                      else if (rev.frequency === 'quarterly') projMonthlyRent += (amount * occupancy) / 3;
                                      else if (rev.frequency === 'annually') projMonthlyRent += (amount * occupancy) / 12;
                                    });
                                    let projMonthlyCharges = 0;
                                    projRecurrentFees.forEach(fee => {
                                      const amount = parseFloat(fee.amount) || 0;
                                      if (fee.frequency === 'monthly') projMonthlyCharges += amount;
                                      else if (fee.frequency === 'quarterly') projMonthlyCharges += amount / 3;
                                      else if (fee.frequency === 'annually') projMonthlyCharges += amount / 12;
                                    });
                                    const projMonthlyPayment = project.creditAmortization?.monthlyPayment || 0;
                                    const projCashFlow = projMonthlyRent - projMonthlyCharges - projMonthlyPayment;

                                    return (
                                      <tr key={project.key} style={{ borderBottom: index < portfolioProjectsList.length - 1 ? '1px solid rgba(58, 58, 58, 0.2)' : 'none' }}>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85em', color: '#ffffff' }}>{project.key}</td>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85em', color: '#4ade80', textAlign: 'right' }}>+{formatNumber(projMonthlyRent, 0)} €</td>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85em', color: '#f87171', textAlign: 'right' }}>-{formatNumber(projMonthlyCharges, 0)} €</td>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85em', color: '#a78bfa', textAlign: 'right' }}>-{formatNumber(projMonthlyPayment, 0)} €</td>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85em', color: projCashFlow >= 0 ? '#4ade80' : '#f87171', textAlign: 'right', fontWeight: '600' }}>{projCashFlow >= 0 ? '+' : ''}{formatNumber(projCashFlow, 0)} €</td>
                                      </tr>
                                    );
                                  })}
                                  {/* Total Row */}
                                  <tr style={{ background: 'rgba(255, 255, 255, 0.03)', fontWeight: '600' }}>
                                    <td style={{ padding: '12px 16px', fontSize: '0.85em', color: '#ffffff' }}>TOTAL</td>
                                    <td style={{ padding: '12px 16px', fontSize: '0.85em', color: '#4ade80', textAlign: 'right' }}>+{formatNumber(totalMonthlyRent, 0)} €</td>
                                    <td style={{ padding: '12px 16px', fontSize: '0.85em', color: '#f87171', textAlign: 'right' }}>-{formatNumber(totalMonthlyCharges, 0)} €</td>
                                    <td style={{ padding: '12px 16px', fontSize: '0.85em', color: '#a78bfa', textAlign: 'right' }}>-{formatNumber(totalMonthlyLoanPayment, 0)} €</td>
                                    <td style={{ padding: '12px 16px', fontSize: '0.85em', color: totalMonthlyCashFlow >= 0 ? '#4ade80' : '#f87171', textAlign: 'right' }}>{totalMonthlyCashFlow >= 0 ? '+' : ''}{formatNumber(totalMonthlyCashFlow, 0)} €</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Aggregated Cash Flow by Date - Detailed Table */}
                          <div style={{ marginTop: '32px' }}>
                            {(() => {
                              // Build aggregated cash flow with detailed breakdown
                              const periodData = {};
                              const chargeTypes = new Map(); // Track all unique charge types across projects

                              // Helper to get period key based on view mode
                              const getPeriodKey = (date, monthIndex) => {
                                const year = date.getFullYear();
                                const month = date.getMonth();
                                if (portfolioCashFlowView === 'monthly') {
                                  return `${year}-${String(month + 1).padStart(2, '0')}`;
                                } else if (portfolioCashFlowView === 'quarterly') {
                                  const quarter = Math.floor(month / 3) + 1;
                                  return `${year}-Q${quarter}`;
                                } else {
                                  return `${year}`;
                                }
                              };

                              // Helper to format period label
                              const formatPeriodLabel = (key) => {
                                if (portfolioCashFlowView === 'monthly') {
                                  const [year, month] = key.split('-');
                                  const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
                                  return `${monthNames[parseInt(month) - 1]} ${year}`;
                                } else if (portfolioCashFlowView === 'quarterly') {
                                  return key.replace('-', ' ');
                                }
                                return key;
                              };

                              // First pass: collect all charge types
                              portfolioProjectsList.forEach(project => {
                                const projRecurrentFees = project.recurrentFees || [];
                                projRecurrentFees.forEach(fee => {
                                  if (!chargeTypes.has(fee.name)) {
                                    chargeTypes.set(fee.name, fee.name);
                                  }
                                });
                              });

                              // Second pass: build period data
                              portfolioProjectsList.forEach(project => {
                                const startDateStr = getPortfolioStartDate(project.key);
                                if (!startDateStr) return;

                                const startDate = new Date(startDateStr);
                                const creditParams = project.creditParams || {};
                                const loanDurationMonths = (creditParams.duree || 20) * 12;
                                const horizonMonths = project.performanceParams?.investmentHorizonMonths || 120;
                                const maxMonths = Math.max(loanDurationMonths, horizonMonths);

                                // Get apport (initial contribution)
                                const projFinancement = project.financementData || {};
                                const projApport = projFinancement.apport || 0;

                                // Store base property revenues for indexation calculation
                                const projPropertyRevenues = project.propertyRevenues || [];

                                // Calculate monthly charges by type
                                const projRecurrentFees = project.recurrentFees || [];
                                const monthlyChargesByType = {};
                                chargeTypes.forEach((_, key) => { monthlyChargesByType[key] = 0; });

                                projRecurrentFees.forEach(fee => {
                                  const amount = parseFloat(fee.amount) || 0;
                                  let monthlyAmount = 0;
                                  if (fee.frequency === 'monthly') monthlyAmount = amount;
                                  else if (fee.frequency === 'quarterly') monthlyAmount = amount / 3;
                                  else if (fee.frequency === 'annually') monthlyAmount = amount / 12;
                                  monthlyChargesByType[fee.name] = (monthlyChargesByType[fee.name] || 0) + monthlyAmount;
                                });

                                // Get credit amortization details
                                const amortization = project.creditAmortization?.schedule || [];
                                const totalCreditAmount = project.creditAmortization?.totalAmount || 0;

                                // Property value calculation
                                const projAcquisition = project.acquisitionData || {};
                                const initialPropertyValue = projAcquisition.prixAchat || 0;

                                // Add data for each month (month 0 = apport only, then months 1+ have cash flows)
                                for (let i = 0; i <= Math.min(maxMonths, 360); i++) {
                                  const currentDate = new Date(startDate);
                                  currentDate.setMonth(currentDate.getMonth() + i);
                                  const periodKey = getPeriodKey(currentDate, i);

                                  if (!periodData[periodKey]) {
                                    periodData[periodKey] = {
                                      apport: 0,
                                      rent: 0,
                                      principal: 0,
                                      interest: 0,
                                      creditTotal: 0,
                                      charges: {},
                                      chargesTotal: 0,
                                      cashFlow: 0,
                                      cashFlowExApport: 0,
                                      propertyValue: 0,
                                      capitalGain: 0,
                                      remainingDebt: 0,
                                      months: 0
                                    };
                                    chargeTypes.forEach((_, key) => { periodData[periodKey].charges[key] = 0; });
                                  }

                                  // Month 0: Only apport (initial investment)
                                  if (i === 0) {
                                    periodData[periodKey].apport += projApport;
                                    periodData[periodKey].cashFlow -= projApport;
                                    periodData[periodKey].propertyValue += initialPropertyValue;
                                    periodData[periodKey].remainingDebt += totalCreditAmount;
                                    periodData[periodKey].months += 1;
                                    continue; // Skip to next month - no rent, charges, or credit in month 0
                                  }

                                  // Month 1+: Regular cash flows
                                  const monthIndex = i - 1; // Amortization index (0-based from month 1)

                                  // Calculate rent with indexation
                                  // Use monthIndex (not i) for indexation to match the graph logic
                                  const yearOfMonth = monthIndex / 12; // Calculate year for indexation
                                  let projMonthlyRent = 0;
                                  projPropertyRevenues.forEach(rev => {
                                    const amount = parseFloat(rev.amount) || 0;
                                    const occupancy = (parseFloat(rev.occupancyRate) || 100) / 100;
                                    const indexation = parseFloat(rev.indexation) || 0;
                                    const startLagMonths = parseFloat(rev.startLagMonths) || 0;

                                    // Only count revenue if we're past the start lag
                                    // Use monthIndex (not i) to match graph logic
                                    if (monthIndex >= startLagMonths) {
                                      // Apply indexation: amount * (1 + indexation%)^years
                                      const indexedAmount = amount * Math.pow(1 + indexation / 100, yearOfMonth);

                                      if (rev.frequency === 'monthly') projMonthlyRent += indexedAmount * occupancy;
                                      else if (rev.frequency === 'quarterly') projMonthlyRent += (indexedAmount * occupancy) / 3;
                                      else if (rev.frequency === 'annually') projMonthlyRent += (indexedAmount * occupancy) / 12;
                                    }
                                  });

                                  // Add rent
                                  periodData[periodKey].rent += projMonthlyRent;

                                  // Add credit details if within loan duration
                                  if (monthIndex < loanDurationMonths && amortization[monthIndex]) {
                                    const principalAmount = amortization[monthIndex].principal || 0;
                                    const interestAmount = amortization[monthIndex].interest || 0;
                                    periodData[periodKey].principal += principalAmount;
                                    periodData[periodKey].interest += interestAmount;
                                    periodData[periodKey].creditTotal += principalAmount + interestAmount;
                                  }

                                  // Add charges by type
                                  let totalChargesThisMonth = 0;
                                  Object.keys(monthlyChargesByType).forEach(chargeType => {
                                    periodData[periodKey].charges[chargeType] += monthlyChargesByType[chargeType];
                                    totalChargesThisMonth += monthlyChargesByType[chargeType];
                                  });
                                  periodData[periodKey].chargesTotal += totalChargesThisMonth;

                                  // Calculate cash flow
                                  const creditThisMonth = (monthIndex < loanDurationMonths && amortization[monthIndex])
                                    ? (amortization[monthIndex].principal || 0) + (amortization[monthIndex].interest || 0)
                                    : 0;
                                  periodData[periodKey].cashFlow += projMonthlyRent - totalChargesThisMonth - creditThisMonth;
                                  periodData[periodKey].cashFlowExApport += projMonthlyRent - totalChargesThisMonth - creditThisMonth;
                                  periodData[periodKey].months += 1;

                                  // Calculate property value with appreciation (at end of period)
                                  const annualAppreciation = (project.performanceParams?.annualAppreciation || 2) / 100;
                                  const yearsElapsed = i / 12; // i months since investment start
                                  const currentPropertyValue = initialPropertyValue * Math.pow(1 + annualAppreciation, yearsElapsed);

                                  // Track remaining debt
                                  const remainingDebt = amortization[monthIndex]?.remainingBalance || 0;

                                  // Capital gain = current value - purchase price
                                  const capitalGain = currentPropertyValue - initialPropertyValue;

                                  // Only add property values at last month of each period
                                  const nextPeriodKey = i + 1 <= maxMonths ? (() => {
                                    const nextDate = new Date(startDate);
                                    nextDate.setMonth(nextDate.getMonth() + i + 1);
                                    return getPeriodKey(nextDate, i + 1);
                                  })() : null;

                                  if (!nextPeriodKey || nextPeriodKey !== periodKey) {
                                    // This is the last month in this period, store cumulative values
                                    periodData[periodKey].propertyValue += currentPropertyValue;
                                    periodData[periodKey].capitalGain += capitalGain;
                                    periodData[periodKey].remainingDebt += remainingDebt;
                                  }
                                }
                              });

                              // Sort periods
                              const sortedPeriods = Object.keys(periodData).sort((a, b) => {
                                // Parse keys for proper sorting
                                if (portfolioCashFlowView === 'monthly') {
                                  return a.localeCompare(b);
                                } else if (portfolioCashFlowView === 'quarterly') {
                                  const [yearA, qA] = a.split('-Q');
                                  const [yearB, qB] = b.split('-Q');
                                  return yearA !== yearB ? parseInt(yearA) - parseInt(yearB) : parseInt(qA) - parseInt(qB);
                                }
                                return parseInt(a) - parseInt(b);
                              });
                              const numPeriods = sortedPeriods.length;

                              // Calculate totals
                              const totals = {
                                apport: 0, rent: 0, principal: 0, interest: 0, creditTotal: 0, chargesTotal: 0,
                                cashFlow: 0, cashFlowExApport: 0, charges: {}
                              };
                              chargeTypes.forEach((_, key) => { totals.charges[key] = 0; });

                              sortedPeriods.forEach(period => {
                                totals.apport += periodData[period].apport;
                                totals.rent += periodData[period].rent;
                                totals.principal += periodData[period].principal;
                                totals.interest += periodData[period].interest;
                                totals.creditTotal += periodData[period].creditTotal;
                                totals.chargesTotal += periodData[period].chargesTotal;
                                totals.cashFlow += periodData[period].cashFlow;
                                totals.cashFlowExApport += periodData[period].cashFlowExApport;
                                Object.keys(periodData[period].charges).forEach(key => {
                                  totals.charges[key] += periodData[period].charges[key];
                                });

                                // Solde = remaining debt to be reimbursed
                                periodData[period].solde = periodData[period].remainingDebt;

                                // Difference = Property Value - Remaining Debt
                                periodData[period].difference = periodData[period].propertyValue - periodData[period].remainingDebt;
                              });

                              // Get last period values for totals
                              const lastPeriod = sortedPeriods[sortedPeriods.length - 1];
                              totals.propertyValue = lastPeriod ? periodData[lastPeriod].propertyValue : 0;
                              totals.capitalGain = lastPeriod ? periodData[lastPeriod].capitalGain : 0;
                              totals.remainingDebt = lastPeriod ? periodData[lastPeriod].remainingDebt : 0;
                              totals.solde = lastPeriod ? periodData[lastPeriod].solde : 0;
                              totals.difference = totals.propertyValue - totals.remainingDebt;

                              // Charge type labels
                              const chargeLabels = {
                                'propertyTax': 'Taxe Foncière',
                                'coOwnershipFees': 'Charges de Copropriété',
                                'insurance': 'Assurance',
                                'maintenance': 'Entretien',
                                'managementFees': 'Frais de Gestion',
                                'other': 'Autres'
                              };

                              const cellStyle = { padding: '10px', textAlign: 'right', borderBottom: '1px solid #2a2a2a' };
                              const headerCellStyle = { ...cellStyle, color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600' };
                              const labelCellStyle = { ...cellStyle, textAlign: 'left', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600' };
                              const totalLabelStyle = { ...labelCellStyle, fontWeight: '700' };
                              const greenStyle = { color: '#4ade80' };
                              const redStyle = { color: '#f87171' };
                              const purpleStyle = { color: '#f87171' };
                              const grayStyle = { color: 'rgba(209, 216, 224, 0.9)' };

                              if (numPeriods === 0) {
                                return (
                                  <div style={{ padding: '32px', textAlign: 'center', color: 'rgba(156, 163, 175, 0.6)', background: 'rgba(255, 255, 255, 0.02)', borderRadius: '12px', border: '1px solid rgba(58, 58, 58, 0.3)' }}>
                                    Aucune donnée disponible. Assurez-vous que les projets ont une date de début définie.
                                  </div>
                                );
                              }

                              return (
                                <div style={{
                                  background: 'rgba(255, 255, 255, 0.02)',
                                  borderRadius: '12px',
                                  border: '1px solid rgba(58, 58, 58, 0.3)',
                                  overflow: 'hidden'
                                }}>
                                  {/* Header */}
                                  <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(58, 58, 58, 0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3 style={{ fontSize: '1em', fontWeight: '600', color: '#ffffff', margin: 0 }}>Résumé du Cash-Flow Agrégé</h3>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                      {/* View Toggle Buttons */}
                                      <div style={{ display: 'flex', gap: '4px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '8px', padding: '4px' }}>
                                        <button
                                          onClick={() => setPortfolioCashFlowView('monthly')}
                                          style={{
                                            padding: '8px 16px',
                                            fontSize: '0.8em',
                                            fontWeight: '500',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            background: portfolioCashFlowView === 'monthly' ? '#10b981' : 'transparent',
                                            color: portfolioCashFlowView === 'monthly' ? '#ffffff' : 'rgba(156, 163, 175, 0.8)'
                                          }}
                                        >
                                          Mensuel
                                        </button>
                                        <button
                                          onClick={() => setPortfolioCashFlowView('quarterly')}
                                          style={{
                                            padding: '8px 16px',
                                            fontSize: '0.8em',
                                            fontWeight: '500',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            background: portfolioCashFlowView === 'quarterly' ? '#10b981' : 'transparent',
                                            color: portfolioCashFlowView === 'quarterly' ? '#ffffff' : 'rgba(156, 163, 175, 0.8)'
                                          }}
                                        >
                                          Trimestriel
                                        </button>
                                        <button
                                          onClick={() => setPortfolioCashFlowView('annual')}
                                          style={{
                                            padding: '8px 16px',
                                            fontSize: '0.8em',
                                            fontWeight: '500',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            background: portfolioCashFlowView === 'annual' ? '#10b981' : 'transparent',
                                            color: portfolioCashFlowView === 'annual' ? '#ffffff' : 'rgba(156, 163, 175, 0.8)'
                                          }}
                                        >
                                          Annuel
                                        </button>
                                      </div>
                                      {/* Excel Export Button */}
                                      <button
                                        onClick={() => {
                                          // Build export data
                                          const exportData = [];
                                          const headers = ['Metric', ...sortedPeriods.map(p => formatPeriodLabel(p)), 'Total'];
                                          exportData.push(headers);

                                          exportData.push(['Fonds Apportés', ...sortedPeriods.map(p => periodData[p].apport), totals.apport]);
                                          exportData.push(['Loyer', ...sortedPeriods.map(p => periodData[p].rent), totals.rent]);
                                          exportData.push(['Total Revenus', ...sortedPeriods.map(p => periodData[p].rent), totals.rent]);
                                          exportData.push(['Principal', ...sortedPeriods.map(p => periodData[p].principal), totals.principal]);
                                          exportData.push(['Intérêts', ...sortedPeriods.map(p => periodData[p].interest), totals.interest]);
                                          exportData.push(['Total Crédit', ...sortedPeriods.map(p => periodData[p].creditTotal), totals.creditTotal]);

                                          Array.from(chargeTypes.keys()).forEach(chargeType => {
                                            const label = chargeLabels[chargeType] || chargeType;
                                            exportData.push([label, ...sortedPeriods.map(p => periodData[p].charges[chargeType] || 0), totals.charges[chargeType] || 0]);
                                          });

                                          exportData.push(['Total Charges', ...sortedPeriods.map(p => periodData[p].chargesTotal), totals.chargesTotal]);
                                          exportData.push(['Cash-Flow Total', ...sortedPeriods.map(p => periodData[p].cashFlow), totals.cashFlow]);
                                          exportData.push(['Cash-Flow Total (hors Apport)', ...sortedPeriods.map(p => periodData[p].cashFlowExApport), totals.cashFlowExApport]);
                                          exportData.push(['Solde', ...sortedPeriods.map(p => periodData[p].solde), totals.solde]);
                                          exportData.push(['Valeur du Bien', ...sortedPeriods.map(p => periodData[p].propertyValue), totals.propertyValue]);
                                          exportData.push(['Différence', ...sortedPeriods.map(p => periodData[p].difference), totals.difference]);

                                          // Create worksheet
                                          const XLSX = window.XLSX;
                                          if (XLSX) {
                                            const ws = XLSX.utils.aoa_to_sheet(exportData);
                                            const wb = XLSX.utils.book_new();
                                            XLSX.utils.book_append_sheet(wb, ws, 'Cash-Flow Agrégé');
                                            XLSX.writeFile(wb, 'portfolio_cashflow.xlsx');
                                          } else {
                                            alert('XLSX library not loaded');
                                          }
                                        }}
                                        style={{
                                          padding: '8px 16px',
                                          background: 'rgba(59, 130, 246, 0.2)',
                                          border: '1px solid rgba(59, 130, 246, 0.4)',
                                          borderRadius: '6px',
                                          color: '#60a5fa',
                                          cursor: 'pointer',
                                          fontSize: '0.85em',
                                          fontWeight: '600',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                          transition: 'all 0.2s ease'
                                        }}
                                        title="Télécharger le tableau en Excel"
                                      >
                                        .xlsx
                                      </button>
                                    </div>
                                  </div>

                                  <div style={{ overflowX: 'auto', maxHeight: '600px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                                      <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                                        <tr style={{ borderBottom: '2px solid #2a2a2a' }}>
                                          <th style={{ ...headerCellStyle, textAlign: 'left', position: 'sticky', left: 0, background: '#1a1a1a', zIndex: 4, minWidth: '180px' }}>Metric</th>
                                          {sortedPeriods.map((period) => (
                                            <th key={period} style={{ ...headerCellStyle, minWidth: portfolioCashFlowView === 'monthly' ? '80px' : '90px', background: '#1a1a1a' }}>
                                              {formatPeriodLabel(period)}
                                            </th>
                                          ))}
                                          <th style={{ ...headerCellStyle, minWidth: '100px', fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)', background: '#1a1a1a' }}>Total</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {/* Fonds Apportés (Neutral) */}
                                        <tr style={{ borderBottom: '1px solid #2a2a2a', background: '#1e1e1e' }}>
                                          <td style={{ ...labelCellStyle, position: 'sticky', left: 0, background: '#1e1e1e', zIndex: 1 }}>Fonds Apportés</td>
                                          {sortedPeriods.map(period => (
                                            <td key={period} style={{ ...cellStyle, color: 'rgba(209, 216, 224, 0.9)' }}>{formatNumber(periodData[period].apport, 0)}</td>
                                          ))}
                                          <td style={{ ...cellStyle, color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)' }}>{formatNumber(totals.apport, 0)}</td>
                                        </tr>

                                        {/* Loyer (Green) */}
                                        <tr style={{ borderBottom: '1px solid #2a2a2a', background: '#1a2e1a' }}>
                                          <td style={{ ...labelCellStyle, position: 'sticky', left: 0, background: '#1a2e1a', zIndex: 1, ...greenStyle }}>Loyer Mensuel</td>
                                          {sortedPeriods.map(period => (
                                            <td key={period} style={{ ...cellStyle, color: 'rgba(209, 216, 224, 0.9)' }}>{formatNumber(periodData[period].rent, 0)}</td>
                                          ))}
                                          <td style={{ ...cellStyle, color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)' }}>{formatNumber(totals.rent, 0)}</td>
                                        </tr>

                                        {/* Total Revenus (Green Subtotal) */}
                                        <tr style={{ borderBottom: '2px solid #4ade80', background: '#1f3d1f' }}>
                                          <td style={{ ...totalLabelStyle, position: 'sticky', left: 0, background: '#1f3d1f', zIndex: 1, ...greenStyle }}>Total Revenus</td>
                                          {sortedPeriods.map(period => (
                                            <td key={period} style={{ ...cellStyle, ...greenStyle, fontWeight: '700' }}>{formatNumber(periodData[period].rent, 0)}</td>
                                          ))}
                                          <td style={{ ...cellStyle, ...greenStyle, fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)' }}>{formatNumber(totals.rent, 0)}</td>
                                        </tr>

                                        {/* Principal (Red section - Loan) */}
                                        <tr style={{ borderBottom: '1px solid #2a2a2a', background: '#261a1a' }}>
                                          <td style={{ ...labelCellStyle, position: 'sticky', left: 0, background: '#261a1a', zIndex: 1, ...redStyle }}>Principal</td>
                                          {sortedPeriods.map(period => (
                                            <td key={period} style={{ ...cellStyle, color: 'rgba(209, 216, 224, 0.9)' }}>{formatNumber(periodData[period].principal, 0)}</td>
                                          ))}
                                          <td style={{ ...cellStyle, color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)' }}>{formatNumber(totals.principal, 0)}</td>
                                        </tr>

                                        {/* Interest (Red section - Loan) */}
                                        <tr style={{ borderBottom: '1px solid #2a2a2a', background: '#261a1a' }}>
                                          <td style={{ ...labelCellStyle, position: 'sticky', left: 0, background: '#261a1a', zIndex: 1, ...redStyle }}>Intérêts</td>
                                          {sortedPeriods.map(period => (
                                            <td key={period} style={{ ...cellStyle, color: 'rgba(209, 216, 224, 0.9)' }}>{formatNumber(periodData[period].interest, 0)}</td>
                                          ))}
                                          <td style={{ ...cellStyle, color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)' }}>{formatNumber(totals.interest, 0)}</td>
                                        </tr>

                                        {/* Crédit Total (Red Subtotal) */}
                                        <tr style={{ borderBottom: '1px solid #f87171', background: '#2d1f1f' }}>
                                          <td style={{ ...totalLabelStyle, position: 'sticky', left: 0, background: '#2d1f1f', zIndex: 1, ...redStyle }}>Total Crédit</td>
                                          {sortedPeriods.map(period => (
                                            <td key={period} style={{ ...cellStyle, ...redStyle, fontWeight: '700' }}>{formatNumber(periodData[period].creditTotal, 0)}</td>
                                          ))}
                                          <td style={{ ...cellStyle, ...redStyle, fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)' }}>{formatNumber(totals.creditTotal, 0)}</td>
                                        </tr>

                                        {/* Individual Charges (Red section - Fees) */}
                                        {Array.from(chargeTypes.keys()).map(chargeType => (
                                          <tr key={chargeType} style={{ borderBottom: '1px solid #2a2a2a', background: '#261a1a' }}>
                                            <td style={{ ...labelCellStyle, position: 'sticky', left: 0, background: '#261a1a', zIndex: 1, ...redStyle }}>
                                              {chargeLabels[chargeType] || chargeType}
                                            </td>
                                            {sortedPeriods.map(period => (
                                              <td key={period} style={{ ...cellStyle, color: 'rgba(209, 216, 224, 0.9)' }}>{formatNumber(periodData[period].charges[chargeType] || 0, 0)}</td>
                                            ))}
                                            <td style={{ ...cellStyle, color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)' }}>{formatNumber(totals.charges[chargeType] || 0, 0)}</td>
                                          </tr>
                                        ))}

                                        {/* Total Charges (Red Subtotal) */}
                                        <tr style={{ borderBottom: '2px solid #f87171', background: '#2d1f1f' }}>
                                          <td style={{ ...totalLabelStyle, position: 'sticky', left: 0, background: '#2d1f1f', zIndex: 1, ...redStyle }}>Total Charges</td>
                                          {sortedPeriods.map(period => (
                                            <td key={period} style={{ ...cellStyle, ...redStyle, fontWeight: '700' }}>{formatNumber(periodData[period].chargesTotal, 0)}</td>
                                          ))}
                                          <td style={{ ...cellStyle, ...redStyle, fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)' }}>{formatNumber(totals.chargesTotal, 0)}</td>
                                        </tr>

                                        {/* Cash Flow Net (Grand Total) */}
                                        <tr style={{ borderTop: '3px solid rgba(209, 216, 224, 0.3)', borderBottom: '3px solid rgba(209, 216, 224, 0.3)', background: '#252525' }}>
                                          <td style={{
                                            ...totalLabelStyle,
                                            position: 'sticky',
                                            left: 0,
                                            background: '#252525',
                                            zIndex: 1,
                                            color: 'rgba(209, 216, 224, 0.95)',
                                            fontSize: '1.05em'
                                          }}>Cash-Flow Total</td>
                                          {sortedPeriods.map(period => {
                                            const cf = periodData[period].cashFlow;
                                            return (
                                              <td key={period} style={{
                                                ...cellStyle,
                                                fontWeight: '700',
                                                fontSize: '1.05em',
                                                color: cf >= 0 ? '#4ade80' : '#f87171'
                                              }}>
                                                {formatNumber(cf, 0)}
                                              </td>
                                            );
                                          })}
                                          <td style={{
                                            ...cellStyle,
                                            fontWeight: '700',
                                            fontSize: '1.05em',
                                            borderLeft: '2px solid rgba(209, 216, 224, 0.3)',
                                            color: totals.cashFlow >= 0 ? '#4ade80' : '#f87171'
                                          }}>
                                            {formatNumber(totals.cashFlow, 0)}
                                          </td>
                                        </tr>

                                        {/* Cash Flow hors Apport */}
                                        <tr style={{ borderBottom: '1px solid #2a2a2a', background: '#202020' }}>
                                          <td style={{
                                            ...labelCellStyle,
                                            position: 'sticky',
                                            left: 0,
                                            background: '#202020',
                                            zIndex: 1,
                                            color: 'rgba(209, 216, 224, 0.8)',
                                            fontStyle: 'italic'
                                          }}>Cash-Flow Total (hors Apport)</td>
                                          {sortedPeriods.map(period => {
                                            const cfEx = periodData[period].cashFlowExApport;
                                            return (
                                              <td key={period} style={{
                                                ...cellStyle,
                                                fontStyle: 'italic',
                                                color: cfEx >= 0 ? 'rgba(74, 222, 128, 0.8)' : 'rgba(248, 113, 113, 0.8)'
                                              }}>
                                                {formatNumber(cfEx, 0)}
                                              </td>
                                            );
                                          })}
                                          <td style={{
                                            ...cellStyle,
                                            fontWeight: '700',
                                            fontStyle: 'italic',
                                            borderLeft: '2px solid rgba(209, 216, 224, 0.3)',
                                            color: totals.cashFlowExApport >= 0 ? 'rgba(74, 222, 128, 0.8)' : 'rgba(248, 113, 113, 0.8)'
                                          }}>
                                            {formatNumber(totals.cashFlowExApport, 0)}
                                          </td>
                                        </tr>

                                        {/* Solde (Balance - Neutral color) */}
                                        <tr style={{ borderBottom: '1px solid #2a2a2a', background: '#1e1e1e' }}>
                                          <td style={{
                                            ...labelCellStyle,
                                            position: 'sticky',
                                            left: 0,
                                            background: '#1e1e1e',
                                            zIndex: 1
                                          }}>Solde</td>
                                          {sortedPeriods.map(period => {
                                            const solde = periodData[period].solde;
                                            return (
                                              <td key={period} style={{
                                                ...cellStyle,
                                                fontWeight: '600',
                                                color: 'rgba(209, 216, 224, 0.9)'
                                              }}>
                                                {formatNumber(solde, 0)}
                                              </td>
                                            );
                                          })}
                                          <td style={{
                                            ...cellStyle,
                                            fontWeight: '700',
                                            borderLeft: '2px solid rgba(209, 216, 224, 0.3)',
                                            color: 'rgba(209, 216, 224, 0.9)'
                                          }}>
                                            {formatNumber(totals.solde, 0)}
                                          </td>
                                        </tr>

                                        {/* Valeur du Bien */}
                                        <tr style={{ borderBottom: '1px solid #2a2a2a', background: '#1e1e1e' }}>
                                          <td style={{
                                            ...labelCellStyle,
                                            position: 'sticky',
                                            left: 0,
                                            background: '#1e1e1e',
                                            zIndex: 1
                                          }}>Valeur du Bien</td>
                                          {sortedPeriods.map(period => (
                                            <td key={period} style={{
                                              ...cellStyle,
                                              fontWeight: '600',
                                              color: 'rgba(209, 216, 224, 0.9)'
                                            }}>
                                              {formatNumber(periodData[period].propertyValue, 0)}
                                            </td>
                                          ))}
                                          <td style={{
                                            ...cellStyle,
                                            fontWeight: '700',
                                            borderLeft: '2px solid rgba(209, 216, 224, 0.3)',
                                            color: 'rgba(209, 216, 224, 0.9)'
                                          }}>
                                            {formatNumber(totals.propertyValue, 0)}
                                          </td>
                                        </tr>

                                        {/* Différence (Property Value - Remaining Debt) - Green */}
                                        <tr style={{ borderBottom: '1px solid #2a2a2a', background: '#1a2e1a' }}>
                                          <td style={{
                                            ...labelCellStyle,
                                            position: 'sticky',
                                            left: 0,
                                            background: '#1a2e1a',
                                            zIndex: 1,
                                            color: '#4ade80'
                                          }}>Différence</td>
                                          {sortedPeriods.map(period => {
                                            const diff = periodData[period].difference;
                                            return (
                                              <td key={period} style={{
                                                ...cellStyle,
                                                fontWeight: '600',
                                                color: '#4ade80'
                                              }}>
                                                {formatNumber(diff, 0)}
                                              </td>
                                            );
                                          })}
                                          <td style={{
                                            ...cellStyle,
                                            fontWeight: '700',
                                            borderLeft: '2px solid rgba(209, 216, 224, 0.3)',
                                            color: '#4ade80'
                                          }}>
                                            {formatNumber(totals.difference, 0)}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Project Detail View - shown when a project is selected */}
            {currentProject && (
              <div>
                {/* Unsaved Changes Popup */}
                {showUnsavedChangesPopup && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000
                  }}>
                    <div style={{
                      background: '#1a1a1a',
                      borderRadius: '12px',
                      padding: '24px 32px',
                      border: '1px solid rgba(209, 178, 135, 0.3)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                      maxWidth: '500px',
                      width: '90%'
                    }}>
                      <div style={{
                        fontSize: '1.1em',
                        fontWeight: '700',
                        color: '#ffffff',
                        marginBottom: '16px'
                      }}>
                        Modifications non sauvegardées
                      </div>
                      <div style={{
                        fontSize: '0.9em',
                        color: 'rgba(156, 163, 175, 0.9)',
                        marginBottom: '24px',
                        lineHeight: '1.5'
                      }}>
                        Vous avez des modifications non sauvegardées. Voulez-vous les sauvegarder avant de quitter ?
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap'
                      }}>
                        <button
                          onClick={() => {
                            setShowUnsavedChangesPopup(false);
                            setHasUnsavedChanges(false);
                            setOriginalProjectData(null);
                            setCurrentProject(null);
                          }}
                          style={{
                            padding: '8px 12px',
                            background: 'rgba(239, 68, 68, 0.2)',
                            border: '1px solid rgba(239, 68, 68, 0.4)',
                            borderRadius: '6px',
                            color: '#ef4444',
                            fontSize: '0.8em',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(239, 68, 68, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                          }}
                        >
                          Ne pas sauvegarder
                        </button>
                        <button
                          onClick={() => {
                            setShowUnsavedChangesPopup(false);
                          }}
                          style={{
                            padding: '8px 12px',
                            background: 'rgba(156, 163, 175, 0.1)',
                            border: '1px solid rgba(156, 163, 175, 0.3)',
                            borderRadius: '6px',
                            color: 'rgba(156, 163, 175, 0.9)',
                            fontSize: '0.8em',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(156, 163, 175, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'rgba(156, 163, 175, 0.1)';
                          }}
                        >
                          Annuler
                        </button>
                        <button
                          onClick={() => {
                            saveProject();
                            setShowUnsavedChangesPopup(false);
                            setCurrentProject(null);
                          }}
                          style={{
                            padding: '8px 12px',
                            background: 'rgba(16, 185, 129, 0.2)',
                            border: '1px solid rgba(16, 185, 129, 0.4)',
                            borderRadius: '6px',
                            color: '#10b981',
                            fontSize: '0.8em',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(16, 185, 129, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'rgba(16, 185, 129, 0.2)';
                          }}
                        >
                          Sauvegarder
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Top buttons row */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '24px'
                }}>
                  {/* Back button */}
                  <button
                    onClick={() => {
                      if (hasUnsavedChanges) {
                        setShowUnsavedChangesPopup(true);
                      } else {
                        setOriginalProjectData(null);
                        setCurrentProject(null);
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(58, 58, 58, 0.4)',
                      borderRadius: '6px',
                      color: 'rgba(156, 163, 175, 0.8)',
                      fontSize: '0.9em',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.color = 'rgba(209, 178, 135, 0.95)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.target.style.color = 'rgba(156, 163, 175, 0.8)';
                    }}
                  >
                    ← Retour aux projets
                  </button>

                  {/* Generate PDF button */}
                  <button
                    onClick={generatePDFReport}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(209, 178, 135, 0.15)',
                      border: '1px solid rgba(209, 178, 135, 0.4)',
                      borderRadius: '6px',
                      color: '#d1b287',
                      fontSize: '0.9em',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(209, 178, 135, 0.25)';
                      e.target.style.borderColor = 'rgba(209, 178, 135, 0.6)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(209, 178, 135, 0.15)';
                      e.target.style.borderColor = 'rgba(209, 178, 135, 0.4)';
                    }}
                  >
                    Générer PDF
                  </button>
                </div>

            {/* Main Layout: Left column (Performance + Bien Immobilier) and Right column (Endettement + Frais) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '20px', marginBottom: '40px' }}>

              {/* Left Column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

            {/* Performance Summary */}
            {(() => {
              // Calculate performance metrics using exact months (same logic as Performance tab)
              const totalMonths = performanceParams.investmentHorizonMonths;
              const yearsExact = totalMonths / 12;
              const totalInvestment = financementData.apport || 0;
              const monthlyPayment = (creditAmortization.monthlyPayment || 0) + (creditTravauxAmortization.monthlyPayment || 0);

              // Calculate monthly revenue helper
              const calculateMonthlyAmount = (amount, frequency) => {
                if (frequency === 'monthly') return amount;
                if (frequency === 'quarterly') return amount / 3;
                if (frequency === 'annually') return amount / 12;
                return 0;
              };

              // Build monthlyData array for exact calculations
              const monthlyData = Array.from({length: totalMonths + 1}, (_, monthIndex) => {
                if (monthIndex === 0) {
                  return { month: 0, netCashFlow: -totalInvestment };
                }

                const yearOfMonth = Math.floor((monthIndex - 1) / 12);
                const monthInYear = ((monthIndex - 1) % 12) + 1;

                // Monthly revenue with startLag and indexation
                const monthRevenue = propertyRevenues.reduce((sum, rev) => {
                  const startLag = rev.startLagMonths || 0;
                  if (monthIndex <= startLag) return sum;

                  const revAmount = parseFloat(rev.amount) || 0;
                  const occupancy = (parseFloat(rev.occupancyRate) || 100) / 100;
                  const freq = rev.frequency || 'monthly';
                  const indexation = parseFloat(rev.indexation) || 0;

                  let amount = 0;
                  if (freq === 'monthly') {
                    amount = revAmount * occupancy;
                  } else if (freq === 'quarterly' && monthInYear % 3 === 0) {
                    amount = revAmount * occupancy;
                  } else if (freq === 'annually' && monthInYear === 12) {
                    amount = revAmount * occupancy;
                  }
                  return sum + (amount * Math.pow(1 + indexation / 100, yearOfMonth));
                }, 0);

                // Monthly fees (with pro-rata for final month)
                const isLastMonth = monthIndex === totalMonths;
                const monthFees = recurrentFees.reduce((sum, fee) => {
                  const feeAmount = parseFloat(fee.amount) || 0;
                  const freq = fee.frequency || 'monthly';
                  let amount = 0;
                  if (freq === 'monthly') {
                    amount = feeAmount;
                  } else if (freq === 'quarterly') {
                    if (monthInYear % 3 === 0) {
                      amount = feeAmount;
                    } else if (isLastMonth) {
                      const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
                      amount = feeAmount * (monthsIntoQuarter / 3);
                    }
                  } else if (freq === 'annually') {
                    if (monthInYear === 12) {
                      amount = feeAmount;
                    } else if (isLastMonth) {
                      amount = feeAmount * (monthInYear / 12);
                    }
                  }
                  return sum + amount;
                }, 0);

                // Get actual credit payment from both schedules (principal + interest)
                const scheduleIndex = monthIndex - 1; // monthIndex is 1-based, schedule is 0-based
                const scheduleEntry = creditAmortization.schedule?.[scheduleIndex];
                const scheduleEntryTravaux = creditTravauxAmortization.schedule?.[scheduleIndex];
                const actualCreditPayment = (scheduleEntry ? (scheduleEntry.principal + scheduleEntry.interest) : 0)
                  + (scheduleEntryTravaux ? (scheduleEntryTravaux.principal + scheduleEntryTravaux.interest) : 0);

                const operationalCashFlow = monthRevenue - monthFees - actualCreditPayment;
                let netCashFlow = operationalCashFlow;

                // Add capital gain in last month
                if (monthIndex === totalMonths) {
                  const totalCreditAmount = (creditData.amount || 0) + (creditTravauxData.amount || 0);
                  const initialHomeValue = estimatedHomeValue || (totalInvestment + totalCreditAmount);
                  const finalHomeValue = initialHomeValue * Math.pow(1 + performanceParams.housePriceInflation / 100, yearsExact);
                  const scheduleLength = creditAmortization.schedule?.length || 0;
                  const scheduleLengthTravaux = creditTravauxAmortization.schedule?.length || 0;
                  const finalBalance = (scheduleLength > 0
                    ? creditAmortization.schedule[Math.min(totalMonths - 1, scheduleLength - 1)]?.remainingBalance || 0
                    : 0) + (scheduleLengthTravaux > 0
                    ? creditTravauxAmortization.schedule[Math.min(totalMonths - 1, scheduleLengthTravaux - 1)]?.remainingBalance || 0
                    : 0);
                  netCashFlow += (finalHomeValue - finalBalance);
                }

                return { month: monthIndex, netCashFlow, operationalCashFlow };
              });

              // Cash-Flow Mensuel Moyen: Sum of all operational Cash-Flow (except month 0) / number of months
              // Excludes capital gain from property sale
              const totalOperationalCashFlow = monthlyData
                .filter((data, idx) => idx > 0)
                .reduce((sum, data) => sum + data.operationalCashFlow, 0);
              const averageMonthlyCashFlow = totalMonths > 0 ? totalOperationalCashFlow / totalMonths : 0;

              // For monthly revenue/charges (used for netProfitability)
              const monthlyRevenue = propertyRevenues.reduce((sum, rev) =>
                sum + (calculateMonthlyAmount(rev.amount, rev.frequency) * (rev.occupancyRate / 100)), 0);
              const monthlyCharges = recurrentFees.reduce((sum, fee) =>
                sum + calculateMonthlyAmount(fee.amount, fee.frequency), 0);

              // Net profitability using exact months
              const totalRent = monthlyRevenue * totalMonths;
              const totalChargesSum = monthlyCharges * totalMonths;
              const totalCreditSum = monthlyPayment * totalMonths;
              const netProfitability = totalInvestment > 0 ? ((totalRent - totalChargesSum - totalCreditSum) / totalInvestment) * 100 : 0;

              // IRR calculation using exact months
              const totalCreditAmount = (creditData.amount || 0) + (creditTravauxData.amount || 0);
              const initialHomeValue = estimatedHomeValue || (totalInvestment + totalCreditAmount);
              const totalCashFlowsSum = monthlyData.reduce((sum, data) => sum + data.netCashFlow, 0);
              const scheduleLength = creditAmortization.schedule?.length || 0;
              const scheduleLengthTravaux = creditTravauxAmortization.schedule?.length || 0;
              const finalBalance = (scheduleLength > 0
                ? creditAmortization.schedule[Math.min(totalMonths - 1, scheduleLength - 1)]?.remainingBalance || 0
                : 0) + (scheduleLengthTravaux > 0
                ? creditTravauxAmortization.schedule[Math.min(totalMonths - 1, scheduleLengthTravaux - 1)]?.remainingBalance || 0
                : 0);
              const finalHomeValue = initialHomeValue * Math.pow(1 + performanceParams.housePriceInflation / 100, yearsExact);
              const irr = totalInvestment > 0 && yearsExact > 0
                ? ((totalCashFlowsSum + (finalHomeValue - finalBalance) - totalInvestment) / totalInvestment / yearsExact) * 100
                : 0;

              // Total Net Cash Flow
              const totalNetCashFlow = monthlyData.reduce((sum, data) => sum + data.netCashFlow, 0);

              return (
                <div style={{
                  background: 'rgba(58, 58, 58, 0.3)',
                  border: '1px solid rgba(58, 58, 58, 0.6)',
                  borderRadius: '12px',
                  padding: '20px'
                }}>
                  <div style={{
                    fontSize: '1.1em',
                    fontWeight: '600',
                    color: '#ffffff',
                    marginBottom: '16px',
                    borderBottom: '1px solid rgba(58, 58, 58, 0.6)',
                    paddingBottom: '10px'
                  }}>
                    Analyse Synthétique de Performance
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '16px'
                  }}>
                    {/* Cash-Flow Mensuel Moyen */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(58, 58, 58, 0.4)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '12px'}}>
                        Cash-Flow Mensuel Moyen
                      </div>
                      <div style={{fontSize: '1.5em', fontWeight: '700', color: averageMonthlyCashFlow >= 0 ? '#10b981' : '#ef4444'}}>
                        {formatNumber(averageMonthlyCashFlow, 1)} €
                      </div>
                    </div>

                    {/* Rentabilité Nette Prévisionnelle */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(58, 58, 58, 0.4)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '12px'}}>
                        Rentabilité Nette
                      </div>
                      <div style={{fontSize: '1.5em', fontWeight: '700', color: netProfitability >= 0 ? '#10b981' : '#ef4444'}}>
                        {formatNumber(netProfitability, 2)}%
                      </div>
                    </div>

                    {/* TRI (Taux de Rentabilité Interne) */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(58, 58, 58, 0.4)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '12px'}}>
                        TRI
                      </div>
                      <div style={{fontSize: '1.5em', fontWeight: '700', color: irr >= 5 ? '#10b981' : irr >= 2 ? '#f59e0b' : '#ef4444'}}>
                        {formatNumber(irr, 2)}%
                      </div>
                    </div>

                    {/* NCF (Net Cash Flow Total) */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(58, 58, 58, 0.4)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '12px'}}>
                        Cash-Flow Net Total
                      </div>
                      <div style={{fontSize: '1.5em', fontWeight: '700', color: totalNetCashFlow >= 0 ? '#10b981' : '#ef4444'}}>
                        {formatNumber(totalNetCashFlow, 0)} €
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Property Description Section */}
            {extractedData && Object.keys(extractedData).length > 0 && (
              <div style={{
                background: 'rgba(58, 58, 58, 0.3)',
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid rgba(58, 58, 58, 0.6)'
              }}>
                <div style={{
                  fontSize: '1.2em',
                  fontWeight: '700',
                  color: '#ffffff',
                  marginBottom: '20px',
                  borderBottom: '1px solid rgba(58, 58, 58, 0.6)',
                  paddingBottom: '12px'
                }}>
                  Bien Immobilier
                </div>

                <div style={{ display: 'flex', gap: '20px' }}>
                  {/* Property Image on the left */}
                  {extractedData.imageUrl && (
                    <div style={{ flexShrink: 0 }}>
                      <img
                        src={extractedData.imageUrl}
                        alt={extractedData.address || 'Property'}
                        style={{
                          width: '250px',
                          height: '180px',
                          borderRadius: '8px',
                          objectFit: 'cover',
                          border: '1px solid rgba(58, 58, 58, 0.4)'
                        }}
                        onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  {/* Info Grid */}
                  <div style={{
                    flex: 1,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '16px'
                  }}>
                  {/* Address */}
                  {extractedData.address && (
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(58, 58, 58, 0.4)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '12px'}}>
                        Adresse
                      </div>
                      <div style={{fontSize: '1em', fontWeight: '600', color: '#ffffff', lineHeight: '1.4'}}>
                        {extractedData.address}
                      </div>
                    </div>
                  )}

                  {/* Surface */}
                  {extractedData.surface && (
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(58, 58, 58, 0.4)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '12px'}}>
                        Surface
                      </div>
                      <div style={{fontSize: '1.5em', fontWeight: '700', color: '#60a5fa'}}>
                        {formatNumber(extractedData.surface, 0)} m²
                      </div>
                    </div>
                  )}

                  {/* Price per m² */}
                  {extractedData.pricePerSqm && (
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(58, 58, 58, 0.4)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '12px'}}>
                        Prix au m²
                      </div>
                      <div style={{fontSize: '1.5em', fontWeight: '700', color: '#fbbf24'}}>
                        {formatNumber(extractedData.pricePerSqm, 0)} €/m²
                      </div>
                    </div>
                  )}

                  {/* Energy Consumption */}
                  {extractedData.energyConsumption && (
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(58, 58, 58, 0.4)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '12px'}}>
                        Conso. Primaire
                      </div>
                      <div style={{fontSize: '1.5em', fontWeight: '700', color: '#4ade80'}}>
                        {formatNumber(extractedData.energyConsumption, 0)} kWh/m²
                      </div>
                    </div>
                  )}

                  {/* EPC Category (Label PEB/DPE) */}
                  {extractedData.epcCategory && (
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(58, 58, 58, 0.4)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '12px'}}>
                        Label PEB (DPE)
                      </div>
                      <div style={{fontSize: '2em', fontWeight: '700', color: '#f87171'}}>
                        {extractedData.epcCategory}
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              </div>
            )}

            {/* Analyse Synthétique d'Endettement */}
            {(() => {
              // Calculate status for each metric - only green or red
              const capaciteStatus = results && results.max_borrowing_capacity !== undefined && financementData.emprunt > 0
                ? (financementData.emprunt <= results.max_borrowing_capacity ? 'green' : 'red')
                : 'gray';

              const restantStatus = creditAmortization.monthlyPayment > 0
                ? ((totalRevenues + totalFutureRevenues - totalCharges - creditAmortization.monthlyPayment - totalFutureCharges) >= 800 ? 'green' : 'red')
                : 'gray';

              const ltvStatus = ltv > 0
                ? (Math.floor(ltv) <= 90 ? 'green' : 'red')
                : 'gray';

              const capaciteEmprunt = results?.max_borrowing_capacity || 0;
              const restantVivre = totalRevenues + totalFutureRevenues - totalCharges - creditAmortization.monthlyPayment - totalFutureCharges;

              return (
                <div style={{
                  background: 'rgba(58, 58, 58, 0.3)',
                  border: '1px solid rgba(58, 58, 58, 0.6)',
                  borderRadius: '12px',
                  padding: '20px'
                }}>
                  <div style={{
                    fontSize: '1.1em',
                    fontWeight: '600',
                    color: '#ffffff',
                    marginBottom: '16px',
                    borderBottom: '1px solid rgba(58, 58, 58, 0.6)',
                    paddingBottom: '10px'
                  }}>
                    Analyse Synthétique d'Endettement
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '16px'
                  }}>
                    {/* Capacité d'Emprunt */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(58, 58, 58, 0.4)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '12px'}}>
                        Capacité d'Emprunt
                      </div>
                      <div style={{fontSize: '1.5em', fontWeight: '700', color: capaciteStatus === 'green' ? '#10b981' : capaciteStatus === 'red' ? '#ef4444' : 'rgba(156, 163, 175, 0.5)'}}>
                        {capaciteEmprunt > 0 ? `${formatNumber(capaciteEmprunt, 0)} €` : '—'}
                      </div>
                    </div>

                    {/* Restant à Vivre */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(58, 58, 58, 0.4)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '12px'}}>
                        Restant à Vivre
                      </div>
                      <div style={{fontSize: '1.5em', fontWeight: '700', color: restantStatus === 'green' ? '#10b981' : restantStatus === 'red' ? '#ef4444' : 'rgba(156, 163, 175, 0.5)'}}>
                        {creditAmortization.monthlyPayment > 0 ? `${formatNumber(restantVivre, 0)} €` : '—'}
                      </div>
                    </div>

                    {/* LTV */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(58, 58, 58, 0.4)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '12px'}}>
                        LTV (Loan-to-Value)
                      </div>
                      <div style={{fontSize: '1.5em', fontWeight: '700', color: ltvStatus === 'green' ? '#10b981' : ltvStatus === 'red' ? '#ef4444' : 'rgba(156, 163, 175, 0.5)'}}>
                        {ltv > 0 ? `${formatNumber(ltv, 1)}%` : '—'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

              </div> {/* End Left Column */}

              {/* Right Column - Combined Endettement + Frais */}
              <div style={{
                background: 'rgba(58, 58, 58, 0.3)',
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid rgba(58, 58, 58, 0.6)',
                display: 'flex',
                flexDirection: 'column',
                gap: '32px',
                alignSelf: 'stretch'
              }}>

            {/* Section 1: Endettement */}
            <div>
              <div style={{
                fontSize: '1.1em',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '20px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(58, 58, 58, 0.6)'
              }}>
                <span>Endettement</span>
              </div>

              <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '48px'}}>

                {/* Situation Crédit */}
                <div>
                  <div style={{
                    fontSize: '0.9em',
                    fontWeight: '600',
                    color: 'rgba(156, 163, 175, 0.8)',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    Situation Crédit
                    <div style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      border: '1px solid rgba(156, 163, 175, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.65em',
                      color: 'rgba(156, 163, 175, 0.5)',
                      cursor: 'help'
                    }}>i</div>
                  </div>

                  <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px', borderBottom: '1px solid rgba(58, 58, 58, 0.3)'}}>
                      <span style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)'}}>Durée du Crédit</span>
                      <span style={{fontSize: '0.9em', fontWeight: '600', color: '#ffffff'}}>
                        {creditData.durationYears} ans
                      </span>
                    </div>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px', borderBottom: '1px solid rgba(58, 58, 58, 0.3)'}}>
                      <span style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)'}}>Taux d'Intérêt</span>
                      <span style={{fontSize: '0.9em', fontWeight: '600', color: '#ffffff'}}>
                        {creditData.interestRate}%
                      </span>
                    </div>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px', borderBottom: '1px solid rgba(58, 58, 58, 0.3)'}}>
                      <span style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)'}}>Type de Crédit</span>
                      <span style={{fontSize: '0.9em', fontWeight: '600', color: '#ffffff'}}>
                        {creditData.type === 'amortizing' ? 'Amortissable' :
                         creditData.type === 'bullet' ? 'In Fine' :
                         creditData.type === 'interestOnly' ? 'Intérêts Seuls' : 'Crescendo'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Financement */}
                <div>
                  <div style={{
                    fontSize: '0.9em',
                    fontWeight: '600',
                    color: 'rgba(156, 163, 175, 0.8)',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    Financement
                    <div style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      border: '1px solid rgba(156, 163, 175, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.65em',
                      color: 'rgba(156, 163, 175, 0.5)',
                      cursor: 'help'
                    }}>i</div>
                  </div>

                  <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px', borderBottom: '1px solid rgba(58, 58, 58, 0.3)'}}>
                      <span style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)'}}>Apport</span>
                      <span style={{fontSize: '0.9em', fontWeight: '600', color: '#ffffff'}}>
                        {formatNumber(financementData.apport)} €
                      </span>
                    </div>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px', borderBottom: '1px solid rgba(58, 58, 58, 0.3)'}}>
                      <span style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)'}}>Dette (Emprunt)</span>
                      <span style={{fontSize: '0.9em', fontWeight: '600', color: '#ffffff'}}>
                        {formatNumber(financementData.emprunt)} €
                      </span>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Section 2: Frais */}
            <div>
              <div style={{
                fontSize: '1.1em',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '20px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(58, 58, 58, 0.6)'
              }}>
                <span>Frais</span>
              </div>

              <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '48px'}}>

                {/* Acquisition */}
                <div>
                  <div style={{
                    fontSize: '0.9em',
                    fontWeight: '600',
                    color: 'rgba(156, 163, 175, 0.8)',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    Acquisition
                    <div style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      border: '1px solid rgba(156, 163, 175, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.65em',
                      color: 'rgba(156, 163, 175, 0.5)',
                      cursor: 'help'
                    }}>i</div>
                  </div>

                  <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px', borderBottom: '1px solid rgba(58, 58, 58, 0.3)'}}>
                      <span style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)'}}>Prix d'Achat</span>
                      <span style={{fontSize: '0.9em', fontWeight: '600', color: '#ffffff'}}>
                        {formatNumber(acquisitionData.prixAchat)} €
                      </span>
                    </div>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px', borderBottom: '1px solid rgba(58, 58, 58, 0.3)'}}>
                      <span style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)'}}>Frais Notaire</span>
                      <span style={{fontSize: '0.9em', fontWeight: '600', color: '#ffffff'}}>
                        {formatNumber(acquisitionData.fraisNotaire)} €
                      </span>
                    </div>
                    {acquisitionData.travaux > 0 && (
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px', borderBottom: '1px solid rgba(58, 58, 58, 0.3)'}}>
                        <span style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)'}}>Travaux</span>
                        <span style={{fontSize: '0.9em', fontWeight: '600', color: '#ffffff'}}>
                          {formatNumber(acquisitionData.travaux)} €
                        </span>
                      </div>
                    )}
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '4px'}}>
                      <span style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.9)', fontWeight: '600'}}>Coût Total</span>
                      <span style={{fontSize: '0.95em', fontWeight: '700', color: '#ffffff'}}>
                        {formatNumber(
                          acquisitionData.prixAchat +
                          acquisitionData.fraisNotaire +
                          acquisitionData.fraisAgence +
                          acquisitionData.travaux +
                          acquisitionData.autresFrais
                        )} €
                      </span>
                    </div>
                  </div>
                </div>

                {/* Charges Récurrentes */}
                <div>
                  <div style={{
                    fontSize: '0.9em',
                    fontWeight: '600',
                    color: 'rgba(156, 163, 175, 0.8)',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    Charges Récurrentes
                    <div style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      border: '1px solid rgba(156, 163, 175, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.65em',
                      color: 'rgba(156, 163, 175, 0.5)',
                      cursor: 'help'
                    }}>i</div>
                  </div>

                  <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                    {recurrentFees.length > 0 ? (
                      recurrentFees.slice(0, 4).map((fee, index) => {
                        const frequencySuffix = fee.frequency === 'monthly' ? '/m' : fee.frequency === 'quarterly' ? '/t' : '/a';

                        const feeNames = {
                          propertyTax: 'Précompte',
                          coOwnershipFees: 'Copropriété',
                          insurance: 'Assurance',
                          maintenance: 'Entretien'
                        };

                        return (
                          <div key={fee.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px', borderBottom: index < Math.min(recurrentFees.length, 4) - 1 ? '1px solid rgba(58, 58, 58, 0.3)' : 'none'}}>
                            <span style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)'}}>
                              {feeNames[fee.name] || fee.name}
                            </span>
                            <span style={{fontSize: '0.9em', fontWeight: '600', color: '#ffffff'}}>
                              {formatNumber(fee.amount)} €{frequencySuffix}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <div style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.5)'}}>
                        Aucune charge configurée
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>

              </div> {/* End Right Column */}

            </div> {/* End Main Layout Grid */}

            {/* Horizontal Timeline - Payment Flow Summary - Full Width */}
            {(() => {
              const prixAchat = acquisitionData.prixAchat || 0;
              const travaux = acquisitionData.travaux || 0;
              const fraisNotaire = acquisitionData.fraisNotaire || 0;
              const fraisAgence = acquisitionData.fraisAgence || 0;
              const apport = financementData.apport || 0;
              const montantCreditImmo = creditData.amount || 0;
              const montantCreditTravaux = creditTravauxData.amount || 0;
              const mensualiteCredit = (creditAmortization.monthlyPayment || 0) + (creditTravauxAmortization.monthlyPayment || 0);
              const apportTravaux = customApportSplit ? (customApportSplit.travaux || 0) : Math.round(travaux / 2);
              const depositAmount = prixAchat * 0.1; // 10% deposit estimate

              const monthlyRevenue = propertyRevenues.reduce((sum, rev) => {
                const amount = parseFloat(rev.amount) || 0;
                const occupancy = (parseFloat(rev.occupancyRate) || 100) / 100;
                const freq = rev.frequency || 'monthly';
                if (freq === 'monthly') return sum + (amount * occupancy);
                if (freq === 'quarterly') return sum + (amount * occupancy / 3);
                if (freq === 'annually') return sum + (amount * occupancy / 12);
                return sum;
              }, 0);

              const monthlyCharges = recurrentFees.reduce((sum, fee) => {
                const amount = parseFloat(fee.amount) || 0;
                const freq = fee.frequency || 'monthly';
                if (freq === 'monthly') return sum + amount;
                if (freq === 'quarterly') return sum + amount / 3;
                if (freq === 'annually') return sum + amount / 12;
                return sum;
              }, 0);

              const horizonYears = Math.round(performanceParams.investmentHorizonMonths / 12);
              const initialValue = estimatedHomeValue || (prixAchat + travaux);
              const finalValue = initialValue * Math.pow(1 + performanceParams.housePriceInflation / 100, horizonYears);

              const timelineSteps = [
                {
                  phase: 'Compromis',
                  timing: 'J0',
                  items: [
                    { label: 'Dépôt de garantie', amount: depositAmount, icon: '↑' }
                  ]
                },
                {
                  phase: 'Acte Authentique',
                  timing: 'J+90',
                  items: [
                    { label: 'Apport achat', sublabel: '(- garantie)', amount: Math.max(0, (apport - apportTravaux) - depositAmount), icon: '↑' },
                    ...(fraisAgence > 0 ? [{ label: 'Frais d\'agence', amount: fraisAgence, icon: '↑' }] : []),
                    { label: 'Déblocage prêt achat', amount: montantCreditImmo, icon: '↓' }
                  ]
                },
                ...(travaux > 0 ? [{
                  phase: 'Travaux',
                  timing: 'J+90→180',
                  items: [
                    ...(apportTravaux > 0 ? [{ label: 'Apport travaux', amount: apportTravaux, icon: '↑' }] : []),
                    ...(montantCreditTravaux > 0 ? [{ label: 'Déblocage prêt travaux', amount: montantCreditTravaux, icon: '↓' }] : [])
                  ]
                }] : []),
                {
                  phase: 'Exploitation Locative',
                  timing: 'Mensuel',
                  items: [
                    { label: 'Loyers', amount: monthlyRevenue, icon: '↓' },
                    { label: 'Crédit', amount: mensualiteCredit, icon: '↑' },
                    ...(monthlyCharges > 0 ? [{ label: 'Charges', amount: monthlyCharges, icon: '↑' }] : [])
                  ]
                },
                {
                  phase: 'Revente',
                  timing: `Année ${horizonYears}`,
                  items: [
                    { label: 'Prix de vente estimé', amount: finalValue, icon: '↓' }
                  ]
                }
              ];

              return (
                <div style={{
                  background: 'rgba(58, 58, 58, 0.3)',
                  border: '1px solid rgba(58, 58, 58, 0.6)',
                  borderRadius: '12px',
                  padding: '24px',
                  marginTop: '20px'
                }}>
                  <div style={{
                    fontSize: '1.2em',
                    fontWeight: '600',
                    color: '#ffffff',
                    marginBottom: '24px',
                    borderBottom: '1px solid rgba(58, 58, 58, 0.6)',
                    paddingBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <span style={{ background: 'rgba(16, 185, 129, 0.2)', color: '#10b981', padding: '6px 14px', borderRadius: '6px', fontSize: '0.8em' }}>Flux</span>
                    Timeline des Paiements
                  </div>

                  {/* Horizontal Timeline */}
                  <div style={{ position: 'relative', padding: '15px 0 30px 0' }}>
                    {/* Horizontal line */}
                    <div style={{
                      position: 'absolute',
                      top: '38px',
                      left: '40px',
                      right: '40px',
                      height: '4px',
                      background: '#10b981',
                      borderRadius: '2px'
                    }}></div>

                    {/* Timeline steps */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      position: 'relative'
                    }}>
                      {timelineSteps.map((step, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          flex: 1,
                          minWidth: 0
                        }}>
                          {/* Timing - above timeline */}
                          <div style={{
                            color: '#10b981',
                            fontSize: '0.85em',
                            fontWeight: '600',
                            marginBottom: '8px'
                          }}>{step.timing}</div>

                          {/* Circle marker */}
                          <div style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            background: '#10b981',
                            border: '4px solid #222222',
                            marginBottom: '12px',
                            zIndex: 1
                          }}></div>

                          {/* Phase name */}
                          <div style={{
                            color: '#ffffff',
                            fontSize: '1.05em',
                            fontWeight: '700',
                            marginBottom: '10px',
                            textAlign: 'center'
                          }}>{step.phase}</div>

                          {/* Items */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                            width: '100%',
                            padding: '0 8px'
                          }}>
                            {step.items.map((item, i) => (
                              <div key={i} style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                fontSize: '0.9em',
                                color: 'rgba(156,163,175,0.9)'
                              }}>
                                <span style={{
                                  color: item.icon === '↓' ? '#4ade80' : '#f87171',
                                  fontWeight: '700',
                                  fontSize: '1.1em'
                                }}>{item.icon}</span>
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {item.label}
                                  {item.sublabel && <span style={{ fontSize: '0.75em', color: 'rgba(156,163,175,0.5)', marginLeft: '4px' }}>{item.sublabel}</span>}
                                </span>
                                <span style={{
                                  color: item.icon === '↓' ? '#4ade80' : '#f87171',
                                  fontWeight: '600',
                                  whiteSpace: 'nowrap'
                                }}>{formatNumber(item.amount, 0)}€</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Legend */}
                  <div style={{
                    display: 'flex',
                    gap: '30px',
                    justifyContent: 'center',
                    marginTop: '16px',
                    paddingTop: '16px',
                    borderTop: '1px solid rgba(58, 58, 58, 0.4)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#f87171', fontWeight: '700', fontSize: '1.1em' }}>↑</span>
                      <span style={{ color: 'rgba(156,163,175,0.85)', fontSize: '0.9em' }}>Décaissement (vous payez)</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#4ade80', fontWeight: '700', fontSize: '1.1em' }}>↓</span>
                      <span style={{ color: 'rgba(156,163,175,0.85)', fontSize: '0.9em' }}>Encaissement (vous recevez)</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Two-Column Layout: Acquisition/Financement & Charges/Revenus */}
            <div className="home-summary-two-column" style={{display: 'none'}}>
              {/* Left: Acquisition & Financement */}
              <div style={{
                background: 'rgba(58, 58, 58, 0.3)',
                border: '1px solid rgba(58, 58, 58, 0.6)',
                borderRadius: '12px',
                padding: '20px'
              }}>
                {(() => {
                  // Calculate status for each metric
                  const capaciteStatus = results && results.max_borrowing_capacity !== undefined && financementData.emprunt > 0
                    ? (financementData.emprunt <= results.max_borrowing_capacity * 0.8 ? 'green' : financementData.emprunt <= results.max_borrowing_capacity ? 'amber' : 'red')
                    : 'gray';

                  const restantStatus = creditAmortization.monthlyPayment > 0
                    ? ((totalRevenues + totalFutureRevenues - totalCharges - creditAmortization.monthlyPayment - totalFutureCharges) >= 1000 ? 'green' : (totalRevenues + totalFutureRevenues - totalCharges - creditAmortization.monthlyPayment - totalFutureCharges) >= 800 ? 'amber' : 'red')
                    : 'gray';

                  const ltvStatus = ltv > 0
                    ? (Math.floor(ltv) <= 80 ? 'green' : Math.floor(ltv) <= 90 ? 'amber' : 'red')
                    : 'gray';

                  // Determine overall status (worst of the three)
                  let overallStatus = 'green';
                  const problems = [];

                  if (capaciteStatus === 'red' || restantStatus === 'red' || ltvStatus === 'red') {
                    overallStatus = 'red';
                    if (capaciteStatus === 'red') problems.push('Capacité d\'emprunt dépassée');
                    if (restantStatus === 'red') problems.push('Restant à vivre insuffisant');
                    if (ltvStatus === 'red') problems.push('LTV trop élevé');
                  } else if (capaciteStatus === 'amber' || restantStatus === 'amber' || ltvStatus === 'amber') {
                    overallStatus = 'amber';
                    if (capaciteStatus === 'amber') problems.push('Capacité d\'emprunt limite');
                    if (restantStatus === 'amber') problems.push('Restant à vivre juste');
                    if (ltvStatus === 'amber') problems.push('LTV acceptable mais élevé');
                  } else if (capaciteStatus === 'gray' && restantStatus === 'gray' && ltvStatus === 'gray') {
                    overallStatus = 'gray';
                  }

                  const statusColor = overallStatus === 'red' ? '#ef4444' : overallStatus === 'amber' ? '#f59e0b' : overallStatus === 'green' ? '#10b981' : 'rgba(156, 163, 175, 0.5)';
                  const statusGlow = overallStatus === 'red' ? '0 0 12px rgba(239, 68, 68, 0.6)' : overallStatus === 'amber' ? '0 0 12px rgba(245, 158, 11, 0.6)' : overallStatus === 'green' ? '0 0 12px rgba(16, 185, 129, 0.6)' : 'none';

                  return (
                    <>
                      <div style={{
                        fontSize: '1.1em',
                        fontWeight: '600',
                        color: '#ffffff',
                        marginBottom: '16px',
                        borderBottom: '1px solid rgba(58, 58, 58, 0.6)',
                        paddingBottom: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px'
                      }}>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          background: statusColor,
                          flexShrink: 0,
                          boxShadow: statusGlow
                        }}></div>
                        <div style={{flex: 1}}>
                          <div>Analyse Synthétique d'Endettement</div>
                          {problems.length > 0 && (
                            <div style={{
                              fontSize: '0.75em',
                              fontWeight: '400',
                              color: 'rgba(156, 163, 175, 0.8)',
                              marginTop: '4px'
                            }}>
                              {problems.join(' • ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  );
                })()}


                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}>
                {/* Capacité d'Emprunt */}
                <div style={{
                  flex: 1,
                  minWidth: '200px',
                  background: 'rgba(255, 255, 255, 0.02)',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: '1px solid rgba(58, 58, 58, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: results && results.max_borrowing_capacity !== undefined && financementData.emprunt > 0
                      ? (financementData.emprunt <= results.max_borrowing_capacity * 0.8
                          ? '#10b981'
                          : financementData.emprunt <= results.max_borrowing_capacity
                            ? '#f59e0b'
                            : '#ef4444')
                      : 'rgba(156, 163, 175, 0.5)',
                    flexShrink: 0,
                    boxShadow: results && results.max_borrowing_capacity !== undefined && financementData.emprunt > 0
                      ? (financementData.emprunt <= results.max_borrowing_capacity * 0.8
                          ? '0 0 8px rgba(16, 185, 129, 0.6)'
                          : financementData.emprunt <= results.max_borrowing_capacity
                            ? '0 0 8px rgba(245, 158, 11, 0.6)'
                            : '0 0 8px rgba(239, 68, 68, 0.6)')
                      : 'none'
                  }}></div>
                  <div style={{flex: 1}}>
                    <div style={{fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '4px'}}>
                      Capacité d'Emprunt Max
                    </div>
                    <div style={{fontSize: '1.3em', fontWeight: '700', color: '#ffffff'}}>
                      {results && results.max_borrowing_capacity !== undefined
                        ? `${formatNumber(results.max_borrowing_capacity, 0)} €`
                        : '—'}
                    </div>
                    <div style={{fontSize: '0.7em', color: 'rgba(156, 163, 175, 0.6)', marginTop: '2px'}}>
                      {results && results.max_borrowing_capacity !== undefined && financementData.emprunt > 0
                        ? `Dette: ${formatNumber(financementData.emprunt, 0)}€ (${formatNumber((financementData.emprunt / results.max_borrowing_capacity) * 100, 0)}%)`
                        : 'Basé sur votre profil'}
                    </div>
                  </div>
                </div>

                {/* Restant à Vivre */}
                <div style={{
                  flex: 1,
                  minWidth: '200px',
                  background: 'rgba(255, 255, 255, 0.02)',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: '1px solid rgba(58, 58, 58, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: creditAmortization.monthlyPayment > 0
                      ? ((totalRevenues + totalFutureRevenues - totalCharges - creditAmortization.monthlyPayment - totalFutureCharges) >= 1000
                          ? '#10b981'
                          : (totalRevenues + totalFutureRevenues - totalCharges - creditAmortization.monthlyPayment - totalFutureCharges) >= 800
                            ? '#f59e0b'
                            : '#ef4444')
                      : 'rgba(156, 163, 175, 0.5)',
                    flexShrink: 0,
                    boxShadow: creditAmortization.monthlyPayment > 0
                      ? ((totalRevenues + totalFutureRevenues - totalCharges - creditAmortization.monthlyPayment - totalFutureCharges) >= 1000
                          ? '0 0 8px rgba(16, 185, 129, 0.6)'
                          : (totalRevenues + totalFutureRevenues - totalCharges - creditAmortization.monthlyPayment - totalFutureCharges) >= 800
                            ? '0 0 8px rgba(245, 158, 11, 0.6)'
                            : '0 0 8px rgba(239, 68, 68, 0.6)')
                      : 'none'
                  }}></div>
                  <div style={{flex: 1}}>
                    <div style={{fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '4px'}}>
                      Restant à Vivre
                    </div>
                    <div style={{fontSize: '1.3em', fontWeight: '700', color: '#ffffff'}}>
                      {creditAmortization.monthlyPayment > 0
                        ? `${formatNumber(totalRevenues + totalFutureRevenues - totalCharges - creditAmortization.monthlyPayment - totalFutureCharges, 0)} €`
                        : '—'}
                    </div>
                    <div style={{fontSize: '0.7em', color: 'rgba(156, 163, 175, 0.6)', marginTop: '2px'}}>
                      {creditAmortization.monthlyPayment > 0
                        ? ((totalRevenues + totalFutureRevenues - totalCharges - creditAmortization.monthlyPayment - totalFutureCharges) >= 1000
                            ? 'Confortable (≥1000€)'
                            : (totalRevenues + totalFutureRevenues - totalCharges - creditAmortization.monthlyPayment - totalFutureCharges) >= 800
                              ? 'Juste (800-1000€)'
                              : 'Insuffisant (<800€)')
                        : 'Min recommandé: 1000€'}
                    </div>
                  </div>
                </div>

                {/* LTV */}
                <div style={{
                  flex: 1,
                  minWidth: '200px',
                  background: 'rgba(255, 255, 255, 0.02)',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: '1px solid rgba(58, 58, 58, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: ltv > 0
                      ? (Math.floor(ltv) <= 80 ? '#10b981' : Math.floor(ltv) <= 90 ? '#f59e0b' : '#ef4444')
                      : 'rgba(156, 163, 175, 0.5)',
                    flexShrink: 0,
                    boxShadow: ltv > 0
                      ? (Math.floor(ltv) <= 80 ? '0 0 8px rgba(16, 185, 129, 0.6)' : Math.floor(ltv) <= 90 ? '0 0 8px rgba(245, 158, 11, 0.6)' : '0 0 8px rgba(239, 68, 68, 0.6)')
                      : 'none'
                  }}></div>
                  <div style={{flex: 1}}>
                    <div style={{fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '4px'}}>
                      LTV (Loan-to-Value)
                    </div>
                    <div style={{fontSize: '1.3em', fontWeight: '700', color: '#ffffff'}}>
                      {ltv > 0 ? `${formatNumber(ltv, 1)}%` : '—'}
                    </div>
                    <div style={{fontSize: '0.7em', color: 'rgba(156, 163, 175, 0.6)', marginTop: '2px'}}>
                      {ltv > 0
                        ? (Math.floor(ltv) <= 80 ? 'Excellent (≤80%)' : Math.floor(ltv) <= 90 ? 'Acceptable (80-90%)' : 'Élevé (>90%)')
                        : 'Max recommandé: 80%'}
                    </div>
                  </div>
                </div>
                </div>
              </div>
            </div>

            {/* Two-Column Layout: Acquisition/Financement & Charges/Revenus */}
            <div className="home-summary-two-column" style={{display: 'none'}}>
              {/* Left: Acquisition & Financement */}
              <div className="summary-card">
                <div className="summary-card-header">🏠 Acquisition & Financement</div>

                <div style={{marginTop: '16px', paddingLeft: '8px'}}>
                  {/* Prix d'Achat */}
                  <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
                    <span style={{marginRight: '12px', color: 'rgba(156, 163, 175, 0.7)', fontSize: '1.2em'}}>•</span>
                    <span className="summary-label" style={{marginRight: '8px'}}>Prix d'Achat:</span>
                    <span className="summary-value" style={{fontWeight: '700'}}>
                      {formatNumber(acquisitionData.prixAchat)} €
                    </span>
                  </div>

                  {/* Frais Notaire */}
                  <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
                    <span style={{marginRight: '12px', color: 'rgba(156, 163, 175, 0.7)', fontSize: '1.2em'}}>•</span>
                    <span className="summary-label" style={{marginRight: '8px'}}>Frais Notaire:</span>
                    <span className="summary-value" style={{fontWeight: '700'}}>
                      {formatNumber(acquisitionData.fraisNotaire)} €
                    </span>
                  </div>

                  {/* Frais Agence */}
                  {acquisitionData.fraisAgence > 0 && (
                    <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
                      <span style={{marginRight: '12px', color: 'rgba(156, 163, 175, 0.7)', fontSize: '1.2em'}}>•</span>
                      <span className="summary-label" style={{marginRight: '8px'}}>Frais Agence:</span>
                      <span className="summary-value" style={{fontWeight: '700'}}>
                        {formatNumber(acquisitionData.fraisAgence)} €
                      </span>
                    </div>
                  )}

                  {/* Travaux */}
                  {acquisitionData.travaux > 0 && (
                    <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
                      <span style={{marginRight: '12px', color: 'rgba(156, 163, 175, 0.7)', fontSize: '1.2em'}}>•</span>
                      <span className="summary-label" style={{marginRight: '8px'}}>Travaux:</span>
                      <span className="summary-value" style={{fontWeight: '700'}}>
                        {formatNumber(acquisitionData.travaux)} €
                      </span>
                    </div>
                  )}

                  {/* Autres Frais */}
                  {acquisitionData.autresFrais > 0 && (
                    <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
                      <span style={{marginRight: '12px', color: 'rgba(156, 163, 175, 0.7)', fontSize: '1.2em'}}>•</span>
                      <span className="summary-label" style={{marginRight: '8px'}}>Autres Frais:</span>
                      <span className="summary-value" style={{fontWeight: '700'}}>
                        {formatNumber(acquisitionData.autresFrais)} €
                      </span>
                    </div>
                  )}

                  {/* Divider */}
                  <div style={{borderTop: '1px solid #3a3a3a', margin: '16px 0'}}></div>

                  {/* Coût Total */}
                  <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
                    <span style={{marginRight: '12px', color: 'rgba(156, 163, 175, 0.7)', fontSize: '1.2em'}}>•</span>
                    <span className="summary-label" style={{marginRight: '8px', fontWeight: '700'}}>Coût Total:</span>
                    <span className="summary-value highlight-color" style={{fontWeight: '700', fontSize: '1.1em'}}>
                      {formatNumber(
                        acquisitionData.prixAchat +
                        acquisitionData.fraisNotaire +
                        acquisitionData.fraisAgence +
                        acquisitionData.travaux +
                        acquisitionData.autresFrais
                      )} €
                    </span>
                  </div>

                  {/* Apport */}
                  <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
                    <span style={{marginRight: '12px', color: 'rgba(156, 163, 175, 0.7)', fontSize: '1.2em'}}>•</span>
                    <span className="summary-label" style={{marginRight: '8px'}}>Apport:</span>
                    <span className="summary-value highlight-color" style={{fontWeight: '700'}}>
                      {formatNumber(financementData.apport)} €
                    </span>
                  </div>

                  {/* Emprunt (Dette) */}
                  <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
                    <span style={{marginRight: '12px', color: 'rgba(156, 163, 175, 0.7)', fontSize: '1.2em'}}>•</span>
                    <span className="summary-label" style={{marginRight: '8px'}}>Dette (Emprunt):</span>
                    <span className="summary-value" style={{fontWeight: '700', color: '#ef4444'}}>
                      {formatNumber(financementData.emprunt)} €
                    </span>
                  </div>
                </div>
              </div>

              {/* Right: Charges Récurrentes & Revenus */}
              <div className="summary-card">
                <div className="summary-card-header">📊 Charges Récurrentes & Revenus</div>

                <div style={{marginTop: '16px', paddingLeft: '8px'}}>
                  {/* Revenus Section */}
                  <div style={{marginBottom: '20px'}}>
                    <div style={{fontWeight: '700', marginBottom: '12px', color: 'rgba(209, 178, 135, 0.95)'}}>
                      Revenus:
                    </div>
                    {propertyRevenues.length > 0 ? (
                      propertyRevenues.map((revenue) => {
                        const monthlyAmount = revenue.frequency === 'monthly'
                          ? revenue.amount
                          : revenue.amount / 12;
                        const effectiveMonthly = monthlyAmount * (revenue.occupancyRate / 100);

                        return (
                          <div key={revenue.id} style={{display: 'flex', alignItems: 'center', marginBottom: '8px', paddingLeft: '16px'}}>
                            <span style={{marginRight: '8px', color: 'rgba(156, 163, 175, 0.5)', fontSize: '0.9em'}}>◦</span>
                            <span className="summary-label" style={{marginRight: '8px', fontSize: '0.9em'}}>
                              {revenue.name === 'rent' ? 'Loyer' : revenue.name}:
                            </span>
                            <span className="summary-value highlight-color" style={{fontSize: '0.9em'}}>
                              {formatNumber(effectiveMonthly)} €/mois
                              {revenue.occupancyRate < 100 && (
                                <span style={{fontSize: '0.85em', opacity: 0.7}}> ({revenue.occupancyRate}%)</span>
                              )}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <div style={{paddingLeft: '16px', fontSize: '0.9em', opacity: 0.6}}>
                        Aucun revenu configuré
                      </div>
                    )}
                  </div>

                  {/* Charges Section */}
                  <div>
                    <div style={{fontWeight: '700', marginBottom: '12px', color: '#ef4444'}}>
                      Charges:
                    </div>
                    {recurrentFees.length > 0 ? (
                      recurrentFees.map((fee) => {
                        const monthlyAmount = fee.frequency === 'monthly'
                          ? fee.amount
                          : fee.frequency === 'annually'
                            ? fee.amount / 12
                            : fee.amount / 4;

                        const feeNames = {
                          propertyTax: 'Précompte immobilier',
                          coOwnershipFees: 'Charges copropriété',
                          insurance: 'Assurance',
                          maintenance: 'Entretien'
                        };

                        return (
                          <div key={fee.id} style={{display: 'flex', alignItems: 'center', marginBottom: '8px', paddingLeft: '16px'}}>
                            <span style={{marginRight: '8px', color: 'rgba(156, 163, 175, 0.5)', fontSize: '0.9em'}}>◦</span>
                            <span className="summary-label" style={{marginRight: '8px', fontSize: '0.9em'}}>
                              {feeNames[fee.name] || fee.name}:
                            </span>
                            <span className="summary-value" style={{fontSize: '0.9em', color: '#ef4444'}}>
                              {formatNumber(monthlyAmount)} €/mois
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <div style={{paddingLeft: '16px', fontSize: '0.9em', opacity: 0.6}}>
                        Aucune charge configurée
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Saved Investments List */}
            {savedInvestments.length > 0 ? (
              <>
                <div className="summary-grid">
                  {savedInvestments.map((investment) => {
                    const isSelected = selectedInvestments.includes(investment.id);
                    const totalAcquisition = investment.data.acquisitionData.prixAchat +
                      investment.data.acquisitionData.fraisNotaire +
                      investment.data.acquisitionData.fraisAgence +
                      investment.data.acquisitionData.travaux +
                      investment.data.acquisitionData.autresFrais;

                    return (
                      <div
                        key={investment.id}
                        className="summary-card"
                        style={{
                          border: isSelected ? '2px solid rgba(209, 178, 135, 0.95)' : '1px solid #2a2a2a',
                          background: isSelected ? 'rgba(209, 178, 135, 0.05)' : '#222222'
                        }}
                      >
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                          <div className="summary-card-header" style={{marginBottom: 0}}>{investment.name}</div>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleInvestmentSelection(investment.id)}
                            style={{width: '20px', height: '20px', cursor: 'pointer'}}
                          />
                        </div>

                        <div className="summary-item">
                          <span className="summary-label">Date:</span>
                          <span className="summary-value">{new Date(investment.date).toLocaleDateString()}</span>
                        </div>

                        <div className="summary-item">
                          <span className="summary-label">Prix d'Achat:</span>
                          <span className="summary-value">{formatNumber(investment.data.acquisitionData.prixAchat)} €</span>
                        </div>

                        <div className="summary-item">
                          <span className="summary-label">Coût Total:</span>
                          <span className="summary-value highlight-color">{formatNumber(totalAcquisition)} €</span>
                        </div>

                        <div className="summary-item">
                          <span className="summary-label">Apport:</span>
                          <span className="summary-value">{formatNumber(investment.data.financementData.apport)} €</span>
                        </div>

                        <div className="summary-item">
                          <span className="summary-label">Emprunt:</span>
                          <span className="summary-value">{formatNumber(investment.data.financementData.emprunt)} €</span>
                        </div>

                        <div className="summary-item">
                          <span className="summary-label">Type de Crédit:</span>
                          <span className="summary-value">{investment.data.creditData.type === 'bullet' ? 'In Fine' : investment.data.creditData.type === 'amortizing' ? 'Amortissable' : 'Autre'}</span>
                        </div>

                        <div style={{display: 'flex', gap: '12px', marginTop: '20px'}}>
                          <button
                            onClick={() => loadInvestment(investment)}
                            className="button"
                            style={{flex: 1}}
                          >
                            Charger
                          </button>
                          <button
                            onClick={() => deleteInvestment(investment.id)}
                            className="button-secondary"
                            style={{flex: 1}}
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Comparison Table */}
                {selectedInvestments.length > 0 && (
                  <div style={{marginTop: '32px'}}>
                    <h3 className="section-title" style={{fontSize: '1.4em'}}>Comparaison des Investissements Sélectionnés</h3>
                    <div className="chart-container">
                      <table className="comparison-table">
                        <thead>
                          <tr>
                            <th>Critère</th>
                            {selectedInvestments.map(id => {
                              const inv = savedInvestments.find(i => i.id === id);
                              return <th key={id}>{inv.name}</th>;
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Prix d'Achat</td>
                            {selectedInvestments.map(id => {
                              const inv = savedInvestments.find(i => i.id === id);
                              return <td key={id}>{formatNumber(inv.data.acquisitionData.prixAchat)} €</td>;
                            })}
                          </tr>
                          <tr>
                            <td>Coût Total</td>
                            {selectedInvestments.map(id => {
                              const inv = savedInvestments.find(i => i.id === id);
                              const total = inv.data.acquisitionData.prixAchat +
                                inv.data.acquisitionData.fraisNotaire +
                                inv.data.acquisitionData.fraisAgence +
                                inv.data.acquisitionData.travaux +
                                inv.data.acquisitionData.autresFrais;
                              return <td key={id}>{formatNumber(total)} €</td>;
                            })}
                          </tr>
                          <tr>
                            <td>Apport</td>
                            {selectedInvestments.map(id => {
                              const inv = savedInvestments.find(i => i.id === id);
                              return <td key={id}>{formatNumber(inv.data.financementData.apport)} €</td>;
                            })}
                          </tr>
                          <tr>
                            <td>Emprunt</td>
                            {selectedInvestments.map(id => {
                              const inv = savedInvestments.find(i => i.id === id);
                              return <td key={id}>{formatNumber(inv.data.financementData.emprunt)} €</td>;
                            })}
                          </tr>
                          <tr>
                            <td>Type de Crédit</td>
                            {selectedInvestments.map(id => {
                              const inv = savedInvestments.find(i => i.id === id);
                              const typeLabel = inv.data.creditData.type === 'bullet' ? 'In Fine' :
                                              inv.data.creditData.type === 'amortizing' ? 'Amortissable' :
                                              inv.data.creditData.type === 'interestOnly' ? 'Intérêts Seuls' : 'Crescendo';
                              return <td key={id}>{typeLabel}</td>;
                            })}
                          </tr>
                          <tr>
                            <td>Taux d'Intérêt</td>
                            {selectedInvestments.map(id => {
                              const inv = savedInvestments.find(i => i.id === id);
                              return <td key={id}>{inv.data.creditData.interestRate}%</td>;
                            })}
                          </tr>
                          <tr>
                            <td>Durée du Crédit</td>
                            {selectedInvestments.map(id => {
                              const inv = savedInvestments.find(i => i.id === id);
                              return <td key={id}>{inv.data.creditData.durationYears} ans</td>;
                            })}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="summary-empty">
                <p>Aucun investissement sauvegardé.</p>
                <p>Configurez vos paramètres dans l'onglet "Investment", puis sauvegardez votre projet ici.</p>
              </div>
            )}
              </div>
            )}
          </div>
        )}

        {/* Situation Financière Tab */}
        {activeTab === 'current-finance' && (
          <div style={{width: '100%', padding: '0 24px'}}>
            {/* Sub-tabs navigation - Sticky */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '24px',
              borderBottom: '1px solid rgba(58, 58, 58, 0.4)',
              position: 'sticky',
              top: 0,
              background: '#0a0a0a',
              zIndex: 100,
              paddingTop: '12px',
              paddingBottom: '0',
              marginLeft: '-24px',
              marginRight: '-24px',
              paddingLeft: '24px',
              paddingRight: '24px'
            }}>
              <button
                onClick={() => { setActiveTab('summary'); setError(''); }}
                style={{
                  padding: '12px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.95em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = 'rgba(209, 178, 135, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = 'rgba(156, 163, 175, 0.7)';
                }}
              >
                Sommaire
              </button>
              <button
                onClick={() => { setActiveTab('current-finance'); setError(''); }}
                style={{
                  padding: '12px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid rgba(209, 178, 135, 0.9)',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.95em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
              >
                Situation Financière
              </button>
              <button
                onClick={() => { setActiveTab('borrowing'); setError(''); }}
                style={{
                  padding: '12px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.95em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = 'rgba(209, 178, 135, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = 'rgba(156, 163, 175, 0.7)';
                }}
              >
                Capacité d'Emprunt
              </button>
              <div style={{ flex: 1 }}></div>
            </div>

            <h2 className="section-title">Situation Financière</h2>

            {/* Apport Personnel */}
            <div className="calculation-section" style={{maxHeight: 'none', marginBottom: '24px', maxWidth: '500px'}}>
              <h2 className="panel-header">Apport Personnel</h2>

              <div className="input-list">
                <div className="input-row">
                  <input
                    type="text"
                    className="input-label-field"
                    value="Montant Disponible"
                    disabled
                  />
                  <input
                    type="number"
                    className="input-amount-field"
                    placeholder="Montant"
                    step="1000"
                    min="0"
                    value={currentFinanceApport || ''}
                    onChange={(e) => setCurrentFinanceApport(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Side-by-side panels */}
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '24px'}}>

              {/* Revenus Mensuels */}
              <div className="calculation-section" style={{maxHeight: 'none'}}>
                <h2 className="panel-header">Revenus Mensuels</h2>

                {/* Column Headers */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 150px 100px 40px',
                  gap: '8px',
                  padding: '8px 16px',
                  marginBottom: '8px'
                }}>
                  <div style={{color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em', fontWeight: '600'}}>
                    Nom
                  </div>
                  <div style={{color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em', fontWeight: '600', textAlign: 'center'}}>
                    Amount
                  </div>
                  <div style={{color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em', fontWeight: '600', textAlign: 'center'}}>
                    Pondération
                  </div>
                  <div></div>
                </div>

                <div className="input-list">
                  {currentFinanceRevenues.map((revenue, index) => (
                    <div key={revenue.id}>
                      <div className="input-row" style={{display: 'grid', gridTemplateColumns: '1fr 150px 100px 40px', gap: '8px', alignItems: 'center'}}>
                        <input
                          type="text"
                          className="input-label-field"
                          value={revenue.name}
                          onChange={(e) => {
                            const updated = [...currentFinanceRevenues];
                            updated[index].name = e.target.value;
                            setCurrentFinanceRevenues(updated);
                          }}
                        />
                        <input
                          type="number"
                          className="input-amount-field"
                          placeholder="Montant"
                          step="100"
                          min="0"
                          value={revenue.amount || ''}
                          onChange={(e) => {
                            const updated = [...currentFinanceRevenues];
                            updated[index].amount = parseFloat(e.target.value) || 0;
                            setCurrentFinanceRevenues(updated);
                          }}
                        />
                        <input
                          type="number"
                          className="input-amount-field"
                          placeholder="%"
                          step="1"
                          min="0"
                          max="100"
                          value={revenue.percentage || ''}
                          onChange={(e) => {
                            const updated = [...currentFinanceRevenues];
                            updated[index].percentage = parseFloat(e.target.value) || 0;
                            setCurrentFinanceRevenues(updated);
                          }}
                        />
                        <button
                          onClick={() => {
                            const updated = currentFinanceRevenues.filter((_, i) => i !== index);
                            setCurrentFinanceRevenues(updated);
                          }}
                          style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '4px',
                            color: '#ef4444',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontSize: '0.9em',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => { e.target.style.background = 'rgba(239, 68, 68, 0.2)'; }}
                          onMouseLeave={(e) => { e.target.style.background = 'rgba(239, 68, 68, 0.1)'; }}
                          title="Supprimer"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => {
                    const newId = Math.max(...currentFinanceRevenues.map(r => r.id), 0) + 1;
                    setCurrentFinanceRevenues([...currentFinanceRevenues, { id: newId, name: '', amount: 0, percentage: 100 }]);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    marginTop: '12px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    borderRadius: '6px',
                    color: '#10b981',
                    fontSize: '0.9em',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => { e.target.style.background = 'rgba(16, 185, 129, 0.2)'; }}
                  onMouseLeave={(e) => { e.target.style.background = 'rgba(16, 185, 129, 0.1)'; }}
                >
                  + Ajouter un Revenu
                </button>

                <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '16px', padding: '12px 16px', borderTop: '2px solid #2a2a2a'}}>
                  <div style={{fontSize: '0.95em', color: 'rgba(156, 163, 175, 0.9)', marginRight: '12px', fontWeight: '600'}}>Total Revenus:</div>
                  <div style={{fontSize: '1.1em', fontWeight: '700', color: '#10b981'}}>
                    {formatNumber(currentFinanceRevenues.reduce((sum, r) => sum + (r.amount * r.percentage / 100), 0), 2)} €
                  </div>
                </div>
              </div>

              {/* Charges Mensuelles */}
              <div className="calculation-section" style={{maxHeight: 'none'}}>
                <h2 className="panel-header">Charges Mensuelles</h2>

                {/* Column Headers */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 150px 100px 40px',
                  gap: '8px',
                  padding: '8px 16px',
                  marginBottom: '8px'
                }}>
                  <div style={{color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em', fontWeight: '600'}}>
                    Nom
                  </div>
                  <div style={{color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em', fontWeight: '600', textAlign: 'center'}}>
                    Amount
                  </div>
                  <div style={{color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em', fontWeight: '600', textAlign: 'center'}}>
                    Pondération
                  </div>
                  <div></div>
                </div>

                <div className="input-list">
                  {currentFinanceCharges.map((charge, index) => (
                    <div key={charge.id}>
                      <div className="input-row" style={{display: 'grid', gridTemplateColumns: '1fr 150px 100px 40px', gap: '8px', alignItems: 'center'}}>
                        <input
                          type="text"
                          className="input-label-field"
                          value={charge.name}
                          onChange={(e) => {
                            const updated = [...currentFinanceCharges];
                            updated[index].name = e.target.value;
                            setCurrentFinanceCharges(updated);
                          }}
                        />
                        <input
                          type="number"
                          className="input-amount-field"
                          placeholder="Montant"
                          step="100"
                          min="0"
                          value={charge.amount || ''}
                          onChange={(e) => {
                            const updated = [...currentFinanceCharges];
                            updated[index].amount = parseFloat(e.target.value) || 0;
                            setCurrentFinanceCharges(updated);
                          }}
                        />
                        <input
                          type="number"
                          className="input-amount-field"
                          placeholder="%"
                          step="1"
                          min="0"
                          max="100"
                          value={charge.percentage || ''}
                          onChange={(e) => {
                            const updated = [...currentFinanceCharges];
                            updated[index].percentage = parseFloat(e.target.value) || 0;
                            setCurrentFinanceCharges(updated);
                          }}
                        />
                        <button
                          onClick={() => {
                            const updated = currentFinanceCharges.filter((_, i) => i !== index);
                            setCurrentFinanceCharges(updated);
                          }}
                          style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '4px',
                            color: '#ef4444',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontSize: '0.9em',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => { e.target.style.background = 'rgba(239, 68, 68, 0.2)'; }}
                          onMouseLeave={(e) => { e.target.style.background = 'rgba(239, 68, 68, 0.1)'; }}
                          title="Supprimer"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => {
                    const newId = Math.max(...currentFinanceCharges.map(c => c.id), 0) + 1;
                    setCurrentFinanceCharges([...currentFinanceCharges, { id: newId, name: '', amount: 0, percentage: 100 }]);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    marginTop: '12px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '6px',
                    color: '#ef4444',
                    fontSize: '0.9em',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => { e.target.style.background = 'rgba(239, 68, 68, 0.2)'; }}
                  onMouseLeave={(e) => { e.target.style.background = 'rgba(239, 68, 68, 0.1)'; }}
                >
                  + Ajouter une Charge
                </button>

                <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '16px', padding: '12px 16px', borderTop: '2px solid #2a2a2a'}}>
                  <div style={{fontSize: '0.95em', color: 'rgba(156, 163, 175, 0.9)', marginRight: '12px', fontWeight: '600'}}>Total Charges:</div>
                  <div style={{fontSize: '1.1em', fontWeight: '700', color: '#ef4444'}}>
                    {formatNumber(currentFinanceCharges.reduce((sum, c) => sum + (c.amount * c.percentage / 100), 0), 2)} €
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Frais Tab */}
        {activeTab === 'frais' && (
          <div style={{width: '100%', padding: '0 24px'}}>
            {/* Sub-tabs navigation - Sticky */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '24px',
              borderBottom: '1px solid rgba(58, 58, 58, 0.4)',
              position: 'sticky',
              top: 0,
              background: '#0a0a0a',
              zIndex: 100,
              paddingTop: '12px',
              paddingBottom: '0',
              marginLeft: '-24px',
              marginRight: '-24px',
              paddingLeft: '24px',
              paddingRight: '24px'
            }}>
              <button
                onClick={() => setFraisSubTab('summary')}
                style={{
                  padding: '12px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: fraisSubTab === 'summary' ? '2px solid rgba(209, 178, 135, 0.9)' : '2px solid transparent',
                  color: fraisSubTab === 'summary' ? 'rgba(209, 178, 135, 0.95)' : 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.95em',
                  fontWeight: fraisSubTab === 'summary' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => {
                  if (fraisSubTab !== 'summary') e.target.style.color = 'rgba(209, 178, 135, 0.8)';
                }}
                onMouseLeave={(e) => {
                  if (fraisSubTab !== 'summary') e.target.style.color = 'rgba(156, 163, 175, 0.7)';
                }}
              >
                Sommaire
              </button>
              <button
                onClick={() => setFraisSubTab('acte-achat')}
                style={{
                  padding: '12px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: fraisSubTab === 'acte-achat' ? '2px solid rgba(209, 178, 135, 0.9)' : '2px solid transparent',
                  color: fraisSubTab === 'acte-achat' ? 'rgba(209, 178, 135, 0.95)' : 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.95em',
                  fontWeight: fraisSubTab === 'acte-achat' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => {
                  if (fraisSubTab !== 'acte-achat') e.target.style.color = 'rgba(209, 178, 135, 0.8)';
                }}
                onMouseLeave={(e) => {
                  if (fraisSubTab !== 'acte-achat') e.target.style.color = 'rgba(156, 163, 175, 0.7)';
                }}
              >
                Frais d'Acte d'achat
              </button>
              <button
                onClick={() => setFraisSubTab('hypothecaire')}
                style={{
                  padding: '12px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: fraisSubTab === 'hypothecaire' ? '2px solid rgba(209, 178, 135, 0.9)' : '2px solid transparent',
                  color: fraisSubTab === 'hypothecaire' ? 'rgba(209, 178, 135, 0.95)' : 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.95em',
                  fontWeight: fraisSubTab === 'hypothecaire' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => {
                  if (fraisSubTab !== 'hypothecaire') e.target.style.color = 'rgba(209, 178, 135, 0.8)';
                }}
                onMouseLeave={(e) => {
                  if (fraisSubTab !== 'hypothecaire') e.target.style.color = 'rgba(156, 163, 175, 0.7)';
                }}
              >
                Frais Hypothécaire
              </button>
            </div>

            {/* Summary Sub-tab */}
            {fraisSubTab === 'summary' && (
              <div>
                <h2 className="section-title">Récapitulatif des Frais</h2>

                <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px'}}>
                  {/* Frais d'Acte d'achat Summary */}
                  <div className="summary-card">
                    <div className="summary-card-header">Frais d'Acte d'achat</div>
                    <div className="summary-item">
                      <span className="summary-label">Droits d'enregistrement:</span>
                      <span className="summary-value">{formatNumber(notaryFeesBreakdown.registrationFees, 2)} €</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Droit pour les annexes:</span>
                      <span className="summary-value">{formatNumber(notaryFeesBreakdown.annexFees, 2)} €</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Honoraires:</span>
                      <span className="summary-value">{formatNumber(notaryFeesBreakdown.notaryFees, 2)} €</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Frais administratifs:</span>
                      <span className="summary-value">{formatNumber(notaryFeesBreakdown.adminFees, 2)} €</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Débours:</span>
                      <span className="summary-value">{formatNumber(notaryFeesBreakdown.disbursements, 2)} €</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Transcription hypothécaire:</span>
                      <span className="summary-value">{formatNumber(notaryFeesBreakdown.mortgageTranscription, 2)} €</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Droit d'écriture:</span>
                      <span className="summary-value">{formatNumber(notaryFeesBreakdown.writingFees, 2)} €</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">TVA:</span>
                      <span className="summary-value">{formatNumber(notaryFeesBreakdown.vat, 2)} €</span>
                    </div>
                    <div className="summary-item" style={{borderTop: '2px solid #2a2a2a', paddingTop: '12px', marginTop: '12px'}}>
                      <span className="summary-label" style={{fontWeight: '700'}}>Total:</span>
                      <span className="summary-value" style={{fontWeight: '700', fontSize: '1.1em'}}>{formatNumber(notaryFeesBreakdown.total, 2)} €</span>
                    </div>
                  </div>

                  {/* Frais Hypothécaire Summary */}
                  <div className="summary-card">
                    <div className="summary-card-header">Frais Hypothécaire</div>
                    <div className="summary-item">
                      <span className="summary-label">Droits d'enregistrement:</span>
                      <span className="summary-value">{formatNumber(mortgageFeesBreakdown.registrationTax, 2)} €</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Droit pour les annexes:</span>
                      <span className="summary-value">{formatNumber(mortgageFeesBreakdown.appendixFee, 2)} €</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Frais d'hypothèque - Droit d'hypothèque:</span>
                      <span className="summary-value">{formatNumber(mortgageFeesBreakdown.mortgageRight, 2)} €</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Frais d'hypothèque - Rétribution:</span>
                      <span className="summary-value">{formatNumber(mortgageFeesBreakdown.mortgageRemuneration, 2)} €</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Honoraires:</span>
                      <span className="summary-value">{formatNumber(mortgageFeesBreakdown.notaryFees, 2)} €</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Frais administratifs:</span>
                      <span className="summary-value">{formatNumber(mortgageFeesBreakdown.adminFees, 2)} €</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Débours:</span>
                      <span className="summary-value">{formatNumber(mortgageFeesBreakdown.disbursements, 2)} €</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Droit d'écriture:</span>
                      <span className="summary-value">{formatNumber(mortgageFeesBreakdown.writingFee, 2)} €</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">TVA:</span>
                      <span className="summary-value">{formatNumber(mortgageFeesBreakdown.vat, 2)} €</span>
                    </div>
                    <div className="summary-item" style={{borderTop: '2px solid #2a2a2a', paddingTop: '12px', marginTop: '12px'}}>
                      <span className="summary-label" style={{fontWeight: '700'}}>Total:</span>
                      <span className="summary-value" style={{fontWeight: '700', fontSize: '1.1em'}}>{formatNumber(mortgageFeesBreakdown.total, 2)} €</span>
                    </div>
                  </div>
                </div>

                {/* Grand Total */}
                <div className="summary-card" style={{marginTop: '24px', background: 'rgba(209, 178, 135, 0.1)', border: '2px solid rgba(209, 178, 135, 0.3)'}}>
                  <div className="summary-item">
                    <span className="summary-label" style={{fontSize: '1.1em', fontWeight: '700'}}>Total Général des Frais:</span>
                    <span className="summary-value" style={{fontSize: '1.3em', fontWeight: '700', color: 'rgba(209, 178, 135, 0.95)'}}>
                      {formatNumber(notaryFeesBreakdown.total + mortgageFeesBreakdown.total, 2)} €
                    </span>
                  </div>
                </div>

                {/* Disclaimer */}
                <div style={{marginTop: '24px', padding: '20px', background: 'rgba(59, 130, 246, 0.05)', borderRadius: '8px', border: '1px solid rgba(59, 130, 246, 0.2)'}}>
                  <p style={{fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.8)', margin: 0, lineHeight: '1.6', fontStyle: 'italic', textAlign: 'center'}}>
                    Ces montants sont donnés à titre indicatif. Date de mise à jour : {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </p>
                </div>
              </div>
            )}

            {/* Frais d'Acte d'achat Sub-tab */}
            {fraisSubTab === 'acte-achat' && (
              <div>
                <h2 className="section-title">Calcul</h2>

                <div className="calculation-section" style={{width: '100%', margin: '0', padding: '0 24px'}}>

                  {/* Grid for Region and Price */}
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', marginBottom: '24px', marginTop: '32px'}}>

                    {/* Section: Calcul pour une habitation située en */}
                    <div>
                      <h3 style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)', marginBottom: '12px', fontWeight: '600'}}>
                        Calcul pour une habitation située en / à
                      </h3>
                      <select
                        value={notaryFeesCalc.region}
                        onChange={(e) => setNotaryFeesCalc({...notaryFeesCalc, region: e.target.value})}
                        style={{
                          width: '100%',
                          background: '#2a2a2a',
                          border: '1px solid #3a3a3a',
                          color: 'rgba(255, 255, 255, 0.9)',
                          padding: '12px',
                          borderRadius: '6px',
                          fontSize: '0.95em',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="brussels">Bruxelles</option>
                        <option value="wallonia">Wallonie</option>
                        <option value="flanders">Flandre</option>
                      </select>
                    </div>

                    {/* Section: Prix d'acquisition */}
                    <div>
                    <h3 style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)', marginBottom: '12px', fontWeight: '600'}}>
                      Prix d'acquisition
                    </h3>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <span style={{fontSize: '1em', color: 'rgba(255, 255, 255, 0.7)'}}>€</span>
                      <input
                        type="number"
                        step="1000"
                        min="0"
                        value={notaryFeesCalc.purchasePrice || ''}
                        onChange={(e) => setNotaryFeesCalc({...notaryFeesCalc, purchasePrice: parseFloat(e.target.value) || 0})}
                        style={{
                          flex: 1,
                          background: '#2a2a2a',
                          border: '1px solid #3a3a3a',
                          color: 'rgba(255, 255, 255, 0.9)',
                          padding: '12px',
                          borderRadius: '6px',
                          fontSize: '0.95em'
                        }}
                      />
                      <button
                        onClick={() => setNotaryFeesCalc({...notaryFeesCalc, purchasePrice: Math.max(0, notaryFeesCalc.purchasePrice - 1000)})}
                        style={{
                          background: '#2a2a2a',
                          border: '1px solid #3a3a3a',
                          color: 'rgba(255, 255, 255, 0.7)',
                          padding: '12px 16px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '1.2em',
                          fontWeight: '600'
                        }}
                      >
                        −
                      </button>
                      <button
                        onClick={() => setNotaryFeesCalc({...notaryFeesCalc, purchasePrice: notaryFeesCalc.purchasePrice + 1000})}
                        style={{
                          background: '#2a2a2a',
                          border: '1px solid #3a3a3a',
                          color: 'rgba(255, 255, 255, 0.7)',
                          padding: '12px 16px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '1.2em',
                          fontWeight: '600'
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  </div>

                  {/* Grid layout for 2 question sections */}
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '48px', marginBottom: '24px'}}>

                    {/* Section 1: Propre et unique habitation */}
                    <div>
                      <h3 style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)', marginBottom: '12px', fontWeight: '600'}}>
                        Propre et unique habitation ?
                      </h3>
                      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '8px'}}>
                          <input
                            type="radio"
                            name="isOnlyHome"
                            checked={notaryFeesCalc.isOnlyHome === true}
                            onChange={() => setNotaryFeesCalc({...notaryFeesCalc, isOnlyHome: true})}
                            style={{width: '18px', height: '18px', cursor: 'pointer'}}
                          />
                          <span style={{fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.9)'}}>Oui</span>
                        </label>
                        <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '8px'}}>
                          <input
                            type="radio"
                            name="isOnlyHome"
                            checked={notaryFeesCalc.isOnlyHome === false}
                            onChange={() => setNotaryFeesCalc({...notaryFeesCalc, isOnlyHome: false})}
                            style={{width: '18px', height: '18px', cursor: 'pointer'}}
                          />
                          <span style={{fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.9)'}}>Non</span>
                        </label>
                      </div>
                    </div>

                    {/* Section 2: Type de droits */}
                    <div>
                      <h3 style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)', marginBottom: '12px', fontWeight: '600'}}>
                        Droit d'enregistrement ou TVA à l'achat ?
                      </h3>
                      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '8px'}}>
                          <input
                            type="radio"
                            name="registrationType"
                            checked={notaryFeesCalc.registrationType === 'registration'}
                            onChange={() => setNotaryFeesCalc({...notaryFeesCalc, registrationType: 'registration'})}
                            style={{width: '18px', height: '18px', cursor: 'pointer'}}
                          />
                          <span style={{fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.9)'}}>Achat avec droits d'enregistrement</span>
                        </label>
                        <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '8px'}}>
                          <input
                            type="radio"
                            name="registrationType"
                            checked={notaryFeesCalc.registrationType === 'vat'}
                            onChange={() => setNotaryFeesCalc({...notaryFeesCalc, registrationType: 'vat'})}
                            style={{width: '18px', height: '18px', cursor: 'pointer'}}
                          />
                          <span style={{fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.9)'}}>Achat avec TVA</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Brussels Abatement Section - Only show when Brussels region AND own home */}
                  {notaryFeesCalc.region === 'brussels' && notaryFeesCalc.isOnlyHome && notaryFeesCalc.purchasePrice <= 600000 && (
                    <div style={{marginBottom: '24px', marginTop: '24px'}}>
                      <h3 style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)', marginBottom: '12px', fontWeight: '600'}}>
                        Abattement
                      </h3>
                      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '8px'}}>
                          <input
                            type="radio"
                            name="brusselsAbatement"
                            checked={notaryFeesCalc.brusselsAbatement === true}
                            onChange={() => setNotaryFeesCalc({...notaryFeesCalc, brusselsAbatement: true})}
                            style={{width: '18px', height: '18px', cursor: 'pointer'}}
                          />
                          <span style={{fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.9)'}}>Abattement de 200,000 €</span>
                        </label>
                        <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '8px'}}>
                          <input
                            type="radio"
                            name="brusselsAbatement"
                            checked={notaryFeesCalc.brusselsAbatement === false}
                            onChange={() => setNotaryFeesCalc({...notaryFeesCalc, brusselsAbatement: false})}
                            style={{width: '18px', height: '18px', cursor: 'pointer'}}
                          />
                          <span style={{fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.9)'}}>Pas d'abattement / ne sais pas</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Results Section */}
                  <div>
                    <h2 className="section-title" style={{fontSize: '1.1em', marginBottom: '24px'}}>Résultats</h2>

                    <div style={{padding: '32px', background: 'rgba(59, 130, 246, 0.05)', borderRadius: '8px', border: '1px solid rgba(59, 130, 246, 0.2)'}}>
                      <p style={{textAlign: 'center', fontSize: '1em', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '32px', lineHeight: '1.6'}}>
                        Sur base du prix d'achat que vous avez indiqué, le total des coûts est évalué à : <strong style={{color: 'rgba(209, 178, 135, 0.95)', fontSize: '1.3em'}}>{formatNumber(notaryFeesBreakdown.total, 2)} €</strong>
                      </p>

                      <p style={{textAlign: 'center', fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.8)', marginBottom: '24px', fontStyle: 'italic'}}>
                        Ces montants sont donnés à titre indicatif. Date de mise à jour : {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </p>

                      {/* Result bars */}
                      <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                        {[
                          { label: 'Droits d\'enregistrement', value: notaryFeesBreakdown.registrationFees, color: '#8b5cf6' },
                          { label: 'Droit pour les annexes', value: notaryFeesBreakdown.annexFees, color: '#ec4899' },
                          { label: 'Honoraires', value: notaryFeesBreakdown.notaryFees, color: '#10b981' },
                          { label: 'Frais administratifs', value: notaryFeesBreakdown.adminFees, color: '#3b82f6' },
                          { label: 'Débours', value: notaryFeesBreakdown.disbursements, color: '#6b7280' },
                          { label: 'Transcription hypothécaire', value: notaryFeesBreakdown.mortgageTranscription, color: '#f59e0b' },
                          { label: 'Droit d\'écriture', value: notaryFeesBreakdown.writingFees, color: '#84cc16' },
                          { label: 'TVA', value: notaryFeesBreakdown.vat, color: '#ef4444' }
                        ].map((item, index) => {
                          const percentage = notaryFeesBreakdown.total > 0 ? (item.value / notaryFeesBreakdown.total) * 100 : 0;
                          return (
                            <div key={index} style={{display: 'flex', alignItems: 'center', gap: '20px'}}>
                              <div style={{minWidth: '220px', fontSize: '0.9em', color: item.color, fontWeight: '600'}}>
                                {item.label}
                              </div>
                              <div style={{minWidth: '120px', fontSize: '0.95em', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600', textAlign: 'right'}}>
                                {formatNumber(item.value, 2)} €
                              </div>
                              <div style={{flex: 1, height: '28px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', position: 'relative', overflow: 'hidden'}}>
                                <div style={{
                                  width: `${percentage}%`,
                                  height: '100%',
                                  background: item.color,
                                  borderRadius: '4px',
                                  transition: 'width 0.3s ease'
                                }}></div>
                              </div>
                              <div style={{minWidth: '60px', fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.7)', textAlign: 'right'}}>
                                {percentage.toFixed(1)}%
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            )}

            {/* Frais Hypothécaire Sub-tab */}
            {fraisSubTab === 'hypothecaire' && (
              <div>
                <h2 className="section-title">Calcul</h2>

                <div className="calculation-section" style={{width: '100%', margin: '0', padding: '0 24px'}}>

                  {/* Grid for 3 input fields */}
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '48px', marginBottom: '24px', marginTop: '32px'}}>

                    {/* Section: Montant du crédit */}
                    <div>
                      <h3 style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)', marginBottom: '12px', fontWeight: '600'}}>
                        Montant du crédit
                      </h3>
                      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <span style={{fontSize: '1em', color: 'rgba(255, 255, 255, 0.7)'}}>€</span>
                        <input
                          type="number"
                          step="1000"
                          min="0"
                          value={mortgageFeesCalc.loanAmount || ''}
                          onChange={(e) => setMortgageFeesCalc({...mortgageFeesCalc, loanAmount: parseFloat(e.target.value) || 0})}
                          style={{
                            flex: 1,
                            background: '#2a2a2a',
                            border: '1px solid #3a3a3a',
                            color: 'rgba(255, 255, 255, 0.9)',
                            padding: '12px',
                            borderRadius: '6px',
                            fontSize: '0.95em'
                          }}
                        />
                        <button
                          onClick={() => setMortgageFeesCalc({...mortgageFeesCalc, loanAmount: Math.max(0, mortgageFeesCalc.loanAmount - 1000)})}
                          style={{
                            background: '#2a2a2a',
                            border: '1px solid #3a3a3a',
                            color: 'rgba(255, 255, 255, 0.7)',
                            padding: '12px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '1.2em',
                            fontWeight: '600'
                          }}
                        >
                          −
                        </button>
                        <button
                          onClick={() => setMortgageFeesCalc({...mortgageFeesCalc, loanAmount: mortgageFeesCalc.loanAmount + 1000})}
                          style={{
                            background: '#2a2a2a',
                            border: '1px solid #3a3a3a',
                            color: 'rgba(255, 255, 255, 0.7)',
                            padding: '12px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '1.2em',
                            fontWeight: '600'
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Section: Montant principal de l'hypothèque */}
                    <div>
                      <h3 style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)', marginBottom: '12px', fontWeight: '600'}}>
                        Montant principal de l'hypothèque
                      </h3>
                      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <span style={{fontSize: '1em', color: 'rgba(255, 255, 255, 0.7)'}}>€</span>
                        <input
                          type="number"
                          step="1000"
                          min="0"
                          value={mortgageFeesCalc.mortgagePrincipal || ''}
                          onChange={(e) => setMortgageFeesCalc({...mortgageFeesCalc, mortgagePrincipal: parseFloat(e.target.value) || 0})}
                          style={{
                            flex: 1,
                            background: '#2a2a2a',
                            border: '1px solid #3a3a3a',
                            color: 'rgba(255, 255, 255, 0.9)',
                            padding: '12px',
                            borderRadius: '6px',
                            fontSize: '0.95em'
                          }}
                        />
                        <button
                          onClick={() => setMortgageFeesCalc({...mortgageFeesCalc, mortgagePrincipal: Math.max(0, mortgageFeesCalc.mortgagePrincipal - 1000)})}
                          style={{
                            background: '#2a2a2a',
                            border: '1px solid #3a3a3a',
                            color: 'rgba(255, 255, 255, 0.7)',
                            padding: '12px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '1.2em',
                            fontWeight: '600'
                          }}
                        >
                          −
                        </button>
                        <button
                          onClick={() => setMortgageFeesCalc({...mortgageFeesCalc, mortgagePrincipal: mortgageFeesCalc.mortgagePrincipal + 1000})}
                          style={{
                            background: '#2a2a2a',
                            border: '1px solid #3a3a3a',
                            color: 'rgba(255, 255, 255, 0.7)',
                            padding: '12px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '1.2em',
                            fontWeight: '600'
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Section: Accessoires de l'hypothèque */}
                    <div>
                      <h3 style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)', marginBottom: '12px', fontWeight: '600'}}>
                        Accessoires de l'hypothèque
                      </h3>
                      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <span style={{fontSize: '1em', color: 'rgba(255, 255, 255, 0.7)'}}>€</span>
                        <input
                          type="number"
                          step="1000"
                          min="0"
                          value={mortgageFeesCalc.mortgageAccessories || ''}
                          onChange={(e) => setMortgageFeesCalc({...mortgageFeesCalc, mortgageAccessories: parseFloat(e.target.value) || 0})}
                          style={{
                            flex: 1,
                            background: '#2a2a2a',
                            border: '1px solid #3a3a3a',
                            color: 'rgba(255, 255, 255, 0.9)',
                            padding: '12px',
                            borderRadius: '6px',
                            fontSize: '0.95em'
                          }}
                        />
                        <button
                          onClick={() => setMortgageFeesCalc({...mortgageFeesCalc, mortgageAccessories: Math.max(0, mortgageFeesCalc.mortgageAccessories - 1000)})}
                          style={{
                            background: '#2a2a2a',
                            border: '1px solid #3a3a3a',
                            color: 'rgba(255, 255, 255, 0.7)',
                            padding: '12px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '1.2em',
                            fontWeight: '600'
                          }}
                        >
                          −
                        </button>
                        <button
                          onClick={() => setMortgageFeesCalc({...mortgageFeesCalc, mortgageAccessories: mortgageFeesCalc.mortgageAccessories + 1000})}
                          style={{
                            background: '#2a2a2a',
                            border: '1px solid #3a3a3a',
                            color: 'rgba(255, 255, 255, 0.7)',
                            padding: '12px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '1.2em',
                            fontWeight: '600'
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Grid layout for 2 question sections */}
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '48px', marginBottom: '24px'}}>

                    {/* Section 1: Utilisation pour habitation unique */}
                    <div>
                      <h3 style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)', marginBottom: '12px', fontWeight: '600'}}>
                        Utilisez-vous un crédit pour financer l'achat de votre habitation unique ?
                      </h3>
                      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '8px'}}>
                          <input
                            type="radio"
                            name="isPrimaryResidence"
                            checked={mortgageFeesCalc.isPrimaryResidence === true}
                            onChange={() => setMortgageFeesCalc({...mortgageFeesCalc, isPrimaryResidence: true})}
                            style={{width: '18px', height: '18px', cursor: 'pointer'}}
                          />
                          <span style={{fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.9)'}}>Oui</span>
                        </label>
                        <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '8px'}}>
                          <input
                            type="radio"
                            name="isPrimaryResidence"
                            checked={mortgageFeesCalc.isPrimaryResidence === false}
                            onChange={() => setMortgageFeesCalc({...mortgageFeesCalc, isPrimaryResidence: false})}
                            style={{width: '18px', height: '18px', cursor: 'pointer'}}
                          />
                          <span style={{fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.9)'}}>Non</span>
                        </label>
                      </div>
                    </div>

                    {/* Section 2: Acte de crédit principal */}
                    <div>
                      <h3 style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)', marginBottom: '12px', fontWeight: '600'}}>
                        L'acte hypothécaire est-il le premier acte ?
                      </h3>
                      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '8px'}}>
                          <input
                            type="radio"
                            name="isMainDeed"
                            checked={mortgageFeesCalc.isMainDeed === true}
                            onChange={() => setMortgageFeesCalc({...mortgageFeesCalc, isMainDeed: true})}
                            style={{width: '18px', height: '18px', cursor: 'pointer'}}
                          />
                          <span style={{fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.9)'}}>Oui / ne sais pas</span>
                        </label>
                        <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '8px'}}>
                          <input
                            type="radio"
                            name="isMainDeed"
                            checked={mortgageFeesCalc.isMainDeed === false}
                            onChange={() => setMortgageFeesCalc({...mortgageFeesCalc, isMainDeed: false})}
                            style={{width: '18px', height: '18px', cursor: 'pointer'}}
                          />
                          <span style={{fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.9)'}}>Non</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Results Section */}
                  <div>
                    <h2 className="section-title" style={{fontSize: '1.1em', marginBottom: '24px'}}>Résultats</h2>

                    <div style={{padding: '32px', background: 'rgba(59, 130, 246, 0.05)', borderRadius: '8px', border: '1px solid rgba(59, 130, 246, 0.2)'}}>
                      <p style={{textAlign: 'center', fontSize: '1em', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '32px', lineHeight: '1.6'}}>
                        Le montant total des frais d'acte pour la somme empruntée est estimé à : <strong style={{color: 'rgba(209, 178, 135, 0.95)', fontSize: '1.3em'}}>{formatNumber(mortgageFeesBreakdown.total, 2)} €</strong>
                      </p>

                      <p style={{textAlign: 'center', fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.8)', marginBottom: '24px', fontStyle: 'italic'}}>
                        Ces montants sont donnés à titre indicatif. Date de mise à jour : {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </p>

                      {/* Result bars */}
                      <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                        {[
                          { label: 'Droits d\'enregistrement', value: mortgageFeesBreakdown.registrationTax, color: '#8b5cf6' },
                          { label: 'Droit pour les annexes', value: mortgageFeesBreakdown.appendixFee, color: '#ec4899' },
                          { label: 'Frais d\'hypothèque - Droit d\'hypothèque', value: mortgageFeesBreakdown.mortgageRight, color: '#a855f7' },
                          { label: 'Frais d\'hypothèque - Rétribution', value: mortgageFeesBreakdown.mortgageRemuneration, color: '#c084fc' },
                          { label: 'Honoraires', value: mortgageFeesBreakdown.notaryFees, color: '#10b981' },
                          { label: 'Frais administratifs', value: mortgageFeesBreakdown.adminFees, color: '#3b82f6' },
                          { label: 'Débours', value: mortgageFeesBreakdown.disbursements, color: '#6b7280' },
                          { label: 'Droit d\'écriture', value: mortgageFeesBreakdown.writingFee, color: '#84cc16' },
                          { label: 'TVA', value: mortgageFeesBreakdown.vat, color: '#ef4444' }
                        ].map((item, index) => {
                          const percentage = mortgageFeesBreakdown.total > 0 ? (item.value / mortgageFeesBreakdown.total) * 100 : 0;
                          return (
                            <div key={index} style={{display: 'flex', alignItems: 'center', gap: '20px'}}>
                              <div style={{minWidth: '280px', fontSize: '0.9em', color: item.color, fontWeight: '600'}}>
                                {item.label}
                              </div>
                              <div style={{minWidth: '120px', fontSize: '0.95em', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600', textAlign: 'right'}}>
                                {formatNumber(item.value, 2)} €
                              </div>
                              <div style={{flex: 1, height: '28px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', position: 'relative', overflow: 'hidden'}}>
                                <div style={{
                                  width: `${percentage}%`,
                                  height: '100%',
                                  background: item.color,
                                  borderRadius: '4px',
                                  transition: 'width 0.3s ease'
                                }}></div>
                              </div>
                              <div style={{minWidth: '60px', fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.7)', textAlign: 'right'}}>
                                {percentage.toFixed(1)}%
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            )}
          </div>
        )}

        {/* Sommaire Tab */}
        {activeTab === 'summary' && (
          <div style={{padding: '0 24px', width: '100%'}}>
            {/* Sub-tabs navigation - Sticky */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '24px',
              borderBottom: '1px solid rgba(58, 58, 58, 0.4)',
              position: 'sticky',
              top: 0,
              background: '#0a0a0a',
              zIndex: 100,
              paddingTop: '12px',
              paddingBottom: '0',
              marginLeft: '-24px',
              marginRight: '-24px',
              paddingLeft: '24px',
              paddingRight: '24px'
            }}>
              <button
                onClick={() => { setActiveTab('summary'); setError(''); }}
                style={{
                  padding: '12px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid rgba(209, 178, 135, 0.9)',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.95em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
              >
                Sommaire
              </button>
              <button
                onClick={() => { setActiveTab('current-finance'); setError(''); }}
                style={{
                  padding: '12px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.95em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = 'rgba(209, 178, 135, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = 'rgba(156, 163, 175, 0.7)';
                }}
              >
                Situation Financière
              </button>
              <button
                onClick={() => { setActiveTab('borrowing'); setError(''); }}
                style={{
                  padding: '12px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.95em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = 'rgba(209, 178, 135, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = 'rgba(156, 163, 175, 0.7)';
                }}
              >
                Capacité d'Emprunt
              </button>
              <div style={{ flex: 1 }}></div>
              <button
                onClick={generateEndettementPDF}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(209, 178, 135, 0.15)',
                  border: '1px solid rgba(209, 178, 135, 0.3)',
                  borderRadius: '6px',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.85em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.15)';
                }}
              >
                PDF Endettement
              </button>
            </div>

            {/* Section Déterminer votre capacité de financement - Collapsible */}
            <div
              onClick={() => setShowInfoSections(!showInfoSections)}
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                padding: showInfoSections ? '24px' : '12px 24px',
                borderRadius: '12px',
                border: '1px solid rgba(58, 58, 58, 0.5)',
                marginBottom: '24px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3 style={{ color: '#ffffff', fontSize: '1.1em', fontWeight: '600', margin: 0 }}>
                      Déterminer votre capacité de financement
                    </h3>
                    <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em', transition: 'transform 0.3s ease', transform: showInfoSections ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                  </div>
                  {showInfoSections && (
                    <>
                      <p style={{ color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.95em', lineHeight: '1.6', marginTop: '16px', marginBottom: '16px' }}>
                        Le montant que vous pouvez investir dépend de deux facteurs clés :
                      </p>
                      <ul style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '0.9em', lineHeight: '1.8', paddingLeft: '20px', marginBottom: '0' }}>
                        <li><strong style={{ color: '#ffffff' }}>Votre apport personnel</strong> — L'argent que vous pouvez mettre sur la table (épargne, héritage, vente d'un bien...)</li>
                        <li><strong style={{ color: '#ffffff' }}>Votre capacité d'emprunt</strong> — Le montant que la banque acceptera de vous prêter, basé sur vos revenus et votre taux d'endettement</li>
                      </ul>
                    </>
                  )}
            </div>

            <h2 className="section-title" style={{ marginBottom: '24px' }}>Résumé Financier</h2>

            <div className="summary-grid">
              <div className="summary-card">
                <div className="summary-card-header">Restant à Vivre</div>

                {results && results.max_monthly_payment !== undefined ? (
                  <>
                    <div className="summary-big-metric">
                      <div className="summary-big-value">
                        {formatNumber(totalRevenues + totalFutureRevenues - totalCharges - results.max_monthly_payment - totalFutureCharges, 2)} €
                      </div>
                      <div className="summary-big-label">Après Mensualité</div>
                    </div>
                    <div className="summary-hypotheses">
                      <div className="hypothesis-title">Hypothèses:</div>
                      <div className="hypothesis-item">
                        <span className="hypothesis-label">Revenus Totaux:</span>
                        <span className="hypothesis-value">{formatNumber(totalRevenues + totalFutureRevenues, 2)} €</span>
                      </div>
                      <div className="hypothesis-item">
                        <span className="hypothesis-label">Charges Totales:</span>
                        <span className="hypothesis-value">{formatNumber(totalCharges + totalFutureCharges, 2)} €</span>
                      </div>
                      <div className="hypothesis-item">
                        <span className="hypothesis-label">Mensualité Crédit:</span>
                        <span className="hypothesis-value">{formatNumber(results.max_monthly_payment, 2)} €</span>
                      </div>
                    </div>

                    <button
                      className="description-toggle"
                      onClick={() => toggleDescription('restantVivre')}
                    >
                      <span>Pourquoi les banques regardent ce critère ?</span>
                      <span className={`toggle-icon ${expandedDescriptions.restantVivre ? 'expanded' : ''}`}>▼</span>
                    </button>

                    {expandedDescriptions.restantVivre && (
                      <div className="card-description">
                        <p><strong>Pourquoi ce critère ?</strong> Les banques vérifient que vous disposez d'un montant suffisant après paiement du crédit pour couvrir vos dépenses quotidiennes (alimentation, transport, loisirs).</p>
                        <p><strong>Seuils indicatifs :</strong> Minimum 800-1000€ pour une personne seule, 1200-1500€ pour un couple, +300€ par enfant.</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="summary-empty">
                    <p>Disponible après calcul de la capacité d'emprunt</p>
                  </div>
                )}
              </div>

              <div className="summary-card">
                <div className="summary-card-header">Taux d'Endettement & Capacité d'Emprunt</div>

                {results && results.max_borrowing_capacity !== undefined ? (
                  <>
                    <div className="summary-big-metric">
                      <div className="summary-big-value">
                        {totalRevenues + totalFutureRevenues > 0
                          ? formatNumber((totalCharges + results.max_monthly_payment + totalFutureCharges) / (totalRevenues + totalFutureRevenues) * 100, 2)
                          : '0.00'}%
                      </div>
                      <div className="summary-big-label">Taux d'Endettement</div>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Capacité d'Emprunt Maximum:</span>
                      <span className="summary-value highlight-color">
                        {formatNumber(results.max_borrowing_capacity, 0)} €
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Mensualité Maximum:</span>
                      <span className="summary-value">
                        {formatNumber(results.max_monthly_payment, 2)} €
                      </span>
                    </div>
                    <div className="summary-hypotheses">
                      <div className="hypothesis-title">Hypothèses:</div>
                      <div className="hypothesis-item">
                        <span className="hypothesis-label">Taux d'Endettement:</span>
                        <span className="hypothesis-value">{loanParams.maxDebtRatio}%</span>
                      </div>
                      <div className="hypothesis-item">
                        <span className="hypothesis-label">Taux Annuel:</span>
                        <span className="hypothesis-value">{loanParams.annualRate}%</span>
                      </div>
                      <div className="hypothesis-item">
                        <span className="hypothesis-label">Durée:</span>
                        <span className="hypothesis-value">{loanParams.durationYears} ans</span>
                      </div>
                      {futureRevenues.length > 0 && (
                        <div className="hypothesis-item">
                          <span className="hypothesis-label">Revenus Futurs:</span>
                          <span className="hypothesis-value">
                            {futureRevenues.map(r => `${r.coefficient}%`).join(', ')}
                          </span>
                        </div>
                      )}
                      {futureCharges.length > 0 && (
                        <div className="hypothesis-item">
                          <span className="hypothesis-label">Charges Futures:</span>
                          <span className="hypothesis-value">
                            {futureCharges.map(c => `${c.coefficient}%`).join(', ')}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="capacity-comparison-table">
                      <div className="table-title">Capacité selon Taux d'Endettement</div>
                      <table className="comparison-table">
                        <thead>
                          <tr>
                            <th>Taux</th>
                            <th>Capacité Max</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[33, 35, 40, 50, 60, 70].map(ratio => {
                            const capacity = calculateCapacityAtRatio(ratio);
                            const isCurrentRatio = ratio === parseFloat(loanParams.maxDebtRatio);
                            return (
                              <tr key={ratio} className={isCurrentRatio ? 'current-ratio' : ''}>
                                <td className="ratio-cell">{ratio}%</td>
                                <td className="capacity-cell">{formatNumber(capacity, 0)} €</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="capacity-comparison-table">
                      <div className="table-title">Capacité selon Taux & Durée</div>
                      <table className="comparison-table double-entry-table">
                        <thead>
                          <tr>
                            <th className="corner-cell">Taux \ Durée</th>
                            {[15, 20, 25, 30].map(duration => (
                              <th key={duration} className={duration === parseFloat(loanParams.durationYears) ? 'current-column' : ''}>
                                {duration} ans
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            // Create dynamic rates array that includes current rate
                            const baseRates = [2.5, 3.0, 3.5, 4.0, 4.5];
                            const currentRate = parseFloat(loanParams.annualRate);

                            // Add current rate if it's valid and not already in base rates
                            let rates = [...baseRates];
                            if (!isNaN(currentRate) && currentRate > 0) {
                              const alreadyExists = baseRates.some(r => Math.abs(r - currentRate) < 0.01);
                              if (!alreadyExists) {
                                rates.push(currentRate);
                              }
                            }

                            // Sort rates in ascending order
                            rates.sort((a, b) => a - b);

                            return rates.map(rate => {
                              const isCurrentRate = Math.abs(rate - currentRate) < 0.01;
                              return (
                                <tr key={rate} className={isCurrentRate ? 'current-row' : ''}>
                                  <td className="ratio-cell">{rate.toFixed(1)}%</td>
                                  {[15, 20, 25, 30].map(duration => {
                                    const capacity = calculateCapacityWithRateAndDuration(rate, duration);
                                    const isCurrentCell = isCurrentRate && duration === parseFloat(loanParams.durationYears);
                                    return (
                                      <td key={duration} className={`capacity-cell ${isCurrentCell ? 'current-cell' : ''}`}>
                                        {formatNumber(capacity, 0)} €
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            });
                          })()}
                        </tbody>
                      </table>
                    </div>

                    <button
                      className="description-toggle"
                      onClick={() => toggleDescription('tauxEndettement')}
                    >
                      <span>Pourquoi les banques regardent ce critère ?</span>
                      <span className={`toggle-icon ${expandedDescriptions.tauxEndettement ? 'expanded' : ''}`}>▼</span>
                    </button>

                    {expandedDescriptions.tauxEndettement && (
                      <div className="card-description">
                        <p><strong>Pourquoi ce critère ?</strong> Le taux d'endettement mesure la part de vos revenus consacrée au remboursement de vos crédits. C'est le critère principal d'évaluation bancaire pour éviter le surendettement.</p>
                        <p><strong>Seuil réglementaire :</strong> Maximum 35% depuis 2022 (HCSF). Au-delà, le dossier peut être refusé sauf profil exceptionnel (hauts revenus, patrimoine important).</p>

                        <p><strong>Pourquoi ce calcul est complexe ?</strong> Pour calculer votre capacité d'emprunt maximum, nous devons résoudre une équation où la mensualité dépend du montant emprunté, mais le montant emprunté dépend aussi de la mensualité via le taux d'endettement. C'est un problème mathématique circulaire :</p>

                        <div className="math-explanation">
                          <div className="math-step">
                            <strong>1. Équation du taux d'endettement :</strong>
                            <div className="formula-text">(Charges + Mensualité) / Revenus = Taux d'endettement max (33-35%)</div>
                          </div>

                          <div className="math-step">
                            <strong>2. Formule de la mensualité :</strong>
                            <div className="formula-text">Mensualité = Montant × [Taux mensuel × (1 + Taux mensuel)^n] / [(1 + Taux mensuel)^n - 1]</div>
                            <div className="formula-detail">où n = nombre de mois, et le Taux mensuel = Taux annuel / 12</div>
                          </div>

                          <div className="math-step">
                            <strong>3. Résolution :</strong>
                            <div className="formula-text">Le calcul nécessite de trouver la mensualité maximale d'abord, puis de déduire le montant empruntable via la formule inverse du crédit amortissable.</div>
                          </div>
                        </div>

                        <p><strong>Exemple :</strong> Avec 3000€ de revenus, 33% de taux max, 500€ de charges, la mensualité max = (3000 × 0.33) - 500 = 490€. Puis avec un taux de 3.5% sur 25 ans, on calcule la capacité d'emprunt correspondante.</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="summary-empty">
                    <p>Calculez votre capacité d'emprunt dans l'onglet correspondant</p>
                  </div>
                )}
              </div>

              <div className="summary-card">
                <div className="summary-card-header">Quotité (LTV)</div>

                {results && results.max_borrowing_capacity !== undefined && quotiteParams.maxContribution && parseFloat(quotiteParams.maxContribution) > 0 ? (
                  <>
                    <div className="summary-big-metric">
                      <div className="summary-big-value">
                        {formatNumber((results.max_borrowing_capacity / (results.max_borrowing_capacity + parseFloat(quotiteParams.maxContribution))) * 100, 2)}%
                      </div>
                      <div className="summary-big-label">Loan-to-Value</div>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Prix Achat Maximum:</span>
                      <span className="summary-value">
                        {formatNumber(results.max_borrowing_capacity + parseFloat(quotiteParams.maxContribution), 0)} €
                      </span>
                    </div>
                    <div className="summary-hypotheses">
                      <div className="hypothesis-title">Hypothèses:</div>
                      <div className="hypothesis-item">
                        <span className="hypothesis-label">Apport:</span>
                        <span className="hypothesis-value">{formatNumber(parseFloat(quotiteParams.maxContribution), 0)} €</span>
                      </div>
                      <div className="hypothesis-item">
                        <span className="hypothesis-label">Emprunt:</span>
                        <span className="hypothesis-value">{formatNumber(results.max_borrowing_capacity, 0)} €</span>
                      </div>
                      <div className="hypothesis-item">
                        <span className="hypothesis-label">Taux Annuel:</span>
                        <span className="hypothesis-value">{loanParams.annualRate}%</span>
                      </div>
                      <div className="hypothesis-item">
                        <span className="hypothesis-label">Durée:</span>
                        <span className="hypothesis-value">{loanParams.durationYears} ans</span>
                      </div>
                    </div>

                    <div className="capacity-comparison-table">
                      <div className="table-title">Apport Requis selon LTV</div>
                      <table className="comparison-table">
                        <thead>
                          <tr>
                            <th>LTV</th>
                            <th>Apport Requis</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[100, 90, 85, 80, 70].map(ltv => {
                            const requiredApport = calculateRequiredApportAtLTV(ltv, results.max_borrowing_capacity);
                            const currentApport = parseFloat(quotiteParams.maxContribution) || 0;
                            const difference = requiredApport - currentApport;
                            const currentLTV = (results.max_borrowing_capacity / (results.max_borrowing_capacity + currentApport)) * 100;
                            const isCurrentLTV = Math.abs(ltv - currentLTV) < 1;

                            return (
                              <tr key={ltv} className={isCurrentLTV ? 'current-ratio' : ''}>
                                <td className="ratio-cell">{ltv}%</td>
                                <td className="capacity-cell">
                                  {ltv === 100 ? (
                                    <span style={{color: '#6b7894', fontStyle: 'italic'}}>N/A</span>
                                  ) : difference > 0 ? (
                                    <span style={{color: '#ef4444'}}>+{formatNumber(difference, 0)} €</span>
                                  ) : difference < -100 ? (
                                    <span style={{color: '#4ade80'}}>{formatNumber(Math.abs(difference), 0)} € en trop</span>
                                  ) : (
                                    <span style={{color: '#4ade80'}}>✓ Atteint</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <button
                      className="description-toggle"
                      onClick={() => toggleDescription('quotite')}
                    >
                      <span>Pourquoi les banques regardent ce critère ?</span>
                      <span className={`toggle-icon ${expandedDescriptions.quotite ? 'expanded' : ''}`}>▼</span>
                    </button>

                    {expandedDescriptions.quotite && (
                      <div className="card-description">
                        <p><strong>Pourquoi ce critère ?</strong> La quotité (Loan-to-Value) représente la part du bien financée par crédit. Plus votre apport est élevé, plus le risque bancaire diminue et meilleures sont vos conditions d'emprunt.</p>
                        <p><strong>Seuils bancaires :</strong> LTV max 80-85% en général, 90-100% possible pour primo-accédants avec garanties. Apport minimum recommandé : 10% du prix + frais de notaire (7-8%).</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="summary-empty">
                    {!results || results.max_borrowing_capacity === undefined ? (
                      <p>Calculez votre capacité d'emprunt d'abord</p>
                    ) : (
                      <p>Entrez votre apport ci-dessus</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {(activeTab === 'debt-ratio' || activeTab === 'quotite') && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '32px',
            padding: '24px',
            width: '100%'
          }}>
            {/* Left Side - Revenues */}
            <div>
              <h2 style={{
                color: '#ffffff',
                fontSize: '1.1em',
                fontWeight: '600',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(58, 58, 58, 0.6)'
              }}>
                Revenus Mensuels
              </h2>

              <div style={{display: 'grid', gap: '10px'}}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingBottom: '10px',
                  borderBottom: '1px solid rgba(58, 58, 58, 0.3)'
                }}>
                  <span style={{color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em'}}>
                    Total Revenus Actuels
                  </span>
                  <span style={{color: '#10b981', fontSize: '0.9em', fontWeight: '600'}}>
                    {totalRevenues.toFixed(2)} €
                  </span>
                </div>
              </div>
            </div>

            {/* Right Side - Charges */}
            <div>
              <h2 style={{
                color: '#ffffff',
                fontSize: '1.1em',
                fontWeight: '600',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(58, 58, 58, 0.6)'
              }}>
                Charges Mensuelles
              </h2>

              <div style={{display: 'grid', gap: '10px'}}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingBottom: '10px',
                  borderBottom: '1px solid rgba(58, 58, 58, 0.3)'
                }}>
                  <span style={{color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em'}}>
                    Total Charges Actuelles
                  </span>
                  <span style={{color: '#ef4444', fontSize: '0.9em', fontWeight: '600'}}>
                    {totalCharges.toFixed(2)} €
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Calculation Section */}
        {activeTab === 'debt-ratio' && (
          <div className="calculation-section">
            <h2 className="section-title">Calcul du Taux d'Endettement</h2>

            <div className="formula-display">
              <div className="formula">
                <div className="fraction">
                  <div className="numerator">{formatNumber(totalCharges, 2)} €</div>
                  <div className="division-line"></div>
                  <div className="denominator">{formatNumber(totalRevenues, 2)} €</div>
                </div>
                <span className="equals">=</span>
                <span className="result-preview">
                  {totalRevenues > 0 ? formatNumber((totalCharges / totalRevenues) * 100, 2) : '0.00'}%
                </span>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button onClick={calculateDebtRatio} disabled={loading} className="primary-button">
              {loading ? 'Calcul en cours...' : 'Calculer le Taux'}
            </button>

            {results && results.debt_ratio !== undefined && (
              <div className="result-panel">
                <h3>Résultat</h3>
                <div className="debt-ratio-result">
                  <div className="big-metric">
                    <div className="metric-value">{formatNumber(results.debt_ratio || 0, 2)}%</div>
                    <div className="metric-label">Taux d'Endettement</div>
                  </div>
                  <div className={`status-message ${(results.debt_ratio || 0) > 33 ? 'warning' : 'success'}`}>
                    {(results.debt_ratio || 0) > 33
                      ? '⚠️ Taux supérieur à 33% - Risque de refus bancaire'
                      : '✓ Taux acceptable pour un prêt bancaire'}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'borrowing' && (
          <div style={{padding: '0 24px', width: '100%'}}>
            {/* Sub-tabs navigation - Sticky */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '24px',
              borderBottom: '1px solid rgba(58, 58, 58, 0.4)',
              position: 'sticky',
              top: 0,
              background: '#0a0a0a',
              zIndex: 100,
              paddingTop: '12px',
              paddingBottom: '0',
              marginLeft: '-24px',
              marginRight: '-24px',
              paddingLeft: '24px',
              paddingRight: '24px'
            }}>
              <button
                onClick={() => { setActiveTab('summary'); setError(''); }}
                style={{
                  padding: '12px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.95em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = 'rgba(209, 178, 135, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = 'rgba(156, 163, 175, 0.7)';
                }}
              >
                Sommaire
              </button>
              <button
                onClick={() => { setActiveTab('current-finance'); setError(''); }}
                style={{
                  padding: '12px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.95em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = 'rgba(209, 178, 135, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = 'rgba(156, 163, 175, 0.7)';
                }}
              >
                Situation Financière
              </button>
              <button
                onClick={() => { setActiveTab('borrowing'); setError(''); }}
                style={{
                  padding: '12px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid rgba(209, 178, 135, 0.9)',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.95em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
              >
                Capacité d'Emprunt
              </button>
              <div style={{ flex: 1 }}></div>
              <button
                onClick={generateEndettementPDF}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(209, 178, 135, 0.15)',
                  border: '1px solid rgba(209, 178, 135, 0.3)',
                  borderRadius: '6px',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.85em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.15)';
                }}
              >
                PDF Endettement
              </button>
            </div>

            {/* Pondération Revenus Futurs - Top of page */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px',
              padding: '8px 12px',
              background: 'rgba(34, 197, 94, 0.08)',
              borderRadius: '6px',
              border: '1px solid rgba(34, 197, 94, 0.2)',
              width: 'fit-content'
            }}>
              <label style={{
                color: 'rgba(34, 197, 94, 0.9)',
                fontSize: '0.8em',
                fontWeight: '600'
              }}>
                Pondération Revenus Futurs
              </label>
              <input
                type="number"
                value={futureRevenuesPonderation}
                onChange={(e) => setFutureRevenuesPonderation(parseFloat(e.target.value) || 0)}
                min="0"
                max="100"
                style={{
                  background: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  color: 'rgba(34, 197, 94, 1)',
                  fontSize: '0.85em',
                  fontWeight: '700',
                  textAlign: 'right',
                  width: '60px',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.background = 'rgba(34, 197, 94, 0.15)';
                  e.target.style.borderColor = 'rgba(34, 197, 94, 0.5)';
                }}
                onBlur={(e) => {
                  e.target.style.background = 'rgba(34, 197, 94, 0.1)';
                  e.target.style.borderColor = 'rgba(34, 197, 94, 0.3)';
                }}
              />
              <span style={{
                color: 'rgba(34, 197, 94, 0.9)',
                fontSize: '0.8em'
              }}>%</span>
            </div>

            {/* Revenus and Charges Summary Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
              gap: '32px',
              marginBottom: '24px'
            }}>
              {/* Left Side - Revenues */}
              <div>
                <h2 style={{
                  color: '#ffffff',
                  fontSize: '1.1em',
                  fontWeight: '600',
                  marginBottom: '16px',
                  paddingBottom: '12px',
                  borderBottom: '1px solid rgba(58, 58, 58, 0.6)'
                }}>
                  Revenus Mensuels
                </h2>

                <div style={{display: 'grid', gap: '10px'}}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: '10px',
                    borderBottom: '1px solid rgba(58, 58, 58, 0.3)'
                  }}>
                    <span style={{color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em'}}>
                      Total Revenus Actuels
                    </span>
                    <span style={{color: '#10b981', fontSize: '0.9em', fontWeight: '600'}}>
                      {totalRevenues.toFixed(2)} €
                    </span>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: '10px',
                    borderBottom: '1px solid rgba(58, 58, 58, 0.3)'
                  }}>
                    <span style={{color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em'}}>
                      Total Revenus Futurs
                    </span>
                    <span style={{color: '#10b981', fontSize: '0.9em', fontWeight: '600'}}>
                      +{totalFutureRevenues.toFixed(2)} €
                    </span>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    borderBottom: '2px solid rgba(16, 185, 129, 0.3)'
                  }}>
                    <span style={{color: '#ffffff', fontSize: '0.9em', fontWeight: '600'}}>
                      TOTAL REVENUS
                    </span>
                    <span style={{color: '#10b981', fontSize: '1em', fontWeight: '700'}}>
                      {(totalRevenues + totalFutureRevenues).toFixed(2)} €
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Side - Charges */}
              <div>
                <h2 style={{
                  color: '#ffffff',
                  fontSize: '1.1em',
                  fontWeight: '600',
                  marginBottom: '16px',
                  paddingBottom: '12px',
                  borderBottom: '1px solid rgba(58, 58, 58, 0.6)'
                }}>
                  Charges Mensuelles
                </h2>

                <div style={{display: 'grid', gap: '10px'}}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: '10px',
                    borderBottom: '1px solid rgba(58, 58, 58, 0.3)'
                  }}>
                    <span style={{color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em'}}>
                      Total Charges Actuelles
                    </span>
                    <span style={{color: '#ef4444', fontSize: '0.9em', fontWeight: '600'}}>
                      {totalCharges.toFixed(2)} €
                    </span>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: '10px',
                    borderBottom: '1px solid rgba(58, 58, 58, 0.3)'
                  }}>
                    <span style={{color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em', display: 'flex', alignItems: 'center', gap: '8px'}}>
                      Total Charges Futures
                      <span style={{
                        color: '#fbbf24',
                        fontSize: '0.75em',
                        fontWeight: '500',
                        padding: '2px 6px',
                        background: 'rgba(251, 191, 36, 0.1)',
                        borderRadius: '4px',
                        border: '1px solid rgba(251, 191, 36, 0.2)'
                      }}>
                        inconnue
                      </span>
                    </span>
                    <span style={{color: '#fbbf24', fontSize: '0.9em', fontWeight: '600'}}>
                      {results && results.max_monthly_payment !== undefined
                        ? `+${(results.max_monthly_payment + totalFutureCharges).toFixed(2)} €`
                        : `+X + ${totalFutureCharges.toFixed(2)} €`}
                    </span>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    borderBottom: '2px solid rgba(239, 68, 68, 0.3)'
                  }}>
                    <span style={{color: '#ffffff', fontSize: '0.9em', fontWeight: '600'}}>
                      TOTAL CHARGES
                    </span>
                    <span style={{color: '#ef4444', fontSize: '1em', fontWeight: '700'}}>
                      {results && results.max_monthly_payment !== undefined
                        ? `${(totalCharges + results.max_monthly_payment + totalFutureCharges).toFixed(2)} €`
                        : `${totalCharges.toFixed(2)} + X + ${totalFutureCharges.toFixed(2)} €`}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
              <h2 className="section-title" style={{marginBottom: 0, fontSize: '1.6em'}}>Calcul de la Capacité d'Emprunt</h2>
              <button
                onClick={() => {
                  setActiveTab('current-finance');
                  setError('');
                }}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(59, 130, 246, 0.15)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '6px',
                  color: '#60a5fa',
                  fontSize: '0.9em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(59, 130, 246, 0.25)';
                  e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(59, 130, 246, 0.15)';
                  e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                }}
                title="Aller à Situation Financière pour modifier les revenus et charges"
              >
                Modifier Revenus/Charges
              </button>
            </div>

            {/* Two Column Layout: Parameters and Formula */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '20px',
              marginBottom: '16px'
            }}>
              {/* Left Side - Paramètres du Prêt */}
              <div>
                <h3 style={{
                  color: '#ffffff',
                  fontSize: '1.2em',
                  fontWeight: '600',
                  marginBottom: '10px',
                  paddingBottom: '8px',
                  borderBottom: '1px solid rgba(58, 58, 58, 0.6)'
                }}>
                  Paramètres du Prêt
                </h3>

                <div style={{display: 'grid', gap: '4px'}}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: '6px',
                    borderBottom: '1px solid rgba(58, 58, 58, 0.3)'
                  }}>
                    <label style={{
                      color: 'rgba(156, 163, 175, 0.7)',
                      fontSize: '1em'
                    }}>
                      Taux Annuel (%)
                    </label>
                    <input
                      type="number"
                      name="annualRate"
                      value={loanParams.annualRate}
                      onChange={handleLoanParamChange}
                      placeholder="Ex: 3.5"
                      step="0.01"
                      min="0"
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        color: '#ffffff',
                        fontSize: '1.1em',
                        fontWeight: '600',
                        textAlign: 'right',
                        width: '80px',
                        outline: 'none',
                        transition: 'all 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                      }}
                      onBlur={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      }}
                    />
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: '6px',
                    borderBottom: '1px solid rgba(58, 58, 58, 0.3)'
                  }}>
                    <label style={{
                      color: 'rgba(156, 163, 175, 0.7)',
                      fontSize: '1em'
                    }}>
                      Durée (années)
                    </label>
                    <input
                      type="number"
                      name="durationYears"
                      value={loanParams.durationYears}
                      onChange={handleLoanParamChange}
                      placeholder="Ex: 20"
                      min="1"
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        color: '#ffffff',
                        fontSize: '1.1em',
                        fontWeight: '600',
                        textAlign: 'right',
                        width: '80px',
                        outline: 'none',
                        transition: 'all 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                      }}
                      onBlur={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      }}
                    />
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: '6px',
                    borderBottom: '1px solid rgba(58, 58, 58, 0.3)'
                  }}>
                    <label style={{
                      color: 'rgba(156, 163, 175, 0.7)',
                      fontSize: '1em'
                    }}>
                      Taux d'Endettement Max (%)
                    </label>
                    <input
                      type="number"
                      name="maxDebtRatio"
                      value={loanParams.maxDebtRatio}
                      onChange={handleLoanParamChange}
                      step="0.1"
                      min="0"
                      max="100"
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        color: '#ffffff',
                        fontSize: '1.1em',
                        fontWeight: '600',
                        textAlign: 'right',
                        width: '80px',
                        outline: 'none',
                        transition: 'all 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                      }}
                      onBlur={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      }}
                    />
                  </div>
                </div>

              </div>

              {/* Right Side - Formule */}
              <div>
                <h3 style={{
                  color: '#ffffff',
                  fontSize: '1.2em',
                  fontWeight: '600',
                  marginBottom: '10px',
                  paddingBottom: '8px',
                  borderBottom: '1px solid rgba(58, 58, 58, 0.6)'
                }}>
                  Formule
                </h3>

                <div style={{
                  padding: '12px',
                  background: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: '6px',
                  border: '1px solid rgba(58, 58, 58, 0.3)'
                }}>
                  <div style={{textAlign: 'center', color: 'rgba(156, 163, 175, 0.9)', fontSize: '1em', marginBottom: '8px'}}>
                    Taux d'Endettement = Charges / Revenus = {loanParams.maxDebtRatio}%
                  </div>
                  <div style={{textAlign: 'center', fontSize: '1.1em', color: '#ffffff'}}>
                    <div>
                      ({totalCharges > 0 ? `${formatNumber(totalCharges, 2)}€ + ` : ''}<span style={{color: '#fbbf24', fontWeight: '700'}}>X</span>{totalFutureCharges > 0 ? ` + ${formatNumber(totalFutureCharges, 2)}€` : ''})
                    </div>
                    <div style={{margin: '4px 0', color: 'rgba(156, 163, 175, 0.5)'}}>÷</div>
                    <div>
                      ({totalRevenues > 0 ? `${formatNumber(totalRevenues, 2)}€` : ''}{totalRevenues > 0 && totalFutureRevenues > 0 ? ' + ' : ''}{totalFutureRevenues > 0 ? `${formatNumber(totalFutureRevenues, 2)}€` : ''})
                    </div>
                    <div style={{margin: '6px 0', color: 'rgba(156, 163, 175, 0.5)'}}>=</div>
                    <div style={{fontSize: '1.2em', fontWeight: '600'}}>{loanParams.maxDebtRatio}%</div>
                  </div>
                  <div style={{textAlign: 'center', marginTop: '10px', fontSize: '1em', color: 'rgba(156, 163, 175, 0.7)'}}>
                    <span style={{color: '#fbbf24', fontWeight: '700'}}>X</span> = Mensualité du crédit recherchée
                  </div>
                </div>
              </div>
            </div>

            {error && <div className="error-message" style={{marginBottom: '10px'}}>{error}</div>}


            {results && results.max_borrowing_capacity !== undefined && (
              <div style={{
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '6px',
                border: '1px solid rgba(58, 58, 58, 0.3)'
              }}>
                <h3 style={{
                  color: '#ffffff',
                  fontSize: '1.3em',
                  fontWeight: '600',
                  marginBottom: '10px',
                  paddingBottom: '8px',
                  borderBottom: '1px solid rgba(58, 58, 58, 0.6)'
                }}>
                  Résultats
                </h3>

                <div style={{display: 'grid', gap: '10px'}}>
                  {/* 1) Mensualité Maximum with formula */}
                  <div style={{
                    padding: '10px',
                    background: 'rgba(251, 191, 36, 0.08)',
                    borderRadius: '6px',
                    border: '1px solid rgba(251, 191, 36, 0.2)'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <span style={{color: 'rgba(251, 191, 36, 0.9)', fontSize: '1.1em', fontWeight: '600'}}>
                        1) Mensualité Maximum
                      </span>
                      <span style={{color: '#fbbf24', fontSize: '1.5em', fontWeight: '700'}}>
                        {formatNumber(results.max_monthly_payment || 0, 2)} €
                      </span>
                    </div>
                    <div style={{
                      fontSize: '0.95em',
                      color: 'rgba(156, 163, 175, 0.8)',
                      textAlign: 'center',
                      padding: '6px',
                      background: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '4px'
                    }}>
                      <div style={{marginBottom: '4px'}}>
                        <span style={{color: '#ef4444'}}>
                          ({totalCharges > 0 ? `${formatNumber(totalCharges, 2)}€ + ` : ''}
                        </span>
                        <span style={{color: '#fbbf24', fontWeight: '700'}}>{formatNumber(results.max_monthly_payment || 0, 2)}€</span>
                        <span style={{color: '#ef4444'}}>
                          {totalFutureCharges > 0 ? ` + ${formatNumber(totalFutureCharges, 2)}€` : ''})
                        </span>
                        <span style={{color: 'rgba(156, 163, 175, 0.6)'}}> ÷ </span>
                        <span style={{color: '#10b981'}}>({formatNumber(totalRevenues + totalFutureRevenues, 2)}€)</span>
                        <span style={{color: 'rgba(156, 163, 175, 0.6)'}}> = </span>
                        <span style={{color: '#ffffff', fontWeight: '600'}}>{loanParams.maxDebtRatio}%</span>
                      </div>
                      <div style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.6)'}}>
                        Σ Charges / Σ Revenus = Taux d'Endettement Max
                      </div>
                    </div>
                  </div>

                  {/* 2) Capacité d'Emprunt Maximum with explanation */}
                  <div style={{
                    padding: '10px',
                    background: 'rgba(209, 178, 135, 0.08)',
                    borderRadius: '6px',
                    border: '1px solid rgba(209, 178, 135, 0.2)'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <span style={{color: 'rgba(209, 178, 135, 0.9)', fontSize: '1.1em', fontWeight: '600'}}>
                        2) Capacité d'Emprunt Maximum
                      </span>
                      <span style={{color: 'rgba(209, 178, 135, 0.95)', fontSize: '1.6em', fontWeight: '700'}}>
                        {formatNumber(results.max_borrowing_capacity || 0, 0)} €
                      </span>
                    </div>
                    <div style={{
                      fontSize: '0.95em',
                      color: 'rgba(156, 163, 175, 0.8)',
                      padding: '6px',
                      background: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '4px'
                    }}>
                      <div>
                        Avec une mensualité de <span style={{color: '#fbbf24', fontWeight: '600'}}>{formatNumber(results.max_monthly_payment || 0, 2)} €</span>,
                        une durée de <span style={{color: '#ffffff', fontWeight: '600'}}>{loanParams.durationYears} ans</span> et
                        un taux annuel de <span style={{color: '#ffffff', fontWeight: '600'}}>{loanParams.annualRate}%</span>,
                        vous pouvez emprunter <span style={{color: 'rgba(209, 178, 135, 0.95)', fontWeight: '700'}}>{formatNumber(results.max_borrowing_capacity || 0, 0)} €</span>
                      </div>
                    </div>
                  </div>

                  {/* Additional info - inline */}
                  <div style={{display: 'flex', gap: '16px', marginTop: '4px', fontSize: '1em'}}>
                    <div style={{color: 'rgba(156, 163, 175, 0.7)'}}>
                      Coût Total: <span style={{color: '#ffffff', fontWeight: '600'}}>{formatNumber(results.total_cost || 0, 0)} €</span>
                    </div>
                    <div style={{color: 'rgba(156, 163, 175, 0.7)'}}>
                      Intérêts: <span style={{color: '#ffffff', fontWeight: '600'}}>{formatNumber(results.total_interest || 0, 0)} €</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Sensitivity Analysis Chart - Borrowing Capacity vs Debt Ratio */}
            <div style={{marginTop: '16px'}}>
              <h3 style={{
                color: 'rgba(209, 216, 224, 0.9)',
                fontSize: '1.1em',
                marginBottom: '15px',
                fontWeight: '600',
                borderBottom: '1px solid rgba(58, 58, 58, 0.6)',
                paddingBottom: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => setCollapsedSections(prev => ({
                ...prev,
                borrowingSensitivity: !prev.borrowingSensitivity
              }))}
              >
                <span>Sensibilité - Capacité d'Emprunt</span>
                <span style={{
                  fontSize: '0.8em',
                  transition: 'transform 0.3s ease',
                  transform: collapsedSections.borrowingSensitivity ? 'rotate(0deg)' : 'rotate(180deg)'
                }}>▼</span>
              </h3>

              {!collapsedSections.borrowingSensitivity && (
                <div className="summary-card">
                  {/* Parameter selector dropdown */}
                  <div style={{marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <span style={{color: 'rgba(209, 216, 224, 0.7)', fontSize: '0.9em'}}>Paramètre (X):</span>
                    <select
                      value={borrowingSensitivityParam}
                      onChange={(e) => setBorrowingSensitivityParam(e.target.value)}
                      style={{
                        padding: '8px 12px',
                        background: '#1a1a1a',
                        border: '1px solid #2a2a2a',
                        borderRadius: '8px',
                        color: 'rgba(209, 216, 224, 0.9)',
                        fontSize: '0.9em',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="debtRatio">Taux d'Endettement (%)</option>
                      <option value="duration">Durée (années)</option>
                      <option value="annualRate">Taux Annuel (%)</option>
                      <option value="revenues">Revenus Mensuels (€)</option>
                    </select>
                  </div>

                  {(() => {
                    // Calculate borrowing capacity for different parameter values
                    const calculateBorrowingCapacityForParam = (paramType, paramValue) => {
                      const baseRevenues = totalRevenues + totalFutureRevenues;
                      const baseCharges = totalCharges + totalFutureCharges;
                      const baseAnnualRate = parseFloat(loanParams.annualRate) || 3.5;
                      const baseDurationYears = parseInt(loanParams.durationYears) || 20;
                      const baseDebtRatio = parseFloat(loanParams.maxDebtRatio) || 35;

                      let futureRevenuesTotal = baseRevenues;
                      let futureChargesTotal = baseCharges;
                      let annualRate = baseAnnualRate;
                      let durationYears = baseDurationYears;
                      let debtRatioPercent = baseDebtRatio;

                      if (paramType === 'debtRatio') {
                        debtRatioPercent = paramValue;
                      } else if (paramType === 'duration') {
                        durationYears = paramValue;
                      } else if (paramType === 'annualRate') {
                        annualRate = paramValue;
                      } else if (paramType === 'revenues') {
                        futureRevenuesTotal = paramValue;
                      }

                      if (futureRevenuesTotal <= 0) return 0;

                      // Max monthly payment based on debt ratio
                      const maxMonthlyPayment = (futureRevenuesTotal * (debtRatioPercent / 100)) - futureChargesTotal;

                      if (maxMonthlyPayment <= 0) return 0;

                      // Calculate borrowing capacity using loan formula
                      const monthlyRate = annualRate / 100 / 12;
                      const numPayments = durationYears * 12;

                      if (monthlyRate === 0) {
                        return maxMonthlyPayment * numPayments;
                      }

                      // P = PMT * [(1 - (1 + r)^-n) / r]
                      const borrowingCapacity = maxMonthlyPayment *
                        ((1 - Math.pow(1 + monthlyRate, -numPayments)) / monthlyRate);

                      return Math.max(0, borrowingCapacity);
                    };

                    // Generate data points based on selected parameter
                    const dataPoints = [];
                    let paramLabel = '';
                    let paramUnit = '';
                    let minX = 0;
                    let maxX = 100;
                    let currentX = 0;

                    if (borrowingSensitivityParam === 'debtRatio') {
                      paramLabel = "Taux d'Endettement";
                      paramUnit = '%';
                      minX = 10;
                      maxX = 90;
                      currentX = parseFloat(loanParams.maxDebtRatio) || 35;
                      for (let ratio = 10; ratio <= 90; ratio += 5) {
                        const capacity = calculateBorrowingCapacityForParam('debtRatio', ratio);
                        dataPoints.push({ x: ratio, y: capacity });
                      }
                    } else if (borrowingSensitivityParam === 'duration') {
                      paramLabel = 'Durée';
                      paramUnit = ' ans';
                      minX = 5;
                      maxX = 30;
                      currentX = parseInt(loanParams.durationYears) || 20;
                      for (let years = 5; years <= 30; years += 1) {
                        const capacity = calculateBorrowingCapacityForParam('duration', years);
                        dataPoints.push({ x: years, y: capacity });
                      }
                    } else if (borrowingSensitivityParam === 'annualRate') {
                      paramLabel = 'Taux Annuel';
                      paramUnit = '%';
                      minX = 0;
                      maxX = 8;
                      currentX = parseFloat(loanParams.annualRate) || 3.5;
                      for (let rate = 0; rate <= 8; rate += 0.5) {
                        const capacity = calculateBorrowingCapacityForParam('annualRate', rate);
                        dataPoints.push({ x: rate, y: capacity });
                      }
                    } else if (borrowingSensitivityParam === 'revenues') {
                      paramLabel = 'Revenus Mensuels';
                      paramUnit = '€';
                      const baseRevenues = totalRevenues + totalFutureRevenues;
                      minX = Math.max(1000, Math.floor(baseRevenues * 0.5 / 1000) * 1000);
                      maxX = Math.ceil(baseRevenues * 2 / 1000) * 1000;
                      currentX = baseRevenues;
                      const step = Math.max(500, Math.round((maxX - minX) / 20 / 500) * 500);
                      for (let rev = minX; rev <= maxX; rev += step) {
                        const capacity = calculateBorrowingCapacityForParam('revenues', rev);
                        dataPoints.push({ x: rev, y: capacity });
                      }
                    }

                    // Filter out invalid values
                    const validPoints = dataPoints.filter(d => isFinite(d.y) && !isNaN(d.y));

                    if (validPoints.length === 0) {
                      return (
                        <div style={{padding: '40px', textAlign: 'center', color: 'rgba(209, 216, 224, 0.5)'}}>
                          Données insuffisantes pour le calcul (ajoutez des revenus)
                        </div>
                      );
                    }

                    // Chart bounds
                    const minY = Math.min(...validPoints.map(d => d.y));
                    const maxY = Math.max(...validPoints.map(d => d.y));
                    const yRange = maxY - minY || 1;
                    const paddedMinY = Math.max(0, minY - yRange * 0.1);
                    const paddedMaxY = maxY + yRange * 0.1;

                    const chartMinX = Math.min(...validPoints.map(d => d.x));
                    const chartMaxX = Math.max(...validPoints.map(d => d.x));

                    // Chart dimensions - Full width responsive
                    const chartWidth = 1600;
                    const chartHeight = 400;
                    const padding = { top: 40, right: 40, bottom: 60, left: 120 };
                    const plotWidth = chartWidth - padding.left - padding.right;
                    const plotHeight = chartHeight - padding.top - padding.bottom;

                    // Scale functions
                    const scaleX = (val) => padding.left + ((val - chartMinX) / (chartMaxX - chartMinX)) * plotWidth;
                    const scaleY = (val) => padding.top + plotHeight - ((val - paddedMinY) / (paddedMaxY - paddedMinY)) * plotHeight;

                    // Generate path
                    const pathData = validPoints.map((d, i) => {
                      const x = scaleX(d.x);
                      const y = scaleY(d.y);
                      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                    }).join(' ');

                    // Y-axis ticks
                    const yTicks = [];
                    const yTickCount = 6;
                    for (let i = 0; i <= yTickCount; i++) {
                      const val = paddedMinY + (paddedMaxY - paddedMinY) * (i / yTickCount);
                      yTicks.push(val);
                    }

                    // X-axis ticks
                    const xTicks = validPoints.map(d => d.x);

                    // Current value marker
                    const currentCapacity = calculateBorrowingCapacityForParam(borrowingSensitivityParam, currentX);
                    const currentXPos = scaleX(currentX);
                    const currentYPos = scaleY(currentCapacity);

                    return (
                      <div style={{width: '100%', position: 'relative'}}>
                        <svg
                          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                          preserveAspectRatio="xMidYMid meet"
                          style={{display: 'block', width: '100%', height: 'auto', maxHeight: '500px'}}
                          onMouseLeave={() => setBorrowingHoveredPoint(null)}
                        >
                          {/* Gradient fill under line */}
                          <defs>
                            <linearGradient id="borrowingGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#d1b287" stopOpacity="0.3" />
                              <stop offset="100%" stopColor="#d1b287" stopOpacity="0.05" />
                            </linearGradient>
                          </defs>

                          {/* Area fill */}
                          <path
                            d={`${pathData} L ${scaleX(validPoints[validPoints.length - 1].x)} ${chartHeight - padding.bottom} L ${scaleX(validPoints[0].x)} ${chartHeight - padding.bottom} Z`}
                            fill="url(#borrowingGradient)"
                          />

                          {/* Grid lines */}
                          {yTicks.map((tick, i) => (
                            <line
                              key={`grid-y-${i}`}
                              x1={padding.left}
                              y1={scaleY(tick)}
                              x2={chartWidth - padding.right}
                              y2={scaleY(tick)}
                              stroke="rgba(209, 216, 224, 0.1)"
                              strokeDasharray="4,4"
                            />
                          ))}

                          {/* 35% reference line (standard French limit) - only for debt ratio */}
                          {borrowingSensitivityParam === 'debtRatio' && (
                            <>
                              <line
                                x1={scaleX(35)}
                                y1={padding.top}
                                x2={scaleX(35)}
                                y2={chartHeight - padding.bottom}
                                stroke="rgba(248, 113, 113, 0.5)"
                                strokeWidth="2"
                                strokeDasharray="6,4"
                              />
                              <text
                                x={scaleX(35)}
                                y={padding.top - 10}
                                textAnchor="middle"
                                fill="rgba(248, 113, 113, 0.8)"
                                fontSize="11"
                              >
                                Limite 35%
                              </text>
                            </>
                          )}

                          {/* Line chart */}
                          <path
                            d={pathData}
                            fill="none"
                            stroke="#d1b287"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />

                          {/* Data points - interactive */}
                          {validPoints.map((d, i) => (
                            <g key={`point-group-${i}`}>
                              {/* Larger invisible hit area for better hover */}
                              <circle
                                cx={scaleX(d.x)}
                                cy={scaleY(d.y)}
                                r="15"
                                fill="transparent"
                                style={{cursor: 'pointer'}}
                                onMouseEnter={(e) => setBorrowingHoveredPoint({ index: i, x: d.x, y: d.y, screenX: e.clientX, screenY: e.clientY })}
                                onMouseMove={(e) => setBorrowingHoveredPoint({ index: i, x: d.x, y: d.y, screenX: e.clientX, screenY: e.clientY })}
                              />
                              {/* Visible point */}
                              <circle
                                cx={scaleX(d.x)}
                                cy={scaleY(d.y)}
                                r={borrowingHoveredPoint?.index === i ? 8 : 5}
                                fill={borrowingHoveredPoint?.index === i ? '#e8c89e' : '#d1b287'}
                                stroke="#1a1a1a"
                                strokeWidth="2"
                                style={{transition: 'r 0.15s ease, fill 0.15s ease', pointerEvents: 'none'}}
                              />
                            </g>
                          ))}

                          {/* Hover vertical line */}
                          {borrowingHoveredPoint && (
                            <line
                              x1={scaleX(borrowingHoveredPoint.x)}
                              y1={padding.top}
                              x2={scaleX(borrowingHoveredPoint.x)}
                              y2={chartHeight - padding.bottom}
                              stroke="rgba(209, 178, 135, 0.5)"
                              strokeWidth="1"
                              strokeDasharray="4,4"
                            />
                          )}

                          {/* Current value marker */}
                          {currentX >= chartMinX && currentX <= chartMaxX && isFinite(currentCapacity) && (
                            <>
                              <line
                                x1={currentXPos}
                                y1={padding.top}
                                x2={currentXPos}
                                y2={chartHeight - padding.bottom}
                                stroke="#10b981"
                                strokeWidth="2"
                                strokeDasharray="8,6"
                              />
                              <circle
                                cx={currentXPos}
                                cy={currentYPos}
                                r="10"
                                fill="#10b981"
                                stroke="#1a1a1a"
                                strokeWidth="3"
                              />
                              <text
                                x={currentXPos}
                                y={padding.top - 25}
                                textAnchor="middle"
                                fill="#10b981"
                                fontSize="12"
                                fontWeight="600"
                              >
                                Actuel: {formatNumber(currentCapacity, 0)}€
                              </text>
                            </>
                          )}

                          {/* Y-axis */}
                          <line
                            x1={padding.left}
                            y1={padding.top}
                            x2={padding.left}
                            y2={chartHeight - padding.bottom}
                            stroke="rgba(209, 216, 224, 0.3)"
                          />

                          {/* Y-axis ticks and labels */}
                          {yTicks.map((tick, i) => (
                            <g key={`y-tick-${i}`}>
                              <line
                                x1={padding.left - 6}
                                y1={scaleY(tick)}
                                x2={padding.left}
                                y2={scaleY(tick)}
                                stroke="rgba(209, 216, 224, 0.5)"
                              />
                              <text
                                x={padding.left - 12}
                                y={scaleY(tick) + 4}
                                textAnchor="end"
                                fill="rgba(209, 216, 224, 0.7)"
                                fontSize="11"
                              >
                                {tick >= 1000 ? (tick / 1000).toFixed(0) + 'k€' : tick.toFixed(0) + '€'}
                              </text>
                            </g>
                          ))}

                          {/* Y-axis label */}
                          <text
                            x={25}
                            y={chartHeight / 2}
                            textAnchor="middle"
                            fill="rgba(209, 216, 224, 0.8)"
                            fontSize="12"
                            fontWeight="500"
                            transform={`rotate(-90, 25, ${chartHeight / 2})`}
                          >
                            Capacité d'Emprunt Maximum (€)
                          </text>

                          {/* X-axis */}
                          <line
                            x1={padding.left}
                            y1={chartHeight - padding.bottom}
                            x2={chartWidth - padding.right}
                            y2={chartHeight - padding.bottom}
                            stroke="rgba(209, 216, 224, 0.3)"
                          />

                          {/* X-axis ticks and labels */}
                          {xTicks.filter((_, i) => i % 2 === 0 || xTicks.length <= 17).map((tick, i) => (
                            <g key={`x-tick-${i}`}>
                              <line
                                x1={scaleX(tick)}
                                y1={chartHeight - padding.bottom}
                                x2={scaleX(tick)}
                                y2={chartHeight - padding.bottom + 6}
                                stroke="rgba(209, 216, 224, 0.5)"
                              />
                              <text
                                x={scaleX(tick)}
                                y={chartHeight - padding.bottom + 20}
                                textAnchor="middle"
                                fill="rgba(209, 216, 224, 0.7)"
                                fontSize="11"
                              >
                                {borrowingSensitivityParam === 'revenues' ? (tick >= 1000 ? (tick / 1000).toFixed(0) + 'k' : tick) : tick}{paramUnit}
                              </text>
                            </g>
                          ))}

                          {/* X-axis label */}
                          <text
                            x={chartWidth / 2}
                            y={chartHeight - 10}
                            textAnchor="middle"
                            fill="rgba(209, 216, 224, 0.8)"
                            fontSize="12"
                            fontWeight="500"
                          >
                            {paramLabel} ({paramUnit.trim() || 'valeur'})
                          </text>
                        </svg>

                        {/* Tooltip */}
                        {borrowingHoveredPoint && (
                          <div
                            style={{
                              position: 'fixed',
                              left: borrowingHoveredPoint.screenX,
                              top: borrowingHoveredPoint.screenY - 80,
                              transform: 'translateX(-50%)',
                              padding: '12px 16px',
                              background: '#222222',
                              borderRadius: '10px',
                              border: '1px solid #d1b287',
                              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                              zIndex: 10000,
                              pointerEvents: 'none',
                              minWidth: '180px'
                            }}
                          >
                            <div style={{color: 'rgba(209, 216, 224, 0.7)', fontSize: '0.85em', marginBottom: '6px'}}>
                              {paramLabel}: <span style={{color: '#fff', fontWeight: '600'}}>
                                {borrowingSensitivityParam === 'revenues' && borrowingHoveredPoint.x >= 1000
                                  ? (borrowingHoveredPoint.x / 1000).toFixed(1) + 'k'
                                  : borrowingHoveredPoint.x}{paramUnit}
                              </span>
                            </div>
                            <div style={{color: 'rgba(209, 216, 224, 0.7)', fontSize: '0.85em'}}>
                              Capacité d'Emprunt: <span style={{color: '#d1b287', fontWeight: '700', fontSize: '1.1em'}}>
                                {formatNumber(borrowingHoveredPoint.y, 0)}€
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Acquisition & Financement Tab */}
        {/* Extraction Characteristics Tab */}
        {activeTab === 'extraction-charac' && (
          <div style={{width: '100%', padding: '0 24px'}}>
            {/* Investment Sub-tabs navigation - Sticky */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '24px',
              borderBottom: '1px solid rgba(58, 58, 58, 0.4)',
              position: 'sticky',
              top: 0,
              background: '#0a0a0a',
              zIndex: 100,
              paddingTop: '12px',
              paddingBottom: '0',
              marginLeft: '-24px',
              marginRight: '-24px',
              paddingLeft: '24px',
              paddingRight: '24px'
            }}>
              <button
                onClick={() => { setActiveTab('extraction-charac'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid rgba(209, 178, 135, 0.9)',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.9em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
              >
                Caractéristiques
              </button>
              <button
                onClick={() => { setActiveTab('acquisition-financement'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Acquisition & Financement
              </button>
              <button
                onClick={() => { setActiveTab('credit'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Crédit
              </button>
              <button
                onClick={() => { setActiveTab('recurrent-fees-income'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Charges & Revenus
              </button>
              <button
                onClick={() => { setActiveTab('performance'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Performance
              </button>
              <div style={{ flex: 1 }}></div>
              <button
                onClick={generateBienImmobilierPDF}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(209, 178, 135, 0.15)',
                  border: '1px solid rgba(209, 178, 135, 0.3)',
                  borderRadius: '6px',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.85em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.15)';
                }}
              >
                PDF Bien Immobilier
              </button>
            </div>

            {/* Section Caractéristiques Info - Collapsible */}
            <div
              onClick={() => setShowInfoSections(!showInfoSections)}
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                padding: showInfoSections ? '24px' : '12px 24px',
                borderRadius: '12px',
                border: '1px solid rgba(58, 58, 58, 0.5)',
                marginBottom: '24px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3 style={{ color: '#ffffff', fontSize: '1.1em', fontWeight: '600', margin: 0 }}>
                      Caractéristiques
                    </h3>
                    <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em', transition: 'transform 0.3s ease', transform: showInfoSections ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                  </div>
                  {showInfoSections && (
                    <>
                      <p style={{ color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.95em', lineHeight: '1.6', marginTop: '16px', marginBottom: '16px' }}>
                        Adresse, surface (m²), nombre de pièces, type de bien, étage, ascenseur...
                      </p>
                      <div style={{
                        borderLeft: '3px solid rgba(156, 163, 175, 0.4)',
                        paddingLeft: '16px',
                        color: 'rgba(156, 163, 175, 0.8)',
                        fontSize: '0.9em',
                        lineHeight: '1.6'
                      }}>
                        <strong style={{ color: '#ffffff' }}>Pourquoi c'est important ?</strong> La localisation et les caractéristiques déterminent le potentiel d'appréciation. Un quartier en développement prendra plus de valeur, mais les loyers y sont souvent plus bas au départ (cash flow réduit).
                      </div>
                    </>
                  )}
            </div>

            <h2 className="section-title">Extracteur de Propriété</h2>

            {/* Section 1: Extraction */}
            <div className="summary-card" style={{marginBottom: '24px'}}>
              <div className="summary-card-header">Extraction Automatique</div>
              <p style={{color: 'rgba(156, 163, 175, 0.8)', fontSize: '0.85em', marginTop: '8px', marginBottom: '16px'}}>
                Entrez une URL Immoweb pour extraire automatiquement les données de la propriété
              </p>
              <div style={{marginTop: '16px'}}>
                <input
                  type="text"
                  value={propertyUrl}
                  onChange={(e) => setPropertyUrl(e.target.value)}
                  placeholder="https://www.immoweb.be/en/classified/apartment/for-sale/..."
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: '#2a2a2a',
                    border: '1px solid #3a3a3a',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '0.95em',
                    marginBottom: '12px'
                  }}
                />
                <button
                  onClick={extractPropertyData}
                  disabled={extracting}
                  className="button"
                  style={{width: '100%'}}
                >
                  {extracting ? 'Extraction en cours...' : 'Extraire les Données'}
                </button>
                {extractError && (
                  <div className="error-message" style={{marginTop: '12px', padding: '12px', background: 'rgba(220, 38, 38, 0.1)', borderRadius: '8px', border: '1px solid rgba(220, 38, 38, 0.3)'}}>
                    <strong>Erreur:</strong> {extractError}
                    <div style={{marginTop: '8px', fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)'}}>
                      <p style={{marginBottom: '8px'}}>Note: Immoweb.be bloque l'extraction automatique. Solutions alternatives:</p>
                      <ol style={{paddingLeft: '20px', margin: 0}}>
                        <li>Ouvrez l'URL dans votre navigateur</li>
                        <li>Copiez manuellement les informations (Prix, Surface, Charges, etc.)</li>
                        <li>Utilisez la section "Saisie Manuelle" ci-dessous</li>
                      </ol>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Section 2: Manual Insertion */}
            <div className="summary-card" style={{marginBottom: '24px'}}>
              <div className="summary-card-header">Saisie Manuelle</div>
              <p style={{color: 'rgba(156, 163, 175, 0.8)', fontSize: '0.85em', marginTop: '8px', marginBottom: '16px'}}>
                Entrez manuellement les informations de la propriété. Les champs marqués d'un <span style={{color: '#ef4444'}}>*</span> sont obligatoires.
              </p>
              <div style={{marginTop: '16px'}}>
                {/* Mandatory Fields Section */}
                <div style={{marginBottom: '20px', padding: '16px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px', border: '1px solid rgba(59, 130, 246, 0.2)'}}>
                  <div style={{fontSize: '0.9em', fontWeight: '600', color: '#3b82f6', marginBottom: '12px'}}>Champs Obligatoires</div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
                    <div style={{gridColumn: '1 / -1'}}>
                      <label style={{display: 'block', marginBottom: '6px', color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.9em'}}>
                        Adresse / Nom de la propriété <span style={{color: '#ef4444'}}>*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Ex: Rue de la Loi 123, 1000 Bruxelles"
                        value={extractedData?.address || ''}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          background: '#2a2a2a',
                          border: '1px solid #3a3a3a',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '0.95em'
                        }}
                        onChange={(e) => setExtractedData({...extractedData, address: e.target.value})}
                      />
                    </div>
                    <div>
                      <label style={{display: 'block', marginBottom: '6px', color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.9em'}}>
                        Prix (€) <span style={{color: '#ef4444'}}>*</span>
                      </label>
                      <input
                        type="number"
                        placeholder="350000"
                        value={extractedData?.price || ''}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          background: '#2a2a2a',
                          border: '1px solid #3a3a3a',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '0.95em'
                        }}
                        onChange={(e) => setExtractedData({...extractedData, price: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div>
                      <label style={{display: 'block', marginBottom: '6px', color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.9em'}}>
                        Surface (m²) <span style={{color: '#ef4444'}}>*</span>
                      </label>
                      <input
                        type="number"
                        placeholder="75"
                        value={extractedData?.surface || ''}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          background: '#2a2a2a',
                          border: '1px solid #3a3a3a',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '0.95em'
                        }}
                        onChange={(e) => setExtractedData({...extractedData, surface: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div>
                      <label style={{display: 'block', marginBottom: '6px', color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.9em'}}>
                        Charges Mensuelles (€) <span style={{color: '#ef4444'}}>*</span>
                      </label>
                      <input
                        type="number"
                        placeholder="150"
                        value={extractedData?.monthlyCharges || ''}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          background: '#2a2a2a',
                          border: '1px solid #3a3a3a',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '0.95em'
                        }}
                        onChange={(e) => setExtractedData({...extractedData, monthlyCharges: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                </div>

                {/* Optional Fields Section */}
                <div style={{marginBottom: '16px'}}>
                  <div style={{fontSize: '0.9em', fontWeight: '600', color: 'rgba(156, 163, 175, 0.9)', marginBottom: '12px'}}>Champs Optionnels</div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
                    <div>
                      <label style={{display: 'block', marginBottom: '6px', color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.9em'}}>Chambres</label>
                      <input
                        type="number"
                        placeholder="2"
                        value={extractedData?.bedrooms || ''}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          background: '#2a2a2a',
                          border: '1px solid #3a3a3a',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '0.95em'
                        }}
                        onChange={(e) => setExtractedData({...extractedData, bedrooms: parseInt(e.target.value) || 0})}
                      />
                    </div>
                    <div>
                      <label style={{display: 'block', marginBottom: '6px', color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.9em'}}>Loyer Mensuel Estimé (€)</label>
                      <input
                        type="number"
                        placeholder="1200"
                        value={extractedData?.monthlyRent || ''}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          background: '#2a2a2a',
                          border: '1px solid #3a3a3a',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '0.95em'
                        }}
                        onChange={(e) => setExtractedData({...extractedData, monthlyRent: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div>
                      <label style={{display: 'block', marginBottom: '6px', color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.9em'}}>Revenu Cadastral (€)</label>
                      <input
                        type="number"
                        placeholder="1500"
                        value={extractedData?.cadastralIncome || ''}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          background: '#2a2a2a',
                          border: '1px solid #3a3a3a',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '0.95em'
                        }}
                        onChange={(e) => setExtractedData({...extractedData, cadastralIncome: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={applyExtractedData}
                  className="button"
                  style={{width: '100%', marginTop: '16px'}}
                  disabled={!extractedData || !extractedData.address || !extractedData.price || !extractedData.surface || !extractedData.monthlyCharges}
                >
                  Appliquer aux Données d'Investissement
                </button>
                {extractedData && (!extractedData.address || !extractedData.price || !extractedData.surface || !extractedData.monthlyCharges) && (
                  <p style={{color: '#ef4444', fontSize: '0.85em', marginTop: '8px', textAlign: 'center'}}>
                    Veuillez remplir tous les champs obligatoires pour continuer
                  </p>
                )}
              </div>
            </div>

            {extractedData && Object.keys(extractedData).length > 0 && (
              <>
                {/* Property Image */}
                {extractedData.imageUrl && (
                  <div className="summary-card" style={{marginBottom: '24px', textAlign: 'center'}}>
                    <img
                      src={extractedData.imageUrl}
                      alt={extractedData.address || 'Property'}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '400px',
                        borderRadius: '8px',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {/* Two-Column Layout */}
                <div className="reader-two-column-layout">
                  {/* Left Column - Financial Information */}
                  <div className="reader-column">
                    <div className="summary-card">
                      <div className="summary-card-header">Informations Financières</div>

                      {extractedData.price && (
                        <div className="summary-item">
                          <span className="summary-label">Prix:</span>
                          <span className="summary-value highlight-color">{formatNumber(extractedData.price)} €</span>
                        </div>
                      )}

                      {extractedData.pricePerSqm && (
                        <div className="summary-item">
                          <span className="summary-label">Prix/m²:</span>
                          <span className="summary-value">{formatNumber(extractedData.pricePerSqm)} €/m²</span>
                        </div>
                      )}

                      {extractedData.cadastralIncome && (
                        <div className="summary-item">
                          <span className="summary-label">Revenu Cadastral:</span>
                          <span className="summary-value">{formatNumber(extractedData.cadastralIncome)} €</span>
                        </div>
                      )}

                      {extractedData.monthlyCharges && (
                        <div className="summary-item">
                          <span className="summary-label">Charges/mois:</span>
                          <span className="summary-value">{formatNumber(extractedData.monthlyCharges)} €</span>
                        </div>
                      )}

                      {extractedData.monthlyRent && (
                        <div className="summary-item">
                          <span className="summary-label">Loyer/mois:</span>
                          <span className="summary-value highlight-color">{formatNumber(extractedData.monthlyRent)} €</span>
                        </div>
                      )}

                      {extractedData.grossRentalYield && (
                        <div className="summary-item">
                          <span className="summary-label">Rendement Brut:</span>
                          <span className="summary-value" style={{color: extractedData.grossRentalYield > 5 ? '#10b981' : '#ef4444'}}>{extractedData.grossRentalYield}%</span>
                        </div>
                      )}

                      {extractedData.publicationDate && (
                        <div className="summary-item">
                          <span className="summary-label">Publié:</span>
                          <span className="summary-value">{new Date(extractedData.publicationDate).toLocaleDateString('fr-BE')}</span>
                        </div>
                      )}

                      {extractedData.sellerType && (
                        <div className="summary-item">
                          <span className="summary-label">Vendeur:</span>
                          <span className="summary-value">{extractedData.sellerType}</span>
                        </div>
                      )}
                    </div>

                    {/* Energy & Environment in Left Column */}
                    <div className="summary-card">
                      <div className="summary-card-header">⚡ Énergie & Environnement</div>

                      {extractedData.energyConsumption && (
                        <div className="summary-item">
                          <span className="summary-label">Conso. Primaire:</span>
                          <span className="summary-value" style={{
                            color: extractedData.energyConsumption < 100 ? '#10b981' :
                                   extractedData.energyConsumption < 200 ? '#f59e0b' :
                                   extractedData.energyConsumption < 300 ? '#ef4444' : '#dc2626'
                          }}>
                            {extractedData.energyConsumption} kWh/m²
                          </span>
                        </div>
                      )}

                      {(extractedData.epcScore || extractedData.epcCategory) && (
                        <div className="summary-item">
                          <span className="summary-label">Label PEB:</span>
                          <span className="summary-value" style={{
                            color: (extractedData.epcScore || extractedData.epcCategory || '').startsWith('A') ? '#10b981' :
                                   (extractedData.epcScore || extractedData.epcCategory || '').startsWith('B') ? '#10b981' :
                                   (extractedData.epcScore || extractedData.epcCategory || '').startsWith('C') ? '#f59e0b' :
                                   (extractedData.epcScore || extractedData.epcCategory || '').startsWith('D') ? '#f59e0b' : '#ef4444'
                          }}>
                            {extractedData.epcScore || extractedData.epcCategory}
                          </span>
                        </div>
                      )}

                      {extractedData.carbonEmission && (
                        <div className="summary-item">
                          <span className="summary-label">Émissions CO₂:</span>
                          <span className="summary-value">{extractedData.carbonEmission} kg/m²</span>
                        </div>
                      )}

                      {extractedData.heatingType && (
                        <div className="summary-item">
                          <span className="summary-label">Chauffage:</span>
                          <span className="summary-value">{extractedData.heatingType}</span>
                        </div>
                      )}

                      {extractedData.hasDoubleGlazing !== undefined && (
                        <div className="summary-item">
                          <span className="summary-label">Double Vitrage:</span>
                          <span className="summary-value">{extractedData.hasDoubleGlazing ? '✓' : '✗'}</span>
                        </div>
                      )}

                      {extractedData.hasSolarPanels && (
                        <div className="summary-item">
                          <span className="summary-label">Panneaux Solaires:</span>
                          <span className="summary-value" style={{color: '#10b981'}}>✓</span>
                        </div>
                      )}

                      {extractedData.floodZoneType && extractedData.floodZoneType !== 'NON_FLOOD_ZONE' && (
                        <div className="summary-item">
                          <span className="summary-label">Zone Inondable:</span>
                          <span className="summary-value" style={{color: '#ef4444'}}>⚠</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Column - Property Characteristics */}
                  <div className="reader-column">
                    <div className="summary-card">
                      <div className="summary-card-header">Caractéristiques du Bien</div>

                      {extractedData.address && (
                        <div className="summary-item">
                          <span className="summary-label">Adresse:</span>
                          <span className="summary-value">{extractedData.address}</span>
                        </div>
                      )}

                      {extractedData.propertyType && (
                        <div className="summary-item">
                          <span className="summary-label">Type:</span>
                          <span className="summary-value">{extractedData.propertyType}</span>
                        </div>
                      )}

                      {extractedData.surface && (
                        <div className="summary-item">
                          <span className="summary-label">Surface:</span>
                          <span className="summary-value">{extractedData.surface} m²</span>
                        </div>
                      )}

                      {extractedData.bedrooms !== undefined && (
                        <div className="summary-item">
                          <span className="summary-label">Chambres:</span>
                          <span className="summary-value">{extractedData.bedrooms}</span>
                        </div>
                      )}

                      {extractedData.bathrooms && (
                        <div className="summary-item">
                          <span className="summary-label">Salles de bain:</span>
                          <span className="summary-value">{extractedData.bathrooms}</span>
                        </div>
                      )}

                      {extractedData.toilets && (
                        <div className="summary-item">
                          <span className="summary-label">Toilettes:</span>
                          <span className="summary-value">{extractedData.toilets}</span>
                        </div>
                      )}

                      {extractedData.floor !== undefined && (
                        <div className="summary-item">
                          <span className="summary-label">Étage:</span>
                          <span className="summary-value">{extractedData.floor} {extractedData.hasLift ? '(Asc. ✓)' : ''}</span>
                        </div>
                      )}

                      {extractedData.constructionYear && (
                        <div className="summary-item">
                          <span className="summary-label">Construction:</span>
                          <span className="summary-value">{extractedData.constructionYear}</span>
                        </div>
                      )}

                      {extractedData.buildingType && (
                        <div className="summary-item">
                          <span className="summary-label">État:</span>
                          <span className="summary-value">{extractedData.buildingType}</span>
                        </div>
                      )}

                      {extractedData.numberOfFacades && (
                        <div className="summary-item">
                          <span className="summary-label">Façades:</span>
                          <span className="summary-value">{extractedData.numberOfFacades}</span>
                        </div>
                      )}

                      {extractedData.kitchenType && (
                        <div className="summary-item">
                          <span className="summary-label">Cuisine:</span>
                          <span className="summary-value">{extractedData.kitchenType}</span>
                        </div>
                      )}
                    </div>

                    {/* Amenities in Right Column */}
                    <div className="summary-card">
                      <div className="summary-card-header">✨ Équipements</div>

                      {extractedData.hasTerrace && (
                        <div className="summary-item">
                          <span className="summary-label">Terrasse:</span>
                          <span className="summary-value">✓ {extractedData.terraceSurface ? `${extractedData.terraceSurface} m²` : ''}</span>
                        </div>
                      )}

                      {extractedData.hasBalcony && (
                        <div className="summary-item">
                          <span className="summary-label">Balcon:</span>
                          <span className="summary-value">✓ {extractedData.balconySurface ? `${extractedData.balconySurface} m²` : ''}</span>
                        </div>
                      )}

                      {extractedData.gardenSurface && (
                        <div className="summary-item">
                          <span className="summary-label">Jardin:</span>
                          <span className="summary-value">{extractedData.gardenSurface} m²</span>
                        </div>
                      )}

                      {extractedData.indoorParkingSpaces && (
                        <div className="summary-item">
                          <span className="summary-label">Parking Int.:</span>
                          <span className="summary-value">{extractedData.indoorParkingSpaces}</span>
                        </div>
                      )}

                      {extractedData.outdoorParkingSpaces && (
                        <div className="summary-item">
                          <span className="summary-label">Parking Ext.:</span>
                          <span className="summary-value">{extractedData.outdoorParkingSpaces}</span>
                        </div>
                      )}

                      {extractedData.hasBasement && (
                        <div className="summary-item">
                          <span className="summary-label">Cave:</span>
                          <span className="summary-value">✓</span>
                        </div>
                      )}

                      {extractedData.hasAttic && (
                        <div className="summary-item">
                          <span className="summary-label">Grenier:</span>
                          <span className="summary-value">✓</span>
                        </div>
                      )}

                      {extractedData.hasSwimmingPool && (
                        <div className="summary-item">
                          <span className="summary-label">Piscine:</span>
                          <span className="summary-value">✓</span>
                        </div>
                      )}

                      {extractedData.hasFireplace && (
                        <div className="summary-item">
                          <span className="summary-label">Cheminée:</span>
                          <span className="summary-value">✓</span>
                        </div>
                      )}

                      {extractedData.accessibleForDisabled && (
                        <div className="summary-item">
                          <span className="summary-label">Accessible PMR:</span>
                          <span className="summary-value">✓</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={applyExtractedData}
                  className="button"
                  style={{width: '100%', marginTop: '20px'}}
                >
                  Appliquer aux Données d'Investissement
                </button>
              </>
            )}
          </div>
        )}

        {/* Acquisition & Financement Tab */}
        {activeTab === 'acquisition-financement' && (
          <div style={{width: '100%', padding: '0 24px'}}>
            {/* Investment Sub-tabs navigation - Sticky */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '24px',
              borderBottom: '1px solid rgba(58, 58, 58, 0.4)',
              position: 'sticky',
              top: 0,
              background: '#0a0a0a',
              zIndex: 100,
              paddingTop: '12px',
              paddingBottom: '0',
              marginLeft: '-24px',
              marginRight: '-24px',
              paddingLeft: '24px',
              paddingRight: '24px'
            }}>
              <button
                onClick={() => { setActiveTab('extraction-charac'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Caractéristiques
              </button>
              <button
                onClick={() => { setActiveTab('acquisition-financement'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid rgba(209, 178, 135, 0.9)',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.9em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
              >
                Acquisition & Financement
              </button>
              <button
                onClick={() => { setActiveTab('credit'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Crédit
              </button>
              <button
                onClick={() => { setActiveTab('recurrent-fees-income'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Charges & Revenus
              </button>
              <button
                onClick={() => { setActiveTab('performance'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Performance
              </button>
              <div style={{ flex: 1 }}></div>
              <button
                onClick={generateBienImmobilierPDF}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(209, 178, 135, 0.15)',
                  border: '1px solid rgba(209, 178, 135, 0.3)',
                  borderRadius: '6px',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.85em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.15)';
                }}
              >
                PDF Bien Immobilier
              </button>
            </div>

            {/* Section Acquisition & Financement Info - Collapsible */}
            <div
              onClick={() => setShowInfoSections(!showInfoSections)}
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                padding: showInfoSections ? '24px' : '12px 24px',
                borderRadius: '12px',
                border: '1px solid rgba(58, 58, 58, 0.5)',
                marginBottom: '24px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3 style={{ color: '#ffffff', fontSize: '1.1em', fontWeight: '600', margin: 0 }}>
                      Acquisition & Financement
                    </h3>
                    <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em', transition: 'transform 0.3s ease', transform: showInfoSections ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                  </div>
                  {showInfoSections && (
                    <>
                      <p style={{ color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.95em', lineHeight: '1.6', marginTop: '16px', marginBottom: '16px' }}>
                        Prix d'achat, frais de notaire (~7-8% ancien), travaux, apport personnel, montant emprunté.
                      </p>
                      <div style={{
                        borderLeft: '3px solid rgba(156, 163, 175, 0.4)',
                        paddingLeft: '16px',
                        color: 'rgba(156, 163, 175, 0.8)',
                        fontSize: '0.9em',
                        lineHeight: '1.8'
                      }}>
                        <p style={{ marginBottom: '12px' }}><strong style={{ color: '#ffffff' }}>Pourquoi c'est important ?</strong></p>
                        <p style={{ marginBottom: '10px' }}>
                          <strong style={{ color: '#60a5fa' }}>→ Impact sur le Cash Flow :</strong> Le budget total (prix + frais + travaux) détermine le montant emprunté et donc la mensualité. Un prix d'achat négocié 10% en dessous du marché, c'est une mensualité réduite chaque mois pendant 20 ans. À l'inverse, des frais de notaire (~7-8% dans l'ancien) et des travaux importants alourdissent l'emprunt sans générer de loyer supplémentaire.
                        </p>
                        <p style={{ marginBottom: '10px' }}>
                          <strong style={{ color: '#60a5fa' }}>→ Impact sur la Plus-value :</strong> Le prix d'achat est votre point de départ. Plus vous achetez bas par rapport au marché, plus votre marge de plus-value est élevée à la revente. Les travaux peuvent créer de la valeur (rénovation énergétique, agrandissement) mais attention : 1€ investi en travaux ne génère pas toujours 1€ de valeur.
                        </p>
                        <p style={{ marginBottom: '0' }}>
                          <strong style={{ color: '#60a5fa' }}>→ Impact sur l'Effet de levier :</strong> L'apport personnel est le capital que vous immobilisez. Moins vous mettez d'apport, plus votre rendement sur capital investi (Cash flow / Apport) est élevé. Exemple : avec 10 000€ d'apport sur un bien à 200 000€, chaque euro de cash flow vous rapporte 20× plus qu'avec 100 000€ d'apport. Mais un apport faible = emprunt élevé = mensualité élevée = cash flow réduit.
                        </p>
                      </div>
                    </>
                  )}
            </div>

            <h2 className="section-title">Acquisition & Financement</h2>

            {/* Side-by-side panels */}
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '24px'}}>
              {/* Acquisition Information */}
              <div className="calculation-section" style={{maxHeight: 'none'}}>
                <h2 className="panel-header">Informations d'Acquisition</h2>

                <div className="input-list">
                  <div className="input-row">
                    <input
                      type="text"
                      className="input-label-field"
                      value="Prix d'Achat"
                      disabled
                    />
                    <input
                      type="number"
                      className="input-amount-field"
                      placeholder="Montant"
                      step="1000"
                      min="0"
                      value={acquisitionData.prixAchat || ''}
                      onChange={(e) => setAcquisitionData({...acquisitionData, prixAchat: parseFloat(e.target.value) || 0})}
                    />
                  </div>

                  <div className="input-row">
                    <input
                      type="text"
                      className="input-label-field"
                      value="Frais de Notaire"
                      disabled
                    />
                    <input
                      type="number"
                      className="input-amount-field"
                      placeholder="Montant"
                      step="100"
                      min="0"
                      value={acquisitionData.fraisNotaire || ''}
                      onChange={(e) => setAcquisitionData({...acquisitionData, fraisNotaire: parseFloat(e.target.value) || 0})}
                    />
                  </div>

                  <div className="input-row">
                    <input
                      type="text"
                      className="input-label-field"
                      value="Frais d'Agence"
                      disabled
                    />
                    <input
                      type="number"
                      className="input-amount-field"
                      placeholder="Montant"
                      step="100"
                      min="0"
                      value={acquisitionData.fraisAgence || ''}
                      onChange={(e) => setAcquisitionData({...acquisitionData, fraisAgence: parseFloat(e.target.value) || 0})}
                    />
                  </div>

                  <div className="input-row">
                    <input
                      type="text"
                      className="input-label-field"
                      value="Travaux Prévus"
                      disabled
                    />
                    <input
                      type="number"
                      className="input-amount-field"
                      placeholder="Montant"
                      step="100"
                      min="0"
                      value={acquisitionData.travaux || ''}
                      onChange={(e) => setAcquisitionData({...acquisitionData, travaux: parseFloat(e.target.value) || 0})}
                    />
                  </div>

                  <div className="input-row">
                    <input
                      type="text"
                      className="input-label-field"
                      value="Autres Frais"
                      disabled
                    />
                    <input
                      type="number"
                      className="input-amount-field"
                      placeholder="Montant"
                      step="100"
                      min="0"
                      value={acquisitionData.autresFrais || ''}
                      onChange={(e) => setAcquisitionData({...acquisitionData, autresFrais: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>

                <div className="total-box">
                  <span className="total-label">Coût Total d'Acquisition:</span>
                  <span className="total-value">{formatNumber(totalAcquisition, 2)} €</span>
                </div>
              </div>

              {/* Financing */}
              <div className="calculation-section" style={{maxHeight: 'none'}}>
                <h2 className="panel-header">Plan de Financement</h2>

                <div className="input-list">
                  {(() => {
                    const apportTotal = quotiteParams.maxContribution || 0;
                    const emprunt = financementData.emprunt || 0;
                    const travaux = acquisitionData.travaux || 0;
                    const creditTravaux = creditTravauxData.amount || 0;
                    const creditImmo = creditData.amount || 0;
                    // Apport: use manual values if set, otherwise 50% travaux in apport, rest in crédit
                    const autoApportTravaux = Math.round(travaux / 2);
                    const autoApportAchat = Math.max(0, apportTotal - autoApportTravaux);
                    const apportTravaux = customApportSplit ? (customApportSplit.travaux || 0) : autoApportTravaux;
                    const apportAchat = customApportSplit ? (customApportSplit.achat || 0) : autoApportAchat;
                    const totalDebt = creditImmo + creditTravaux;
                    const totalEquity = apportAchat + apportTravaux;

                    // Validation
                    const equityMismatch = Math.round(totalEquity) !== Math.round(apportTotal);
                    const debtMismatch = Math.round(totalDebt) !== Math.round(emprunt);
                    const travauxMismatch = Math.round(creditTravaux + apportTravaux) !== Math.round(travaux);

                    const handleCreditTravauxChange = (val) => {
                      setHasCustomCreditSplit(true);
                      setCreditTravauxData(prev => ({...prev, amount: Math.max(0, val)}));
                    };
                    const handleCreditImmoChange = (val) => {
                      setHasCustomCreditSplit(true);
                      setCreditData(prev => ({...prev, amount: Math.max(0, val)}));
                    };
                    const handleApportAchatChange = (val) => {
                      setCustomApportSplit({achat: Math.max(0, val), travaux: apportTravaux});
                    };
                    const handleApportTravauxChange = (val) => {
                      setCustomApportSplit({achat: apportAchat, travaux: Math.max(0, val)});
                    };

                    const warningStyle = {fontSize: '0.78em', color: '#f59e0b', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px'};

                    const handleResetDefaults = () => {
                      setCustomApportSplit(null);
                      setHasCustomCreditSplit(false);
                      const defaultCreditTravaux = Math.round((travaux || 0) / 2);
                      setCreditTravauxData(prev => ({...prev, amount: defaultCreditTravaux}));
                      setCreditData(prev => ({...prev, amount: Math.max(0, emprunt - defaultCreditTravaux)}));
                    };

                    const isCustom = customApportSplit || hasCustomCreditSplit;

                    return (
                      <>
                        {isCustom && (
                          <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '6px'}}>
                            <button onClick={handleResetDefaults} style={{
                              fontSize: '0.72em', padding: '3px 10px', borderRadius: '4px',
                              background: 'rgba(96, 165, 250, 0.1)', border: '1px solid rgba(96, 165, 250, 0.3)',
                              color: '#60a5fa', cursor: 'pointer', whiteSpace: 'nowrap'
                            }}>Par défaut</button>
                          </div>
                        )}
                        {/* Equity section */}
                        <div style={{display: 'flex', alignItems: 'stretch', gap: '12px', marginBottom: '8px', minWidth: 0}}>
                          <div style={{flex: 1, minWidth: 0}}>
                            <div className="input-row" style={{marginBottom: '4px', display: 'flex', gap: '8px'}}>
                              <input type="text" className="input-label-field" value="Apport Achat" disabled style={{flex: '0 0 auto', width: '200px'}} />
                              <input type="number" className="input-amount-field" value={apportAchat || ''}
                                step="1000" min="0"
                                onChange={(e) => handleApportAchatChange(parseFloat(e.target.value) || 0)}
                                style={{flex: 1, minWidth: '80px'}}
                              />
                            </div>
                            <div className="input-row" style={{display: 'flex', gap: '8px'}}>
                              <input type="text" className="input-label-field" value="Apport Travaux" disabled style={{flex: '0 0 auto', width: '200px'}} />
                              <input type="number" className="input-amount-field" value={apportTravaux || ''}
                                step="1000" min="0"
                                onChange={(e) => handleApportTravauxChange(parseFloat(e.target.value) || 0)}
                                style={{flex: 1, minWidth: '80px'}}
                              />
                            </div>
                            {equityMismatch && (
                              <div style={warningStyle}>
                                <span>&#9888;</span> La somme Apport Achat + Apport Travaux ({formatNumber(totalEquity, 0)} €) ne correspond pas à l'Apport Disponible ({formatNumber(apportTotal, 0)} €). {totalEquity < apportTotal ? `Il faut ajouter ${formatNumber(apportTotal - totalEquity, 0)} € dans l'un ou l'autre.` : `Il y a un excédent de ${formatNumber(totalEquity - apportTotal, 0)} € à retirer.`}
                              </div>
                            )}
                          </div>
                          <div style={{
                            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                            padding: '8px 12px',
                            background: equityMismatch ? 'rgba(245, 158, 11, 0.08)' : 'rgba(74, 222, 128, 0.08)',
                            border: `1px solid ${equityMismatch ? 'rgba(245, 158, 11, 0.3)' : 'rgba(74, 222, 128, 0.2)'}`,
                            borderRadius: '8px', minWidth: '100px', flexShrink: 0
                          }}>
                            <div style={{fontSize: '0.7em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '4px'}}>Equity</div>
                            <div style={{fontSize: '1em', fontWeight: '700', color: equityMismatch ? '#f59e0b' : '#4ade80', whiteSpace: 'nowrap'}}>{formatNumber(totalEquity, 0)} €</div>
                          </div>
                        </div>

                        {/* Debt section */}
                        <div style={{display: 'flex', alignItems: 'stretch', gap: '12px', marginBottom: '0', minWidth: 0}}>
                          <div style={{flex: 1, minWidth: 0}}>
                            <div className="input-row" style={{marginBottom: '4px', display: 'flex', gap: '8px'}}>
                              <input type="text" className="input-label-field" value="Crédit Bien Immobilier" disabled style={{flex: '0 0 auto', width: '200px'}} />
                              <input type="number" className="input-amount-field" value={creditImmo || ''}
                                step="1000" min="0"
                                onChange={(e) => handleCreditImmoChange(parseFloat(e.target.value) || 0)}
                                style={{flex: 1, minWidth: '80px'}}
                              />
                            </div>
                            <div className="input-row" style={{display: 'flex', gap: '8px'}}>
                              <input type="text" className="input-label-field" value="Crédit Travaux" disabled style={{flex: '0 0 auto', width: '200px'}} />
                              <input type="number" className="input-amount-field" value={creditTravaux || ''}
                                step="1000" min="0"
                                onChange={(e) => handleCreditTravauxChange(parseFloat(e.target.value) || 0)}
                                style={{flex: 1, minWidth: '80px'}}
                              />
                            </div>
                            {debtMismatch && (
                              <div style={warningStyle}>
                                <span>&#9888;</span> La somme Crédit Immo + Crédit Travaux ({formatNumber(totalDebt, 0)} €) ne correspond pas à l'Emprunt ({formatNumber(emprunt, 0)} €). {totalDebt < emprunt ? `Il faut ajouter ${formatNumber(emprunt - totalDebt, 0)} € dans l'un ou l'autre.` : `Il y a un excédent de ${formatNumber(totalDebt - emprunt, 0)} € à retirer.`}
                              </div>
                            )}
                          </div>
                          <div style={{
                            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                            padding: '8px 12px',
                            background: debtMismatch ? 'rgba(245, 158, 11, 0.08)' : 'rgba(96, 165, 250, 0.08)',
                            border: `1px solid ${debtMismatch ? 'rgba(245, 158, 11, 0.3)' : 'rgba(96, 165, 250, 0.2)'}`,
                            borderRadius: '8px', minWidth: '100px', flexShrink: 0
                          }}>
                            <div style={{fontSize: '0.7em', color: 'rgba(156, 163, 175, 0.7)', marginBottom: '4px'}}>Debt</div>
                            <div style={{fontSize: '1em', fontWeight: '700', color: debtMismatch ? '#f59e0b' : '#60a5fa', whiteSpace: 'nowrap'}}>{formatNumber(totalDebt, 0)} €</div>
                          </div>
                        </div>

                        {/* Travaux constraint */}
                        {travauxMismatch && (
                          <div style={{...warningStyle, marginTop: '8px'}}>
                            <span>&#9888;</span> La somme Crédit Travaux + Apport Travaux ({formatNumber(creditTravaux + apportTravaux, 0)} €) ne correspond pas aux Travaux Prévus ({formatNumber(travaux, 0)} €). {(creditTravaux + apportTravaux) < travaux ? `Il faut ajouter ${formatNumber(travaux - (creditTravaux + apportTravaux), 0)} € dans l'un ou l'autre.` : `Il y a un excédent de ${formatNumber((creditTravaux + apportTravaux) - travaux, 0)} € à retirer.`}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                <div className="total-box" style={Math.round(totalFinancement) !== Math.round(totalAcquisition) ? {borderColor: 'rgba(245, 158, 11, 0.5)', background: 'rgba(245, 158, 11, 0.08)'} : {}}>
                  <span className="total-label" style={Math.round(totalFinancement) !== Math.round(totalAcquisition) ? {color: '#f59e0b'} : {}}>Total Financement:</span>
                  <span className="total-value" style={{color: Math.round(totalFinancement) !== Math.round(totalAcquisition) ? '#f59e0b' : undefined}}>{formatNumber(totalFinancement, 0)} €</span>
                </div>

                <div className="summary-card" style={{marginTop: '20px'}}>
                  <div className="summary-card-header">Ratios de Financement</div>

                  <div className="summary-item">
                    <span className="summary-label">Loan-to-Value (LTV):</span>
                    <span className="summary-value">{formatNumber(ltv, 2)}%</span>
                  </div>

                  <div className="summary-item">
                    <span className="summary-label">Equity Ratio:</span>
                    <span className="summary-value">{formatNumber(equityRatio, 2)}%</span>
                  </div>

                  <div className="summary-item">
                    <span className="summary-label">Différence:</span>
                    <span className="summary-value" style={{color: difference >= 0 ? '#4ade80' : '#ef4444'}}>
                      {formatNumber(difference, 2)} €
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Chart Section */}
            <div className="calculation-section" style={{maxHeight: 'none', marginTop: '15px'}}>
              <h2 className="section-title">Répartition des Coûts vs Financement</h2>

              <div className="chart-container">
                <div className="chart-placeholder" style={{position: 'relative'}}>
                  <svg viewBox="0 0 800 400" style={{width: '100%', height: '400px'}}>
                    {(() => {
                      // Chart dimensions
                      const chartHeight = 330;
                      const chartBottom = 360;
                      const chartTop = 30;

                      // Calculate max value for scaling
                      const maxValue = Math.max(totalAcquisition, totalFinancement, 100000);
                      const scale = chartHeight / maxValue;

                      // Calculate heights for acquisition components
                      const fraisTotal = acquisitionData.fraisNotaire + acquisitionData.fraisAgence + acquisitionData.autresFrais;
                      const hauteurFrais = fraisTotal * scale;
                      const hauteurTravaux = acquisitionData.travaux * scale;
                      const hauteurPrixAchat = acquisitionData.prixAchat * scale;
                      const hauteurTotalAcquisition = totalAcquisition * scale;

                      // Calculate heights for financing components
                      const hauteurApport = financementData.apport * scale;
                      const hauteurEmprunt = financementData.emprunt * scale;
                      const hauteurTotalFinancement = totalFinancement * scale;

                      // Y-axis labels (5 steps)
                      const step = maxValue / 4;
                      const yLabels = [
                        { y: chartTop, value: maxValue },
                        { y: chartTop + chartHeight * 0.25, value: maxValue * 0.75 },
                        { y: chartTop + chartHeight * 0.5, value: maxValue * 0.5 },
                        { y: chartTop + chartHeight * 0.75, value: maxValue * 0.25 },
                        { y: chartBottom, value: 0 }
                      ];

                      return (
                        <>
                          {/* Gradient definitions */}
                          <defs>
                            {/* Prix d'Achat - Light Blue */}
                            <linearGradient id="gradientPrixAchat" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgba(147, 197, 253, 0.8)" />
                              <stop offset="100%" stopColor="rgba(147, 197, 253, 0.1)" />
                            </linearGradient>
                            {/* Travaux - Light Yellow */}
                            <linearGradient id="gradientTravaux" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgba(253, 230, 138, 0.8)" />
                              <stop offset="100%" stopColor="rgba(253, 230, 138, 0.1)" />
                            </linearGradient>
                            {/* Frais - Gold/Beige */}
                            <linearGradient id="gradientFrais" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgba(209, 178, 135, 0.8)" />
                              <stop offset="100%" stopColor="rgba(209, 178, 135, 0.1)" />
                            </linearGradient>
                            {/* Emprunt - Soft Blue */}
                            <linearGradient id="gradientEmprunt" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgba(96, 165, 250, 0.8)" />
                              <stop offset="100%" stopColor="rgba(96, 165, 250, 0.1)" />
                            </linearGradient>
                            {/* Apport - Light Green */}
                            <linearGradient id="gradientApport" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgba(74, 222, 128, 0.8)" />
                              <stop offset="100%" stopColor="rgba(74, 222, 128, 0.1)" />
                            </linearGradient>
                          </defs>

                          {/* Y-axis labels */}
                          {yLabels.map((label, i) => (
                            <text key={i} x="50" y={label.y + 4} fill="rgba(156, 163, 175, 0.7)" fontSize="12" textAnchor="end">
                              {Math.round(label.value).toLocaleString()}
                            </text>
                          ))}

                          {/* Grid lines */}
                          {yLabels.map((label, i) => (
                            <line
                              key={i}
                              x1="60"
                              y1={label.y}
                              x2="760"
                              y2={label.y}
                              stroke={i === yLabels.length - 1 ? "rgba(58, 58, 58, 0.8)" : "rgba(58, 58, 58, 0.3)"}
                              strokeWidth={i === yLabels.length - 1 ? "2" : "1"}
                            />
                          ))}

                          {/* Coûts du Projet Bar */}
                          <g>
                            {/* Prix d'Achat (Buying Value) - Light Blue gradient */}
                            {hauteurPrixAchat > 0 && (
                              <g>
                                <rect
                                  x="150"
                                  y={chartBottom - hauteurTotalAcquisition}
                                  width="150"
                                  height={hauteurPrixAchat}
                                  fill="url(#gradientPrixAchat)"
                                  stroke="rgba(147, 197, 253, 0.9)"
                                  strokeWidth="2"
                                  style={{cursor: 'pointer', transition: 'all 0.3s ease', opacity: hoveredSegment === 'prixAchat' ? 1 : 0.9}}
                                  onMouseEnter={() => setHoveredSegment('prixAchat')}
                                  onMouseLeave={() => setHoveredSegment(null)}
                                />
                                {hoveredSegment === 'prixAchat' && (
                                  <text
                                    x="225"
                                    y={chartBottom - hauteurTotalAcquisition + hauteurPrixAchat / 2}
                                    fill="#93c5fd"
                                    fontSize="11"
                                    fontWeight="700"
                                    textAnchor="middle"
                                  >
                                    {formatNumber(acquisitionData.prixAchat, 0)} €
                                  </text>
                                )}
                              </g>
                            )}
                            {/* Travaux (Renovation) - Light Yellow gradient */}
                            {hauteurTravaux > 0 && (
                              <g>
                                <rect
                                  x="150"
                                  y={chartBottom - hauteurFrais - hauteurTravaux}
                                  width="150"
                                  height={hauteurTravaux}
                                  fill="url(#gradientTravaux)"
                                  stroke="rgba(253, 230, 138, 0.9)"
                                  strokeWidth="2"
                                  style={{cursor: 'pointer', transition: 'all 0.3s ease', opacity: hoveredSegment === 'travaux' ? 1 : 0.9}}
                                  onMouseEnter={() => setHoveredSegment('travaux')}
                                  onMouseLeave={() => setHoveredSegment(null)}
                                />
                                {hoveredSegment === 'travaux' && hauteurTravaux > 20 && (
                                  <text
                                    x="225"
                                    y={chartBottom - hauteurFrais - hauteurTravaux / 2}
                                    fill="#fde68a"
                                    fontSize="11"
                                    fontWeight="700"
                                    textAnchor="middle"
                                  >
                                    {formatNumber(acquisitionData.travaux, 0)} €
                                  </text>
                                )}
                              </g>
                            )}
                            {/* Frais (Fees) - Gold gradient */}
                            {hauteurFrais > 0 && (
                              <g>
                                <rect
                                  x="150"
                                  y={chartBottom - hauteurFrais}
                                  width="150"
                                  height={hauteurFrais}
                                  fill="url(#gradientFrais)"
                                  stroke="rgba(209, 178, 135, 0.9)"
                                  strokeWidth="2"
                                  style={{cursor: 'pointer', transition: 'all 0.3s ease', opacity: hoveredSegment === 'frais' ? 1 : 0.9}}
                                  onMouseEnter={() => setHoveredSegment('frais')}
                                  onMouseLeave={() => setHoveredSegment(null)}
                                />
                                {hoveredSegment === 'frais' && hauteurFrais > 20 && (
                                  <text
                                    x="225"
                                    y={chartBottom - hauteurFrais / 2}
                                    fill="#d1b287"
                                    fontSize="11"
                                    fontWeight="700"
                                    textAnchor="middle"
                                  >
                                    {formatNumber(fraisTotal, 0)} €
                                  </text>
                                )}
                              </g>
                            )}
                            {/* Label */}
                            <text x="225" y="20" fill="rgba(156, 163, 175, 0.9)" fontSize="14" fontWeight="600" textAnchor="middle">Coûts du Projet</text>
                            {totalAcquisition > 0 && (
                              <text x="170" y={Math.max(chartBottom - hauteurTotalAcquisition + 20, 45)} fill="rgba(255, 255, 255, 0.9)" fontSize="12" fontWeight="700">
                                {totalAcquisition >= 1000 ? `${Math.round(totalAcquisition / 1000)}k` : Math.round(totalAcquisition)}
                              </text>
                            )}
                          </g>

                          {/* Financement Bar */}
                          <g>
                            {/* Emprunt (Debt) - Soft Blue gradient */}
                            {hauteurEmprunt > 0 && (
                              <g>
                                <rect
                                  x="500"
                                  y={chartBottom - hauteurTotalFinancement}
                                  width="150"
                                  height={hauteurEmprunt}
                                  fill="url(#gradientEmprunt)"
                                  stroke="rgba(96, 165, 250, 0.9)"
                                  strokeWidth="2"
                                  style={{cursor: 'pointer', transition: 'all 0.3s ease', opacity: hoveredSegment === 'emprunt' ? 1 : 0.9}}
                                  onMouseEnter={() => setHoveredSegment('emprunt')}
                                  onMouseLeave={() => setHoveredSegment(null)}
                                />
                                {hoveredSegment === 'emprunt' && hauteurEmprunt > 20 && (
                                  <text
                                    x="575"
                                    y={chartBottom - hauteurTotalFinancement + hauteurEmprunt / 2}
                                    fill="#60a5fa"
                                    fontSize="11"
                                    fontWeight="700"
                                    textAnchor="middle"
                                  >
                                    {formatNumber(financementData.emprunt, 0)} €
                                  </text>
                                )}
                              </g>
                            )}
                            {/* Apport (Equity) - Light Green gradient */}
                            {hauteurApport > 0 && (
                              <g>
                                <rect
                                  x="500"
                                  y={chartBottom - hauteurApport}
                                  width="150"
                                  height={hauteurApport}
                                  fill="url(#gradientApport)"
                                  stroke="rgba(74, 222, 128, 0.9)"
                                  strokeWidth="2"
                                  style={{cursor: 'pointer', transition: 'all 0.3s ease', opacity: hoveredSegment === 'apport' ? 1 : 0.9}}
                                  onMouseEnter={() => setHoveredSegment('apport')}
                                  onMouseLeave={() => setHoveredSegment(null)}
                                />
                                {hoveredSegment === 'apport' && hauteurApport > 20 && (
                                  <text
                                    x="575"
                                    y={chartBottom - hauteurApport / 2}
                                    fill="#4ade80"
                                    fontSize="11"
                                    fontWeight="700"
                                    textAnchor="middle"
                                  >
                                    {formatNumber(financementData.apport, 0)} €
                                  </text>
                                )}
                              </g>
                            )}
                            {/* Label */}
                            <text x="575" y="20" fill="rgba(156, 163, 175, 0.9)" fontSize="14" fontWeight="600" textAnchor="middle">Financement</text>
                            {totalFinancement > 0 && (
                              <text x="520" y={Math.max(chartBottom - hauteurTotalFinancement + 20, 45)} fill="rgba(255, 255, 255, 0.9)" fontSize="12" fontWeight="700">
                                {totalFinancement >= 1000 ? `${Math.round(totalFinancement / 1000)}k` : Math.round(totalFinancement)}
                              </text>
                            )}
                          </g>

                          {/* X-axis labels */}
                          <text x="225" y="385" fill="rgba(156, 163, 175, 0.7)" fontSize="13" fontWeight="600" textAnchor="middle">Coûts du Projet</text>
                          <text x="575" y="385" fill="rgba(156, 163, 175, 0.7)" fontSize="13" fontWeight="600" textAnchor="middle">Financement</text>
                        </>
                      );
                    })()}
                  </svg>

                  {/* Floating Tooltip */}
                  {hoveredSegment && (
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      background: '#222222',
                      backdropFilter: 'blur(10px)',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '2px solid #10b981',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                      zIndex: 1000,
                      minWidth: '180px'
                    }}>
                      <div style={{fontSize: '0.75em', color: 'rgba(156, 163, 175, 0.9)', fontWeight: '600', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                        {hoveredSegment === 'prixAchat' && 'Prix d\'Achat'}
                        {hoveredSegment === 'travaux' && 'Travaux Prévus'}
                        {hoveredSegment === 'frais' && 'Frais Total'}
                        {hoveredSegment === 'emprunt' && 'Emprunt Bancaire'}
                        {hoveredSegment === 'apport' && 'Apport Personnel'}
                      </div>
                      <div style={{fontSize: '1.2em', color: '#ffffff', fontWeight: '700'}}>
                        {hoveredSegment === 'prixAchat' && formatNumber(acquisitionData.prixAchat, 2) + ' €'}
                        {hoveredSegment === 'travaux' && formatNumber(acquisitionData.travaux, 2) + ' €'}
                        {hoveredSegment === 'frais' && formatNumber(acquisitionData.fraisNotaire + acquisitionData.fraisAgence + acquisitionData.autresFrais, 2) + ' €'}
                        {hoveredSegment === 'emprunt' && formatNumber(financementData.emprunt, 2) + ' €'}
                        {hoveredSegment === 'apport' && formatNumber(financementData.apport, 2) + ' €'}
                      </div>
                      {hoveredSegment === 'prixAchat' && (
                        <div style={{fontSize: '0.7em', color: '#9ca3af', marginTop: '4px'}}>
                          {((acquisitionData.prixAchat / totalAcquisition) * 100).toFixed(1)}% du total
                        </div>
                      )}
                      {hoveredSegment === 'travaux' && (
                        <div style={{fontSize: '0.7em', color: '#9ca3af', marginTop: '4px'}}>
                          {((acquisitionData.travaux / totalAcquisition) * 100).toFixed(1)}% du total
                        </div>
                      )}
                      {hoveredSegment === 'frais' && (
                        <div style={{fontSize: '0.7em', color: '#9ca3af', marginTop: '4px'}}>
                          {(((acquisitionData.fraisNotaire + acquisitionData.fraisAgence + acquisitionData.autresFrais) / totalAcquisition) * 100).toFixed(1)}% du total
                        </div>
                      )}
                      {hoveredSegment === 'emprunt' && (
                        <div style={{fontSize: '0.7em', color: '#9ca3af', marginTop: '4px'}}>
                          LTV: {ltv.toFixed(1)}%
                        </div>
                      )}
                      {hoveredSegment === 'apport' && (
                        <div style={{fontSize: '0.7em', color: '#9ca3af', marginTop: '4px'}}>
                          Equity: {equityRatio.toFixed(1)}%
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Legend */}
                <div className="chart-legend">
                  <div className="legend-item">
                    <span className="legend-color" style={{background: 'linear-gradient(to bottom, rgba(147, 197, 253, 0.8), rgba(147, 197, 253, 0.1))', border: '2px solid rgba(147, 197, 253, 0.9)'}}></span>
                    <span className="legend-label">Prix d'Achat</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color" style={{background: 'linear-gradient(to bottom, rgba(253, 230, 138, 0.8), rgba(253, 230, 138, 0.1))', border: '2px solid rgba(253, 230, 138, 0.9)'}}></span>
                    <span className="legend-label">Travaux</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color" style={{background: 'linear-gradient(to bottom, rgba(209, 178, 135, 0.8), rgba(209, 178, 135, 0.1))', border: '2px solid rgba(209, 178, 135, 0.9)'}}></span>
                    <span className="legend-label">Frais (Notaire, Agence, Autres)</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color" style={{background: 'linear-gradient(to bottom, rgba(96, 165, 250, 0.8), rgba(96, 165, 250, 0.1))', border: '2px solid rgba(96, 165, 250, 0.9)'}}></span>
                    <span className="legend-label">Emprunt (Crédit)</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color" style={{background: 'linear-gradient(to bottom, rgba(74, 222, 128, 0.8), rgba(74, 222, 128, 0.1))', border: '2px solid rgba(74, 222, 128, 0.9)'}}></span>
                    <span className="legend-label">Apport (Fonds Propres)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Credit Tab */}
        {activeTab === 'credit' && (
          <div style={{width: '100%', padding: '0 24px'}}>
            {/* Investment Sub-tabs navigation - Sticky */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '24px',
              borderBottom: '1px solid rgba(58, 58, 58, 0.4)',
              position: 'sticky',
              top: 0,
              background: '#0a0a0a',
              zIndex: 100,
              paddingTop: '12px',
              paddingBottom: '0',
              marginLeft: '-24px',
              marginRight: '-24px',
              paddingLeft: '24px',
              paddingRight: '24px'
            }}>
              <button
                onClick={() => { setActiveTab('extraction-charac'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Caractéristiques
              </button>
              <button
                onClick={() => { setActiveTab('acquisition-financement'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Acquisition & Financement
              </button>
              <button
                onClick={() => { setActiveTab('credit'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid rgba(209, 178, 135, 0.9)',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.9em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
              >
                Crédit
              </button>
              <button
                onClick={() => { setActiveTab('recurrent-fees-income'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Charges & Revenus
              </button>
              <button
                onClick={() => { setActiveTab('performance'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Performance
              </button>
              <div style={{ flex: 1 }}></div>
              <button
                onClick={generateBienImmobilierPDF}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(209, 178, 135, 0.15)',
                  border: '1px solid rgba(209, 178, 135, 0.3)',
                  borderRadius: '6px',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.85em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.15)';
                }}
              >
                PDF Bien Immobilier
              </button>
            </div>

            {/* Section Crédit Info - Collapsible */}
            <div
              onClick={() => setShowInfoSections(!showInfoSections)}
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                padding: showInfoSections ? '24px' : '12px 24px',
                borderRadius: '12px',
                border: '1px solid rgba(58, 58, 58, 0.5)',
                marginBottom: '24px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3 style={{ color: '#ffffff', fontSize: '1.1em', fontWeight: '600', margin: 0 }}>
                      Crédit
                    </h3>
                    <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em', transition: 'transform 0.3s ease', transform: showInfoSections ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                  </div>
                  {showInfoSections && (
                    <>
                      <p style={{ color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.95em', lineHeight: '1.6', marginTop: '16px', marginBottom: '16px' }}>
                        Durée (15-25 ans), taux d'intérêt, type de crédit (amortissable, in fine, crescendo), assurance emprunteur.
                      </p>
                      <div style={{
                        borderLeft: '3px solid rgba(156, 163, 175, 0.4)',
                        paddingLeft: '16px',
                        color: 'rgba(156, 163, 175, 0.8)',
                        fontSize: '0.9em',
                        lineHeight: '1.8'
                      }}>
                        <p style={{ marginBottom: '12px' }}><strong style={{ color: '#ffffff' }}>Pourquoi c'est important ?</strong></p>
                        <p style={{ marginBottom: '10px' }}>
                          <strong style={{ color: '#60a5fa' }}>→ Impact sur le Cash Flow :</strong> La mensualité est la charge la plus lourde de votre investissement. Une durée longue (25 ans vs 15 ans) réduit la mensualité et améliore le cash flow mensuel. Un taux d'intérêt plus bas a le même effet : 0.5% de différence sur 200 000€ = ~50€/mois de cash flow en plus. Le type de crédit compte aussi : un crédit in fine (remboursement du capital à l'échéance) maximise le cash flow mensuel mais vous ne remboursez aucun capital pendant la durée.
                        </p>
                        <p style={{ marginBottom: '10px' }}>
                          <strong style={{ color: '#60a5fa' }}>→ Impact sur l'Amortissement du capital :</strong> Chaque mensualité d'un crédit amortissable se décompose en Intérêts + Capital. En début de prêt, la part intérêts est dominante. Avec le temps, la part capital augmente : c'est votre enrichissement patrimonial silencieux. Sur un prêt de 200 000€ à 3.5% sur 20 ans, après 10 ans vous aurez remboursé ~80 000€ de capital — c'est de l'argent que vos locataires ont payé pour vous.
                        </p>
                        <p style={{ marginBottom: '0' }}>
                          <strong style={{ color: '#60a5fa' }}>→ Impact sur le coût total :</strong> Durée longue = plus d'intérêts payés au total. Sur 200 000€ à 3.5%, vous payez ~65 000€ d'intérêts sur 15 ans contre ~115 000€ sur 25 ans. C'est le compromis fondamental : cash flow mensuel vs coût total du financement. L'assurance emprunteur (~0.3%) s'ajoute au taux et impacte directement la mensualité.
                        </p>
                      </div>
                    </>
                  )}
            </div>

            <h2 className="section-title">{t.creditTitle}</h2>

            {/* Input and Results Section - Side by Side */}
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '24px'}}>

              {/* Left: Input Section */}
              <div className="calculation-section" style={{maxHeight: 'none'}}>
                <div className="input-list">
                  {/* Credit Amount */}
                  <div className="input-row" style={{gridTemplateColumns: '2fr 3fr', marginBottom: '15px'}}>
                    <input
                      type="text"
                      className="input-label-field"
                      value={t.creditAmount}
                      disabled
                    />
                    <input
                      type="number"
                      className="input-amount-field"
                      placeholder="Montant"
                      step="1000"
                      min="0"
                      value={creditData.amount || ''}
                      onChange={(e) => {
                        const newAmount = parseFloat(e.target.value) || 0;
                        setHasCustomCreditSplit(true);
                        setCreditData({...creditData, amount: newAmount});
                      }}
                    />
                  </div>
                  {(() => {
                    const defaultCreditTravaux = Math.round((acquisitionData.travaux || 0) / 2);
                    const defaultCreditImmo = Math.max(0, (financementData.emprunt || 0) - defaultCreditTravaux);
                    if (Math.round(creditData.amount || 0) === Math.round(defaultCreditImmo)) return null;
                    return (
                      <div style={{fontSize: '0.78em', color: '#f59e0b', marginTop: '-8px', marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '4px'}}>
                        <span>&#9888;</span> Ce montant ({formatNumber(creditData.amount || 0, 0)} €) ne correspond pas au Crédit Bien Immobilier renseigné dans le Plan de Financement ({formatNumber(defaultCreditImmo, 0)} €).
                      </div>
                    );
                  })()}

                  {/* Duration Slider */}
                  <div className="input-row" style={{gridTemplateColumns: '2fr 3fr', marginBottom: '15px', alignItems: 'center'}}>
                    <input
                      type="text"
                      className="input-label-field"
                      value={t.over}
                      disabled
                    />
                    <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                      <input
                        type="range"
                        min="5"
                        max="30"
                        step="1"
                        value={creditData.durationYears}
                        onChange={(e) => setCreditData({...creditData, durationYears: parseInt(e.target.value)})}
                        style={{
                          width: '100%',
                          height: '6px',
                          borderRadius: '3px',
                          background: `linear-gradient(to right, #4ade80 0%, #4ade80 ${((creditData.durationYears - 5) / 25) * 100}%, rgba(209, 216, 224, 0.5) ${((creditData.durationYears - 5) / 25) * 100}%, rgba(209, 216, 224, 0.5) 100%)`,
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      />
                      <div style={{
                        textAlign: 'right',
                        color: '#6b7894',
                        fontSize: '0.85em',
                        fontWeight: '600'
                      }}>
                        {creditData.durationYears} {t.years}
                      </div>
                    </div>
                  </div>

                  {/* Credit Type */}
                  <div className="input-row" style={{gridTemplateColumns: '2fr 3fr', marginBottom: '15px'}}>
                    <input
                      type="text"
                      className="input-label-field"
                      value={t.creditType}
                      disabled
                    />
                    <select
                      className="input-amount-field"
                      style={{
                        cursor: 'pointer',
                        textAlign: 'left',
                        paddingRight: '30px',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%232c3e5c' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        appearance: 'none'
                      }}
                      value={creditData.type}
                      onChange={(e) => setCreditData({...creditData, type: e.target.value})}
                    >
                      <option value="bullet">{t.bullet}</option>
                      <option value="amortizing">{t.amortizing}</option>
                      <option value="interest-only">{t.interestOnly}</option>
                    </select>
                  </div>

                  {/* Interest Only Period - Only show when Interest Only is selected */}
                  {creditData.type === 'interest-only' && (
                    <div className="input-row" style={{gridTemplateColumns: '2fr 3fr', marginBottom: '15px', alignItems: 'center'}}>
                      <input
                        type="text"
                        className="input-label-field"
                        value={t.interestOnlyPeriod}
                        disabled
                      />
                      <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                        <input
                          type="range"
                          min="1"
                          max={creditData.durationYears * 12}
                          step="1"
                          value={creditData.interestOnlyMonths}
                          onChange={(e) => setCreditData({...creditData, interestOnlyMonths: parseInt(e.target.value)})}
                          style={{
                            width: '100%',
                            height: '6px',
                            borderRadius: '3px',
                            background: `linear-gradient(to right, #60a5fa 0%, #60a5fa ${((creditData.interestOnlyMonths - 1) / (creditData.durationYears * 12 - 1)) * 100}%, rgba(209, 216, 224, 0.5) ${((creditData.interestOnlyMonths - 1) / (creditData.durationYears * 12 - 1)) * 100}%, rgba(209, 216, 224, 0.5) 100%)`,
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        />
                        <div style={{
                          textAlign: 'right',
                          color: '#6b7894',
                          fontSize: '0.85em',
                          fontWeight: '600'
                        }}>
                          {creditData.interestOnlyMonths} mois
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Crescendo Rate - Only show when Crescendo is selected */}
                  {creditData.type === 'crescendo' && (
                    <div className="input-row" style={{gridTemplateColumns: '2fr 3fr', marginBottom: '15px', alignItems: 'center'}}>
                      <input
                        type="text"
                        className="input-label-field"
                        value={t.crescendoRate}
                        disabled
                      />
                      <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                        <input
                          type="range"
                          min="0.5"
                          max="10"
                          step="0.5"
                          value={creditData.crescendoRate}
                          onChange={(e) => setCreditData({...creditData, crescendoRate: parseFloat(e.target.value)})}
                          style={{
                            width: '100%',
                            height: '6px',
                            borderRadius: '3px',
                            background: `linear-gradient(to right, #60a5fa 0%, #60a5fa ${((creditData.crescendoRate - 0.5) / (10 - 0.5)) * 100}%, rgba(209, 216, 224, 0.5) ${((creditData.crescendoRate - 0.5) / (10 - 0.5)) * 100}%, rgba(209, 216, 224, 0.5) 100%)`,
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        />
                        <div style={{
                          textAlign: 'right',
                          color: '#6b7894',
                          fontSize: '0.85em',
                          fontWeight: '600'
                        }}>
                          {creditData.crescendoRate}%
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Interest Rate */}
                  <div className="input-row" style={{gridTemplateColumns: '2fr 3fr', marginBottom: '15px'}}>
                    <input
                      type="text"
                      className="input-label-field"
                      value={t.interestRate}
                      disabled
                    />
                    <input
                      type="number"
                      className="input-amount-field"
                      placeholder="Taux"
                      step="0.01"
                      min="0"
                      max="100"
                      value={creditData.interestRate || ''}
                      onChange={(e) => setCreditData({...creditData, interestRate: parseFloat(e.target.value) || 0})}
                    />
                  </div>

                  {/* Payment Frequency */}
                  <div className="input-row" style={{gridTemplateColumns: '2fr 3fr', marginBottom: '15px'}}>
                    <input
                      type="text"
                      className="input-label-field"
                      value={t.paymentFrequency}
                      disabled
                    />
                    <select
                      className="input-amount-field"
                      style={{
                        cursor: 'pointer',
                        textAlign: 'left',
                        paddingRight: '30px',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%232c3e5c' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        appearance: 'none'
                      }}
                      value={creditData.paymentFrequency || 'monthly'}
                      onChange={(e) => setCreditData({...creditData, paymentFrequency: e.target.value})}
                    >
                      <option value="monthly">{t.monthly}</option>
                      <option value="quarterly">{t.quarterly}</option>
                      <option value="annually">{t.annually}</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Right: Results Section */}
              {creditData.amount > 0 && creditData.interestRate > 0 && (
                <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                  {/* Payment Card */}
                  <div style={{
                    padding: '30px',
                    background: '#222222',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '16px',
                    border: '1px solid #2a2a2a',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease-in-out'
                  }}>
                    <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.9em', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '500'}}>
                      {creditData.paymentFrequency === 'annually' ? t.annualPayment : creditData.paymentFrequency === 'quarterly' ? t.quarterlyPayment : t.monthlyPayment}
                    </div>

                    {creditData.type === 'interest-only' && creditAmortization.interestOnlyPayment > 0 && creditAmortization.amortizingPayment > 0 ? (
                      <>
                        <div style={{marginBottom: '15px'}}>
                          <div style={{color: 'rgba(156, 163, 175, 0.8)', fontSize: '0.8em', marginBottom: '6px'}}>
                            {creditData.paymentFrequency === 'annually' ? t.interestOnlyPaymentAnnually : creditData.paymentFrequency === 'quarterly' ? t.interestOnlyPaymentQuarterly : t.interestOnlyPayment}
                          </div>
                          <div style={{color: '#ffffff', fontSize: '1.8em', fontWeight: '700', lineHeight: '1'}}>{formatNumber(creditAmortization.interestOnlyPayment, 0)}</div>
                        </div>
                        <div>
                          <div style={{color: 'rgba(156, 163, 175, 0.8)', fontSize: '0.8em', marginBottom: '6px'}}>
                            {creditData.paymentFrequency === 'annually' ? t.amortizingPaymentAnnually : creditData.paymentFrequency === 'quarterly' ? t.amortizingPaymentQuarterly : t.amortizingPayment}
                          </div>
                          <div style={{color: '#60a5fa', fontSize: '2.2em', fontWeight: '700', lineHeight: '1'}}>{formatNumber(creditAmortization.amortizingPayment, 0)}</div>
                        </div>
                      </>
                    ) : creditData.type === 'crescendo' && creditAmortization.initialPayment > 0 && creditAmortization.finalPayment > 0 ? (
                      <>
                        <div style={{marginBottom: '15px'}}>
                          <div style={{color: 'rgba(156, 163, 175, 0.8)', fontSize: '0.8em', marginBottom: '6px'}}>
                            {creditData.paymentFrequency === 'annually' ? t.initialPaymentAnnually : creditData.paymentFrequency === 'quarterly' ? t.initialPaymentQuarterly : t.initialPayment}
                          </div>
                          <div style={{color: '#ffffff', fontSize: '1.8em', fontWeight: '700', lineHeight: '1'}}>{formatNumber(creditAmortization.initialPayment, 0)}</div>
                        </div>
                        <div>
                          <div style={{color: 'rgba(156, 163, 175, 0.8)', fontSize: '0.8em', marginBottom: '6px'}}>
                            {creditData.paymentFrequency === 'annually' ? t.finalPaymentAnnually : creditData.paymentFrequency === 'quarterly' ? t.finalPaymentQuarterly : t.finalPayment}
                          </div>
                          <div style={{color: '#60a5fa', fontSize: '2.2em', fontWeight: '700', lineHeight: '1'}}>{formatNumber(creditAmortization.finalPayment, 0)}</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{color: '#ffffff', fontSize: '2.5em', fontWeight: '700', lineHeight: '1', transition: 'all 0.3s ease-in-out'}}>{formatNumber(creditAmortization.periodicPayment, 0)}</div>
                        {creditData.paymentFrequency !== 'monthly' && (
                          <div style={{color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.8em', marginTop: '12px'}}>
                            {language === 'fr' ? 'Équivalent mensuel' : 'Monthly equivalent'}: {formatNumber(creditAmortization.monthlyPayment, 0)} €
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Total Credit Cost Card */}
                  <div style={{
                    padding: '30px',
                    background: '#222222',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '16px',
                    border: '1px solid #2a2a2a',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease-in-out'
                  }}>
                    <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.9em', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '500'}}>{t.interest}</div>
                    <div style={{color: '#ffffff', fontSize: '2.5em', fontWeight: '700', lineHeight: '1', transition: 'all 0.3s ease-in-out'}}>{formatNumber(creditAmortization.totalInterest, 0)}</div>
                    <div style={{color: 'rgba(156, 163, 175, 0.8)', fontSize: '0.85em', marginTop: '8px', transition: 'all 0.3s ease-in-out'}}>{t.totalCreditCost}: {formatNumber(creditAmortization.totalCost, 0)} €</div>
                    {creditData.type === 'interest-only' && creditAmortization.totalInterestOnlyPeriodCost > 0 && (
                      <div style={{color: 'rgba(16, 185, 129, 0.9)', fontSize: '0.85em', marginTop: '6px', transition: 'all 0.3s ease-in-out'}}>
                        {t.totalInterestOnlyPeriod}: {formatNumber(creditAmortization.totalInterestOnlyPeriodCost, 0)} €
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Crédit Travaux Section */}
            <h2 className="section-title" style={{ marginTop: '32px' }}>Crédit Travaux</h2>

            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '24px'}}>

              {/* Left: Input Section */}
              <div className="calculation-section" style={{maxHeight: 'none'}}>
                <div className="input-list">
                  {/* Credit Travaux Amount */}
                  <div className="input-row" style={{gridTemplateColumns: '2fr 3fr', marginBottom: '15px'}}>
                    <input type="text" className="input-label-field" value="Montant du Crédit" disabled />
                    <input
                      type="number"
                      className="input-amount-field"
                      placeholder="Montant"
                      step="1000"
                      min="0"
                      value={creditTravauxData.amount || ''}
                      onChange={(e) => setCreditTravauxData({...creditTravauxData, amount: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  {(() => {
                    const defaultCreditTravaux = Math.round((acquisitionData.travaux || 0) / 2);
                    if (Math.round(creditTravauxData.amount || 0) === Math.round(defaultCreditTravaux)) return null;
                    return (
                      <div style={{fontSize: '0.78em', color: '#f59e0b', marginTop: '-8px', marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '4px'}}>
                        <span>&#9888;</span> Ce montant ({formatNumber(creditTravauxData.amount || 0, 0)} €) ne correspond pas au Crédit Travaux renseigné dans le Plan de Financement ({formatNumber(defaultCreditTravaux, 0)} €).
                      </div>
                    );
                  })()}

                  {/* Duration Slider */}
                  <div className="input-row" style={{gridTemplateColumns: '2fr 3fr', marginBottom: '15px', alignItems: 'center'}}>
                    <input type="text" className="input-label-field" value={t.over} disabled />
                    <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                      <input
                        type="range" min="1" max="25" step="1"
                        value={creditTravauxData.durationYears}
                        onChange={(e) => setCreditTravauxData({...creditTravauxData, durationYears: parseInt(e.target.value)})}
                        style={{
                          width: '100%', height: '6px', borderRadius: '3px',
                          background: `linear-gradient(to right, #60a5fa 0%, #60a5fa ${((creditTravauxData.durationYears - 1) / 24) * 100}%, rgba(209, 216, 224, 0.5) ${((creditTravauxData.durationYears - 1) / 24) * 100}%, rgba(209, 216, 224, 0.5) 100%)`,
                          outline: 'none', cursor: 'pointer'
                        }}
                      />
                      <div style={{ textAlign: 'right', color: '#6b7894', fontSize: '0.85em', fontWeight: '600' }}>
                        {creditTravauxData.durationYears} {t.years}
                      </div>
                    </div>
                  </div>

                  {/* Credit Type */}
                  <div className="input-row" style={{gridTemplateColumns: '2fr 3fr', marginBottom: '15px'}}>
                    <input type="text" className="input-label-field" value={t.creditType} disabled />
                    <select
                      className="input-amount-field"
                      style={{ cursor: 'pointer', textAlign: 'left', paddingRight: '30px', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%232c3e5c' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', appearance: 'none' }}
                      value={creditTravauxData.type}
                      onChange={(e) => setCreditTravauxData({...creditTravauxData, type: e.target.value})}
                    >
                      <option value="bullet">{t.bullet}</option>
                      <option value="amortizing">{t.amortizing}</option>
                      <option value="interest-only">{t.interestOnly}</option>
                    </select>
                  </div>

                  {/* Interest Only Period */}
                  {creditTravauxData.type === 'interest-only' && (
                    <div className="input-row" style={{gridTemplateColumns: '2fr 3fr', marginBottom: '15px', alignItems: 'center'}}>
                      <input type="text" className="input-label-field" value={t.interestOnlyPeriod} disabled />
                      <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                        <input
                          type="range" min="1" max={creditTravauxData.durationYears * 12} step="1"
                          value={creditTravauxData.interestOnlyMonths}
                          onChange={(e) => setCreditTravauxData({...creditTravauxData, interestOnlyMonths: parseInt(e.target.value)})}
                          style={{
                            width: '100%', height: '6px', borderRadius: '3px',
                            background: `linear-gradient(to right, #60a5fa 0%, #60a5fa ${((creditTravauxData.interestOnlyMonths - 1) / (creditTravauxData.durationYears * 12 - 1)) * 100}%, rgba(209, 216, 224, 0.5) ${((creditTravauxData.interestOnlyMonths - 1) / (creditTravauxData.durationYears * 12 - 1)) * 100}%, rgba(209, 216, 224, 0.5) 100%)`,
                            outline: 'none', cursor: 'pointer'
                          }}
                        />
                        <div style={{ textAlign: 'right', color: '#6b7894', fontSize: '0.85em', fontWeight: '600' }}>
                          {creditTravauxData.interestOnlyMonths} mois
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Interest Rate */}
                  <div className="input-row" style={{gridTemplateColumns: '2fr 3fr', marginBottom: '15px'}}>
                    <input type="text" className="input-label-field" value={t.interestRate} disabled />
                    <input
                      type="number"
                      className="input-amount-field"
                      placeholder="Taux"
                      step="0.01" min="0" max="100"
                      value={creditTravauxData.interestRate || ''}
                      onChange={(e) => setCreditTravauxData({...creditTravauxData, interestRate: parseFloat(e.target.value) || 0})}
                    />
                  </div>

                  {/* Payment Frequency */}
                  <div className="input-row" style={{gridTemplateColumns: '2fr 3fr', marginBottom: '15px'}}>
                    <input type="text" className="input-label-field" value={t.paymentFrequency} disabled />
                    <select
                      className="input-amount-field"
                      style={{ cursor: 'pointer', textAlign: 'left', paddingRight: '30px', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%232c3e5c' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', appearance: 'none' }}
                      value={creditTravauxData.paymentFrequency || 'monthly'}
                      onChange={(e) => setCreditTravauxData({...creditTravauxData, paymentFrequency: e.target.value})}
                    >
                      <option value="monthly">{t.monthly}</option>
                      <option value="quarterly">{t.quarterly}</option>
                      <option value="annually">{t.annually}</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Right: Results Section */}
              {creditTravauxData.amount > 0 && creditTravauxData.interestRate > 0 && (
                <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                  {/* Payment Card */}
                  <div style={{
                    padding: '30px', background: '#222222', backdropFilter: 'blur(20px)', borderRadius: '16px',
                    border: '1px solid #2a2a2a', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)', textAlign: 'center', transition: 'all 0.3s ease-in-out'
                  }}>
                    <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.9em', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '500'}}>
                      {creditTravauxData.paymentFrequency === 'annually' ? t.annualPayment : creditTravauxData.paymentFrequency === 'quarterly' ? t.quarterlyPayment : t.monthlyPayment}
                    </div>
                    {creditTravauxData.type === 'interest-only' && creditTravauxAmortization.interestOnlyPayment > 0 && creditTravauxAmortization.amortizingPayment > 0 ? (
                      <>
                        <div style={{marginBottom: '15px'}}>
                          <div style={{color: 'rgba(156, 163, 175, 0.8)', fontSize: '0.8em', marginBottom: '6px'}}>{t.interestOnlyPayment}</div>
                          <div style={{color: '#ffffff', fontSize: '1.8em', fontWeight: '700', lineHeight: '1'}}>{formatNumber(creditTravauxAmortization.interestOnlyPayment, 0)}</div>
                        </div>
                        <div>
                          <div style={{color: 'rgba(156, 163, 175, 0.8)', fontSize: '0.8em', marginBottom: '6px'}}>{t.amortizingPayment}</div>
                          <div style={{color: '#60a5fa', fontSize: '2.2em', fontWeight: '700', lineHeight: '1'}}>{formatNumber(creditTravauxAmortization.amortizingPayment, 0)}</div>
                        </div>
                      </>
                    ) : (
                      <div style={{color: '#ffffff', fontSize: '2.5em', fontWeight: '700', lineHeight: '1', transition: 'all 0.3s ease-in-out'}}>{formatNumber(creditTravauxAmortization.periodicPayment, 0)}</div>
                    )}
                  </div>

                  {/* Total Interest Card */}
                  <div style={{
                    padding: '30px', background: '#222222', backdropFilter: 'blur(20px)', borderRadius: '16px',
                    border: '1px solid #2a2a2a', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)', textAlign: 'center', transition: 'all 0.3s ease-in-out'
                  }}>
                    <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.9em', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '500'}}>{t.interest}</div>
                    <div style={{color: '#ffffff', fontSize: '2.5em', fontWeight: '700', lineHeight: '1', transition: 'all 0.3s ease-in-out'}}>{formatNumber(creditTravauxAmortization.totalInterest, 0)}</div>
                    <div style={{color: 'rgba(156, 163, 175, 0.8)', fontSize: '0.85em', marginTop: '8px', transition: 'all 0.3s ease-in-out'}}>{t.totalCreditCost}: {formatNumber(creditTravauxAmortization.totalCost, 0)} €</div>
                  </div>
                </div>
              )}
            </div>

            {/* Chart and Table Section */}
            {((creditData.amount > 0 && creditData.interestRate > 0) || (creditTravauxData.amount > 0 && creditTravauxData.interestRate > 0)) && (
              <>

                {/* Amortization Chart */}
                <div style={{
                  padding: '20px',
                  background: '#222222',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '16px',
                  border: '1px solid #2a2a2a',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  marginBottom: '15px',
                  width: '100%'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                    <h2 style={{fontSize: '1.1em', color: '#ffffff', fontWeight: '600', margin: 0}}>{t.loanPaymentSchedule}</h2>
                    {creditTravauxData.amount > 0 && creditTravauxData.interestRate > 0 && (
                      <select
                        value={amortizationCreditView}
                        onChange={(e) => setAmortizationCreditView(e.target.value)}
                        style={{
                          padding: '6px 28px 6px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(58,58,58,0.6)',
                          borderRadius: '6px', color: '#ffffff', fontSize: '0.85em', cursor: 'pointer', appearance: 'none',
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center'
                        }}
                      >
                        <option value="immobilier" style={{ background: '#1a1a1a' }}>Crédit Immobilier</option>
                        <option value="travaux" style={{ background: '#1a1a1a' }}>Crédit Travaux</option>
                      </select>
                    )}
                  </div>
                  <div style={{marginTop: '10px', width: '100%'}}>
                    <div style={{position: 'relative', padding: '15px 10px', width: '100%'}}>
                      <svg viewBox="0 0 1000 300" preserveAspectRatio="xMidYMid meet" style={{width: '100%', height: 'auto', minHeight: '250px', maxHeight: '400px'}}>
                        {(() => {
                          const activeCredit = amortizationCreditView === 'travaux' ? creditTravauxAmortization : creditAmortization;
                          const schedule = activeCredit.schedule;
                          if (schedule.length === 0) return null;

                          const chartHeight = 230;
                          const chartBottom = 260;
                          const chartTop = 20;
                          const chartLeft = 60;
                          const chartRight = 980;
                          const chartWidth = chartRight - chartLeft;

                          // Find max payment for scaling
                          const maxPayment = Math.max(...schedule.map(s => s.payment));
                          const scale = chartHeight / maxPayment;

                          // Sample points (show every Nth month to avoid overcrowding)
                          const totalMonths = schedule.length;
                          const sampleRate = Math.ceil(totalMonths / 25); // Show ~25 points
                          const sampledSchedule = schedule.filter((_, i) => i % sampleRate === 0 || i === schedule.length - 1);

                          return (
                            <>
                              {/* Y-axis labels */}
                              {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
                                const value = maxPayment * (1 - ratio);
                                const y = chartTop + (chartHeight * ratio);
                                return (
                                  <g key={i}>
                                    <text x={chartLeft - 10} y={y + 4} fill="rgba(156, 163, 175, 0.9)" fontSize="11" textAnchor="end" style={{transition: 'all 0.5s ease-in-out'}}>
                                      {formatNumber(value, 0)}
                                    </text>
                                    <line
                                      x1={chartLeft}
                                      y1={y}
                                      x2={chartRight}
                                      y2={y}
                                      stroke="#2a2a2a"
                                      strokeWidth="1"
                                      style={{transition: 'all 0.5s ease-in-out'}}
                                    />
                                  </g>
                                );
                              })}

                              {/* Principal area (stacked) */}
                              <path
                                d={`M ${chartLeft} ${chartBottom} ${sampledSchedule.map((s, i) => {
                                  const x = chartLeft + (i / (sampledSchedule.length - 1)) * chartWidth;
                                  const y = chartBottom - (s.principal * scale);
                                  return `L ${x} ${y}`;
                                }).join(' ')} L ${chartRight} ${chartBottom} Z`}
                                fill="rgba(74, 222, 128, 0.6)"
                                stroke="rgba(74, 222, 128, 0.8)"
                                strokeWidth="2"
                                style={{transition: 'd 0.5s ease-in-out'}}
                              />

                              {/* Interest area (on top of principal) */}
                              <path
                                d={`M ${chartLeft} ${chartBottom} ${sampledSchedule.map((s, i) => {
                                  const x = chartLeft + (i / (sampledSchedule.length - 1)) * chartWidth;
                                  const y = chartBottom - ((s.principal + s.interest) * scale);
                                  return `L ${x} ${y}`;
                                }).join(' ')} L ${chartRight} ${chartBottom} Z`}
                                fill="rgba(16, 185, 129, 0.6)"
                                stroke="rgba(16, 185, 129, 0.8)"
                                strokeWidth="2"
                                style={{transition: 'd 0.5s ease-in-out'}}
                              />

                              {/* Interactive hover points */}
                              {sampledSchedule.map((s, i) => {
                                const x = chartLeft + (i / (sampledSchedule.length - 1)) * chartWidth;
                                const y = chartBottom - ((s.principal + s.interest) * scale);
                                const isHovered = chartTooltip && chartTooltip.type === 'amortization' && chartTooltip.data.month === s.month;
                                return (
                                  <g key={`point-${i}`}>
                                    {/* Vertical line on hover */}
                                    {isHovered && (
                                      <line
                                        x1={x}
                                        y1={chartTop}
                                        x2={x}
                                        y2={chartBottom}
                                        stroke="rgba(255, 255, 255, 0.3)"
                                        strokeWidth="1"
                                        strokeDasharray="4,4"
                                      />
                                    )}
                                    {/* Invisible larger hit area */}
                                    <rect
                                      x={x - 20}
                                      y={chartTop}
                                      width={40}
                                      height={chartHeight}
                                      fill="transparent"
                                      style={{cursor: 'pointer'}}
                                      onMouseEnter={(e) => {
                                        const rect = e.target.getBoundingClientRect();
                                        setChartTooltip({
                                          x: rect.left + rect.width / 2,
                                          y: rect.top,
                                          data: s,
                                          type: 'amortization'
                                        });
                                      }}
                                      onMouseLeave={() => setChartTooltip(null)}
                                    />
                                    {/* Visible point */}
                                    <circle
                                      cx={x}
                                      cy={y}
                                      r={isHovered ? 6 : 4}
                                      fill={isHovered ? '#10b981' : 'rgba(16, 185, 129, 0.8)'}
                                      stroke="#ffffff"
                                      strokeWidth={isHovered ? 2 : 1}
                                      style={{transition: 'all 0.2s ease', pointerEvents: 'none'}}
                                    />
                                  </g>
                                );
                              })}

                              {/* X-axis labels (year markers) */}
                              {[0, Math.floor(totalMonths / 4), Math.floor(totalMonths / 2), Math.floor(3 * totalMonths / 4), totalMonths - 1].map((monthIdx, i) => {
                                const month = schedule[monthIdx];
                                const x = chartLeft + (monthIdx / (totalMonths - 1)) * chartWidth;
                                return (
                                  <text key={i} x={x} y={chartBottom + 20} fill="rgba(156, 163, 175, 0.9)" fontSize="11" textAnchor="middle" style={{transition: 'all 0.5s ease-in-out'}}>
                                    {Math.floor(month.month / 12)}y
                                  </text>
                                );
                              })}

                              {/* Axis labels */}
                              <text x={chartLeft - 50} y={chartTop + chartHeight / 2} fill="rgba(156, 163, 175, 0.9)" fontSize="13" fontWeight="600" textAnchor="middle" transform={`rotate(-90, ${chartLeft - 50}, ${chartTop + chartHeight / 2})`}>
                                {t.paymentEuro}
                              </text>
                              <text x={chartLeft + chartWidth / 2} y={chartBottom + 40} fill="rgba(156, 163, 175, 0.9)" fontSize="13" fontWeight="600" textAnchor="middle">
                                {t.yearsAxis}
                              </text>
                            </>
                          );
                        })()}
                      </svg>
                    </div>

                    {/* Legend */}
                    <div style={{marginTop: '15px', display: 'flex', gap: '20px', justifyContent: 'center'}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <span style={{backgroundColor: 'rgba(16, 185, 129, 0.8)', width: '16px', height: '16px', borderRadius: '3px', display: 'inline-block'}}></span>
                        <span style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>{t.interest}</span>
                      </div>
                      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <span style={{backgroundColor: 'rgba(74, 222, 128, 0.8)', width: '16px', height: '16px', borderRadius: '3px', display: 'inline-block'}}></span>
                        <span style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>{t.principal}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Amortization Table */}
                <div style={{
                  background: '#222222',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '16px',
                  border: '1px solid #2a2a2a',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}>
                  {/* Fixed header outside scrollable area */}
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 15px 20px', flexWrap: 'wrap', gap: '12px'}}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <h2 style={{fontSize: '1.3em', color: '#ffffff', fontWeight: '600', margin: 0}}>{t.amortizationTable}</h2>
                    </div>

                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                      {/* View mode buttons */}
                      <div style={{display: 'flex', gap: '8px', background: '#1a1a1a', borderRadius: '8px', padding: '4px'}}>
                        <button
                          onClick={() => setAmortizationViewMode('monthly')}
                          style={{
                            padding: '6px 14px',
                            background: amortizationViewMode === 'monthly' ? '#10b981' : 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#ffffff',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: '600',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          Mensuel
                        </button>
                        <button
                          onClick={() => setAmortizationViewMode('quarterly')}
                          style={{
                            padding: '6px 14px',
                            background: amortizationViewMode === 'quarterly' ? '#10b981' : 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#ffffff',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: '600',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          Trimestriel
                        </button>
                        <button
                          onClick={() => setAmortizationViewMode('annually')}
                          style={{
                            padding: '6px 14px',
                            background: amortizationViewMode === 'annually' ? '#10b981' : 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#ffffff',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: '600',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          Annuel
                        </button>
                      </div>

                      <button
                      onClick={() => {
                        const XLSX = require('xlsx');
                        const wb = XLSX.utils.book_new();
                        // Sheet 1: Crédit Immobilier
                        const wsImmo = XLSX.utils.aoa_to_sheet([
                          [t.month, ...creditAmortization.schedule.map(row => row.month)],
                          [t.payment, ...creditAmortization.schedule.map(row => row.payment)],
                          [t.principal, ...creditAmortization.schedule.map(row => row.principal)],
                          [t.interest, ...creditAmortization.schedule.map(row => row.interest)],
                          [t.remainingBalance, ...creditAmortization.schedule.map(row => row.remainingBalance)]
                        ]);
                        wsImmo['!cols'] = [{wch: 18}, ...creditAmortization.schedule.map(() => ({wch: 12}))];
                        XLSX.utils.book_append_sheet(wb, wsImmo, 'Crédit Immobilier');
                        // Sheet 2: Crédit Travaux (if applicable)
                        if (creditTravauxData.amount > 0 && creditTravauxAmortization.schedule.length > 0) {
                          const wsTrav = XLSX.utils.aoa_to_sheet([
                            [t.month, ...creditTravauxAmortization.schedule.map(row => row.month)],
                            [t.payment, ...creditTravauxAmortization.schedule.map(row => row.payment)],
                            [t.principal, ...creditTravauxAmortization.schedule.map(row => row.principal)],
                            [t.interest, ...creditTravauxAmortization.schedule.map(row => row.interest)],
                            [t.remainingBalance, ...creditTravauxAmortization.schedule.map(row => row.remainingBalance)]
                          ]);
                          wsTrav['!cols'] = [{wch: 18}, ...creditTravauxAmortization.schedule.map(() => ({wch: 12}))];
                          XLSX.utils.book_append_sheet(wb, wsTrav, 'Crédit Travaux');
                        }
                        XLSX.writeFile(wb, 'tableau_amortissement.xlsx');
                      }}
                      style={{
                        padding: '8px 16px',
                        background: 'rgba(59, 130, 246, 0.2)',
                        border: '1px solid rgba(59, 130, 246, 0.4)',
                        borderRadius: '6px',
                        color: '#60a5fa',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                      onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      title="Télécharger le tableau en Excel"
                    >
                      .xlsx
                    </button>
                    </div>
                  </div>
                  {/* Scrollable table area */}
                  <div style={{maxHeight: '400px', overflow: 'auto', padding: '0 20px 20px 20px'}}>
                  <table style={{borderCollapse: 'collapse', fontSize: '1.05em', width: '100%'}}>
                    <tbody>
                      {(() => {
                        const hasTravaux = creditTravauxData.amount > 0 && creditTravauxAmortization.schedule.length > 0;
                        const creditSets = [
                          { label: 'Crédit Immobilier', color: '#60a5fa', schedule: creditAmortization.schedule },
                          ...(hasTravaux ? [{ label: 'Crédit Travaux', color: '#60a5fa', schedule: creditTravauxAmortization.schedule }] : [])
                        ];

                        // Use the longest schedule for period headers
                        const longestSchedule = creditSets.reduce((longest, cs) => cs.schedule.length > longest.length ? cs.schedule : longest, []);

                        const aggregateSchedule = (schedule) => {
                          let aggregatedData = [];
                          if (amortizationViewMode === 'monthly') {
                            aggregatedData = schedule.map((row) => ({
                              period: `Mois ${row.month}`,
                              payment: row.payment,
                              principal: row.principal,
                              interest: row.interest,
                              balance: row.remainingBalance
                            }));
                          } else if (amortizationViewMode === 'quarterly') {
                            const quarters = Math.ceil(schedule.length / 3);
                            for (let i = 0; i < quarters; i++) {
                              const startIdx = i * 3;
                              const endIdx = Math.min((i + 1) * 3, schedule.length);
                              const quarterData = schedule.slice(startIdx, endIdx);
                              aggregatedData.push({
                                period: `Trimestre ${i + 1}`,
                                payment: quarterData.reduce((sum, r) => sum + r.payment, 0),
                                principal: quarterData.reduce((sum, r) => sum + r.principal, 0),
                                interest: quarterData.reduce((sum, r) => sum + r.interest, 0),
                                balance: quarterData[quarterData.length - 1].remainingBalance
                              });
                            }
                          } else {
                            const years = Math.ceil(schedule.length / 12);
                            for (let i = 0; i < years; i++) {
                              const startIdx = i * 12;
                              const endIdx = Math.min((i + 1) * 12, schedule.length);
                              const yearData = schedule.slice(startIdx, endIdx);
                              aggregatedData.push({
                                period: `Année ${i + 1}`,
                                payment: yearData.reduce((sum, r) => sum + r.payment, 0),
                                principal: yearData.reduce((sum, r) => sum + r.principal, 0),
                                interest: yearData.reduce((sum, r) => sum + r.interest, 0),
                                balance: yearData[yearData.length - 1].remainingBalance
                              });
                            }
                          }
                          return aggregatedData;
                        };

                        const periodHeaders = aggregateSchedule(longestSchedule);

                        return (
                          <>
                            {/* Period header row */}
                            <tr style={{borderBottom: '2px solid #2a2a2a', background: '#1f1f1f'}}>
                              <td style={{
                                padding: '10px 15px', textAlign: 'left', color: 'rgba(156, 163, 175, 0.9)',
                                fontWeight: '600', fontSize: '0.9em', position: 'sticky', left: 0,
                                background: '#1f1f1f', minWidth: '180px', zIndex: 10
                              }}>
                              </td>
                              {periodHeaders.map((data, idx) => (
                                <td key={idx} style={{
                                  padding: '8px 12px', textAlign: 'right', fontWeight: '600',
                                  color: '#ffffff', fontSize: '0.85em', minWidth: '110px'
                                }}>
                                  {data.period}
                                </td>
                              ))}
                              <td style={{
                                padding: '8px 12px', textAlign: 'right', fontWeight: '700',
                                color: '#ffffff', fontSize: '0.85em', minWidth: '100px'
                              }}>
                                Total
                              </td>
                            </tr>

                            {creditSets.map((creditSet, csIdx) => {
                              const aggregatedData = aggregateSchedule(creditSet.schedule);
                              return (
                                <React.Fragment key={csIdx}>
                                  {/* Credit label row */}
                                  <tr style={{borderBottom: '1px solid #2a2a2a', background: 'rgba(255,255,255,0.03)'}}>
                                    <td colSpan={periodHeaders.length + 2} style={{
                                      padding: '8px 15px', fontWeight: '700', fontSize: '0.9em',
                                      color: creditSet.color, position: 'sticky', left: 0,
                                      background: 'rgba(255,255,255,0.03)', zIndex: 10
                                    }}>
                                      {creditSet.label}
                                    </td>
                                  </tr>

                                  {/* Payment row */}
                                  <tr style={{borderBottom: '1px solid #2a2a2a'}}>
                                    <td style={{
                                      padding: '8px 15px', textAlign: 'left', color: 'rgba(156, 163, 175, 0.9)',
                                      fontWeight: '600', fontSize: '0.85em', position: 'sticky', left: 0,
                                      background: '#222222', zIndex: 10, paddingLeft: '25px'
                                    }}>
                                      {t.payment}
                                    </td>
                                    {aggregatedData.map((data, idx) => (
                                      <td key={idx} style={{
                                        padding: '8px 12px', textAlign: 'right', fontWeight: '500',
                                        color: '#ffffff', fontSize: '0.85em', whiteSpace: 'nowrap'
                                      }}>
                                        {formatNumber(data.payment, 0)}
                                      </td>
                                    ))}
                                    {/* Pad empty cells if shorter schedule */}
                                    {Array.from({length: periodHeaders.length - aggregatedData.length}).map((_, idx) => (
                                      <td key={`pad-${idx}`} style={{padding: '8px 12px'}}></td>
                                    ))}
                                    <td style={{
                                      padding: '8px 12px', textAlign: 'right', fontWeight: '700',
                                      color: '#ffffff', fontSize: '0.85em', whiteSpace: 'nowrap'
                                    }}>
                                      {formatNumber(aggregatedData.reduce((sum, d) => sum + d.payment, 0), 0)}
                                    </td>
                                  </tr>

                                  {/* Principal row */}
                                  <tr style={{borderBottom: '1px solid #2a2a2a', background: 'rgba(34, 197, 94, 0.05)'}}>
                                    <td style={{
                                      padding: '8px 15px', textAlign: 'left', color: '#4ade80',
                                      fontWeight: '600', fontSize: '0.85em', position: 'sticky', left: 0,
                                      background: 'rgba(34, 197, 94, 0.05)', zIndex: 10, paddingLeft: '25px'
                                    }}>
                                      {t.principal}
                                    </td>
                                    {aggregatedData.map((data, idx) => (
                                      <td key={idx} style={{
                                        padding: '8px 12px', textAlign: 'right', fontWeight: '500',
                                        color: '#4ade80', fontSize: '0.85em', whiteSpace: 'nowrap',
                                        background: 'rgba(34, 197, 94, 0.05)'
                                      }}>
                                        {formatNumber(data.principal, 0)}
                                      </td>
                                    ))}
                                    {Array.from({length: periodHeaders.length - aggregatedData.length}).map((_, idx) => (
                                      <td key={`pad-${idx}`} style={{padding: '8px 12px', background: 'rgba(34, 197, 94, 0.05)'}}></td>
                                    ))}
                                    <td style={{
                                      padding: '8px 12px', textAlign: 'right', fontWeight: '700',
                                      color: '#4ade80', fontSize: '0.85em', whiteSpace: 'nowrap',
                                      background: 'rgba(34, 197, 94, 0.05)'
                                    }}>
                                      {formatNumber(aggregatedData.reduce((sum, d) => sum + d.principal, 0), 0)}
                                    </td>
                                  </tr>

                                  {/* Interest row */}
                                  <tr style={{borderBottom: '1px solid #2a2a2a', background: 'rgba(239, 68, 68, 0.05)'}}>
                                    <td style={{
                                      padding: '8px 15px', textAlign: 'left', color: '#f87171',
                                      fontWeight: '600', fontSize: '0.85em', position: 'sticky', left: 0,
                                      background: 'rgba(239, 68, 68, 0.05)', zIndex: 10, paddingLeft: '25px'
                                    }}>
                                      {t.interest}
                                    </td>
                                    {aggregatedData.map((data, idx) => (
                                      <td key={idx} style={{
                                        padding: '8px 12px', textAlign: 'right', fontWeight: '500',
                                        color: '#f87171', fontSize: '0.85em', whiteSpace: 'nowrap',
                                        background: 'rgba(239, 68, 68, 0.05)'
                                      }}>
                                        {formatNumber(data.interest, 0)}
                                      </td>
                                    ))}
                                    {Array.from({length: periodHeaders.length - aggregatedData.length}).map((_, idx) => (
                                      <td key={`pad-${idx}`} style={{padding: '8px 12px', background: 'rgba(239, 68, 68, 0.05)'}}></td>
                                    ))}
                                    <td style={{
                                      padding: '8px 12px', textAlign: 'right', fontWeight: '700',
                                      color: '#f87171', fontSize: '0.85em', whiteSpace: 'nowrap',
                                      background: 'rgba(239, 68, 68, 0.05)'
                                    }}>
                                      {formatNumber(aggregatedData.reduce((sum, d) => sum + d.interest, 0), 0)}
                                    </td>
                                  </tr>

                                  {/* Remaining Balance row */}
                                  <tr style={{background: '#1a1a1a', borderBottom: csIdx < creditSets.length - 1 ? '3px solid #333' : 'none'}}>
                                    <td style={{
                                      padding: '8px 15px', textAlign: 'left', color: 'rgba(156, 163, 175, 0.9)',
                                      fontWeight: '600', fontSize: '0.85em', position: 'sticky', left: 0,
                                      background: '#1a1a1a', zIndex: 10, paddingLeft: '25px'
                                    }}>
                                      {t.remainingBalance}
                                    </td>
                                    {aggregatedData.map((data, idx) => (
                                      <td key={idx} style={{
                                        padding: '8px 12px', textAlign: 'right', fontWeight: '500',
                                        color: '#ffffff', fontSize: '0.85em', whiteSpace: 'nowrap'
                                      }}>
                                        {formatNumber(data.balance, 0)}
                                      </td>
                                    ))}
                                    {Array.from({length: periodHeaders.length - aggregatedData.length}).map((_, idx) => (
                                      <td key={`pad-${idx}`} style={{padding: '8px 12px'}}></td>
                                    ))}
                                    <td style={{
                                      padding: '8px 12px', textAlign: 'right', fontWeight: '700',
                                      color: '#ffffff', fontSize: '0.85em', whiteSpace: 'nowrap'
                                    }}>
                                      0
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            })}

                            {/* Total rows combining both credits */}
                            {creditSets.length > 1 && (() => {
                              const allAggregated = creditSets.map(cs => aggregateSchedule(cs.schedule));
                              // Sum per period across all credits
                              const totalCapitalPerPeriod = periodHeaders.map((_, idx) =>
                                allAggregated.reduce((sum, agg) => sum + (agg[idx]?.principal || 0), 0)
                              );
                              const totalInterestPerPeriod = periodHeaders.map((_, idx) =>
                                allAggregated.reduce((sum, agg) => sum + (agg[idx]?.interest || 0), 0)
                              );
                              const grandTotalPerPeriod = periodHeaders.map((_, idx) =>
                                allAggregated.reduce((sum, agg) => sum + (agg[idx]?.payment || 0), 0)
                              );
                              const totalCapital = allAggregated.reduce((sum, agg) => sum + agg.reduce((s, d) => s + d.principal, 0), 0);
                              const totalInterest = allAggregated.reduce((sum, agg) => sum + agg.reduce((s, d) => s + d.interest, 0), 0);
                              const grandTotal = totalCapital + totalInterest;

                              return (
                                <>
                                  {/* Separator */}
                                  <tr><td colSpan={periodHeaders.length + 2} style={{borderTop: '3px solid #444', padding: 0}}></td></tr>

                                  {/* Total Capital */}
                                  <tr style={{borderBottom: '1px solid #2a2a2a', background: 'rgba(34, 197, 94, 0.08)'}}>
                                    <td style={{
                                      padding: '8px 15px', textAlign: 'left', color: '#4ade80',
                                      fontWeight: '700', fontSize: '0.85em', position: 'sticky', left: 0,
                                      background: 'rgba(34, 197, 94, 0.08)', zIndex: 10
                                    }}>
                                      Total Capital
                                    </td>
                                    {totalCapitalPerPeriod.map((val, idx) => (
                                      <td key={idx} style={{
                                        padding: '8px 12px', textAlign: 'right', fontWeight: '600',
                                        color: '#4ade80', fontSize: '0.85em', whiteSpace: 'nowrap',
                                        background: 'rgba(34, 197, 94, 0.08)'
                                      }}>
                                        {formatNumber(val, 0)}
                                      </td>
                                    ))}
                                    <td style={{
                                      padding: '8px 12px', textAlign: 'right', fontWeight: '700',
                                      color: '#4ade80', fontSize: '0.85em', whiteSpace: 'nowrap',
                                      background: 'rgba(34, 197, 94, 0.08)'
                                    }}>
                                      {formatNumber(totalCapital, 0)}
                                    </td>
                                  </tr>

                                  {/* Total Intérêts */}
                                  <tr style={{borderBottom: '1px solid #2a2a2a', background: 'rgba(239, 68, 68, 0.08)'}}>
                                    <td style={{
                                      padding: '8px 15px', textAlign: 'left', color: '#f87171',
                                      fontWeight: '700', fontSize: '0.85em', position: 'sticky', left: 0,
                                      background: 'rgba(239, 68, 68, 0.08)', zIndex: 10
                                    }}>
                                      Total Intérêts
                                    </td>
                                    {totalInterestPerPeriod.map((val, idx) => (
                                      <td key={idx} style={{
                                        padding: '8px 12px', textAlign: 'right', fontWeight: '600',
                                        color: '#f87171', fontSize: '0.85em', whiteSpace: 'nowrap',
                                        background: 'rgba(239, 68, 68, 0.08)'
                                      }}>
                                        {formatNumber(val, 0)}
                                      </td>
                                    ))}
                                    <td style={{
                                      padding: '8px 12px', textAlign: 'right', fontWeight: '700',
                                      color: '#f87171', fontSize: '0.85em', whiteSpace: 'nowrap',
                                      background: 'rgba(239, 68, 68, 0.08)'
                                    }}>
                                      {formatNumber(totalInterest, 0)}
                                    </td>
                                  </tr>

                                  {/* Grand Total */}
                                  <tr style={{background: 'rgba(96, 165, 250, 0.1)'}}>
                                    <td style={{
                                      padding: '10px 15px', textAlign: 'left', color: '#60a5fa',
                                      fontWeight: '700', fontSize: '0.9em', position: 'sticky', left: 0,
                                      background: 'rgba(96, 165, 250, 0.1)', zIndex: 10
                                    }}>
                                      Grand Total
                                    </td>
                                    {grandTotalPerPeriod.map((val, idx) => (
                                      <td key={idx} style={{
                                        padding: '8px 12px', textAlign: 'right', fontWeight: '700',
                                        color: '#60a5fa', fontSize: '0.85em', whiteSpace: 'nowrap',
                                        background: 'rgba(96, 165, 250, 0.1)'
                                      }}>
                                        {formatNumber(val, 0)}
                                      </td>
                                    ))}
                                    <td style={{
                                      padding: '8px 12px', textAlign: 'right', fontWeight: '700',
                                      color: '#60a5fa', fontSize: '0.9em', whiteSpace: 'nowrap',
                                      background: 'rgba(96, 165, 250, 0.1)'
                                    }}>
                                      {formatNumber(grandTotal, 0)}
                                    </td>
                                  </tr>
                                </>
                              );
                            })()}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                  </div>
                </div>

                {/* Interest Comparison Table */}
                <div
                  style={{
                    marginTop: '20px',
                    background: '#222222',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '16px',
                    border: '1px solid #2a2a2a',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    position: 'relative'
                  }}
                  onMouseLeave={() => setInterestTableHovered(null)}
                >
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                      <h2 style={{fontSize: '1.3em', color: '#ffffff', fontWeight: '600', margin: 0}}>Comparaison</h2>
                      <select
                        value={interestTableMetric}
                        onChange={(e) => setInterestTableMetric(e.target.value)}
                        style={{
                          padding: '6px 12px',
                          background: '#1a1a1a',
                          border: '1px solid #3a3a3a',
                          borderRadius: '6px',
                          color: '#ffffff',
                          fontSize: '0.9em',
                          cursor: 'pointer',
                          outline: 'none'
                        }}
                      >
                        <option value="interest">Intérêts Totaux</option>
                        <option value="monthlyPayment">Mensualité</option>
                      </select>
                      <select
                        value={amortizationCreditView}
                        onChange={(e) => setAmortizationCreditView(e.target.value)}
                        style={{
                          padding: '6px 12px',
                          background: '#1a1a1a',
                          border: '1px solid #3a3a3a',
                          borderRadius: '6px',
                          color: '#60a5fa',
                          fontSize: '0.9em',
                          cursor: 'pointer',
                          outline: 'none'
                        }}
                      >
                        <option value="immobilier">Crédit Immobilier</option>
                        <option value="travaux">Crédit Travaux</option>
                      </select>
                    </div>
                    <button
                      onClick={() => {
                        const XLSX = require('xlsx');
                        const compCreditData = amortizationCreditView === 'travaux' ? creditTravauxData : creditData;
                        const currentRate = parseFloat(compCreditData.interestRate) || 3.5;
                        const currentDuration = parseInt(compCreditData.durationYears) || 20;
                        const amount = parseFloat(compCreditData.amount) || 0;

                        const rates = [];
                        for (let i = -10; i <= 10; i++) {
                          rates.push(Math.max(0.1, currentRate + (i * 0.05)));
                        }
                        const durations = [
                          Math.max(5, currentDuration - 5),
                          Math.max(5, currentDuration - 4),
                          Math.max(5, currentDuration - 3),
                          Math.max(5, currentDuration - 2),
                          Math.max(5, currentDuration - 1),
                          currentDuration,
                          currentDuration + 1,
                          currentDuration + 2,
                          currentDuration + 3,
                          currentDuration + 4,
                          currentDuration + 5
                        ];

                        const calculateTotalInterest = (rate, years) => {
                          if (amount <= 0 || rate <= 0 || years <= 0) return 0;
                          const monthlyRate = rate / 100 / 12;
                          const numPayments = years * 12;
                          const monthlyPayment = (amount * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
                                               (Math.pow(1 + monthlyRate, numPayments) - 1);
                          return monthlyPayment * numPayments - amount;
                        };

                        const wsData = [
                          ['Taux / Durée', ...durations.map(d => `${d} ans`)],
                          ...rates.map(rate => [
                            `${rate.toFixed(2)}%`,
                            ...durations.map(d => Math.round(calculateTotalInterest(rate, d)))
                          ])
                        ];

                        const ws = XLSX.utils.aoa_to_sheet(wsData);
                        ws['!cols'] = [{wch: 12}, ...durations.map(() => ({wch: 12}))];
                        const wb = XLSX.utils.book_new();
                        const compLabel = amortizationCreditView === 'travaux' ? 'travaux' : 'immobilier';
                        XLSX.utils.book_append_sheet(wb, ws, 'Comparaison Intérêts');
                        XLSX.writeFile(wb, `comparaison_interets_${compLabel}.xlsx`);
                      }}
                      style={{
                        padding: '8px 16px',
                        background: 'rgba(59, 130, 246, 0.2)',
                        border: '1px solid rgba(59, 130, 246, 0.4)',
                        borderRadius: '6px',
                        color: '#60a5fa',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                      onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      title="Télécharger le tableau en Excel"
                    >
                      .xlsx
                    </button>
                  </div>
                  {(() => {
                    const compCreditData = amortizationCreditView === 'travaux' ? creditTravauxData : creditData;
                    const currentRate = parseFloat(compCreditData.interestRate) || 3.5;
                    const currentDuration = parseInt(compCreditData.durationYears) || 20;
                    const amount = parseFloat(compCreditData.amount) || 0;

                    // Define rate and duration variations (0.05% steps for rates, 1 year steps for durations)
                    const rates = [];
                    for (let i = -10; i <= 10; i++) {
                      rates.push(Math.max(0.1, currentRate + (i * 0.05)));
                    }
                    const durations = [
                      Math.max(5, currentDuration - 5),
                      Math.max(5, currentDuration - 4),
                      Math.max(5, currentDuration - 3),
                      Math.max(5, currentDuration - 2),
                      Math.max(5, currentDuration - 1),
                      currentDuration,
                      currentDuration + 1,
                      currentDuration + 2,
                      currentDuration + 3,
                      currentDuration + 4,
                      currentDuration + 5
                    ];

                    // Calculate total interest and monthly payment for each combination
                    const calculateLoanDetails = (rate, years) => {
                      if (amount <= 0 || rate <= 0 || years <= 0) return { interest: 0, monthlyPayment: 0 };
                      const monthlyRate = rate / 100 / 12;
                      const numPayments = years * 12;
                      const monthlyPayment = (amount * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
                                           (Math.pow(1 + monthlyRate, numPayments) - 1);
                      const totalCost = monthlyPayment * numPayments;
                      return { interest: totalCost - amount, monthlyPayment };
                    };

                    // Get current values for comparison
                    const currentDetails = calculateLoanDetails(currentRate, currentDuration);

                    return (
                      <div style={{overflowX: 'auto'}}>
                        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '1.05em'}}>
                          <thead>
                            <tr>
                              <th style={{padding: '8px 6px', textAlign: 'center', color: 'rgba(156, 163, 175, 0.9)', fontWeight: '600', borderBottom: '2px solid #2a2a2a', position: 'sticky', left: 0, background: '#222222', zIndex: 1}}></th>
                              {durations.map(d => (
                                <th key={d} style={{
                                  padding: '8px 6px',
                                  textAlign: 'center',
                                  color: d === currentDuration ? '#10b981' : '#ffffff',
                                  fontWeight: '600',
                                  fontSize: '0.85em',
                                  borderBottom: '2px solid #2a2a2a',
                                  background: d === currentDuration ? 'rgba(16, 185, 129, 0.1)' : 'transparent'
                                }}>{d} ans</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rates.map(rate => (
                              <tr key={rate} style={{borderBottom: '1px solid #2a2a2a'}}>
                                <td style={{
                                  padding: '8px 6px',
                                  textAlign: 'center',
                                  color: Math.abs(rate - currentRate) < 0.01 ? '#10b981' : '#ffffff',
                                  fontWeight: '600',
                                  fontSize: '0.85em',
                                  background: Math.abs(rate - currentRate) < 0.01 ? 'rgba(16, 185, 129, 0.1)' : '#222222',
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 1
                                }}>{rate.toFixed(2)}%</td>
                                {durations.map(duration => {
                                  const details = calculateLoanDetails(rate, duration);
                                  const isCurrentCombo = Math.abs(rate - currentRate) < 0.01 && duration === currentDuration;
                                  const isHovered = interestTableHovered &&
                                    Math.abs(interestTableHovered.rate - rate) < 0.001 &&
                                    interestTableHovered.duration === duration;
                                  const interestDiff = details.interest - currentDetails.interest;
                                  const monthlyDiff = details.monthlyPayment - currentDetails.monthlyPayment;
                                  const displayValue = interestTableMetric === 'interest' ? details.interest : details.monthlyPayment;

                                  return (
                                    <td
                                      key={`${rate}-${duration}`}
                                      style={{
                                        padding: '8px 6px',
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        fontSize: '0.8em',
                                        color: isCurrentCombo ? '#10b981' : isHovered ? '#ffffff' : '#60a5fa',
                                        background: isCurrentCombo ? 'rgba(16, 185, 129, 0.15)' : isHovered ? 'rgba(96, 165, 250, 0.2)' : 'transparent',
                                        border: isCurrentCombo ? '2px solid #10b981' : isHovered ? '2px solid #60a5fa' : 'none',
                                        borderRadius: (isCurrentCombo || isHovered) ? '6px' : '0',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        whiteSpace: 'nowrap'
                                      }}
                                      onMouseEnter={(e) => setInterestTableHovered({
                                        rate,
                                        duration,
                                        interest: details.interest,
                                        monthlyPayment: details.monthlyPayment,
                                        interestDiff,
                                        monthlyDiff,
                                        screenX: e.clientX,
                                        screenY: e.clientY
                                      })}
                                      onMouseMove={(e) => setInterestTableHovered(prev => prev ? {
                                        ...prev,
                                        screenX: e.clientX,
                                        screenY: e.clientY
                                      } : null)}
                                    >
                                      {formatNumber(displayValue, 0)} €
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* Tooltip */}
                        {interestTableHovered && (
                          <div
                            style={{
                              position: 'fixed',
                              left: interestTableHovered.screenX + 10,
                              bottom: window.innerHeight - interestTableHovered.screenY + 10,
                              padding: '14px 18px',
                              background: '#1a1a1a',
                              borderRadius: '12px',
                              border: '1px solid #60a5fa',
                              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                              zIndex: 10000,
                              pointerEvents: 'none',
                              minWidth: '220px'
                            }}
                          >
                            <div style={{color: '#ffffff', fontSize: '1em', fontWeight: '700', marginBottom: '10px', borderBottom: '1px solid #333', paddingBottom: '8px'}}>
                              {interestTableHovered.rate.toFixed(2)}% sur {interestTableHovered.duration} ans
                            </div>
                            <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <span style={{color: 'rgba(209, 216, 224, 0.7)', fontSize: '0.85em'}}>Intérêts Totaux:</span>
                                <span style={{color: '#60a5fa', fontWeight: '700', fontSize: '1em'}}>{formatNumber(interestTableHovered.interest, 0)} €</span>
                              </div>
                              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <span style={{color: 'rgba(209, 216, 224, 0.7)', fontSize: '0.85em'}}>Mensualité:</span>
                                <span style={{color: '#ffffff', fontWeight: '600', fontSize: '0.95em'}}>{formatNumber(interestTableHovered.monthlyPayment, 0)} €/mois</span>
                              </div>
                              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', paddingTop: '6px', borderTop: '1px solid #333'}}>
                                <span style={{color: 'rgba(209, 216, 224, 0.7)', fontSize: '0.85em'}}>vs Actuel (Intérêts):</span>
                                <span style={{
                                  color: interestTableHovered.interestDiff > 0 ? '#f87171' : interestTableHovered.interestDiff < 0 ? '#4ade80' : '#9ca3af',
                                  fontWeight: '700',
                                  fontSize: '0.95em'
                                }}>
                                  {interestTableHovered.interestDiff > 0 ? '+' : ''}{formatNumber(interestTableHovered.interestDiff, 0)} €
                                </span>
                              </div>
                              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <span style={{color: 'rgba(209, 216, 224, 0.7)', fontSize: '0.85em'}}>vs Actuel (Mensualité):</span>
                                <span style={{
                                  color: (interestTableHovered.monthlyDiff || 0) > 0 ? '#f87171' : (interestTableHovered.monthlyDiff || 0) < 0 ? '#4ade80' : '#9ca3af',
                                  fontWeight: '700',
                                  fontSize: '0.95em'
                                }}>
                                  {(interestTableHovered.monthlyDiff || 0) > 0 ? '+' : ''}{formatNumber(interestTableHovered.monthlyDiff || 0, 0)} €
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </>
            )}
          </div>
        )}

        {/* Recurrent Fees & Income Tab */}
        {activeTab === 'recurrent-fees-income' && (
          <div style={{width: '100%', padding: '0 24px'}}>
            {/* Investment Sub-tabs navigation - Sticky */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '24px',
              borderBottom: '1px solid rgba(58, 58, 58, 0.4)',
              position: 'sticky',
              top: 0,
              background: '#0a0a0a',
              zIndex: 100,
              paddingTop: '12px',
              paddingBottom: '0',
              marginLeft: '-24px',
              marginRight: '-24px',
              paddingLeft: '24px',
              paddingRight: '24px'
            }}>
              <button
                onClick={() => { setActiveTab('extraction-charac'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Caractéristiques
              </button>
              <button
                onClick={() => { setActiveTab('acquisition-financement'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Acquisition & Financement
              </button>
              <button
                onClick={() => { setActiveTab('credit'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Crédit
              </button>
              <button
                onClick={() => { setActiveTab('recurrent-fees-income'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid rgba(209, 178, 135, 0.9)',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.9em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
              >
                Charges & Revenus
              </button>
              <button
                onClick={() => { setActiveTab('performance'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Performance
              </button>
              <div style={{ flex: 1 }}></div>
              <button
                onClick={generateBienImmobilierPDF}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(209, 178, 135, 0.15)',
                  border: '1px solid rgba(209, 178, 135, 0.3)',
                  borderRadius: '6px',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.85em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.15)';
                }}
              >
                PDF Bien Immobilier
              </button>
            </div>

            {/* Section Charges & Revenus Info - Collapsible */}
            <div
              onClick={() => setShowInfoSections(!showInfoSections)}
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                padding: showInfoSections ? '24px' : '12px 24px',
                borderRadius: '12px',
                border: '1px solid rgba(58, 58, 58, 0.5)',
                marginBottom: '24px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3 style={{ color: '#ffffff', fontSize: '1.1em', fontWeight: '600', margin: 0 }}>
                      Charges & Revenus
                    </h3>
                    <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.85em', transition: 'transform 0.3s ease', transform: showInfoSections ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                  </div>
                  {showInfoSections && (
                    <>
                      <p style={{ color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.95em', lineHeight: '1.6', marginTop: '16px', marginBottom: '16px' }}>
                        Précompte immobilier, copropriété, assurance PNO, loyer mensuel attendu, taux d'occupation.
                      </p>
                      <div style={{
                        borderLeft: '3px solid rgba(156, 163, 175, 0.4)',
                        paddingLeft: '16px',
                        color: 'rgba(156, 163, 175, 0.8)',
                        fontSize: '0.9em',
                        lineHeight: '1.8'
                      }}>
                        <p style={{ marginBottom: '12px' }}><strong style={{ color: '#ffffff' }}>Pourquoi c'est important ?</strong></p>
                        <p style={{ marginBottom: '10px' }}>
                          <strong style={{ color: '#60a5fa' }}>→ Impact sur le Cash Flow :</strong> C'est ici que se joue directement votre cash flow mensuel : <span style={{ textDecoration: 'underline' }}>Loyer - Mensualité - Charges - Taxes = Cash Flow</span>. Le loyer est votre seul revenu, tout le reste sont des sorties. Le précompte immobilier (annuel), les charges de copropriété (trimestrielles), l'assurance PNO, les frais de gestion locative (~6-8% du loyer si délégué) — chaque poste réduit votre cash flow. Le taux d'occupation est critique : 1 mois de vacance locative = 8% de loyer annuel perdu.
                        </p>
                        <p style={{ marginBottom: '10px' }}>
                          <strong style={{ color: '#60a5fa' }}>→ Impact sur le rendement brut vs net :</strong> Le rendement brut (Loyer annuel / Prix d'achat) est trompeur car il ignore toutes les charges. Le rendement net déduit charges, taxes et vacance locative. Un bien affiché à 8% brut peut tomber à 4-5% net après déduction de toutes les charges. C'est pourquoi il est essentiel de recenser précisément chaque poste de charge.
                        </p>
                        <p style={{ marginBottom: '0' }}>
                          <strong style={{ color: '#60a5fa' }}>→ Cash flow négatif ≠ mauvais investissement :</strong> Un cash flow mensuel négatif de -200€ signifie que vous "mettez de votre poche" chaque mois. Mais si pendant ce temps vos locataires remboursent 600€ de capital (amortissement) et que le bien prend 3% par an (plus-value), votre patrimoine net augmente quand même. Le TRI capture cette réalité globale.
                        </p>
                      </div>
                    </>
                  )}
            </div>

            <h2 className="section-title">{t.recurrentFeesIncome}</h2>

            {/* Two Column Layout: Fees on Left, Income on Right */}
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px'}}>

              {/* LEFT: Recurrent Fees Section */}
              <div className="calculation-section" style={{maxHeight: 'none', padding: '15px'}}>
                <h3 style={{color: 'rgba(209, 216, 224, 0.9)', fontSize: '0.95em', marginBottom: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px'}}>{t.recurrentFees}</h3>

                <div className="input-list">
                  {recurrentFees.map((fee, index) => (
                    <div key={fee.id} style={{marginBottom: '10px', padding: '10px', background: '#2a2a2a', borderRadius: '8px', border: '1px solid #2a2a2a'}}>
                      {/* Compact layout with remove button at the end */}
                      <div style={{display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr auto', gap: '6px', alignItems: 'center'}}>
                        {/* Fee Name */}
                        <input
                          type="text"
                          className="input-amount-field"
                          style={{fontSize: '0.85em', padding: '6px 10px'}}
                          placeholder={t.feeName}
                          value={fee.name === 'propertyTax' ? t.propertyTax :
                                 fee.name === 'coOwnershipFees' ? t.coOwnershipFees :
                                 fee.name === 'insurance' ? t.insurance :
                                 fee.name === 'maintenance' ? t.maintenance : fee.name}
                          onChange={(e) => {
                            const newFees = [...recurrentFees];
                            newFees[index].name = e.target.value;
                            setRecurrentFees(newFees);
                          }}
                        />

                        {/* Amount */}
                        <input
                          type="number"
                          className="input-amount-field"
                          style={{fontSize: '0.85em', padding: '6px 10px'}}
                          placeholder="€"
                          min="0"
                          step="10"
                          value={fee.amount || ''}
                          onChange={(e) => {
                            const newFees = [...recurrentFees];
                            newFees[index].amount = parseFloat(e.target.value) || 0;
                            setRecurrentFees(newFees);
                          }}
                        />

                        {/* Frequency */}
                        <select
                          className="input-amount-field"
                          style={{cursor: 'pointer', fontSize: '0.9em', padding: '6px 8px'}}
                          value={fee.frequency}
                          onChange={(e) => {
                            const newFees = [...recurrentFees];
                            newFees[index].frequency = e.target.value;
                            setRecurrentFees(newFees);
                          }}
                        >
                          <option value="monthly">Mensuel</option>
                          <option value="quarterly">Trimestriel</option>
                          <option value="annually">Annuel</option>
                        </select>

                        {/* Remove Button - At the end of the line */}
                        {recurrentFees.length > 1 && (
                          <button
                            onClick={() => setRecurrentFees(recurrentFees.filter(f => f.id !== fee.id))}
                            style={{
                              padding: '4px 8px',
                              background: 'rgba(239, 68, 68, 0.2)',
                              border: '1px solid rgba(239, 68, 68, 0.4)',
                              borderRadius: '4px',
                              color: 'rgba(254, 202, 202, 0.9)',
                              cursor: 'pointer',
                              fontSize: '0.9em',
                              fontWeight: '600',
                              minWidth: '24px',
                              height: '28px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Add Fee Button */}
                  <button
                    onClick={() => {
                      const newId = Math.max(...recurrentFees.map(f => f.id), 0) + 1;
                      setRecurrentFees([...recurrentFees, { id: newId, name: '', amount: 0, frequency: 'monthly' }]);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      marginTop: '5px',
                      background: '#2a2a2a',
                      border: '1px solid #2a2a2a',
                      borderRadius: '8px',
                      color: 'rgba(209, 216, 224, 0.9)',
                      cursor: 'pointer',
                      fontSize: '0.85em',
                      fontWeight: '600'
                    }}
                  >
                    + {t.addFee}
                  </button>
                </div>
              </div>

              {/* RIGHT: Revenues Section */}
              <div className="calculation-section" style={{maxHeight: 'none', padding: '15px'}}>
                <h3 style={{color: 'rgba(209, 216, 224, 0.9)', fontSize: '0.95em', marginBottom: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px'}}>{t.income}</h3>

                <div className="input-list">
                  {propertyRevenues.map((revenue, index) => (
                    <div key={revenue.id} style={{marginBottom: '10px', padding: '10px', background: '#2a2a2a', borderRadius: '8px', border: '1px solid #2a2a2a'}}>
                      {/* Row 1: Name, Amount, Frequency, Remove Button */}
                      <div style={{display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr auto', gap: '6px', alignItems: 'center', marginBottom: '6px'}}>
                        <input
                          type="text"
                          className="input-amount-field"
                          style={{fontSize: '0.85em', padding: '6px 10px'}}
                          placeholder={t.revenueName}
                          value={revenue.name === 'rent' ? t.rent : revenue.name}
                          onChange={(e) => {
                            const newRevenues = [...propertyRevenues];
                            newRevenues[index].name = e.target.value;
                            setPropertyRevenues(newRevenues);
                          }}
                        />

                        <input
                          type="number"
                          className="input-amount-field"
                          style={{fontSize: '0.85em', padding: '6px 10px'}}
                          placeholder="€"
                          min="0"
                          step="10"
                          value={revenue.amount || ''}
                          onChange={(e) => {
                            const newRevenues = [...propertyRevenues];
                            newRevenues[index].amount = parseFloat(e.target.value) || 0;
                            setPropertyRevenues(newRevenues);
                          }}
                        />

                        <select
                          className="input-amount-field"
                          style={{cursor: 'pointer', fontSize: '0.9em', padding: '6px 8px'}}
                          value={revenue.frequency}
                          onChange={(e) => {
                            const newRevenues = [...propertyRevenues];
                            newRevenues[index].frequency = e.target.value;
                            setPropertyRevenues(newRevenues);
                          }}
                        >
                          <option value="monthly">Mensuel</option>
                          <option value="quarterly">Trimestriel</option>
                          <option value="annually">Annuel</option>
                        </select>

                        {/* Remove Button - At the end of the first row */}
                        {propertyRevenues.length > 1 && (
                          <button
                            onClick={() => setPropertyRevenues(propertyRevenues.filter(r => r.id !== revenue.id))}
                            style={{
                              padding: '4px 8px',
                              background: 'rgba(239, 68, 68, 0.2)',
                              border: '1px solid rgba(239, 68, 68, 0.4)',
                              borderRadius: '4px',
                              color: 'rgba(254, 202, 202, 0.9)',
                              cursor: 'pointer',
                              fontSize: '0.9em',
                              fontWeight: '600',
                              minWidth: '24px',
                              height: '28px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gridRow: 'span 2'
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>

                      {/* Row 2: Occupancy Rate and Indexation */}
                      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', alignItems: 'center', paddingRight: propertyRevenues.length > 1 ? '34px' : '0'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                          <span style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)', whiteSpace: 'nowrap'}}>Occupation:</span>
                          <input
                            type="number"
                            className="input-amount-field"
                            style={{fontSize: '0.9em', padding: '4px 8px', flex: 1}}
                            placeholder="100"
                            min="0"
                            max="100"
                            step="1"
                            value={revenue.occupancyRate || ''}
                            onChange={(e) => {
                              const newRevenues = [...propertyRevenues];
                              newRevenues[index].occupancyRate = parseFloat(e.target.value) || 0;
                              setPropertyRevenues(newRevenues);
                            }}
                          />
                          <span style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)'}}>%</span>
                        </div>

                        <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                          <span style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)', whiteSpace: 'nowrap'}}>Indexation:</span>
                          <input
                            type="number"
                            className="input-amount-field"
                            style={{fontSize: '0.9em', padding: '4px 8px', flex: 1}}
                            placeholder="0"
                            min="0"
                            max="100"
                            step="0.1"
                            value={revenue.indexation || ''}
                            onChange={(e) => {
                              const newRevenues = [...propertyRevenues];
                              newRevenues[index].indexation = parseFloat(e.target.value) || 0;
                              setPropertyRevenues(newRevenues);
                            }}
                          />
                          <span style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)'}}>%</span>
                        </div>
                      </div>

                      {/* Row 3: Start Lag */}
                      <div style={{display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px', paddingRight: propertyRevenues.length > 1 ? '34px' : '0'}}>
                        <span style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)', whiteSpace: 'nowrap'}}>Début après:</span>
                        <input
                          type="number"
                          className="input-amount-field"
                          style={{fontSize: '0.9em', padding: '4px 8px', flex: 1}}
                          placeholder="0"
                          min="0"
                          max="360"
                          step="1"
                          value={revenue.startLagMonths || ''}
                          onChange={(e) => {
                            const newRevenues = [...propertyRevenues];
                            newRevenues[index].startLagMonths = parseFloat(e.target.value) || 0;
                            setPropertyRevenues(newRevenues);
                          }}
                        />
                        <span style={{fontSize: '0.9em', color: 'rgba(156, 163, 175, 0.9)', whiteSpace: 'nowrap'}}>mois</span>
                      </div>
                    </div>
                  ))}

                  {/* Add Revenue Button */}
                  <button
                    onClick={() => {
                      const newId = Math.max(...propertyRevenues.map(r => r.id), 0) + 1;
                      setPropertyRevenues([...propertyRevenues, { id: newId, name: '', amount: 0, frequency: 'monthly', occupancyRate: 100, indexation: 0, startLagMonths: 0 }]);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      marginTop: '5px',
                      background: '#2a2a2a',
                      border: '1px solid #2a2a2a',
                      borderRadius: '8px',
                      color: 'rgba(209, 216, 224, 0.9)',
                      cursor: 'pointer',
                      fontSize: '0.85em',
                      fontWeight: '600'
                    }}
                  >
                    + {t.addRevenue}
                  </button>
                </div>
              </div>

            </div>

            {/* KPIs and Summary Table Section */}
            {(() => {
              // Calculate monthly totals
              const calculateMonthlyAmount = (amount, frequency) => {
                if (frequency === 'monthly') return amount;
                if (frequency === 'quarterly') return amount / 3;
                if (frequency === 'annually') return amount / 12;
                return 0;
              };

              const totalMonthlyFees = recurrentFees.reduce((sum, fee) =>
                sum + calculateMonthlyAmount(fee.amount, fee.frequency), 0);

              const totalMonthlyRevenue = propertyRevenues.reduce((sum, rev) =>
                sum + (calculateMonthlyAmount(rev.amount, rev.frequency) * (rev.occupancyRate / 100)), 0);

              const monthlyCashFlow = totalMonthlyRevenue - totalMonthlyFees;

              return (
                <>
                  {/* KPI Cards */}
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '20px'}}>
                    {/* Monthly Cash Flow KPI */}
                    <div style={{
                      padding: '20px',
                      background: '#222222',
                      backdropFilter: 'blur(20px)',
                      borderRadius: '12px',
                      border: '1px solid #2a2a2a',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      textAlign: 'center'
                    }}>
                      <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '500'}}>
                        {t.monthlyCashFlow}
                      </div>
                      <div style={{color: monthlyCashFlow >= 0 ? '#4ade80' : '#f87171', fontSize: '2em', fontWeight: '700'}}>
                        {formatNumber(monthlyCashFlow, 0)}
                      </div>
                    </div>

                    {/* Monthly Revenue KPI */}
                    <div style={{
                      padding: '20px',
                      background: '#222222',
                      backdropFilter: 'blur(20px)',
                      borderRadius: '12px',
                      border: '1px solid #2a2a2a',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      textAlign: 'center'
                    }}>
                      <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '500'}}>
                        {t.effectiveMonthlyRent}
                      </div>
                      <div style={{color: '#ffffff', fontSize: '2em', fontWeight: '700'}}>
                        {formatNumber(totalMonthlyRevenue, 0)}
                      </div>
                    </div>
                  </div>

                  {/* Summary Table - Monthly */}
                  <div style={{marginTop: '20px'}}>
                    <h3 style={{color: 'rgba(209, 216, 224, 0.9)', fontSize: '1.1em', marginBottom: '15px', fontWeight: '600'}}>
                      {t.summaryTable}
                    </h3>
                    <div style={{
                      background: '#222222',
                      backdropFilter: 'blur(20px)',
                      borderRadius: '12px',
                      border: '1px solid #2a2a2a',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      overflowX: 'auto'
                    }}>
                      <div style={{fontStyle: 'italic', color: 'rgba(156, 163, 175, 0.9)', fontSize: '1em', marginBottom: '15px'}}>
                        Monthly breakdown of rental income and fees
                      </div>

                      <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '1em'}}>
                        <thead>
                          <tr style={{borderBottom: '2px solid #2a2a2a'}}>
                            <th style={{padding: '10px', textAlign: 'left', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600'}}>{t.month}</th>
                            {/* Revenue columns */}
                            {propertyRevenues.map(rev => (
                              <th key={`rev-${rev.id}`} style={{padding: '10px', textAlign: 'right', color: '#4ade80', fontWeight: '600'}}>
                                {rev.name === 'rent' ? t.rent : rev.name}
                              </th>
                            ))}
                            {/* Fee columns */}
                            {recurrentFees.map(fee => (
                              <th key={`fee-${fee.id}`} style={{padding: '10px', textAlign: 'right', color: '#f87171', fontWeight: '600'}}>
                                {t[fee.name] || fee.name}
                              </th>
                            ))}
                            <th style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600'}}>{t.netCashFlow}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.from({length: 12}, (_, i) => {
                            const month = i + 1;

                            // Calculate total revenue for this month
                            const monthRevenue = propertyRevenues.reduce((sum, rev) => {
                              const startLag = rev.startLagMonths || 0;

                              // Skip if we haven't reached the start lag yet (month is 1-12, startLag is in months)
                              if (month <= startLag) {
                                return sum;
                              }

                              let amount = 0;
                              if (rev.frequency === 'monthly') {
                                amount = rev.amount * (rev.occupancyRate / 100);
                              } else if (rev.frequency === 'quarterly' && month % 3 === 0) {
                                amount = rev.amount * (rev.occupancyRate / 100);
                              } else if (rev.frequency === 'annually' && month === 12) {
                                amount = rev.amount * (rev.occupancyRate / 100);
                              }
                              return sum + amount;
                            }, 0);

                            // Calculate total fees for this month
                            const monthFees = recurrentFees.reduce((sum, fee) => {
                              let amount = 0;
                              if (fee.frequency === 'monthly') {
                                amount = fee.amount;
                              } else if (fee.frequency === 'quarterly' && month % 3 === 0) {
                                amount = fee.amount;
                              } else if (fee.frequency === 'annually' && month === 12) {
                                amount = fee.amount;
                              }
                              return sum + amount;
                            }, 0);

                            return (
                              <tr key={month} style={{borderBottom: '1px solid #2a2a2a'}}>
                                <td style={{padding: '10px', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600'}}>{month}</td>

                                {/* Revenue values */}
                                {propertyRevenues.map(rev => {
                                  const startLag = rev.startLagMonths || 0;
                                  let amount = 0;

                                  // Only calculate amount if we're past the start lag
                                  if (month > startLag) {
                                    if (rev.frequency === 'monthly') {
                                      amount = rev.amount * (rev.occupancyRate / 100);
                                    } else if (rev.frequency === 'quarterly' && month % 3 === 0) {
                                      amount = rev.amount * (rev.occupancyRate / 100);
                                    } else if (rev.frequency === 'annually' && month === 12) {
                                      amount = rev.amount * (rev.occupancyRate / 100);
                                    }
                                  }

                                  return (
                                    <td key={`rev-${rev.id}-${month}`} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)'}}>
                                      {amount > 0 ? formatNumber(amount, 0) : '-'}
                                    </td>
                                  );
                                })}

                                {/* Fee values */}
                                {recurrentFees.map(fee => {
                                  let amount = 0;
                                  if (fee.frequency === 'monthly') {
                                    amount = fee.amount;
                                  } else if (fee.frequency === 'quarterly' && month % 3 === 0) {
                                    amount = fee.amount;
                                  } else if (fee.frequency === 'annually' && month === 12) {
                                    amount = fee.amount;
                                  }
                                  return (
                                    <td key={`fee-${fee.id}-${month}`} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)'}}>
                                      {amount > 0 ? formatNumber(amount, 0) : '-'}
                                    </td>
                                  );
                                })}

                                {/* Net Cash Flow */}
                                <td style={{padding: '10px', textAlign: 'right', color: (monthRevenue - monthFees) >= 0 ? '#4ade80' : '#f87171', fontWeight: '600'}}>
                                  {formatNumber(monthRevenue - monthFees, 0)}
                                </td>
                              </tr>
                            );
                          })}

                          {/* Total Row */}
                          <tr style={{borderTop: '2px solid #2a2a2a', background: '#2a2a2a'}}>
                            <td style={{padding: '12px', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700'}}>Total (12 mois)</td>

                            {/* Revenue totals */}
                            {propertyRevenues.map(rev => {
                              const startLag = rev.startLagMonths || 0;
                              let total = 0;

                              // Calculate total for months 1-12 only, accounting for lag
                              for (let m = 1; m <= 12; m++) {
                                if (m > startLag) {
                                  if (rev.frequency === 'monthly') {
                                    total += rev.amount * (rev.occupancyRate / 100);
                                  } else if (rev.frequency === 'quarterly' && m % 3 === 0) {
                                    total += rev.amount * (rev.occupancyRate / 100);
                                  } else if (rev.frequency === 'annually' && m === 12) {
                                    total += rev.amount * (rev.occupancyRate / 100);
                                  }
                                }
                              }

                              return (
                                <td key={`rev-total-${rev.id}`} style={{padding: '12px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700'}}>
                                  {formatNumber(total, 0)}
                                </td>
                              );
                            })}

                            {/* Fee totals */}
                            {recurrentFees.map(fee => {
                              let total = 0;
                              if (fee.frequency === 'monthly') {
                                total = fee.amount * 12;
                              } else if (fee.frequency === 'quarterly') {
                                total = fee.amount * 4;
                              } else if (fee.frequency === 'annually') {
                                total = fee.amount;
                              }
                              return (
                                <td key={`fee-total-${fee.id}`} style={{padding: '12px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700'}}>
                                  {formatNumber(total, 0)}
                                </td>
                              );
                            })}

                            {/* Net Cash Flow total */}
                            <td style={{padding: '12px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700'}}>
                              {formatNumber(
                                Array.from({length: 12}, (_, i) => {
                                  const month = i + 1;
                                  const monthRevenue = propertyRevenues.reduce((sum, rev) => {
                                    const startLag = rev.startLagMonths || 0;

                                    // Skip if we haven't reached the start lag yet
                                    if (month <= startLag) {
                                      return sum;
                                    }

                                    let amount = 0;
                                    if (rev.frequency === 'monthly') {
                                      amount = rev.amount * (rev.occupancyRate / 100);
                                    } else if (rev.frequency === 'quarterly' && month % 3 === 0) {
                                      amount = rev.amount * (rev.occupancyRate / 100);
                                    } else if (rev.frequency === 'annually' && month === 12) {
                                      amount = rev.amount * (rev.occupancyRate / 100);
                                    }
                                    return sum + amount;
                                  }, 0);
                                  const monthFees = recurrentFees.reduce((sum, fee) => {
                                    let amount = 0;
                                    if (fee.frequency === 'monthly') {
                                      amount = fee.amount;
                                    } else if (fee.frequency === 'quarterly' && month % 3 === 0) {
                                      amount = fee.amount;
                                    } else if (fee.frequency === 'annually' && month === 12) {
                                      amount = fee.amount;
                                    }
                                    return sum + amount;
                                  }, 0);
                                  return monthRevenue - monthFees;
                                }).reduce((a, b) => a + b, 0), 0
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      {/* Monthly Breakdown */}
                      <div style={{marginTop: '20px', padding: '15px', background: '#2a2a2a', borderRadius: '8px'}}>
                        <div style={{color: 'rgba(209, 216, 224, 0.9)', fontSize: '0.9em', fontWeight: '600', marginBottom: '10px'}}>
                          {t.monthlyBreakdown}:
                        </div>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '0.85em'}}>
                          <div style={{color: 'rgba(156, 163, 175, 0.9)'}}>
                            • {t.effectiveMonthlyRent}: <span style={{color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600'}}>{formatNumber(totalMonthlyRevenue, 0)} €</span>
                          </div>
                          <div style={{color: 'rgba(156, 163, 175, 0.9)'}}>
                            • {t.monthlyFees}: <span style={{color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600'}}>{formatNumber(totalMonthlyFees, 0)} €</span>
                          </div>
                          <div style={{color: 'rgba(156, 163, 175, 0.9)'}}>
                            • {t.netMonthlyCashFlow}: <span style={{color: monthlyCashFlow >= 0 ? '#4ade80' : '#f87171', fontWeight: '700'}}>{formatNumber(monthlyCashFlow, 0)} €</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <div style={{width: '100%', padding: '0 24px'}}>
            {/* Investment Sub-tabs navigation - Sticky */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '24px',
              borderBottom: '1px solid rgba(58, 58, 58, 0.4)',
              position: 'sticky',
              top: 0,
              background: '#0a0a0a',
              zIndex: 100,
              paddingTop: '12px',
              paddingBottom: '0',
              marginLeft: '-24px',
              marginRight: '-24px',
              paddingLeft: '24px',
              paddingRight: '24px'
            }}>
              <button
                onClick={() => { setActiveTab('extraction-charac'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Caractéristiques
              </button>
              <button
                onClick={() => { setActiveTab('acquisition-financement'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Acquisition & Financement
              </button>
              <button
                onClick={() => { setActiveTab('credit'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Crédit
              </button>
              <button
                onClick={() => { setActiveTab('recurrent-fees-income'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  color: 'rgba(156, 163, 175, 0.7)',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => { e.target.style.color = 'rgba(209, 178, 135, 0.8)'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(156, 163, 175, 0.7)'; }}
              >
                Charges & Revenus
              </button>
              <button
                onClick={() => { setActiveTab('performance'); setError(''); }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid rgba(209, 178, 135, 0.9)',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.9em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '-1px'
                }}
              >
                Performance
              </button>
              <div style={{ flex: 1 }}></div>
              <button
                onClick={generateBienImmobilierPDF}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(209, 178, 135, 0.15)',
                  border: '1px solid rgba(209, 178, 135, 0.3)',
                  borderRadius: '6px',
                  color: 'rgba(209, 178, 135, 0.95)',
                  fontSize: '0.85em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(209, 178, 135, 0.15)';
                }}
              >
                PDF Bien Immobilier
              </button>
            </div>

            {/* Sticky Header Section */}
            <div style={{
              position: 'sticky',
              top: 0,
              zIndex: 100,
              background: '#0a0a0a',
              paddingBottom: '16px',
              marginBottom: '8px'
            }}>
              <h2 className="section-title">{t.performance}</h2>

              {/* Estimated Home Value After Renovation */}
              <div style={{
                marginBottom: '16px',
                padding: '20px',
                background: '#222222',
                backdropFilter: 'blur(20px)',
                borderRadius: '12px',
                border: '1px solid #2a2a2a',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
              }}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                <label style={{color: 'rgba(209, 216, 224, 0.9)', fontSize: '1em', fontWeight: '600'}}>
                  Valeur estimée du bien après travaux
                </label>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <input
                    type="number"
                    className="input-amount-field"
                    style={{
                      fontSize: '1em',
                      padding: '8px 12px',
                      width: '180px',
                      textAlign: 'right',
                      fontWeight: '600',
                      color: '#60a5fa'
                    }}
                    placeholder="0"
                    value={estimatedHomeValue || ''}
                    onChange={(e) => {
                      const newValue = parseFloat(e.target.value) || 0;
                      setEstimatedHomeValue(newValue);
                      setHasCustomEstimatedValue(true);
                    }}
                  />
                  <span style={{color: 'rgba(209, 216, 224, 0.9)', fontSize: '0.9em', fontWeight: '600'}}>€</span>
                  {hasCustomEstimatedValue && (
                    <button
                      onClick={() => {
                        setHasCustomEstimatedValue(false);
                        setEstimatedHomeValue(acquisitionData.prixAchat + acquisitionData.travaux);
                      }}
                      style={{
                        marginLeft: '8px',
                        padding: '4px 8px',
                        background: 'rgba(156, 163, 175, 0.2)',
                        border: '1px solid rgba(156, 163, 175, 0.3)',
                        borderRadius: '4px',
                        color: 'rgba(156, 163, 175, 0.9)',
                        fontSize: '0.8em',
                        cursor: 'pointer'
                      }}
                      title="Réinitialiser à la valeur par défaut"
                    >
                      ↺
                    </button>
                  )}
                </div>
              </div>

              {/* Slider for estimated home value */}
              <div style={{marginTop: '16px'}}>
                <input
                  type="range"
                  min="0"
                  max="5000000"
                  step="10000"
                  value={estimatedHomeValue || 0}
                  onChange={(e) => {
                    const newValue = parseFloat(e.target.value) || 0;
                    setEstimatedHomeValue(newValue);
                    setHasCustomEstimatedValue(true);
                  }}
                  style={{width: '100%'}}
                />
              </div>

              <div style={{
                fontSize: '0.85em',
                color: 'rgba(156, 163, 175, 0.7)',
                marginTop: '8px',
                paddingTop: '8px',
                borderTop: '1px solid rgba(58, 58, 58, 0.3)'
              }}>
                Par défaut: Prix d'achat ({formatNumber(acquisitionData.prixAchat, 0)}€) + Travaux ({formatNumber(acquisitionData.travaux, 0)}€) = {formatNumber(acquisitionData.prixAchat + acquisitionData.travaux, 0)}€
                {hasCustomEstimatedValue && <span style={{color: '#60a5fa', marginLeft: '8px'}}>(valeur personnalisée)</span>}
              </div>
            </div>

            {/* Performance Parameters Sliders */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px',
              marginBottom: '30px',
              padding: '20px',
              background: '#222222',
              backdropFilter: 'blur(20px)',
              borderRadius: '12px',
              border: '1px solid #2a2a2a',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
            }}>
              {/* Investment Horizon Slider */}
              <div>
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '10px'}}>
                  <label style={{color: 'rgba(209, 216, 224, 0.9)', fontSize: '0.9em', fontWeight: '600'}}>
                    {t.investmentHorizon}
                  </label>
                  <span style={{color: '#60a5fa', fontSize: '0.9em', fontWeight: '700'}}>
                    {performanceParams.investmentHorizonMonths} mois ({(performanceParams.investmentHorizonMonths / 12).toFixed(1)} ans)
                  </span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="360"
                  step="1"
                  value={performanceParams.investmentHorizonMonths}
                  onChange={(e) => setPerformanceParams({...performanceParams, investmentHorizonMonths: parseInt(e.target.value)})}
                  style={{width: '100%'}}
                />
              </div>

              {/* House Price Inflation Slider */}
              <div>
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '10px'}}>
                  <label style={{color: 'rgba(209, 216, 224, 0.9)', fontSize: '0.9em', fontWeight: '600'}}>
                    {t.housePriceInflation}
                  </label>
                  <span style={{color: '#60a5fa', fontSize: '0.9em', fontWeight: '700'}}>
                    {performanceParams.housePriceInflation}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="0.1"
                  value={performanceParams.housePriceInflation}
                  onChange={(e) => setPerformanceParams({...performanceParams, housePriceInflation: parseFloat(e.target.value)})}
                  style={{width: '100%'}}
                />
              </div>
            </div>
            </div>
            {/* End of Sticky Header Section */}

            {(() => {
              // Calculate monthly amounts helper
              const calculateMonthlyAmount = (amount, frequency) => {
                if (frequency === 'monthly') return amount;
                if (frequency === 'quarterly') return amount / 3;
                if (frequency === 'annually') return amount / 12;
                return 0;
              };

              // Get total monthly revenue and fees
              const totalMonthlyRevenue = propertyRevenues.reduce((sum, rev) =>
                sum + (calculateMonthlyAmount(rev.amount, rev.frequency) * (rev.occupancyRate / 100)), 0);

              const totalMonthlyFees = recurrentFees.reduce((sum, fee) =>
                sum + calculateMonthlyAmount(fee.amount, fee.frequency), 0);

              // Get credit monthly payment (both credits combined)
              const monthlyPayment = (creditAmortization.monthlyPayment || 0) + (creditTravauxAmortization.monthlyPayment || 0);

              // Calculate total investment (apport from financement)
              const totalInvestment = financementData.apport || 0;
              const totalCreditAmount = (creditData.amount || 0) + (creditTravauxData.amount || 0);
              // Use estimated home value after renovation (defaults to house price + renovation)
              const initialHomeValue = estimatedHomeValue || (totalInvestment + totalCreditAmount);

              // Function to calculate home value with inflation for a given year
              const getHomeValueAtYear = (year) => {
                return initialHomeValue * Math.pow(1 + performanceParams.housePriceInflation / 100, year);
              };

              // Calculate cash flows based on exact investment horizon in months
              const totalMonths = performanceParams.investmentHorizonMonths;
              const years = Math.ceil(totalMonths / 12); // For annual view compatibility

              // Build monthly data for precise calculations
              const monthlyData = Array.from({length: totalMonths + 1}, (_, monthIndex) => {
                // Month 0: Initial investment
                if (monthIndex === 0) {
                  return {
                    month: 0,
                    revenue: 0,
                    fees: 0,
                    creditPayments: 0,
                    netCashFlow: -totalInvestment
                  };
                }

                const yearOfMonth = Math.floor((monthIndex - 1) / 12); // Year 0 for months 1-12, year 1 for months 13-24, etc.
                const monthInYear = ((monthIndex - 1) % 12) + 1; // 1-12

                // Monthly revenue with startLag and indexation
                const monthRevenue = propertyRevenues.reduce((sum, rev) => {
                  const startLag = rev.startLagMonths || 0;
                  if (monthIndex <= startLag) return sum;

                  let amount = 0;
                  if (rev.frequency === 'monthly') {
                    amount = rev.amount * (rev.occupancyRate / 100);
                  } else if (rev.frequency === 'quarterly' && monthInYear % 3 === 0) {
                    amount = rev.amount * (rev.occupancyRate / 100);
                  } else if (rev.frequency === 'annually' && monthInYear === 12) {
                    amount = rev.amount * (rev.occupancyRate / 100);
                  }
                  // Apply indexation based on year
                  return sum + (amount * Math.pow(1 + (rev.indexation || 0) / 100, yearOfMonth));
                }, 0);

                // Monthly fees (with pro-rata for final month)
                const isLastMonth = monthIndex === totalMonths;
                const monthFees = recurrentFees.reduce((sum, fee) => {
                  let amount = 0;
                  if (fee.frequency === 'monthly') {
                    amount = fee.amount;
                  } else if (fee.frequency === 'quarterly') {
                    if (monthInYear % 3 === 0) {
                      // Full quarterly payment on months 3, 6, 9, 12
                      amount = fee.amount;
                    } else if (isLastMonth) {
                      // Pro-rata for final month: months into current quarter / 3
                      const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
                      amount = fee.amount * (monthsIntoQuarter / 3);
                    }
                  } else if (fee.frequency === 'annually') {
                    if (monthInYear === 12) {
                      // Full annual payment on month 12
                      amount = fee.amount;
                    } else if (isLastMonth) {
                      // Pro-rata for final month: months into current year / 12
                      amount = fee.amount * (monthInYear / 12);
                    }
                  }
                  return sum + amount;
                }, 0);

                // Get actual credit payment from both schedules (principal + interest)
                const scheduleIndex = monthIndex - 1; // monthIndex is 1-based, schedule is 0-based
                const scheduleEntry = creditAmortization.schedule?.[scheduleIndex];
                const scheduleEntryTravaux = creditTravauxAmortization.schedule?.[scheduleIndex];
                const monthCreditPayment = (scheduleEntry ? (scheduleEntry.principal + scheduleEntry.interest) : 0)
                  + (scheduleEntryTravaux ? (scheduleEntryTravaux.principal + scheduleEntryTravaux.interest) : 0);

                // Operational cash flow (excludes capital gain)
                const operationalCashFlow = monthRevenue - monthFees - monthCreditPayment;
                let netCashFlow = operationalCashFlow;

                // Add capital gain in last month (sale proceeds - loan payoff)
                if (monthIndex === totalMonths) {
                  const yearsElapsed = totalMonths / 12;
                  const finalHomeValue = initialHomeValue * Math.pow(1 + performanceParams.housePriceInflation / 100, yearsElapsed);
                  const immoLen = creditAmortization.schedule?.length || 0;
                  const travLen = creditTravauxAmortization.schedule?.length || 0;
                  const finalBalance = (immoLen > 0
                    ? creditAmortization.schedule[Math.min(totalMonths - 1, immoLen - 1)]?.remainingBalance || 0
                    : 0) + (travLen > 0
                    ? creditTravauxAmortization.schedule[Math.min(totalMonths - 1, travLen - 1)]?.remainingBalance || 0
                    : 0);
                  const saleProceeds = finalHomeValue - finalBalance;
                  netCashFlow += saleProceeds;
                }

                return {
                  month: monthIndex,
                  revenue: monthRevenue,
                  fees: monthFees,
                  creditPayments: monthCreditPayment,
                  netCashFlow,
                  operationalCashFlow
                };
              });

              // Aggregate monthly data into annual data for annual view
              const annualData = Array.from({length: years + 1}, (_, yearIndex) => {
                if (yearIndex === 0) {
                  return {
                    year: 0,
                    revenue: 0,
                    fees: 0,
                    creditPayments: 0,
                    netCashFlow: -totalInvestment
                  };
                }

                // Get months for this year (handling partial last year)
                const startMonth = (yearIndex - 1) * 12 + 1;
                const endMonth = Math.min(yearIndex * 12, totalMonths);

                let yearRevenue = 0, yearFees = 0, yearCreditPayments = 0, yearNetCashFlow = 0;

                for (let m = startMonth; m <= endMonth; m++) {
                  const monthData = monthlyData[m];
                  if (monthData) {
                    yearRevenue += monthData.revenue;
                    yearFees += monthData.fees;
                    yearCreditPayments += monthData.creditPayments;
                    yearNetCashFlow += monthData.netCashFlow;
                  }
                }

                return {
                  year: yearIndex,
                  revenue: yearRevenue,
                  fees: yearFees,
                  creditPayments: yearCreditPayments,
                  netCashFlow: yearNetCashFlow
                };
              });

              // Calculate cumulative cash flow
              let cumulative = 0;
              const cumulativeData = annualData.map(data => {
                cumulative += data.netCashFlow;
                return {
                  year: data.year,
                  cumulative
                };
              });

              // ROE - Return on Equity (total net revenues over investment / equity invested)
              // Use monthlyData for exact calculation
              const totalNetRevenues = monthlyData.reduce((sum, data) => sum + data.netCashFlow, 0);
              const roe = totalInvestment > 0 ? (totalNetRevenues / totalInvestment) * 100 : 0;

              // Ratio Loyer / Prix avec Travaux (rendement brut)
              const totalAcqWithWorks = (acquisitionData.prixAchat || 0) + (acquisitionData.travaux || 0);
              const dscr = totalAcqWithWorks > 0 ? (totalMonthlyRevenue * 12) / totalAcqWithWorks : 0;

              // IRR approximation (simplified - uses average return based on exact months)
              const totalCashFlows = monthlyData.reduce((sum, d) => sum + d.netCashFlow, 0);
              const yearsExact = totalMonths / 12;
              const finalHomeValueExact = initialHomeValue * Math.pow(1 + performanceParams.housePriceInflation / 100, yearsExact);
              const finalBalanceExact = creditAmortization.schedule && creditAmortization.schedule.length > 0
                ? creditAmortization.schedule[Math.min(totalMonths - 1, creditAmortization.schedule.length - 1)]?.remainingBalance || 0
                : 0;
              const irr = totalInvestment > 0 && yearsExact > 0
                ? ((totalCashFlows + (finalHomeValueExact - finalBalanceExact) - totalInvestment) / totalInvestment / yearsExact) * 100
                : 0;

              // Calculate NPV (Net Present Value) using monthly discounting for precision
              const monthlyDiscountRate = creditData.interestRate / 100 / 12; // Monthly discount rate
              const npv = monthlyData.reduce((sum, data) => {
                const month = data.month;
                if (month === 0) {
                  return sum + data.netCashFlow;
                }
                const discountFactor = Math.pow(1 + monthlyDiscountRate, month);
                return sum + (data.netCashFlow / discountFactor);
              }, 0);

              // Calculate total Net Cash Flow (NCF) over exact investment horizon
              const totalNetCashFlow = monthlyData.reduce((sum, data) => sum + data.netCashFlow, 0);

              // Update state with calculated Performance Totale values for saving
              if (calculatedPerformanceTotale.ncf !== totalNetCashFlow || calculatedPerformanceTotale.tri !== irr) {
                setTimeout(() => setCalculatedPerformanceTotale({ ncf: totalNetCashFlow, tri: irr }), 0);
              }

              // Calculate monthly revenue and charges
              const monthlyRevenue = propertyRevenues.reduce((sum, rev) =>
                sum + (calculateMonthlyAmount(rev.amount, rev.frequency) * (rev.occupancyRate / 100)), 0);
              const monthlyCharges = recurrentFees.reduce((sum, fee) =>
                sum + calculateMonthlyAmount(fee.amount, fee.frequency), 0);

              // Cash-Flow Mensuel Moyen: Sum of all operational Cash-Flow (except month 0) / number of months
              // Excludes capital gain from property sale
              const totalOperationalCashFlow = monthlyData
                .filter((data, idx) => idx > 0)
                .reduce((sum, data) => sum + data.operationalCashFlow, 0);
              const averageMonthlyCashFlow = totalMonths > 0 ? totalOperationalCashFlow / totalMonths : 0;

              // Calculate Rentabilité nette prévisionnelle using exact months
              const totalRent = monthlyRevenue * totalMonths;
              const totalCharges = monthlyCharges * totalMonths;
              const totalCredit = monthlyPayment * totalMonths;
              const netProfitability = totalInvestment > 0 ? ((totalRent - totalCharges - totalCredit) / totalInvestment) * 100 : 0;

              return (
                <>
                  {/* Performance après travaux Section */}
                  <div style={{marginTop: '20px'}}>
                    <h3 style={{
                      color: 'rgba(209, 216, 224, 0.9)',
                      fontSize: '1.1em',
                      marginBottom: '15px',
                      fontWeight: '600',
                      borderBottom: '1px solid rgba(58, 58, 58, 0.6)',
                      paddingBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => setCollapsedSections(prev => ({
                      ...prev,
                      performanceAfterRenovation: !prev.performanceAfterRenovation
                    }))}
                    >
                      <span>{t.renovationPerformance}</span>
                      <span style={{
                        fontSize: '0.8em',
                        transition: 'transform 0.3s ease',
                        transform: collapsedSections.performanceAfterRenovation ? 'rotate(0deg)' : 'rotate(180deg)'
                      }}>▼</span>
                    </h3>

                    {!collapsedSections.performanceAfterRenovation && (() => {
                      const purchasePrice = acquisitionData.prixAchat || 0;
                      const renovationCost = acquisitionData.travaux || 0;
                      const estimatedValue = estimatedHomeValue || (purchasePrice + renovationCost);
                      const addedValue = estimatedValue - (purchasePrice + renovationCost);
                      const surfaceArea = extractedData?.surface || 0;

                      const pricePerSqmBefore = surfaceArea > 0 ? purchasePrice / surfaceArea : 0;
                      const pricePerSqmAfter = surfaceArea > 0 ? estimatedValue / surfaceArea : 0;
                      const pricePerSqmWithRenovation = surfaceArea > 0 ? (purchasePrice + renovationCost) / surfaceArea : 0;
                      const addedValuePercent = (purchasePrice + renovationCost) > 0
                        ? (addedValue / (purchasePrice + renovationCost)) * 100
                        : 0;

                      return (
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px'}}>
                          {/* Added Value After Renovation */}
                          <div style={{
                            padding: '20px',
                            background: '#222222',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '12px',
                            border: '1px solid #2a2a2a',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                          }}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                              <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>
                                {t.addedValueAfterRenovation}
                              </div>
                              <div
                                style={{
                                  width: '14px',
                                  height: '14px',
                                  borderRadius: '50%',
                                  border: '1px solid rgba(156, 163, 175, 0.5)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.7em',
                                  color: 'rgba(156, 163, 175, 0.7)',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                                onClick={() => setInfoModal({
                                  title: 'Plus-value après travaux',
                                  description: 'Différence entre la valeur estimée du bien après travaux et le prix d\'achat initial. Évalue la valeur créée par les travaux de rénovation, indépendamment de la location.',
                                  formula: 'Plus-value = Valeur après travaux - Prix d\'achat',
                                  interpretation: 'Attention : cette plus-value est brute, elle n\'inclut pas le coût des travaux eux-mêmes. La plus-value nette = Plus-value brute - Coût des travaux. C\'est la première source potentielle de gain.'
                                })}
                                onMouseEnter={(e) => {
                                  e.target.style.background = 'rgba(156, 163, 175, 0.1)';
                                  e.target.style.borderColor = 'rgba(156, 163, 175, 0.7)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'transparent';
                                  e.target.style.borderColor = 'rgba(156, 163, 175, 0.5)';
                                }}
                              >i</div>
                            </div>
                            <div style={{color: addedValue >= 0 ? '#4ade80' : '#f87171', fontSize: '1.8em', fontWeight: '700'}}>
                              {formatNumber(addedValue, 0)} €
                            </div>
                            <div style={{color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.8em', marginTop: '4px'}}>
                              {addedValue >= 0 ? '+' : ''}{formatNumber(addedValuePercent, 2)}%
                            </div>
                          </div>

                          {/* Price per m² Before Renovation */}
                          <div style={{
                            padding: '20px',
                            background: '#222222',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '12px',
                            border: '1px solid #2a2a2a',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                          }}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                              <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>
                                {t.pricePerSqmBeforeRenovation}
                              </div>
                              <div
                                style={{
                                  width: '14px',
                                  height: '14px',
                                  borderRadius: '50%',
                                  border: '1px solid rgba(156, 163, 175, 0.5)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.7em',
                                  color: 'rgba(156, 163, 175, 0.7)',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                                onClick={() => setInfoModal({
                                  title: 'Prix au m² avant travaux',
                                  description: 'Permet de comparer votre achat au marché local. C\'est le prix que vous payez réellement par mètre carré.',
                                  formula: 'Prix au m² = Prix d\'achat / Surface (m²)',
                                  interpretation: 'Si le marché local est à 3 500€/m² et que vous achetez à 3 077€/m², vous avez acheté 12% en dessous du marché. C\'est une marge de sécurité en cas de revente.'
                                })}
                                onMouseEnter={(e) => {
                                  e.target.style.background = 'rgba(156, 163, 175, 0.1)';
                                  e.target.style.borderColor = 'rgba(156, 163, 175, 0.7)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'transparent';
                                  e.target.style.borderColor = 'rgba(156, 163, 175, 0.5)';
                                }}
                              >i</div>
                            </div>
                            <div style={{color: '#60a5fa', fontSize: '1.8em', fontWeight: '700'}}>
                              {surfaceArea > 0 ? formatNumber(pricePerSqmBefore, 0) : '-'} {surfaceArea > 0 ? '€/m²' : ''}
                            </div>
                            {surfaceArea === 0 && (
                              <div style={{color: 'rgba(156, 163, 175, 0.5)', fontSize: '0.75em', marginTop: '4px'}}>
                                Surface non renseignée
                              </div>
                            )}
                          </div>

                          {/* Price per m² with Renovation (without added value) */}
                          <div style={{
                            padding: '20px',
                            background: '#222222',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '12px',
                            border: '1px solid #2a2a2a',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                          }}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                              <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>
                                {t.pricePerSqmWithRenovation}
                              </div>
                              <div
                                style={{
                                  width: '14px',
                                  height: '14px',
                                  borderRadius: '50%',
                                  border: '1px solid rgba(156, 163, 175, 0.5)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.7em',
                                  color: 'rgba(156, 163, 175, 0.7)',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                                onClick={() => setInfoModal({
                                  title: 'Prix au m² avec travaux (coût réel)',
                                  description: 'Votre coût réel d\'acquisition par mètre carré, incluant le budget travaux. C\'est ce que vous avez réellement investi par m².',
                                  formula: 'Prix au m² = (Prix d\'achat + Travaux) / Surface (m²)',
                                  interpretation: 'Ce prix représente votre investissement réel. Comparez-le au marché : si vous êtes à 3 231€/m² avec travaux et que le marché est à 3 500€/m², vous avez encore une marge de 8%.'
                                })}
                                onMouseEnter={(e) => {
                                  e.target.style.background = 'rgba(156, 163, 175, 0.1)';
                                  e.target.style.borderColor = 'rgba(156, 163, 175, 0.7)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'transparent';
                                  e.target.style.borderColor = 'rgba(156, 163, 175, 0.5)';
                                }}
                              >i</div>
                            </div>
                            <div style={{color: '#fbbf24', fontSize: '1.8em', fontWeight: '700'}}>
                              {surfaceArea > 0 ? formatNumber(pricePerSqmWithRenovation, 0) : '-'} {surfaceArea > 0 ? '€/m²' : ''}
                            </div>
                            {surfaceArea === 0 && (
                              <div style={{color: 'rgba(156, 163, 175, 0.5)', fontSize: '0.75em', marginTop: '4px'}}>
                                Surface non renseignée
                              </div>
                            )}
                          </div>

                          {/* Price per m² After Renovation */}
                          <div style={{
                            padding: '20px',
                            background: '#222222',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '12px',
                            border: '1px solid #2a2a2a',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                          }}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                              <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>
                                {t.pricePerSqmAfterRenovation}
                              </div>
                              <div
                                style={{
                                  width: '14px',
                                  height: '14px',
                                  borderRadius: '50%',
                                  border: '1px solid rgba(156, 163, 175, 0.5)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.7em',
                                  color: 'rgba(156, 163, 175, 0.7)',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                                onClick={() => setInfoModal({
                                  title: 'Prix au m² après travaux (valeur marché)',
                                  description: 'La valeur estimée du bien par mètre carré après rénovation. C\'est le prix auquel vous pourriez revendre chaque m².',
                                  formula: 'Prix au m² = Valeur estimée après travaux / Surface (m²)',
                                  interpretation: 'La différence avec le "Prix au m² avec travaux" représente la création de valeur par m². Si vous passez de 3 231€/m² à 3 538€/m², vous avez créé 307€/m² de valeur.'
                                })}
                                onMouseEnter={(e) => {
                                  e.target.style.background = 'rgba(156, 163, 175, 0.1)';
                                  e.target.style.borderColor = 'rgba(156, 163, 175, 0.7)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'transparent';
                                  e.target.style.borderColor = 'rgba(156, 163, 175, 0.5)';
                                }}
                              >i</div>
                            </div>
                            <div style={{color: '#34d399', fontSize: '1.8em', fontWeight: '700'}}>
                              {surfaceArea > 0 ? formatNumber(pricePerSqmAfter, 0) : '-'} {surfaceArea > 0 ? '€/m²' : ''}
                            </div>
                            {surfaceArea > 0 && (
                              <div style={{
                                color: pricePerSqmAfter - pricePerSqmWithRenovation > 0 ? '#4ade80' : pricePerSqmAfter - pricePerSqmWithRenovation < 0 ? '#f87171' : 'rgba(156, 163, 175, 0.7)',
                                fontSize: '0.8em',
                                marginTop: '4px'
                              }}>
                                {pricePerSqmAfter - pricePerSqmWithRenovation > 0 ? '+' : ''}{formatNumber(pricePerSqmAfter - pricePerSqmWithRenovation, 0)} €/m²
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Performance Locative Section */}
                  <div style={{marginTop: '20px'}}>
                    <h3 style={{
                      color: 'rgba(209, 216, 224, 0.9)',
                      fontSize: '1.1em',
                      marginBottom: '15px',
                      fontWeight: '600',
                      borderBottom: '1px solid rgba(58, 58, 58, 0.6)',
                      paddingBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => setCollapsedSections(prev => ({
                      ...prev,
                      performanceLocative: !prev.performanceLocative
                    }))}
                    >
                      <span>{t.rentalPerformance}</span>
                      <span style={{
                        fontSize: '0.8em',
                        transition: 'transform 0.3s ease',
                        transform: collapsedSections.performanceLocative ? 'rotate(0deg)' : 'rotate(180deg)'
                      }}>▼</span>
                    </h3>

                    {!collapsedSections.performanceLocative && <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px'}}>
                      {/* Monthly Cash Flow KPI */}
                      <div style={{
                        padding: '20px',
                        background: '#222222',
                        backdropFilter: 'blur(20px)',
                        borderRadius: '12px',
                        border: '1px solid #2a2a2a',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                          <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>{t.monthlyCashFlow}</div>
                          <div
                            style={{
                              width: '14px',
                              height: '14px',
                              borderRadius: '50%',
                              border: '1px solid rgba(156, 163, 175, 0.5)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7em',
                              color: 'rgba(156, 163, 175, 0.7)',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => setInfoModal({
                              title: 'Cash-Flow Mensuel Net',
                              description: 'Ce qui reste (ou manque) chaque mois après avoir tout payé. C\'est le nerf de la guerre pour la viabilité à court terme.',
                              formula: 'Cash-Flow = Loyer effectif - Mensualité crédit - Charges mensuelles',
                              interpretation: 'Cash flow négatif = vous devez compléter chaque mois de votre poche. Ce n\'est pas rédhibitoire si les 2 autres leviers (plus-value, amortissement du capital) compensent — voir le TRI pour la vision globale.'
                            })}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'rgba(156, 163, 175, 0.1)';
                              e.target.style.borderColor = 'rgba(156, 163, 175, 0.7)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'transparent';
                              e.target.style.borderColor = 'rgba(156, 163, 175, 0.5)';
                            }}
                          >i</div>
                        </div>
                        <div style={{color: averageMonthlyCashFlow >= 0 ? '#4ade80' : '#f87171', fontSize: '2em', fontWeight: '700'}}>{formatNumber(averageMonthlyCashFlow, 1)}</div>
                      </div>

                      {/* Rentabilité Nette Prévisionnelle KPI */}
                      <div style={{
                        padding: '20px',
                        background: '#222222',
                        backdropFilter: 'blur(20px)',
                        borderRadius: '12px',
                        border: '1px solid #2a2a2a',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                          <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>{t.netProfitability}</div>
                          <div
                            style={{
                              width: '14px',
                              height: '14px',
                              borderRadius: '50%',
                              border: '1px solid rgba(156, 163, 175, 0.5)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7em',
                              color: 'rgba(156, 163, 175, 0.7)',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => setInfoModal({
                              title: 'Rentabilité Nette Prévisionnelle',
                              description: 'Intègre les charges réelles, la vacance locative et le budget total (y compris frais et travaux). Beaucoup plus réaliste que le rendement brut.',
                              formula: 'Rentabilité nette = ((Loyer effectif - Charges) × 12) / Budget total × 100',
                              interpretation: 'On passe souvent de 5-6% brut à 3-4% net : les charges et la vacance "mangent" 2 points de rendement. C\'est pourquoi le rendement brut seul est trompeur.'
                            })}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'rgba(156, 163, 175, 0.1)';
                              e.target.style.borderColor = 'rgba(156, 163, 175, 0.7)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'transparent';
                              e.target.style.borderColor = 'rgba(156, 163, 175, 0.5)';
                            }}
                          >i</div>
                        </div>
                        <div style={{color: netProfitability >= 0 ? '#4ade80' : '#f87171', fontSize: '2em', fontWeight: '700'}}>{formatNumber(netProfitability, 2)}%</div>
                      </div>

                      {/* Ratio Loyer / Prix avec Travaux KPI */}
                      <div style={{
                        padding: '20px',
                        background: '#222222',
                        backdropFilter: 'blur(20px)',
                        borderRadius: '12px',
                        border: '1px solid #2a2a2a',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                          <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>{t.debtServiceCoverageRatio}</div>
                          <div
                            style={{
                              width: '14px',
                              height: '14px',
                              borderRadius: '50%',
                              border: '1px solid rgba(156, 163, 175, 0.5)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7em',
                              color: 'rgba(156, 163, 175, 0.7)',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => setInfoModal({
                              title: 'Ratio Loyer / Prix avec Travaux',
                              description: 'Variante du rendement brut qui intègre le coût des travaux dans le prix d\'acquisition. Plus réaliste pour les biens nécessitant des rénovations. Permet de comparer objectivement ville vs campagne.',
                              formula: '(Loyer annuel / (Prix d\'achat + Travaux)) × 100',
                              interpretation: '< 4% = rendement faible | 4-6% = rendement correct | > 6% = bon rendement. Exemple : Ville (14 400€/an, 300 000€) = 4.8% vs Campagne (7 200€/an, 115 000€) = 6.3%'
                            })}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'rgba(156, 163, 175, 0.1)';
                              e.target.style.borderColor = 'rgba(156, 163, 175, 0.7)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'transparent';
                              e.target.style.borderColor = 'rgba(156, 163, 175, 0.5)';
                            }}
                          >i</div>
                        </div>
                        <div style={{color: dscr >= 0.06 ? '#4ade80' : dscr >= 0.04 ? '#fbbf24' : '#f87171', fontSize: '2em', fontWeight: '700'}}>{formatNumber(dscr * 100, 2)}%</div>
                      </div>

                      {/* Rent per m² KPI */}
                      {(() => {
                        const surfaceArea = extractedData?.surface || 0;
                        const rentPerSqm = surfaceArea > 0 ? totalMonthlyRevenue / surfaceArea : 0;

                        return (
                          <div style={{
                            padding: '20px',
                            background: '#222222',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '12px',
                            border: '1px solid #2a2a2a',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                          }}>
                            <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em', marginBottom: '8px'}}>
                              {t.rentPerSqm}
                            </div>
                            <div style={{color: '#60a5fa', fontSize: '2em', fontWeight: '700'}}>
                              {surfaceArea > 0 ? formatNumber(rentPerSqm, 2) : '-'} {surfaceArea > 0 ? '€/m²' : ''}
                            </div>
                            {surfaceArea === 0 && (
                              <div style={{color: 'rgba(156, 163, 175, 0.5)', fontSize: '0.75em', marginTop: '4px'}}>
                                Surface non renseignée
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>}
                  </div>

                  {/* Performance Totale (à la revente) Section */}
                  <div style={{marginTop: '30px'}}>
                    <h3 style={{
                      color: 'rgba(209, 216, 224, 0.9)',
                      fontSize: '1.1em',
                      marginBottom: '15px',
                      fontWeight: '600',
                      borderBottom: '1px solid rgba(58, 58, 58, 0.6)',
                      paddingBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => setCollapsedSections(prev => ({
                      ...prev,
                      performanceTotale: !prev.performanceTotale
                    }))}
                    >
                      <span>{t.totalPerformance}</span>
                      <span style={{
                        fontSize: '0.8em',
                        transition: 'transform 0.3s ease',
                        transform: collapsedSections.performanceTotale ? 'rotate(0deg)' : 'rotate(180deg)'
                      }}>▼</span>
                    </h3>

                    {!collapsedSections.performanceTotale && <>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px'}}>
                      {/* IRR KPI */}
                      <div style={{
                        padding: '20px',
                        background: '#222222',
                        backdropFilter: 'blur(20px)',
                        borderRadius: '12px',
                        border: '1px solid #2a2a2a',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                          <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>{t.internalRateOfReturn}</div>
                          <div
                            style={{
                              width: '14px',
                              height: '14px',
                              borderRadius: '50%',
                              border: '1px solid rgba(156, 163, 175, 0.5)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7em',
                              color: 'rgba(156, 163, 175, 0.7)',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => setInfoModal({
                              title: 'TRI — Taux de Rendement Interne',
                              description: 'LA métrique de référence. Le TRI est le taux d\'actualisation qui rend la somme de tous les flux (apport, cash flows mensuels, produit de revente) égale à zéro. Il permet de comparer des investissements aux profils très différents.',
                              formula: 'Flux mois 0 : -Apport (€) | Flux mois 1 à N : Cash flow mensuel (€) | Flux mois N : Vente - Solde crédit (€)',
                              interpretation: 'Un TRI de 11% signifie que votre investissement équivaut à un placement qui rapporterait 11% par an. À comparer avec un livret (3%), des actions (7-8% long terme) ou un autre bien immobilier.'
                            })}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'rgba(156, 163, 175, 0.1)';
                              e.target.style.borderColor = 'rgba(156, 163, 175, 0.7)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'transparent';
                              e.target.style.borderColor = 'rgba(156, 163, 175, 0.5)';
                            }}
                          >i</div>
                        </div>
                        <div style={{color: '#60a5fa', fontSize: '2em', fontWeight: '700'}}>{formatNumber(irr, 2)}%</div>
                      </div>

                      {/* ROE KPI */}
                      <div style={{
                        padding: '20px',
                        background: '#222222',
                        backdropFilter: 'blur(20px)',
                        borderRadius: '12px',
                        border: '1px solid #2a2a2a',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                          <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>{t.returnOnEquity}</div>
                          <div
                            style={{
                              width: '14px',
                              height: '14px',
                              borderRadius: '50%',
                              border: '1px solid rgba(156, 163, 175, 0.5)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7em',
                              color: 'rgba(156, 163, 175, 0.7)',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => setInfoModal({
                              title: 'ROE — Rentabilité des Fonds Propres',
                              description: 'Mesure le gain total par rapport au capital que vous avez réellement investi (apport). Montre la puissance de l\'effet de levier.',
                              formula: 'Gain net / Apport = (Produit vente - Solde crédit + Cash flow cumulé - Apport) / Apport × 100',
                              interpretation: 'Avec 25 000€ d\'apport, si vous générez 68 400€ de gain, ROE = 274%. Sans levier (achat cash), le ROE tomberait à ~30%. C\'est tout l\'intérêt d\'emprunter.'
                            })}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'rgba(156, 163, 175, 0.1)';
                              e.target.style.borderColor = 'rgba(156, 163, 175, 0.7)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'transparent';
                              e.target.style.borderColor = 'rgba(156, 163, 175, 0.5)';
                            }}
                          >i</div>
                        </div>
                        <div style={{color: roe >= 0 ? '#4ade80' : '#f87171', fontSize: '2em', fontWeight: '700'}}>{formatNumber(roe, 2)}%</div>
                      </div>

                      {/* NPV KPI */}
                      <div style={{
                        padding: '20px',
                        background: '#222222',
                        backdropFilter: 'blur(20px)',
                        borderRadius: '12px',
                        border: '1px solid #2a2a2a',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                          <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>{t.netPresentValue}</div>
                          <div
                            style={{
                              width: '14px',
                              height: '14px',
                              borderRadius: '50%',
                              border: '1px solid rgba(156, 163, 175, 0.5)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7em',
                              color: 'rgba(156, 163, 175, 0.7)',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => setInfoModal({
                              title: 'VAN — Valeur Actuelle Nette',
                              description: 'Répond à la question : "Cet investissement rapporte-t-il plus qu\'un placement de référence ?" On actualise tous les flux futurs à un taux de référence (ex : 5%).',
                              formula: 'VAN = Σ(Cash flow mois / (1 + taux mensuel)^mois) - Investissement initial',
                              interpretation: 'VAN > 0 → l\'investissement rapporte plus que le taux de référence. VAN = 0 → rapporte exactement ce taux. VAN < 0 → rapporte moins. La VAN donne une valeur en euros.'
                            })}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'rgba(156, 163, 175, 0.1)';
                              e.target.style.borderColor = 'rgba(156, 163, 175, 0.7)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'transparent';
                              e.target.style.borderColor = 'rgba(156, 163, 175, 0.5)';
                            }}
                          >i</div>
                        </div>
                        <div style={{color: npv >= 0 ? '#4ade80' : '#f87171', fontSize: '2em', fontWeight: '700'}}>{formatNumber(npv, 0)}</div>
                      </div>

                      {/* Net Cash Flow (NCF) KPI */}
                      <div style={{
                        padding: '20px',
                        background: '#222222',
                        backdropFilter: 'blur(20px)',
                        borderRadius: '12px',
                        border: '1px solid #2a2a2a',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                          <div style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>{t.netCashFlowNCF}</div>
                          <div
                            style={{
                              width: '14px',
                              height: '14px',
                              borderRadius: '50%',
                              border: '1px solid rgba(156, 163, 175, 0.5)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7em',
                              color: 'rgba(156, 163, 175, 0.7)',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => setInfoModal({
                              title: 'Cash-Flow Net cumulé (NCF)',
                              description: 'Somme de tous les cash flows mensuels sur la durée de détention. Représente l\'argent que vous avez gagné (ou perdu) au fil du temps.',
                              formula: 'NCF = Cash flow mensuel × Nombre de mois = (Loyer effectif - Crédit - Charges) × N mois',
                              interpretation: 'Un NCF négatif signifie que vous avez dû injecter de l\'argent sur la période pour maintenir l\'investissement. C\'est l\'effort d\'épargne réel. Exemple : -555€/mois × 120 mois = -66 600€.'
                            })}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'rgba(156, 163, 175, 0.1)';
                              e.target.style.borderColor = 'rgba(156, 163, 175, 0.7)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'transparent';
                              e.target.style.borderColor = 'rgba(156, 163, 175, 0.5)';
                            }}
                          >i</div>
                        </div>
                        <div style={{color: totalNetCashFlow >= 0 ? '#4ade80' : '#f87171', fontSize: '2em', fontWeight: '700'}}>{formatNumber(totalNetCashFlow, 0)}</div>
                      </div>
                    </div>

                    {/* NCF Calculation Breakdown */}
                    <div style={{
                      marginTop: '20px',
                      padding: '20px',
                      background: '#222222',
                      borderRadius: '12px',
                      border: '1px solid #2a2a2a',
                    }}>
                      <div
                        style={{
                          fontSize: '0.95em',
                          fontWeight: '600',
                          color: 'rgba(209, 216, 224, 0.9)',
                          marginBottom: collapsedSections.ncfDetail ? '0' : '15px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer'
                        }}
                        onClick={() => setCollapsedSections(prev => ({
                          ...prev,
                          ncfDetail: !prev.ncfDetail
                        }))}
                      >
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <span>Détail du calcul du Cash-Flow Net (NCF)</span>
                          <span style={{fontSize: '0.75em', color: 'rgba(156, 163, 175, 0.7)', fontWeight: 'normal'}}>(basé sur le tableau Résumé du Cash-Flow)</span>
                        </div>
                        <span style={{
                          fontSize: '0.8em',
                          transition: 'transform 0.3s ease',
                          transform: collapsedSections.ncfDetail ? 'rotate(0deg)' : 'rotate(180deg)'
                        }}>▼</span>
                      </div>

                      {!collapsedSections.ncfDetail && (() => {
                        // Calculate detailed breakdown
                        const apportValue = financementData.apport || 0;
                        const totalCreditAmountNCF = (creditData.amount || 0) + (creditTravauxData.amount || 0);
                        const initialHomeValueNCF = estimatedHomeValue || (apportValue + totalCreditAmountNCF);
                        const yearsExact = totalMonths / 12;
                        const finalHomeValueCalc = initialHomeValueNCF * Math.pow(1 + performanceParams.housePriceInflation / 100, yearsExact);

                        const totalRevenue = annualData.reduce((sum, d) => sum + d.revenue, 0);
                        const totalCreditPayments = (creditAmortization.schedule?.slice(0, totalMonths).reduce((sum, month) => sum + month.principal + month.interest, 0) || 0)
                          + (creditTravauxAmortization.schedule?.slice(0, totalMonths).reduce((sum, month) => sum + month.principal + month.interest, 0) || 0);
                        const totalPrincipal = (creditAmortization.schedule?.slice(0, totalMonths).reduce((sum, month) => sum + month.principal, 0) || 0)
                          + (creditTravauxAmortization.schedule?.slice(0, totalMonths).reduce((sum, month) => sum + month.principal, 0) || 0);
                        const totalInterest = (creditAmortization.schedule?.slice(0, totalMonths).reduce((sum, month) => sum + month.interest, 0) || 0)
                          + (creditTravauxAmortization.schedule?.slice(0, totalMonths).reduce((sum, month) => sum + month.interest, 0) || 0);
                        let totalFeesCalc = 0;
                        recurrentFees.forEach(fee => {
                          for (let month = 0; month < totalMonths; month++) {
                            const monthInYear = (month % 12) + 1;
                            const isLastMonthOfHorizon = (month + 1) === totalMonths;
                            let amount = 0;
                            if (fee.frequency === 'monthly') {
                              amount = fee.amount;
                            } else if (fee.frequency === 'quarterly') {
                              if (monthInYear % 3 === 0) amount = fee.amount;
                              else if (isLastMonthOfHorizon) amount = fee.amount * (((monthInYear - 1) % 3) + 1) / 3;
                            } else if (fee.frequency === 'annually') {
                              if (monthInYear === 12) amount = fee.amount;
                              else if (isLastMonthOfHorizon) amount = fee.amount * (monthInYear / 12);
                            }
                            totalFeesCalc += amount;
                          }
                        });
                        const operationalCashFlow = totalRevenue - totalCreditPayments - totalFeesCalc;
                        const immoLen = creditAmortization.schedule?.length || 0;
                        const travLen = creditTravauxAmortization.schedule?.length || 0;
                        const finalBalance = (immoLen > 0 ? creditAmortization.schedule[Math.min(totalMonths - 1, immoLen - 1)]?.remainingBalance || 0 : 0)
                          + (travLen > 0 ? creditTravauxAmortization.schedule[Math.min(totalMonths - 1, travLen - 1)]?.remainingBalance || 0 : 0);
                        const capitalGain = finalHomeValueCalc - finalBalance;

                        const lineStyle = {
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '8px 0',
                          borderBottom: '1px solid rgba(58, 58, 58, 0.3)',
                          fontSize: '0.9em'
                        };
                        const labelStyle = { color: 'rgba(209, 216, 224, 0.8)' };
                        const valueStyle = { color: 'rgba(209, 216, 224, 0.9)', fontWeight: '500' };
                        const positiveStyle = { color: '#4ade80', fontWeight: '600' };
                        const negativeStyle = { color: '#f87171', fontWeight: '600' };
                        const subtotalStyle = { background: 'rgba(255, 255, 255, 0.03)', padding: '8px 10px', margin: '5px -10px', borderRadius: '6px' };

                        return (
                          <div>
                            {/* Initial Investment */}
                            <div style={{...lineStyle}}>
                              <span style={labelStyle}>1. Fonds Apportés (Apport initial)</span>
                              <span style={negativeStyle}>- {formatNumber(apportValue, 0)} €</span>
                            </div>

                            {/* Operational Cash Flow */}
                            <div style={{marginTop: '10px', marginBottom: '5px', fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)', fontStyle: 'italic'}}>
                              2. Cash-Flow Opérationnel sur {Math.floor(totalMonths/12)} ans ({totalMonths} mois):
                            </div>
                            <div style={{...lineStyle, paddingLeft: '15px'}}>
                              <span style={labelStyle}>+ Revenus locatifs totaux</span>
                              <span style={positiveStyle}>+ {formatNumber(totalRevenue, 0)} €</span>
                            </div>
                            <div style={{...lineStyle, paddingLeft: '15px'}}>
                              <span style={labelStyle}>- Remboursement capital crédit</span>
                              <span style={negativeStyle}>- {formatNumber(totalPrincipal, 0)} €</span>
                            </div>
                            <div style={{...lineStyle, paddingLeft: '15px'}}>
                              <span style={labelStyle}>- Intérêts crédit</span>
                              <span style={negativeStyle}>- {formatNumber(totalInterest, 0)} €</span>
                            </div>
                            <div style={{...lineStyle, paddingLeft: '15px'}}>
                              <span style={labelStyle}>- Charges et frais récurrents</span>
                              <span style={negativeStyle}>- {formatNumber(totalFeesCalc, 0)} €</span>
                            </div>
                            <div style={{...subtotalStyle, ...lineStyle, fontWeight: '600'}}>
                              <span style={labelStyle}>= Cash-Flow Opérationnel Total</span>
                              <span style={operationalCashFlow >= 0 ? positiveStyle : negativeStyle}>{operationalCashFlow >= 0 ? '+' : ''} {formatNumber(operationalCashFlow, 0)} €</span>
                            </div>

                            {/* Capital Gain at Exit */}
                            <div style={{marginTop: '15px', marginBottom: '5px', fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.7)', fontStyle: 'italic'}}>
                              3. Plus-value à la revente (fin de période):
                            </div>
                            <div style={{...lineStyle, paddingLeft: '15px'}}>
                              <span style={labelStyle}>Valeur du bien (avec appréciation {performanceParams.housePriceInflation}%/an)</span>
                              <span style={valueStyle}>{formatNumber(finalHomeValueCalc, 0)} €</span>
                            </div>
                            <div style={{...lineStyle, paddingLeft: '15px'}}>
                              <span style={labelStyle}>- Solde restant du crédit</span>
                              <span style={negativeStyle}>- {formatNumber(finalBalance, 0)} €</span>
                            </div>
                            <div style={{...subtotalStyle, ...lineStyle, fontWeight: '600'}}>
                              <span style={labelStyle}>= Plus-value nette à la revente</span>
                              <span style={capitalGain >= 0 ? positiveStyle : negativeStyle}>{capitalGain >= 0 ? '+' : ''} {formatNumber(capitalGain, 0)} €</span>
                            </div>

                            {/* Final NCF */}
                            <div style={{
                              marginTop: '15px',
                              padding: '15px',
                              background: 'rgba(59, 130, 246, 0.1)',
                              borderRadius: '8px',
                              border: '1px solid rgba(59, 130, 246, 0.3)'
                            }}>
                              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <span style={{color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700', fontSize: '1em'}}>
                                  NCF = Apport + CF Opérationnel + Plus-value
                                </span>
                                <span style={{
                                  color: totalNetCashFlow >= 0 ? '#4ade80' : '#f87171',
                                  fontWeight: '700',
                                  fontSize: '1.2em'
                                }}>
                                  {formatNumber(totalNetCashFlow, 0)} €
                                </span>
                              </div>
                              <div style={{marginTop: '8px', fontSize: '0.8em', color: 'rgba(156, 163, 175, 0.7)'}}>
                                = {formatNumber(-apportValue, 0)} + {formatNumber(operationalCashFlow, 0)} + {formatNumber(capitalGain, 0)}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    </>}
                  </div>

                  {/* Cash Flow Section */}
                  <div style={{marginTop: '30px'}}>
                    <h3 style={{
                      color: 'rgba(209, 216, 224, 0.9)',
                      fontSize: '1.1em',
                      marginBottom: '15px',
                      fontWeight: '600',
                      borderBottom: '1px solid rgba(58, 58, 58, 0.6)',
                      paddingBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => setCollapsedSections(prev => ({
                      ...prev,
                      cashFlow: !prev.cashFlow
                    }))}
                    >
                      <span>{t.cashFlow}</span>
                      <span style={{
                        fontSize: '0.8em',
                        transition: 'transform 0.3s ease',
                        transform: collapsedSections.cashFlow ? 'rotate(0deg)' : 'rotate(180deg)'
                      }}>▼</span>
                    </h3>

                    {!collapsedSections.cashFlow && <>
                    {/* Charts Section */}
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px'}}>
                    {/* Annual Cash Flow Chart */}
                    <div style={{
                      padding: '20px',
                      background: '#222222',
                      backdropFilter: 'blur(20px)',
                      borderRadius: '12px',
                      border: '1px solid #2a2a2a',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                    }}>
                      <h3 style={{color: 'rgba(209, 216, 224, 0.9)', fontSize: '1.1em', marginBottom: '20px', fontWeight: '600'}}>
                        {t.annualCashFlow}
                      </h3>

                      {/* Bar Chart */}
                      <div style={{position: 'relative', height: '300px', marginTop: '20px'}}>
                        {(() => {
                          // Calculate dynamic scale based on actual data
                          const maxCashFlow = Math.max(...annualData.map(d => d.netCashFlow));
                          const minCashFlow = Math.min(...annualData.map(d => d.netCashFlow));
                          const maxAbsValue = Math.max(Math.abs(maxCashFlow), Math.abs(minCashFlow));
                          const padding = maxAbsValue * 0.1; // 10% padding
                          const chartMax = maxCashFlow > 0 ? maxCashFlow + padding : padding;
                          const chartMin = minCashFlow < 0 ? minCashFlow - padding : -padding;
                          const chartRange = chartMax - chartMin;
                          const zeroPoint = (chartMax / chartRange) * 100; // Zero position as percentage from top

                          // Generate Y-axis labels
                          const yAxisLabels = [];
                          const labelCount = 5;
                          for (let i = 0; i < labelCount; i++) {
                            const value = chartMax - (i * chartRange / (labelCount - 1));
                            yAxisLabels.push(Math.round(value));
                          }

                          return (
                            <>
                              {/* Y-axis */}
                              <div style={{position: 'absolute', left: 0, top: 0, bottom: '30px', width: '50px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: '0.75em', color: 'rgba(156, 163, 175, 0.9)'}}>
                                {yAxisLabels.map((label, i) => (
                                  <div key={i}>{formatNumber(label, 0)}</div>
                                ))}
                              </div>

                              {/* Chart area */}
                              <div style={{position: 'absolute', left: '60px', right: '20px', top: 0, bottom: '30px', borderLeft: '1px solid #2a2a2a', borderBottom: '1px solid #2a2a2a'}}>
                                {/* Zero line */}
                                <div style={{position: 'absolute', top: `${zeroPoint}%`, left: 0, right: 0, height: '1px', background: 'rgba(156, 163, 175, 0.5)', zIndex: 1}}></div>

                                {/* Bars */}
                                <div style={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', paddingBottom: 0}}>
                                  {annualData.map((data, idx) => {
                                    const cashFlow = data.netCashFlow;
                                    const barHeightPercent = (Math.abs(cashFlow) / chartRange) * 100;
                                    const barHeight = `${barHeightPercent}%`;
                                    const isPositive = cashFlow >= 0;

                                    return (
                                      <div
                                        key={idx}
                                        style={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: isPositive ? 'flex-end' : 'flex-start', height: '100%', position: 'relative', cursor: 'pointer'}}
                                        onMouseEnter={(e) => {
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          setChartTooltip({
                                            x: rect.left + rect.width / 2,
                                            y: rect.top,
                                            data: data,
                                            type: 'annual'
                                          });
                                        }}
                                        onMouseLeave={() => setChartTooltip(null)}
                                      >
                                        <div style={{
                                          width: '60%',
                                          height: barHeight,
                                          background: isPositive ? 'rgba(74, 222, 128, 0.7)' : 'rgba(248, 113, 113, 0.7)',
                                          borderRadius: '4px 4px 0 0',
                                          position: 'absolute',
                                          bottom: isPositive ? `${100 - zeroPoint}%` : 'auto',
                                          top: isPositive ? 'auto' : `${zeroPoint}%`,
                                          transition: 'opacity 0.2s',
                                          opacity: chartTooltip && chartTooltip.type === 'annual' && chartTooltip.data.year === data.year ? 1 : 0.7,
                                        }}></div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* X-axis labels */}
                              <div style={{position: 'absolute', left: '60px', right: '20px', bottom: 0, height: '30px', display: 'flex', justifyContent: 'space-around', fontSize: '0.75em', color: 'rgba(156, 163, 175, 0.9)'}}>
                                {annualData.map(d => <div key={d.year}>{d.year}</div>)}
                              </div>
                            </>
                          );
                        })()}
                      </div>

                      <div style={{marginTop: '10px', textAlign: 'center', fontSize: '0.75em', color: 'rgba(16, 185, 129, 0.9)', padding: '8px', background: '#2a2a2a', borderRadius: '6px'}}>
                        Cash Flow
                      </div>
                    </div>

                    {/* Cumulative Cash Flow Chart */}
                    <div style={{
                      padding: '20px',
                      background: '#222222',
                      backdropFilter: 'blur(20px)',
                      borderRadius: '12px',
                      border: '1px solid #2a2a2a',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                    }}>
                      <h3 style={{color: 'rgba(209, 216, 224, 0.9)', fontSize: '1.1em', marginBottom: '20px', fontWeight: '600'}}>
                        {t.cumulativeCashFlow}
                      </h3>

                      {/* Waterfall Chart */}
                      <div style={{position: 'relative', height: '300px', marginTop: '20px'}}>
                        {(() => {
                          // Calculate dynamic scale based on cumulative data
                          const maxCumulative = Math.max(...cumulativeData.map(d => d.cumulative), 0);
                          const minCumulative = Math.min(...cumulativeData.map(d => d.cumulative), 0);
                          const dataRange = maxCumulative - minCumulative;
                          const padding = Math.max(dataRange * 0.15, 50000); // At least 50k padding or 15% of range

                          // Ensure we show positive range above zero
                          const chartMax = Math.max(maxCumulative + padding, padding * 0.3); // At least 30% of padding above zero
                          const chartMin = minCumulative - padding;
                          const chartRange = chartMax - chartMin;
                          const zeroPosition = (chartMax / chartRange) * 100; // Zero line position as percentage from top

                          // Generate Y-axis labels
                          const yAxisLabels = [];
                          const labelCount = 5;
                          for (let i = 0; i < labelCount; i++) {
                            const value = chartMax - (i * chartRange / (labelCount - 1));
                            yAxisLabels.push(Math.round(value));
                          }

                          return (
                            <>
                              {/* Y-axis */}
                              <div style={{position: 'absolute', left: 0, top: 0, bottom: '30px', width: '50px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: '0.75em', color: 'rgba(156, 163, 175, 0.9)'}}>
                                {yAxisLabels.map((label, i) => (
                                  <div key={i}>{formatNumber(label, 0)}</div>
                                ))}
                              </div>

                              {/* Chart area */}
                              <div style={{position: 'absolute', left: '60px', right: '20px', top: 0, bottom: '30px', borderLeft: '1px solid #2a2a2a', borderBottom: '1px solid #2a2a2a'}}>
                                {/* Zero line */}
                                <div style={{position: 'absolute', top: `${zeroPosition}%`, left: 0, right: 0, height: '2px', background: 'rgba(156, 163, 175, 0.7)', zIndex: 1}}></div>

                                {/* Bars */}
                                <div style={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around'}}>
                                  {cumulativeData.map((data, idx) => {
                                    const isPositive = data.cumulative >= 0;
                                    const barHeightPercent = (Math.abs(data.cumulative) / chartRange) * 100;
                                    const barHeight = `${barHeightPercent}%`;

                                    return (
                                      <div
                                        key={idx}
                                        style={{flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', height: '100%', cursor: 'pointer', position: 'relative'}}
                                        onMouseEnter={(e) => {
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          setChartTooltip({
                                            x: rect.left + rect.width / 2,
                                            y: rect.top,
                                            data: data,
                                            type: 'cumulative'
                                          });
                                        }}
                                        onMouseLeave={() => setChartTooltip(null)}
                                      >
                                        <div style={{
                                          width: '60%',
                                          height: barHeight,
                                          background: isPositive ? 'rgba(74, 222, 128, 0.7)' : 'rgba(52, 211, 153, 0.7)',
                                          borderRadius: '4px 4px 0 0',
                                          border: '1px solid #2a2a2a',
                                          transition: 'opacity 0.2s',
                                          opacity: chartTooltip && chartTooltip.type === 'cumulative' && chartTooltip.data.year === data.year ? 1 : 0.7,
                                          position: 'absolute',
                                          bottom: isPositive ? `${100 - zeroPosition}%` : 'auto',
                                          top: isPositive ? 'auto' : `${zeroPosition}%`,
                                        }}></div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* X-axis labels */}
                              <div style={{position: 'absolute', left: '60px', right: '20px', bottom: 0, height: '30px', display: 'flex', justifyContent: 'space-around', fontSize: '0.75em', color: 'rgba(156, 163, 175, 0.9)'}}>
                                {cumulativeData.map(d => <div key={d.year}>{d.year}</div>)}
                              </div>
                            </>
                          );
                        })()}
                      </div>

                      <div style={{marginTop: '10px', textAlign: 'center', fontSize: '0.75em', color: 'rgba(52, 211, 153, 0.9)', padding: '8px', background: '#2a2a2a', borderRadius: '6px'}}>
                        cumulative
                      </div>
                    </div>
                  </div>

                  {/* Cash Flow Summary Table */}
                  <div style={{
                    marginTop: '30px',
                    padding: '20px',
                    background: '#222222',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '12px',
                    border: '1px solid #2a2a2a',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                    overflowX: 'auto'
                  }}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
                      <h3 style={{color: 'rgba(209, 216, 224, 0.9)', fontSize: '1.1em', fontWeight: '600', margin: 0}}>
                        {t.cashFlowSummary}
                      </h3>

                      {/* View Mode Selector and Download Button */}
                      <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                        <button
                          onClick={() => setPerformanceViewMode('monthly')}
                          style={{
                            padding: '8px 16px',
                            background: performanceViewMode === 'monthly' ? '#10b981' : '#2a2a2a',
                            border: '1px solid #2a2a2a',
                            borderRadius: '6px',
                            color: 'rgba(209, 216, 224, 0.9)',
                            fontSize: '0.85em',
                            cursor: 'pointer',
                            fontWeight: performanceViewMode === 'monthly' ? '600' : '400'
                          }}
                        >
                          {t.monthly}
                        </button>
                        <button
                          onClick={() => setPerformanceViewMode('quarterly')}
                          style={{
                            padding: '8px 16px',
                            background: performanceViewMode === 'quarterly' ? '#10b981' : '#2a2a2a',
                            border: '1px solid #2a2a2a',
                            borderRadius: '6px',
                            color: 'rgba(209, 216, 224, 0.9)',
                            fontSize: '0.85em',
                            cursor: 'pointer',
                            fontWeight: performanceViewMode === 'quarterly' ? '600' : '400'
                          }}
                        >
                          {t.quarterly}
                        </button>
                        <button
                          onClick={() => setPerformanceViewMode('annual')}
                          style={{
                            padding: '8px 16px',
                            background: performanceViewMode === 'annual' ? '#10b981' : '#2a2a2a',
                            border: '1px solid #2a2a2a',
                            borderRadius: '6px',
                            color: 'rgba(209, 216, 224, 0.9)',
                            fontSize: '0.85em',
                            cursor: 'pointer',
                            fontWeight: performanceViewMode === 'annual' ? '600' : '400'
                          }}
                        >
                          {t.annual}
                        </button>

                        {/* Download Excel Button */}
                        <button
                          onClick={() => {
                            // Generate period data based on current view mode using exact months
                            let exportPeriodData = [];
                            const exportTotalMonths = performanceParams.investmentHorizonMonths;
                            const exportYears = Math.ceil(exportTotalMonths / 12);

                            if (performanceViewMode === 'monthly') {
                              // Use exact months + 1 for M0
                              exportPeriodData = Array.from({length: exportTotalMonths + 1}, (_, i) => {
                                if (i === 0) return { index: 0, label: 'M0', year: 0, month: 0 };
                                return { index: i, label: `M${i}`, year: Math.floor((i - 1) / 12) + 1, month: ((i - 1) % 12) + 1 };
                              });
                            } else if (performanceViewMode === 'quarterly') {
                              const totalQuarters = Math.ceil(exportTotalMonths / 3) + 1;
                              exportPeriodData = Array.from({length: totalQuarters}, (_, i) => {
                                if (i === 0) return { index: 0, label: 'Q0', year: 0, quarter: 0 };
                                return { index: i, label: `Q${((i - 1) % 4) + 1}-Y${Math.floor((i - 1) / 4) + 1}`, year: Math.floor((i - 1) / 4) + 1, quarter: ((i - 1) % 4) + 1 };
                              });
                            } else {
                              exportPeriodData = Array.from({length: exportYears + 1}, (_, i) => ({
                                index: i,
                                label: i === 0 ? 'Y0' : `Année ${i}`,
                                year: i
                              }));
                            }

                            // Helper functions for export
                            // Maps period index to creditAmortization.schedule array indices
                            // M1 -> schedule[0], M2 -> schedule[1], etc.
                            // Pro-rata: cap end at totalMonths for partial final periods
                            const getExportPeriodMonthRange = (period) => {
                              if (performanceViewMode === 'monthly') {
                                // period.index 1 (M1) should map to schedule[0]
                                return { start: period.index - 1, end: Math.min(period.index, exportTotalMonths) };
                              } else if (performanceViewMode === 'quarterly') {
                                const startMonth = (period.year - 1) * 12 + (period.quarter - 1) * 3;
                                return { start: startMonth, end: Math.min(startMonth + 3, exportTotalMonths) };
                              } else {
                                const startMonth = (period.year - 1) * 12;
                                return { start: startMonth, end: Math.min(startMonth + 12, exportTotalMonths) };
                              }
                            };

                            const exportTotalInvestment = financementData.apport || 0;
                            const exportTotalCreditAmount = (creditData.amount || 0) + (creditTravauxData.amount || 0);
                            const exportInitialHomeValue = estimatedHomeValue || (exportTotalInvestment + exportTotalCreditAmount);
                            const exportMonthlyPayment = (creditAmortization.monthlyPayment || 0) + (creditTravauxAmortization.monthlyPayment || 0);

                            // Build export data rows
                            const rows = [];

                            // Header row
                            const headerRow = ['Metric', ...exportPeriodData.map(p => p.label)];
                            rows.push(headerRow);

                            // Initial Investment row
                            const investmentRow = ['Investissement Initial', ...exportPeriodData.map((p, idx) => idx === 0 ? -exportTotalInvestment : 0)];
                            rows.push(investmentRow);

                            // Revenue row
                            const revenueRow = ['Loyer', ...exportPeriodData.map((p, idx) => {
                              if (p.year === 0) return '-';
                              const { start, end } = getExportPeriodMonthRange(p);
                              let revenue = 0;
                              for (let monthIndex = start; monthIndex < end; monthIndex++) {
                                const absoluteMonth = monthIndex + 1;
                                const monthRevenue = propertyRevenues.reduce((sum, rev) => {
                                  const startLag = rev.startLagMonths || 0;
                                  if (absoluteMonth <= startLag) return sum;
                                  const monthInYear = (monthIndex % 12) + 1;
                                  let amount = 0;
                                  if (rev.frequency === 'monthly') amount = rev.amount * (rev.occupancyRate / 100);
                                  else if (rev.frequency === 'quarterly' && monthInYear % 3 === 0) amount = rev.amount * (rev.occupancyRate / 100);
                                  else if (rev.frequency === 'annually' && monthInYear === 12) amount = rev.amount * (rev.occupancyRate / 100);
                                  return sum + amount;
                                }, 0);
                                revenue += monthRevenue;
                              }
                              return revenue;
                            })];
                            rows.push(revenueRow);

                            // Total Revenues row
                            rows.push(['Total Revenus', ...revenueRow.slice(1)]);

                            // Principal row (both credits combined)
                            const principalRow = ['Principal', ...exportPeriodData.map((p, idx) => {
                              if (p.year === 0) return '-';
                              const { start, end } = getExportPeriodMonthRange(p);
                              return (creditAmortization.schedule?.slice(start, end).reduce((sum, month) => sum + month.principal, 0) || 0)
                                + (creditTravauxAmortization.schedule?.slice(start, end).reduce((sum, month) => sum + month.principal, 0) || 0);
                            })];
                            rows.push(principalRow);

                            // Interest row (both credits combined)
                            const interestRow = ['Intérêts', ...exportPeriodData.map((p, idx) => {
                              if (p.year === 0) return '-';
                              const { start, end } = getExportPeriodMonthRange(p);
                              return (creditAmortization.schedule?.slice(start, end).reduce((sum, month) => sum + month.interest, 0) || 0)
                                + (creditTravauxAmortization.schedule?.slice(start, end).reduce((sum, month) => sum + month.interest, 0) || 0);
                            })];
                            rows.push(interestRow);

                            // Total Credit row (both credits combined)
                            const totalCreditRow = ['Total Crédit', ...exportPeriodData.map((p, idx) => {
                              if (p.year === 0) return '-';
                              const { start, end } = getExportPeriodMonthRange(p);
                              const principal = (creditAmortization.schedule?.slice(start, end).reduce((sum, month) => sum + month.principal, 0) || 0)
                                + (creditTravauxAmortization.schedule?.slice(start, end).reduce((sum, month) => sum + month.principal, 0) || 0);
                              const interest = (creditAmortization.schedule?.slice(start, end).reduce((sum, month) => sum + month.interest, 0) || 0)
                                + (creditTravauxAmortization.schedule?.slice(start, end).reduce((sum, month) => sum + month.interest, 0) || 0);
                              return principal + interest;
                            })];
                            rows.push(totalCreditRow);

                            // Recurrent fees rows (with pro-rata for final month)
                            recurrentFees.forEach(fee => {
                              const feeNames = { propertyTax: 'Précompte immobilier', coOwnershipFees: 'Charges copropriété', insurance: 'Assurance', maintenance: 'Entretien' };
                              const feeRow = [feeNames[fee.name] || fee.name, ...exportPeriodData.map((p, idx) => {
                                if (p.year === 0) return '-';
                                const { start, end } = getExportPeriodMonthRange(p);
                                let periodTotal = 0;
                                for (let month = start; month < end; month++) {
                                  const monthInYear = (month % 12) + 1;
                                  const isLastMonthOfHorizon = (month + 1) === exportTotalMonths;
                                  let amount = 0;
                                  if (fee.frequency === 'monthly') {
                                    amount = fee.amount;
                                  } else if (fee.frequency === 'quarterly') {
                                    if (monthInYear % 3 === 0) {
                                      amount = fee.amount;
                                    } else if (isLastMonthOfHorizon) {
                                      const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
                                      amount = fee.amount * (monthsIntoQuarter / 3);
                                    }
                                  } else if (fee.frequency === 'annually') {
                                    if (monthInYear === 12) {
                                      amount = fee.amount;
                                    } else if (isLastMonthOfHorizon) {
                                      amount = fee.amount * (monthInYear / 12);
                                    }
                                  }
                                  periodTotal += amount;
                                }
                                return periodTotal;
                              })];
                              rows.push(feeRow);
                            });

                            // Total Charges row (with pro-rata for final month, both credits combined)
                            const totalChargesRow = ['Total Charges', ...exportPeriodData.map((p, idx) => {
                              if (p.year === 0) return '-';
                              const { start, end } = getExportPeriodMonthRange(p);
                              const principal = (creditAmortization.schedule?.slice(start, end).reduce((sum, month) => sum + month.principal, 0) || 0)
                                + (creditTravauxAmortization.schedule?.slice(start, end).reduce((sum, month) => sum + month.principal, 0) || 0);
                              const interest = (creditAmortization.schedule?.slice(start, end).reduce((sum, month) => sum + month.interest, 0) || 0)
                                + (creditTravauxAmortization.schedule?.slice(start, end).reduce((sum, month) => sum + month.interest, 0) || 0);
                              let feesTotal = 0;
                              recurrentFees.forEach(fee => {
                                for (let month = start; month < end; month++) {
                                  const monthInYear = (month % 12) + 1;
                                  const isLastMonthOfHorizon = (month + 1) === exportTotalMonths;
                                  let amount = 0;
                                  if (fee.frequency === 'monthly') {
                                    amount = fee.amount;
                                  } else if (fee.frequency === 'quarterly') {
                                    if (monthInYear % 3 === 0) {
                                      amount = fee.amount;
                                    } else if (isLastMonthOfHorizon) {
                                      const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
                                      amount = fee.amount * (monthsIntoQuarter / 3);
                                    }
                                  } else if (fee.frequency === 'annually') {
                                    if (monthInYear === 12) {
                                      amount = fee.amount;
                                    } else if (isLastMonthOfHorizon) {
                                      amount = fee.amount * (monthInYear / 12);
                                    }
                                  }
                                  feesTotal += amount;
                                }
                              });
                              return principal + interest + feesTotal;
                            })];
                            rows.push(totalChargesRow);

                            // Net Cash Flow row
                            const netCashFlowRow = ['Cash Flow Net', ...exportPeriodData.map((p, idx) => {
                              if (p.year === 0) return -exportTotalInvestment;
                              const revenueVal = typeof revenueRow[idx + 1] === 'number' ? revenueRow[idx + 1] : 0;
                              const chargesVal = typeof totalChargesRow[idx + 1] === 'number' ? totalChargesRow[idx + 1] : 0;
                              return revenueVal - chargesVal;
                            })];
                            rows.push(netCashFlowRow);

                            // Balance row (both credits combined)
                            const balanceRow = ['Solde Restant Dû', ...exportPeriodData.map((p, idx) => {
                              if (p.year === 0) return exportTotalCreditAmount;
                              const { end } = getExportPeriodMonthRange(p);
                              const immoLen = creditAmortization.schedule?.length || 0;
                              const travLen = creditTravauxAmortization.schedule?.length || 0;
                              return (immoLen > 0 ? creditAmortization.schedule[Math.min(end - 1, immoLen - 1)]?.remainingBalance || 0 : 0)
                                + (travLen > 0 ? creditTravauxAmortization.schedule[Math.min(end - 1, travLen - 1)]?.remainingBalance || 0 : 0);
                            })];
                            rows.push(balanceRow);

                            // Home Value row
                            const homeValueRow = ['Valeur du Bien', ...exportPeriodData.map((p, idx) => {
                              if (p.year === 0) return exportInitialHomeValue;
                              const { end } = getExportPeriodMonthRange(p);
                              const yearFraction = end / 12;
                              return exportInitialHomeValue * Math.pow(1 + performanceParams.housePriceInflation / 100, yearFraction);
                            })];
                            rows.push(homeValueRow);

                            // Difference row
                            const differenceRow = ['Différence (Valeur - Solde)', ...exportPeriodData.map((p, idx) => {
                              const homeVal = typeof homeValueRow[idx + 1] === 'number' ? homeValueRow[idx + 1] : 0;
                              const balanceVal = typeof balanceRow[idx + 1] === 'number' ? balanceRow[idx + 1] : 0;
                              return homeVal - balanceVal;
                            })];
                            rows.push(differenceRow);

                            // Capital Gain row (only last period has value, both credits combined)
                            const capitalGainRow = ['Plus-Value', ...exportPeriodData.map((p, idx) => {
                              if (idx !== exportPeriodData.length - 1) return '-';
                              const { end } = getExportPeriodMonthRange(p);
                              const yearFraction = end / 12;
                              const finalHomeValue = exportInitialHomeValue * Math.pow(1 + performanceParams.housePriceInflation / 100, yearFraction);
                              const immoLen = creditAmortization.schedule?.length || 0;
                              const travLen = creditTravauxAmortization.schedule?.length || 0;
                              const finalBalance = (immoLen > 0 ? creditAmortization.schedule[Math.min(end - 1, immoLen - 1)]?.remainingBalance || 0 : 0)
                                + (travLen > 0 ? creditTravauxAmortization.schedule[Math.min(end - 1, travLen - 1)]?.remainingBalance || 0 : 0);
                              return finalHomeValue - finalBalance - exportTotalInvestment;
                            })];
                            rows.push(capitalGainRow);

                            // Create worksheet and workbook
                            const ws = XLSX.utils.aoa_to_sheet(rows);

                            // Set column widths
                            const colWidths = [{ wch: 25 }, ...exportPeriodData.map(() => ({ wch: 12 }))];
                            ws['!cols'] = colWidths;

                            const wb = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(wb, ws, 'Cash Flow');

                            // Generate filename with project info
                            const projectName = extractedData?.address ? extractedData.address.split(',')[0].trim().replace(/\s+/g, '_') : 'projet';
                            const viewModeLabel = performanceViewMode === 'monthly' ? 'mensuel' : performanceViewMode === 'quarterly' ? 'trimestriel' : 'annuel';
                            const filename = `cashflow_${projectName}_${viewModeLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`;

                            // Download
                            XLSX.writeFile(wb, filename);
                          }}
                          style={{
                            padding: '8px 16px',
                            background: 'rgba(59, 130, 246, 0.2)',
                            border: '1px solid rgba(59, 130, 246, 0.4)',
                            borderRadius: '6px',
                            color: '#60a5fa',
                            fontSize: '0.85em',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            marginLeft: '10px'
                          }}
                          title="Télécharger le tableau en Excel"
                        >
                          .xlsx
                        </button>
                      </div>
                    </div>

                    {(() => {
                      // Generate period data based on view mode using exact months
                      let periodData = [];
                      let periodLabel = '';

                      if (performanceViewMode === 'monthly') {
                        // Add month 0 for initial investment, then months 1 to totalMonths
                        periodData = Array.from({length: totalMonths + 1}, (_, i) => {
                          if (i === 0) {
                            return {
                              index: 0,
                              label: 'M0',
                              year: 0,
                              month: 0
                            };
                          }
                          return {
                            index: i,
                            label: `M${i}`,
                            year: Math.floor((i - 1) / 12) + 1,
                            month: ((i - 1) % 12) + 1
                          };
                        });
                        periodLabel = t.month;
                      } else if (performanceViewMode === 'quarterly') {
                        // Add quarter 0 for initial investment, then quarters based on exact months
                        const totalQuarters = Math.ceil(totalMonths / 3) + 1;
                        periodData = Array.from({length: totalQuarters}, (_, i) => {
                          if (i === 0) {
                            return {
                              index: 0,
                              label: 'Q0',
                              year: 0,
                              quarter: 0
                            };
                          }
                          return {
                            index: i,
                            label: `Q${((i - 1) % 4) + 1}-Y${Math.floor((i - 1) / 4) + 1}`,
                            year: Math.floor((i - 1) / 4) + 1,
                            quarter: ((i - 1) % 4) + 1
                          };
                        });
                        periodLabel = t.period;
                      } else {
                        periodData = annualData.map((d, i) => ({
                          index: i,
                          label: `${t.year} ${d.year}`,
                          year: d.year
                        }));
                        periodLabel = t.period;
                      }

                      // Helper function to get period month range
                      // Maps period index to creditAmortization.schedule array indices
                      // M1 -> schedule[0], M2 -> schedule[1], etc.
                      // Pro-rata: cap end at totalMonths for partial final periods
                      const getPeriodMonthRange = (period) => {
                        if (performanceViewMode === 'monthly') {
                          // period.index 1 (M1) should map to schedule[0]
                          return { start: period.index - 1, end: Math.min(period.index, totalMonths) };
                        } else if (performanceViewMode === 'quarterly') {
                          const startMonth = (period.year - 1) * 12 + (period.quarter - 1) * 3;
                          return { start: startMonth, end: Math.min(startMonth + 3, totalMonths) };
                        } else {
                          const startMonth = (period.year - 1) * 12;
                          return { start: startMonth, end: Math.min(startMonth + 12, totalMonths) };
                        }
                      };

                      // Helper: Get combined credit data from both schedules for a given range
                      const getCombinedCredit = (start, end, field) => {
                        const immo = creditAmortization.schedule?.slice(start, end).reduce((sum, month) => sum + month[field], 0) || 0;
                        const trav = creditTravauxAmortization.schedule?.slice(start, end).reduce((sum, month) => sum + month[field], 0) || 0;
                        return immo + trav;
                      };
                      const getCombinedBalance = (monthIdx) => {
                        const immoLen = creditAmortization.schedule?.length || 0;
                        const travLen = creditTravauxAmortization.schedule?.length || 0;
                        const immo = immoLen > 0 ? creditAmortization.schedule[Math.min(monthIdx, immoLen - 1)]?.remainingBalance || 0 : 0;
                        const trav = travLen > 0 ? creditTravauxAmortization.schedule[Math.min(monthIdx, travLen - 1)]?.remainingBalance || 0 : 0;
                        return immo + trav;
                      };
                      const totalCreditAmountCombined = (creditData.amount || 0) + (creditTravauxData.amount || 0);

                      // Helper: Get data for a specific period from annual data
                      const getPeriodData = (periodIndex) => {
                        if (performanceViewMode === 'annual') {
                          return annualData[periodIndex];
                        }

                        // Special case: Period 0 (month 0 or quarter 0) = initial investment
                        const period = periodData[periodIndex];
                        if (period.year === 0) {
                          return {
                            revenue: 0,
                            fees: 0,
                            creditPayments: 0,
                            netCashFlow: -totalInvestment
                          };
                        }

                        // For monthly/quarterly, calculate actual values accounting for lag
                        const { start, end } = getPeriodMonthRange(period);

                        let revenue = 0;
                        let fees = 0;
                        let creditPayments = 0;

                        // Calculate for each month in the period
                        for (let monthIndex = start; monthIndex < end; monthIndex++) {
                          const absoluteMonth = monthIndex + 1; // Month 0 = month 1, month 1 = month 2, etc.

                          // Calculate revenue for this month, accounting for lag
                          const monthRevenue = propertyRevenues.reduce((sum, rev) => {
                            const startLag = rev.startLagMonths || 0;

                            // Skip if we haven't reached the start lag yet
                            if (absoluteMonth <= startLag) {
                              return sum;
                            }

                            const monthInYear = (monthIndex % 12) + 1;
                            let amount = 0;
                            if (rev.frequency === 'monthly') {
                              amount = rev.amount * (rev.occupancyRate / 100);
                            } else if (rev.frequency === 'quarterly' && monthInYear % 3 === 0) {
                              amount = rev.amount * (rev.occupancyRate / 100);
                            } else if (rev.frequency === 'annually' && monthInYear === 12) {
                              amount = rev.amount * (rev.occupancyRate / 100);
                            }
                            return sum + amount;
                          }, 0);

                          // Calculate fees for this month (with pro-rata for final month)
                          const isLastMonthOfHorizon = (monthIndex + 1) === totalMonths;
                          const monthFees = recurrentFees.reduce((sum, fee) => {
                            const monthInYear = (monthIndex % 12) + 1;
                            let amount = 0;
                            if (fee.frequency === 'monthly') {
                              amount = fee.amount;
                            } else if (fee.frequency === 'quarterly') {
                              if (monthInYear % 3 === 0) {
                                amount = fee.amount;
                              } else if (isLastMonthOfHorizon) {
                                const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
                                amount = fee.amount * (monthsIntoQuarter / 3);
                              }
                            } else if (fee.frequency === 'annually') {
                              if (monthInYear === 12) {
                                amount = fee.amount;
                              } else if (isLastMonthOfHorizon) {
                                amount = fee.amount * (monthInYear / 12);
                              }
                            }
                            return sum + amount;
                          }, 0);

                          revenue += monthRevenue;
                          fees += monthFees;
                          creditPayments += monthlyPayment;
                        }

                        return {
                          revenue,
                          fees,
                          creditPayments,
                          netCashFlow: revenue - fees - creditPayments
                        };
                      };

                      return (
                        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.85em'}}>
                          <thead>
                            <tr style={{borderBottom: '2px solid #2a2a2a'}}>
                              <th style={{padding: '10px', textAlign: 'left', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600'}}>Metric</th>
                              {periodData.map((p, idx) => (
                                <th key={idx} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600'}}>
                                  {p.label}
                                </th>
                              ))}
                              <th style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)'}}>Total</th>
                            </tr>
                          </thead>
                      <tbody>
                        {/* INITIAL INVESTMENT (Neutral) */}
                        <tr style={{borderBottom: '1px solid #2a2a2a', background: 'rgba(255, 255, 255, 0.03)'}}>
                          <td style={{padding: '10px', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600'}}>{t.cashFlowFund}</td>
                          {periodData.map((p, idx) => (
                            <td key={idx} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)'}}>
                              {idx === 0 ? formatNumber(-(totalInvestment), 0) : '0'}
                            </td>
                          ))}
                          <td style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)'}}>
                            {formatNumber(-totalInvestment, 0)}
                          </td>
                        </tr>

                        {/* GREEN SECTION - REVENUES */}
                        <tr style={{borderBottom: '1px solid #2a2a2a', background: 'rgba(74, 222, 128, 0.1)'}}>
                          <td style={{padding: '10px', color: '#4ade80', fontWeight: '600'}}>{t.monthlyRent}</td>
                          {periodData.map((p, idx) => {
                            const pData = getPeriodData(idx);
                            return (
                              <td key={idx} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)'}}>
                                {p.year === 0 ? '-' : formatNumber(pData.revenue, 0)}
                              </td>
                            );
                          })}
                          <td style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)'}}>
                            {formatNumber(periodData.reduce((sum, p, idx) => sum + (p.year === 0 ? 0 : getPeriodData(idx).revenue), 0), 0)}
                          </td>
                        </tr>

                        {/* GREEN SUBTOTAL - Total Revenues */}
                        <tr style={{borderBottom: '2px solid #4ade80', background: 'rgba(74, 222, 128, 0.15)'}}>
                          <td style={{padding: '10px', color: '#4ade80', fontWeight: '700'}}>Total Revenus</td>
                          {periodData.map((p, idx) => {
                            const pData = getPeriodData(idx);
                            return (
                              <td key={idx} style={{padding: '10px', textAlign: 'right', color: '#4ade80', fontWeight: '700'}}>
                                {p.year === 0 ? '-' : formatNumber(pData.revenue, 0)}
                              </td>
                            );
                          })}
                          <td style={{padding: '10px', textAlign: 'right', color: '#4ade80', fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)'}}>
                            {formatNumber(periodData.reduce((sum, p, idx) => sum + (p.year === 0 ? 0 : getPeriodData(idx).revenue), 0), 0)}
                          </td>
                        </tr>

                        {/* RED SECTION - LOAN PAYMENTS */}
                        <tr style={{borderBottom: '1px solid #2a2a2a', background: 'rgba(248, 113, 113, 0.05)'}}>
                          <td style={{padding: '10px', color: '#f87171', fontWeight: '600'}}>Principal</td>
                          {periodData.map((p, idx) => {
                            if (p.year === 0) {
                              return (
                                <td key={idx} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)'}}>
                                  -
                                </td>
                              );
                            }
                            const { start, end } = getPeriodMonthRange(p);
                            const principal = getCombinedCredit(start, end, 'principal');
                            return (
                              <td key={idx} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)'}}>
                                {principal > 0 ? formatNumber(principal, 0) : '0'}
                              </td>
                            );
                          })}
                          <td style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)'}}>
                            {formatNumber(getCombinedCredit(0, totalMonths, 'principal'), 0)}
                          </td>
                        </tr>

                        <tr style={{borderBottom: '1px solid #2a2a2a', background: 'rgba(248, 113, 113, 0.05)'}}>
                          <td style={{padding: '10px', color: '#f87171', fontWeight: '600'}}>Interest</td>
                          {periodData.map((p, idx) => {
                            if (p.year === 0) {
                              return (
                                <td key={idx} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)'}}>
                                  -
                                </td>
                              );
                            }
                            const { start, end } = getPeriodMonthRange(p);
                            const interest = getCombinedCredit(start, end, 'interest');
                            return (
                              <td key={idx} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)'}}>
                                {interest > 0 ? formatNumber(interest, 0) : '0'}
                              </td>
                            );
                          })}
                          <td style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)'}}>
                            {formatNumber(getCombinedCredit(0, totalMonths, 'interest'), 0)}
                          </td>
                        </tr>

                        {/* RED SUBTOTAL - Total Credit */}
                        <tr style={{borderBottom: '1px solid #f87171', background: 'rgba(248, 113, 113, 0.15)'}}>
                          <td style={{padding: '10px', color: '#f87171', fontWeight: '700'}}>{t.totalCredit}</td>
                          {periodData.map((p, idx) => {
                            if (p.year === 0) {
                              return (
                                <td key={idx} style={{padding: '10px', textAlign: 'right', color: '#f87171', fontWeight: '700'}}>
                                  -
                                </td>
                              );
                            }
                            const { start, end } = getPeriodMonthRange(p);
                            const principal = getCombinedCredit(start, end, 'principal');
                            const interest = getCombinedCredit(start, end, 'interest');
                            return (
                              <td key={idx} style={{padding: '10px', textAlign: 'right', color: '#f87171', fontWeight: '700'}}>
                                {formatNumber(principal + interest, 0)}
                              </td>
                            );
                          })}
                          <td style={{padding: '10px', textAlign: 'right', color: '#f87171', fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)'}}>
                            {formatNumber(getCombinedCredit(0, totalMonths, 'principal') + getCombinedCredit(0, totalMonths, 'interest'), 0)}
                          </td>
                        </tr>

                        {/* RED SECTION - RECURRENT FEES */}
                        {recurrentFees.map(fee => {
                          // Calculate total for this fee across all months
                          let feeGrandTotal = 0;
                          for (let month = 0; month < totalMonths; month++) {
                            const monthInYear = (month % 12) + 1;
                            const isLastMonthOfHorizon = (month + 1) === totalMonths;
                            let amount = 0;
                            if (fee.frequency === 'monthly') {
                              amount = fee.amount;
                            } else if (fee.frequency === 'quarterly') {
                              if (monthInYear % 3 === 0) {
                                amount = fee.amount;
                              } else if (isLastMonthOfHorizon) {
                                const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
                                amount = fee.amount * (monthsIntoQuarter / 3);
                              }
                            } else if (fee.frequency === 'annually') {
                              if (monthInYear === 12) {
                                amount = fee.amount;
                              } else if (isLastMonthOfHorizon) {
                                amount = fee.amount * (monthInYear / 12);
                              }
                            }
                            feeGrandTotal += amount;
                          }

                          return (
                          <tr key={fee.id} style={{borderBottom: '1px solid #2a2a2a', background: 'rgba(248, 113, 113, 0.05)'}}>
                            <td style={{padding: '10px', color: '#f87171', fontWeight: '600'}}>{t[fee.name] || fee.name}</td>
                            {periodData.map((p, idx) => {
                              if (p.year === 0) {
                                return (
                                  <td key={idx} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)'}}>
                                    -
                                  </td>
                                );
                              }
                              const { start, end } = getPeriodMonthRange(p);
                              let periodTotal = 0;
                              for (let month = start; month < end; month++) {
                                const monthInYear = (month % 12) + 1;
                                const isLastMonthOfHorizon = (month + 1) === totalMonths;
                                let amount = 0;
                                if (fee.frequency === 'monthly') {
                                  amount = fee.amount;
                                } else if (fee.frequency === 'quarterly') {
                                  if (monthInYear % 3 === 0) {
                                    amount = fee.amount;
                                  } else if (isLastMonthOfHorizon) {
                                    const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
                                    amount = fee.amount * (monthsIntoQuarter / 3);
                                  }
                                } else if (fee.frequency === 'annually') {
                                  if (monthInYear === 12) {
                                    amount = fee.amount;
                                  } else if (isLastMonthOfHorizon) {
                                    amount = fee.amount * (monthInYear / 12);
                                  }
                                }
                                periodTotal += amount;
                              }
                              return (
                                <td key={idx} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)'}}>
                                  {formatNumber(periodTotal, 0)}
                                </td>
                              );
                            })}
                            <td style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)'}}>
                              {formatNumber(feeGrandTotal, 0)}
                            </td>
                          </tr>
                        );})}

                        {/* RED SUBTOTAL - Total Charges */}
                        <tr style={{borderBottom: '2px solid #f87171', background: 'rgba(248, 113, 113, 0.15)'}}>
                          <td style={{padding: '10px', color: '#f87171', fontWeight: '700'}}>Total Charges</td>
                          {periodData.map((p, idx) => {
                            if (p.year === 0) {
                              return (
                                <td key={idx} style={{padding: '10px', textAlign: 'right', color: '#f87171', fontWeight: '700'}}>
                                  -
                                </td>
                              );
                            }
                            const { start, end } = getPeriodMonthRange(p);
                            // Calculate credit payments (both credits combined)
                            const principal = getCombinedCredit(start, end, 'principal');
                            const interest = getCombinedCredit(start, end, 'interest');
                            const creditTotal = principal + interest;

                            // Calculate fees (with pro-rata for final month)
                            let feesTotal = 0;
                            recurrentFees.forEach(fee => {
                              for (let month = start; month < end; month++) {
                                const monthInYear = (month % 12) + 1;
                                const isLastMonthOfHorizon = (month + 1) === totalMonths;
                                let amount = 0;
                                if (fee.frequency === 'monthly') {
                                  amount = fee.amount;
                                } else if (fee.frequency === 'quarterly') {
                                  if (monthInYear % 3 === 0) {
                                    amount = fee.amount;
                                  } else if (isLastMonthOfHorizon) {
                                    const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
                                    amount = fee.amount * (monthsIntoQuarter / 3);
                                  }
                                } else if (fee.frequency === 'annually') {
                                  if (monthInYear === 12) {
                                    amount = fee.amount;
                                  } else if (isLastMonthOfHorizon) {
                                    amount = fee.amount * (monthInYear / 12);
                                  }
                                }
                                feesTotal += amount;
                              }
                            });

                            return (
                              <td key={idx} style={{padding: '10px', textAlign: 'right', color: '#f87171', fontWeight: '700'}}>
                                {formatNumber(creditTotal + feesTotal, 0)}
                              </td>
                            );
                          })}
                          {(() => {
                            // Calculate grand total of all charges (both credits combined)
                            const totalCreditPayments = getCombinedCredit(0, totalMonths, 'principal') + getCombinedCredit(0, totalMonths, 'interest');
                            let totalFeesPayments = 0;
                            recurrentFees.forEach(fee => {
                              for (let month = 0; month < totalMonths; month++) {
                                const monthInYear = (month % 12) + 1;
                                const isLastMonthOfHorizon = (month + 1) === totalMonths;
                                let amount = 0;
                                if (fee.frequency === 'monthly') {
                                  amount = fee.amount;
                                } else if (fee.frequency === 'quarterly') {
                                  if (monthInYear % 3 === 0) {
                                    amount = fee.amount;
                                  } else if (isLastMonthOfHorizon) {
                                    const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
                                    amount = fee.amount * (monthsIntoQuarter / 3);
                                  }
                                } else if (fee.frequency === 'annually') {
                                  if (monthInYear === 12) {
                                    amount = fee.amount;
                                  } else if (isLastMonthOfHorizon) {
                                    amount = fee.amount * (monthInYear / 12);
                                  }
                                }
                                totalFeesPayments += amount;
                              }
                            });
                            return (
                              <td style={{padding: '10px', textAlign: 'right', color: '#f87171', fontWeight: '700', borderLeft: '2px solid rgba(209, 216, 224, 0.3)'}}>
                                {formatNumber(totalCreditPayments + totalFeesPayments, 0)}
                              </td>
                            );
                          })()}
                        </tr>

                        {/* GRAND TOTAL - Net Cash Flow */}
                        <tr style={{borderTop: '3px solid rgba(209, 216, 224, 0.3)', borderBottom: '3px solid rgba(209, 216, 224, 0.3)', background: 'rgba(255, 255, 255, 0.08)'}}>
                          <td style={{padding: '12px', color: 'rgba(209, 216, 224, 0.95)', fontWeight: '700', fontSize: '1.05em'}}>{t.totalCashFlow}</td>
                          {periodData.map((p, idx) => {
                            // Period 0 shows only the initial investment (negative)
                            if (p.year === 0) {
                              const netCashFlow = -totalInvestment;
                              return (
                                <td key={idx} style={{padding: '12px', textAlign: 'right', color: netCashFlow >= 0 ? '#4ade80' : '#f87171', fontWeight: '700', fontSize: '1.05em'}}>
                                  {formatNumber(netCashFlow, 0)}
                                </td>
                              );
                            }

                            const { start, end } = getPeriodMonthRange(p);

                            // Revenues
                            const pData = getPeriodData(idx);
                            const revenue = pData.revenue;

                            // Credit payments (both credits combined)
                            const principal = getCombinedCredit(start, end, 'principal');
                            const interest = getCombinedCredit(start, end, 'interest');

                            // Fees (with pro-rata for final month)
                            let feesTotal = 0;
                            recurrentFees.forEach(fee => {
                              for (let month = start; month < end; month++) {
                                const monthInYear = (month % 12) + 1;
                                const isLastMonth = month === totalMonths - 1;
                                let amount = 0;
                                if (fee.frequency === 'monthly') {
                                  amount = fee.amount;
                                } else if (fee.frequency === 'quarterly') {
                                  if (monthInYear % 3 === 0) {
                                    amount = fee.amount;
                                  } else if (isLastMonth) {
                                    const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
                                    amount = fee.amount * (monthsIntoQuarter / 3);
                                  }
                                } else if (fee.frequency === 'annually') {
                                  if (monthInYear === 12) {
                                    amount = fee.amount;
                                  } else if (isLastMonth) {
                                    amount = fee.amount * (monthInYear / 12);
                                  }
                                }
                                feesTotal += amount;
                              }
                            });

                            const netCashFlow = revenue - (principal + interest + feesTotal);

                            return (
                              <td key={idx} style={{padding: '12px', textAlign: 'right', color: netCashFlow >= 0 ? '#4ade80' : '#f87171', fontWeight: '700', fontSize: '1.05em'}}>
                                {formatNumber(netCashFlow, 0)}
                              </td>
                            );
                          })}
                          {(() => {
                            // Calculate grand total of Cash-Flow (both credits combined)
                            const totalRevenue = periodData.reduce((sum, p, idx) => sum + (p.year === 0 ? 0 : getPeriodData(idx).revenue), 0);
                            const totalCreditPayments = getCombinedCredit(0, totalMonths, 'principal') + getCombinedCredit(0, totalMonths, 'interest');
                            let totalFeesPayments = 0;
                            recurrentFees.forEach(fee => {
                              for (let month = 0; month < totalMonths; month++) {
                                const monthInYear = (month % 12) + 1;
                                const isLastMonthOfHorizon = (month + 1) === totalMonths;
                                let amount = 0;
                                if (fee.frequency === 'monthly') {
                                  amount = fee.amount;
                                } else if (fee.frequency === 'quarterly') {
                                  if (monthInYear % 3 === 0) {
                                    amount = fee.amount;
                                  } else if (isLastMonthOfHorizon) {
                                    const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
                                    amount = fee.amount * (monthsIntoQuarter / 3);
                                  }
                                } else if (fee.frequency === 'annually') {
                                  if (monthInYear === 12) {
                                    amount = fee.amount;
                                  } else if (isLastMonthOfHorizon) {
                                    amount = fee.amount * (monthInYear / 12);
                                  }
                                }
                                totalFeesPayments += amount;
                              }
                            });
                            const grandTotalCashFlow = -totalInvestment + totalRevenue - totalCreditPayments - totalFeesPayments;
                            return (
                              <td style={{padding: '12px', textAlign: 'right', color: grandTotalCashFlow >= 0 ? '#4ade80' : '#f87171', fontWeight: '700', fontSize: '1.05em', borderLeft: '2px solid rgba(209, 216, 224, 0.3)'}}>
                                {formatNumber(grandTotalCashFlow, 0)}
                              </td>
                            );
                          })()}
                        </tr>

                        {/* Cash-Flow Total without Fonds Apportés */}
                        <tr style={{borderBottom: '1px solid #2a2a2a', background: 'rgba(255, 255, 255, 0.05)'}}>
                          <td style={{padding: '10px', color: 'rgba(209, 216, 224, 0.8)', fontWeight: '600', fontStyle: 'italic'}}>{t.totalCashFlow} (hors Apport)</td>
                          {periodData.map((p, idx) => {
                            // Period 0 shows 0 (excluding the initial investment)
                            if (p.year === 0) {
                              return (
                                <td key={idx} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.7)', fontStyle: 'italic'}}>
                                  0
                                </td>
                              );
                            }

                            const { start, end } = getPeriodMonthRange(p);

                            // Revenues
                            const pData = getPeriodData(idx);
                            const revenue = pData.revenue;

                            // Credit payments (both credits combined)
                            const principal = getCombinedCredit(start, end, 'principal');
                            const interest = getCombinedCredit(start, end, 'interest');

                            // Fees (with pro-rata for final month)
                            let feesTotal = 0;
                            recurrentFees.forEach(fee => {
                              for (let month = start; month < end; month++) {
                                const monthInYear = (month % 12) + 1;
                                const isLastMonth = month === totalMonths - 1;
                                let amount = 0;
                                if (fee.frequency === 'monthly') {
                                  amount = fee.amount;
                                } else if (fee.frequency === 'quarterly') {
                                  if (monthInYear % 3 === 0) {
                                    amount = fee.amount;
                                  } else if (isLastMonth) {
                                    const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
                                    amount = fee.amount * (monthsIntoQuarter / 3);
                                  }
                                } else if (fee.frequency === 'annually') {
                                  if (monthInYear === 12) {
                                    amount = fee.amount;
                                  } else if (isLastMonth) {
                                    amount = fee.amount * (monthInYear / 12);
                                  }
                                }
                                feesTotal += amount;
                              }
                            });

                            const netCashFlow = revenue - (principal + interest + feesTotal);

                            return (
                              <td key={idx} style={{padding: '10px', textAlign: 'right', color: netCashFlow >= 0 ? 'rgba(74, 222, 128, 0.8)' : 'rgba(248, 113, 113, 0.8)', fontStyle: 'italic'}}>
                                {formatNumber(netCashFlow, 0)}
                              </td>
                            );
                          })}
                          {(() => {
                            // Calculate grand total of Cash-Flow excluding initial investment (both credits combined)
                            const totalRevenue = periodData.reduce((sum, p, idx) => sum + (p.year === 0 ? 0 : getPeriodData(idx).revenue), 0);
                            const totalCreditPayments = getCombinedCredit(0, totalMonths, 'principal') + getCombinedCredit(0, totalMonths, 'interest');
                            let totalFeesPayments = 0;
                            recurrentFees.forEach(fee => {
                              for (let month = 0; month < totalMonths; month++) {
                                const monthInYear = (month % 12) + 1;
                                const isLastMonthOfHorizon = (month + 1) === totalMonths;
                                let amount = 0;
                                if (fee.frequency === 'monthly') {
                                  amount = fee.amount;
                                } else if (fee.frequency === 'quarterly') {
                                  if (monthInYear % 3 === 0) {
                                    amount = fee.amount;
                                  } else if (isLastMonthOfHorizon) {
                                    const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
                                    amount = fee.amount * (monthsIntoQuarter / 3);
                                  }
                                } else if (fee.frequency === 'annually') {
                                  if (monthInYear === 12) {
                                    amount = fee.amount;
                                  } else if (isLastMonthOfHorizon) {
                                    amount = fee.amount * (monthInYear / 12);
                                  }
                                }
                                totalFeesPayments += amount;
                              }
                            });
                            const cashFlowWithoutInvestment = totalRevenue - totalCreditPayments - totalFeesPayments;
                            return (
                              <td style={{padding: '10px', textAlign: 'right', color: cashFlowWithoutInvestment >= 0 ? 'rgba(74, 222, 128, 0.8)' : 'rgba(248, 113, 113, 0.8)', fontWeight: '700', fontStyle: 'italic', borderLeft: '2px solid rgba(209, 216, 224, 0.3)'}}>
                                {formatNumber(cashFlowWithoutInvestment, 0)}
                              </td>
                            );
                          })()}
                        </tr>

                        {/* ADDITIONAL INFO ROWS */}
                        <tr style={{borderBottom: '1px solid #2a2a2a', background: 'rgba(255, 255, 255, 0.03)'}}>
                          <td style={{padding: '10px', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600'}}>{t.balance}</td>
                          {periodData.map((p, idx) => {
                            // Period 0 should show the full initial credit amount
                            if (p.year === 0) {
                              return (
                                <td key={idx} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600'}}>
                                  {formatNumber(totalCreditAmountCombined, 0)}
                                </td>
                              );
                            }

                            const { end } = getPeriodMonthRange(p);
                            const balance = getCombinedBalance(end - 1);
                            return (
                              <td key={idx} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600'}}>
                                {formatNumber(balance, 0)}
                              </td>
                            );
                          })}
                          <td style={{padding: '10px', textAlign: 'right', color: 'rgba(150, 150, 150, 0.6)', borderLeft: '2px solid rgba(209, 216, 224, 0.3)'}}>-</td>
                        </tr>

                        <tr style={{borderBottom: '1px solid #2a2a2a', background: 'rgba(255, 255, 255, 0.03)'}}>
                          <td style={{padding: '10px', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600'}}>{t.homeValue}</td>
                          {periodData.map((p, idx) => {
                            // Period 0 should show initial home value (no appreciation yet)
                            if (p.year === 0) {
                              const periodHomeValue = initialHomeValue;
                              return (
                                <td key={idx} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600'}}>
                                  {formatNumber(periodHomeValue, 0)}
                                </td>
                              );
                            }

                            const { end } = getPeriodMonthRange(p);
                            const yearFraction = end / 12;
                            const periodHomeValue = getHomeValueAtYear(yearFraction);
                            return (
                              <td key={idx} style={{padding: '10px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '600'}}>
                                {formatNumber(periodHomeValue, 0)}
                              </td>
                            );
                          })}
                          <td style={{padding: '10px', textAlign: 'right', color: 'rgba(150, 150, 150, 0.6)', borderLeft: '2px solid rgba(209, 216, 224, 0.3)'}}>-</td>
                        </tr>

                        <tr style={{borderBottom: '1px solid #2a2a2a', background: 'rgba(74, 222, 128, 0.1)'}}>
                          <td style={{padding: '10px', color: '#4ade80', fontWeight: '700'}}>{t.difference}</td>
                          {periodData.map((p, idx) => {
                            // Period 0: Initial home value - initial credit amount
                            if (p.year === 0) {
                              const diff = initialHomeValue - totalCreditAmountCombined;
                              return (
                                <td key={idx} style={{padding: '10px', textAlign: 'right', color: '#4ade80', fontWeight: '700'}}>
                                  {formatNumber(diff, 0)}
                                </td>
                              );
                            }

                            const { end } = getPeriodMonthRange(p);
                            const balance = getCombinedBalance(end - 1);
                            const yearFraction = end / 12;
                            const periodHomeValue = getHomeValueAtYear(yearFraction);
                            const diff = periodHomeValue - balance;
                            return (
                              <td key={idx} style={{padding: '10px', textAlign: 'right', color: '#4ade80', fontWeight: '700'}}>
                                {formatNumber(diff, 0)}
                              </td>
                            );
                          })}
                          <td style={{padding: '10px', textAlign: 'right', color: 'rgba(150, 150, 150, 0.6)', borderLeft: '2px solid rgba(209, 216, 224, 0.3)'}}>-</td>
                        </tr>

                        {/* Capital Gain Row - Only shown in last period */}
                        <tr style={{borderTop: '2px solid #2a2a2a', background: 'rgba(52, 211, 153, 0.15)'}}>
                          <td style={{padding: '12px', color: 'rgba(209, 216, 224, 0.9)', fontWeight: '700'}}>{t.capitalGain}</td>
                          {periodData.map((p, idx) => {
                            const isLastPeriod = idx === periodData.length - 1;
                            if (!isLastPeriod) {
                              return (
                                <td key={idx} style={{padding: '12px', textAlign: 'right', color: 'rgba(209, 216, 224, 0.7)'}}>
                                  -
                                </td>
                              );
                            }

                            // Last period: Sale proceeds - remaining loan balance - initial investment
                            const { end } = getPeriodMonthRange(p);
                            const yearFraction = end / 12;
                            const finalHomeValue = getHomeValueAtYear(yearFraction);
                            const finalBalance = getCombinedBalance(end - 1);
                            const capitalGain = finalHomeValue - finalBalance - totalInvestment;

                            return (
                              <td key={idx} style={{padding: '12px', textAlign: 'right', color: capitalGain >= 0 ? '#10b981' : '#f87171', fontWeight: '700', fontSize: '1.1em'}}>
                                {formatNumber(capitalGain, 0)}
                              </td>
                            );
                          })}
                          <td style={{padding: '12px', textAlign: 'right', color: 'rgba(150, 150, 150, 0.6)', borderLeft: '2px solid rgba(209, 216, 224, 0.3)'}}>-</td>
                        </tr>
                      </tbody>
                    </table>
                      );
                    })()}
                  </div>
                  </>}
                  </div>

                  {/* SENSITIVITY ANALYSIS SECTION */}
                  <div style={{marginTop: '30px'}}>
                    <h3 style={{
                      color: 'rgba(209, 216, 224, 0.9)',
                      fontSize: '1.1em',
                      marginBottom: '15px',
                      fontWeight: '600',
                      borderBottom: '1px solid rgba(58, 58, 58, 0.6)',
                      paddingBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => setCollapsedSections(prev => ({
                      ...prev,
                      sensitivity: !prev.sensitivity
                    }))}
                    >
                      <span>{t.sensitivity}</span>
                      <span style={{
                        fontSize: '0.8em',
                        transition: 'transform 0.3s ease',
                        transform: collapsedSections.sensitivity ? 'rotate(0deg)' : 'rotate(180deg)'
                      }}>▼</span>
                    </h3>

                    {!collapsedSections.sensitivity && <div className="summary-card">
              {/* Parameter selectors */}
              <div style={{marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap'}}>
                {/* X-axis parameter */}
                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                  <span style={{color: 'rgba(209, 216, 224, 0.7)', fontSize: '0.9em'}}>{t.sensitivityParameter}:</span>
                  <select
                    value={sensitivityParam}
                    onChange={(e) => setSensitivityParam(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      background: '#1a1a1a',
                      border: '1px solid #2a2a2a',
                      borderRadius: '8px',
                      color: 'rgba(209, 216, 224, 0.9)',
                      fontSize: '0.9em',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="interestRate">{t.interestRateParam}</option>
                    <option value="equityPercent">{t.equityPercentParam}</option>
                    <option value="occupancyRate">{t.occupancyRateParam}</option>
                    <option value="duration">{t.durationParam}</option>
                  </select>
                </div>
                {/* Y-axis metric */}
                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                  <span style={{color: 'rgba(209, 216, 224, 0.7)', fontSize: '0.9em'}}>{t.sensitivityYAxis}:</span>
                  <select
                    value={sensitivityYMetric}
                    onChange={(e) => setSensitivityYMetric(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      background: '#1a1a1a',
                      border: '1px solid #2a2a2a',
                      borderRadius: '8px',
                      color: 'rgba(209, 216, 224, 0.9)',
                      fontSize: '0.9em',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="irr">{t.irrMetric}</option>
                    <option value="ncf">{t.ncfMetric}</option>
                    <option value="npv">{t.npvMetric}</option>
                    <option value="roe">{t.roeMetric}</option>
                  </select>
                </div>
              </div>

              {/* Sensitivity Chart */}
              {(() => {
                // Calculate metrics (IRR, NCF, NPV, ROE) for different parameter values
                const calculateMetricsForParams = (paramType, paramValue) => {
                  // Get base values
                  const baseInterestRate = creditData.interestRate;
                  const baseTotalAcquisition = (acquisitionData.prixAchat || 0) + (acquisitionData.fraisNotaire || 0) +
                                               (acquisitionData.fraisAgence || 0) + (acquisitionData.travaux || 0) +
                                               (acquisitionData.autresFrais || 0);
                  const baseApport = financementData.apport || 0;
                  const baseEquityPercent = baseTotalAcquisition > 0 ? (baseApport / baseTotalAcquisition) * 100 : 0;

                  // Determine values to use based on parameter being varied
                  let testInterestRate = baseInterestRate;
                  let testEquityPercent = baseEquityPercent;
                  let testOccupancyMultiplier = 1;
                  let testDurationMonths = performanceParams.investmentHorizonMonths || 120;

                  if (paramType === 'interestRate') {
                    testInterestRate = paramValue;
                  } else if (paramType === 'equityPercent') {
                    testEquityPercent = paramValue;
                  } else if (paramType === 'occupancyRate') {
                    testOccupancyMultiplier = paramValue / 100;
                  } else if (paramType === 'duration') {
                    testDurationMonths = paramValue;
                  }

                  // Calculate loan amount based on equity percent
                  const testApport = baseTotalAcquisition * (testEquityPercent / 100);
                  const testLoanAmount = baseTotalAcquisition - testApport;

                  // Calculate monthly payment for the test scenario
                  const monthlyRate = testInterestRate / 100 / 12;
                  const numPayments = (creditData.durationYears || 20) * 12;
                  let testMonthlyPayment = 0;
                  if (testLoanAmount > 0 && monthlyRate > 0 && numPayments > 0) {
                    testMonthlyPayment = (testLoanAmount * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
                  }

                  // Build cash flows
                  const cashFlows = [-testApport]; // Initial investment
                  const testTotalMonths = testDurationMonths;

                  for (let monthIdx = 1; monthIdx <= testTotalMonths; monthIdx++) {
                    const yearOfMonth = Math.floor((monthIdx - 1) / 12);
                    const monthInYear = ((monthIdx - 1) % 12) + 1;
                    const isLastMonth = monthIdx === testTotalMonths;

                    // Monthly revenue (adjusted for occupancy if testing that parameter)
                    let monthRevenue = 0;
                    propertyRevenues.forEach(rev => {
                      const startLag = rev.startLagMonths || 0;
                      if (monthIdx <= startLag) return;

                      const revAmount = parseFloat(rev.amount) || 0;
                      let baseOccupancy = (parseFloat(rev.occupancyRate) || 100) / 100;
                      if (paramType === 'occupancyRate') {
                        baseOccupancy = testOccupancyMultiplier;
                      }
                      const freq = rev.frequency || 'monthly';
                      const indexation = parseFloat(rev.indexation) || 0;

                      let amount = 0;
                      if (freq === 'monthly') {
                        amount = revAmount * baseOccupancy;
                      } else if (freq === 'quarterly' && monthInYear % 3 === 0) {
                        amount = revAmount * baseOccupancy;
                      } else if (freq === 'annually' && monthInYear === 12) {
                        amount = revAmount * baseOccupancy;
                      }
                      monthRevenue += amount * Math.pow(1 + indexation / 100, yearOfMonth);
                    });

                    // Monthly fees
                    let monthFees = 0;
                    recurrentFees.forEach(fee => {
                      const feeAmount = parseFloat(fee.amount) || 0;
                      const freq = fee.frequency || 'monthly';
                      let amount = 0;
                      if (freq === 'monthly') {
                        amount = feeAmount;
                      } else if (freq === 'quarterly') {
                        if (monthInYear % 3 === 0) {
                          amount = feeAmount;
                        } else if (isLastMonth) {
                          const monthsIntoQuarter = ((monthInYear - 1) % 3) + 1;
                          amount = feeAmount * (monthsIntoQuarter / 3);
                        }
                      } else if (freq === 'annually') {
                        if (monthInYear === 12) {
                          amount = feeAmount;
                        } else if (isLastMonth) {
                          amount = feeAmount * (monthInYear / 12);
                        }
                      }
                      monthFees += amount;
                    });

                    let cf = monthRevenue - monthFees - testMonthlyPayment;

                    // Add sale proceeds in last month
                    if (isLastMonth) {
                      const yearsExact = testTotalMonths / 12;
                      const testInitialHomeValue = estimatedHomeValue || baseTotalAcquisition;
                      const finalHomeValue = testInitialHomeValue * Math.pow(1 + performanceParams.housePriceInflation / 100, yearsExact);
                      // Calculate remaining balance
                      let remainingBalance = testLoanAmount;
                      for (let m = 0; m < testTotalMonths; m++) {
                        const interestPayment = remainingBalance * monthlyRate;
                        const principalPayment = testMonthlyPayment - interestPayment;
                        remainingBalance = Math.max(0, remainingBalance - principalPayment);
                      }
                      cf += finalHomeValue - remainingBalance;
                    }

                    cashFlows.push(cf);
                  }

                  // Calculate IRR using Newton-Raphson with multiple initial guesses
                  const calculateIRR = (flows) => {
                    const maxIterations = 200;
                    const tolerance = 0.00001;
                    const guesses = [0.01, 0.05, 0.1, 0.2, -0.01, -0.05];

                    for (const guess of guesses) {
                      let rate = guess;
                      let converged = false;

                      for (let i = 0; i < maxIterations; i++) {
                        let npv = 0;
                        let dnpv = 0;
                        for (let j = 0; j < flows.length; j++) {
                          const discountFactor = Math.pow(1 + rate, j);
                          if (!isFinite(discountFactor) || discountFactor === 0) break;
                          npv += flows[j] / discountFactor;
                          if (j > 0) {
                            dnpv -= j * flows[j] / Math.pow(1 + rate, j + 1);
                          }
                        }
                        if (Math.abs(npv) < tolerance) {
                          converged = true;
                          break;
                        }
                        if (Math.abs(dnpv) < tolerance || !isFinite(dnpv)) break;
                        const newRate = rate - npv / dnpv;
                        if (!isFinite(newRate)) break;
                        rate = newRate;
                      }

                      if (converged && isFinite(rate)) {
                        return rate * 12 * 100; // Convert monthly to annual percentage
                      }
                    }
                    return NaN;
                  };

                  // Calculate NPV at discount rate (use base interest rate as discount rate)
                  const discountRate = baseInterestRate / 100 / 12; // Monthly discount rate
                  let npv = 0;
                  for (let j = 0; j < cashFlows.length; j++) {
                    npv += cashFlows[j] / Math.pow(1 + discountRate, j);
                  }

                  // Calculate total NCF (sum of all cash flows = Performance Totale à la revente)
                  const totalNCF = cashFlows.reduce((sum, cf) => sum + cf, 0);

                  // Calculate ROE (Return on Equity) = Total profit / Initial equity investment
                  const roe = testApport > 0 ? (totalNCF / testApport) * 100 : 0;

                  // Calculate IRR
                  const irr = calculateIRR(cashFlows);

                  return { irr, ncf: totalNCF, npv, roe };
                };

                // Generate data points based on selected parameter
                let dataPoints = [];
                let paramLabel = '';
                let paramUnit = '%';

                // Y-axis metric label and unit
                let yAxisLabel = '';
                let yAxisUnit = '';
                if (sensitivityYMetric === 'irr') {
                  yAxisLabel = t.irrMetric;
                  yAxisUnit = '%';
                } else if (sensitivityYMetric === 'ncf') {
                  yAxisLabel = t.ncfMetric;
                  yAxisUnit = '€';
                } else if (sensitivityYMetric === 'npv') {
                  yAxisLabel = t.npvMetric;
                  yAxisUnit = '€';
                } else if (sensitivityYMetric === 'roe') {
                  yAxisLabel = t.roeMetric;
                  yAxisUnit = '%';
                }

                if (sensitivityParam === 'interestRate') {
                  paramLabel = t.interestRateParam;
                  for (let rate = 0; rate <= 8; rate += 0.5) {
                    const metrics = calculateMetricsForParams('interestRate', rate);
                    dataPoints.push({ x: rate, y: metrics[sensitivityYMetric], ...metrics });
                  }
                } else if (sensitivityParam === 'equityPercent') {
                  paramLabel = t.equityPercentParam;
                  for (let equity = 5; equity <= 100; equity += 5) {
                    const metrics = calculateMetricsForParams('equityPercent', equity);
                    dataPoints.push({ x: equity, y: metrics[sensitivityYMetric], ...metrics });
                  }
                } else if (sensitivityParam === 'occupancyRate') {
                  paramLabel = t.occupancyRateParam;
                  for (let occ = 0; occ <= 100; occ += 5) {
                    const metrics = calculateMetricsForParams('occupancyRate', occ);
                    dataPoints.push({ x: occ, y: metrics[sensitivityYMetric], ...metrics });
                  }
                } else if (sensitivityParam === 'duration') {
                  paramLabel = t.durationParam;
                  paramUnit = '';
                  // Range from 1 month to max credit length in months, one data point per month
                  // This varies the investment horizon (Horizon d'Investissement)
                  const maxMonths = (creditData.durationYears || 20) * 12;
                  for (let months = 1; months <= maxMonths; months++) {
                    const metrics = calculateMetricsForParams('duration', months);
                    dataPoints.push({ x: months, y: metrics[sensitivityYMetric], ...metrics });
                  }
                }

                // Filter out invalid values
                dataPoints = dataPoints.filter(d => isFinite(d.y) && !isNaN(d.y));

                if (dataPoints.length === 0) {
                  return (
                    <div style={{padding: '40px', textAlign: 'center', color: 'rgba(209, 216, 224, 0.5)'}}>
                      Données insuffisantes pour le calcul
                    </div>
                  );
                }

                // Calculate chart bounds
                const minY = Math.min(...dataPoints.map(d => d.y));
                const maxY = Math.max(...dataPoints.map(d => d.y));
                const yRange = maxY - minY || 1;
                const paddedMinY = minY - yRange * 0.1;
                const paddedMaxY = maxY + yRange * 0.1;

                const minX = Math.min(...dataPoints.map(d => d.x));
                const maxX = Math.max(...dataPoints.map(d => d.x));

                // Chart dimensions - Full width responsive
                const chartWidth = 1600;
                const chartHeight = 400;
                const padding = { top: 40, right: 40, bottom: 60, left: 100 };
                const plotWidth = chartWidth - padding.left - padding.right;
                const plotHeight = chartHeight - padding.top - padding.bottom;

                // Scale functions
                const scaleX = (val) => padding.left + ((val - minX) / (maxX - minX)) * plotWidth;
                const scaleY = (val) => padding.top + plotHeight - ((val - paddedMinY) / (paddedMaxY - paddedMinY)) * plotHeight;

                // Generate path
                const pathData = dataPoints.map((d, i) => {
                  const x = scaleX(d.x);
                  const y = scaleY(d.y);
                  return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                }).join(' ');

                // Y-axis ticks
                const yTicks = [];
                const yTickCount = 6;
                for (let i = 0; i <= yTickCount; i++) {
                  const val = paddedMinY + (paddedMaxY - paddedMinY) * (i / yTickCount);
                  yTicks.push(val);
                }

                // X-axis ticks - show all data points
                const xTicks = dataPoints.map(d => d.x);

                // Current value marker
                let currentX = 0;
                if (sensitivityParam === 'interestRate') {
                  currentX = creditData.interestRate || 0;
                } else if (sensitivityParam === 'equityPercent') {
                  const totalAcq = (acquisitionData.prixAchat || 0) + (acquisitionData.fraisNotaire || 0) +
                                  (acquisitionData.fraisAgence || 0) + (acquisitionData.travaux || 0) +
                                  (acquisitionData.autresFrais || 0);
                  currentX = totalAcq > 0 ? ((financementData.apport || 0) / totalAcq) * 100 : 0;
                } else if (sensitivityParam === 'occupancyRate') {
                  const avgOccupancy = propertyRevenues.length > 0
                    ? propertyRevenues.reduce((sum, r) => sum + (parseFloat(r.occupancyRate) || 100), 0) / propertyRevenues.length
                    : 100;
                  currentX = avgOccupancy;
                } else if (sensitivityParam === 'duration') {
                  currentX = performanceParams.investmentHorizonMonths || 120; // Already in months
                }

                const currentMetrics = calculateMetricsForParams(sensitivityParam, currentX);
                const currentY = currentMetrics[sensitivityYMetric];
                const currentXPos = scaleX(currentX);
                const currentYPos = scaleY(currentY);

                return (
                  <div style={{width: '100%', position: 'relative'}}>
                    <svg
                      viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                      preserveAspectRatio="xMidYMid meet"
                      style={{display: 'block', width: '100%', height: 'auto', maxHeight: '500px'}}
                      onMouseLeave={() => setSensitivityHoveredPoint(null)}
                    >
                      {/* Gradient fill under line */}
                      <defs>
                        <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05" />
                        </linearGradient>
                      </defs>

                      {/* Area fill */}
                      <path
                        d={`${pathData} L ${scaleX(dataPoints[dataPoints.length - 1].x)} ${chartHeight - padding.bottom} L ${scaleX(dataPoints[0].x)} ${chartHeight - padding.bottom} Z`}
                        fill="url(#areaGradient)"
                      />

                      {/* Grid lines */}
                      {yTicks.map((tick, i) => (
                        <line
                          key={`grid-y-${i}`}
                          x1={padding.left}
                          y1={scaleY(tick)}
                          x2={chartWidth - padding.right}
                          y2={scaleY(tick)}
                          stroke="rgba(209, 216, 224, 0.1)"
                          strokeDasharray="4,4"
                        />
                      ))}

                      {/* Zero line if in range */}
                      {paddedMinY < 0 && paddedMaxY > 0 && (
                        <line
                          x1={padding.left}
                          y1={scaleY(0)}
                          x2={chartWidth - padding.right}
                          y2={scaleY(0)}
                          stroke="rgba(248, 113, 113, 0.6)"
                          strokeWidth="2"
                        />
                      )}

                      {/* Line chart */}
                      <path
                        d={pathData}
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />

                      {/* Data points - interactive */}
                      {dataPoints.map((d, i) => (
                        <g key={`point-group-${i}`}>
                          {/* Larger invisible hit area for better hover */}
                          <circle
                            cx={scaleX(d.x)}
                            cy={scaleY(d.y)}
                            r="15"
                            fill="transparent"
                            style={{cursor: 'pointer'}}
                            onMouseEnter={(e) => setSensitivityHoveredPoint({ index: i, x: d.x, y: d.y, metrics: d, screenX: e.clientX, screenY: e.clientY })}
                            onMouseMove={(e) => setSensitivityHoveredPoint({ index: i, x: d.x, y: d.y, metrics: d, screenX: e.clientX, screenY: e.clientY })}
                          />
                          {/* Visible point */}
                          <circle
                            cx={scaleX(d.x)}
                            cy={scaleY(d.y)}
                            r={sensitivityHoveredPoint?.index === i ? 8 : 5}
                            fill={sensitivityHoveredPoint?.index === i ? '#60a5fa' : '#3b82f6'}
                            stroke="#1a1a1a"
                            strokeWidth="2"
                            style={{transition: 'r 0.15s ease, fill 0.15s ease', pointerEvents: 'none'}}
                          />
                        </g>
                      ))}

                      {/* Current value marker */}
                      {currentX >= minX && currentX <= maxX && isFinite(currentY) && (
                        <>
                          <line
                            x1={currentXPos}
                            y1={padding.top}
                            x2={currentXPos}
                            y2={chartHeight - padding.bottom}
                            stroke="#10b981"
                            strokeWidth="2"
                            strokeDasharray="8,6"
                          />
                          <circle
                            cx={currentXPos}
                            cy={currentYPos}
                            r="10"
                            fill="#10b981"
                            stroke="#1a1a1a"
                            strokeWidth="3"
                          />
                          <text
                            x={currentXPos}
                            y={padding.top - 15}
                            textAnchor="middle"
                            fill="#10b981"
                            fontSize="13"
                            fontWeight="600"
                          >
                            Actuel: {sensitivityYMetric === 'ncf' || sensitivityYMetric === 'npv' ? formatNumber(currentY, 0) + '€' : currentY.toFixed(2) + '%'}
                          </text>
                        </>
                      )}

                      {/* Hover vertical line */}
                      {sensitivityHoveredPoint && (
                        <line
                          x1={scaleX(sensitivityHoveredPoint.x)}
                          y1={padding.top}
                          x2={scaleX(sensitivityHoveredPoint.x)}
                          y2={chartHeight - padding.bottom}
                          stroke="rgba(96, 165, 250, 0.5)"
                          strokeWidth="1"
                          strokeDasharray="4,4"
                        />
                      )}

                      {/* Y-axis */}
                      <line
                        x1={padding.left}
                        y1={padding.top}
                        x2={padding.left}
                        y2={chartHeight - padding.bottom}
                        stroke="rgba(209, 216, 224, 0.3)"
                      />

                      {/* Y-axis ticks and labels */}
                      {yTicks.map((tick, i) => (
                        <g key={`y-tick-${i}`}>
                          <line
                            x1={padding.left - 6}
                            y1={scaleY(tick)}
                            x2={padding.left}
                            y2={scaleY(tick)}
                            stroke="rgba(209, 216, 224, 0.5)"
                          />
                          <text
                            x={padding.left - 12}
                            y={scaleY(tick) + 4}
                            textAnchor="end"
                            fill="rgba(209, 216, 224, 0.7)"
                            fontSize="12"
                          >
                            {sensitivityYMetric === 'ncf' || sensitivityYMetric === 'npv'
                              ? (tick >= 1000 || tick <= -1000 ? (tick / 1000).toFixed(0) + 'k€' : tick.toFixed(0) + '€')
                              : tick.toFixed(1) + '%'}
                          </text>
                        </g>
                      ))}

                      {/* Y-axis label */}
                      <text
                        x={20}
                        y={chartHeight / 2}
                        textAnchor="middle"
                        fill="rgba(209, 216, 224, 0.8)"
                        fontSize="13"
                        fontWeight="500"
                        transform={`rotate(-90, 20, ${chartHeight / 2})`}
                      >
                        {yAxisLabel}
                      </text>

                      {/* X-axis */}
                      <line
                        x1={padding.left}
                        y1={chartHeight - padding.bottom}
                        x2={chartWidth - padding.right}
                        y2={chartHeight - padding.bottom}
                        stroke="rgba(209, 216, 224, 0.3)"
                      />

                      {/* X-axis ticks and labels */}
                      {xTicks.filter((_, i) => i % 2 === 0 || xTicks.length <= 12).map((tick, i) => (
                        <g key={`x-tick-${i}`}>
                          <line
                            x1={scaleX(tick)}
                            y1={chartHeight - padding.bottom}
                            x2={scaleX(tick)}
                            y2={chartHeight - padding.bottom + 6}
                            stroke="rgba(209, 216, 224, 0.5)"
                          />
                          <text
                            x={scaleX(tick)}
                            y={chartHeight - padding.bottom + 22}
                            textAnchor="middle"
                            fill="rgba(209, 216, 224, 0.7)"
                            fontSize="12"
                          >
                            {tick}{paramUnit}
                          </text>
                        </g>
                      ))}

                      {/* X-axis label */}
                      <text
                        x={chartWidth / 2}
                        y={chartHeight - 10}
                        textAnchor="middle"
                        fill="rgba(209, 216, 224, 0.8)"
                        fontSize="13"
                        fontWeight="500"
                      >
                        {paramLabel}
                      </text>
                    </svg>

                    {/* Tooltip */}
                    {sensitivityHoveredPoint && (
                      <div
                        style={{
                          position: 'fixed',
                          left: sensitivityHoveredPoint.screenX,
                          top: sensitivityHoveredPoint.screenY - 80,
                          transform: 'translateX(-50%)',
                          padding: '12px 16px',
                          background: '#222222',
                          borderRadius: '10px',
                          border: '1px solid #3b82f6',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                          zIndex: 10000,
                          pointerEvents: 'none',
                          minWidth: '160px'
                        }}
                      >
                        <div style={{color: 'rgba(209, 216, 224, 0.7)', fontSize: '0.85em', marginBottom: '6px'}}>
                          {paramLabel}: <span style={{color: '#fff', fontWeight: '600'}}>{sensitivityHoveredPoint.x}{paramUnit}</span>
                        </div>
                        <div style={{color: 'rgba(209, 216, 224, 0.7)', fontSize: '0.85em'}}>
                          {yAxisLabel}: <span style={{color: sensitivityHoveredPoint.y >= 0 ? '#4ade80' : '#f87171', fontWeight: '700', fontSize: '1.1em'}}>
                            {sensitivityYMetric === 'ncf' || sensitivityYMetric === 'npv'
                              ? formatNumber(sensitivityHoveredPoint.y, 0) + '€'
                              : sensitivityHoveredPoint.y.toFixed(2) + '%'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
                    </div>}
                  </div>
                </>
              );
            })()}
          </div>
        )}

          </div>
        </div>
      </div>

      {/* Interactive Tooltip for Charts */}
      {chartTooltip && (
        <div
          style={{
            position: 'fixed',
            left: `${Math.min(Math.max(chartTooltip.x, 150), window.innerWidth - 150)}px`,
            top: `${Math.max(chartTooltip.y - 120, 10)}px`,
            transform: 'translateX(-50%)',
            padding: '12px 16px',
            background: '#222222',
            backdropFilter: 'blur(20px)',
            borderRadius: '10px',
            border: '1px solid #2a2a2a',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            zIndex: 10000,
            pointerEvents: 'none',
            minWidth: '220px'
          }}
        >
          {chartTooltip.type === 'amortization' ? (
            <>
              <div style={{color: 'rgba(209, 216, 224, 0.9)', fontSize: '0.9em', marginBottom: '8px', fontWeight: '600'}}>
                Mois {chartTooltip.data.month} ({Math.floor(chartTooltip.data.month / 12)} ans {chartTooltip.data.month % 12} mois)
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                <span style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>Mensualité:</span>
                <span style={{color: '#ffffff', fontSize: '0.85em', fontWeight: '600'}}>
                  {formatNumber(chartTooltip.data.payment, 2)} €
                </span>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                <span style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>Capital:</span>
                <span style={{color: '#4ade80', fontSize: '0.85em', fontWeight: '600'}}>
                  {formatNumber(chartTooltip.data.principal, 2)} €
                </span>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                <span style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>Intérêts:</span>
                <span style={{color: '#10b981', fontSize: '0.85em', fontWeight: '600'}}>
                  {formatNumber(chartTooltip.data.interest, 2)} €
                </span>
              </div>
              <div style={{borderTop: '1px solid #2a2a2a', marginTop: '8px', paddingTop: '8px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                  <span style={{color: 'rgba(209, 216, 224, 0.9)', fontSize: '0.9em', fontWeight: '600'}}>Solde Restant:</span>
                  <span style={{color: '#60a5fa', fontSize: '0.95em', fontWeight: '700'}}>
                    {formatNumber(chartTooltip.data.remainingBalance, 0)} €
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={{color: 'rgba(209, 216, 224, 0.9)', fontSize: '0.9em', marginBottom: '8px', fontWeight: '600'}}>
                {chartTooltip.type === 'annual' ? t.year : t.year} {chartTooltip.data.year}
              </div>

              {chartTooltip.type === 'annual' ? (
                <>
                  <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                    <span style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>{t.revenues}:</span>
                    <span style={{color: '#4ade80', fontSize: '0.85em', fontWeight: '600'}}>
                      {formatNumber(chartTooltip.data.revenue, 0)}
                    </span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                    <span style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>{t.recurrentFees}:</span>
                    <span style={{color: '#f87171', fontSize: '0.85em', fontWeight: '600'}}>
                      {formatNumber(chartTooltip.data.fees, 0)}
                    </span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                    <span style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>{t.credit}:</span>
                    <span style={{color: '#f87171', fontSize: '0.85em', fontWeight: '600'}}>
                      {formatNumber(chartTooltip.data.creditPayments, 0)}
                    </span>
                  </div>
                  <div style={{borderTop: '1px solid #2a2a2a', marginTop: '8px', paddingTop: '8px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                      <span style={{color: 'rgba(209, 216, 224, 0.9)', fontSize: '0.9em', fontWeight: '600'}}>{t.netCashFlow}:</span>
                      <span style={{
                        color: chartTooltip.data.netCashFlow >= 0 ? '#4ade80' : '#f87171',
                        fontSize: '0.95em',
                        fontWeight: '700'
                      }}>
                        {formatNumber(chartTooltip.data.netCashFlow, 0)}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                  <span style={{color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em'}}>{t.cumulativeCashFlow}:</span>
                  <span style={{
                    color: chartTooltip.data.cumulative >= 0 ? '#4ade80' : '#52d99d',
                    fontSize: '0.95em',
                    fontWeight: '700'
                  }}>
                    {formatNumber(chartTooltip.data.cumulative, 0)}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Info Modal for Performance Metrics */}
      {infoModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setInfoModal(null)}
        >
          <div
            style={{
              background: '#1a1a1a',
              borderRadius: '16px',
              border: '1px solid #2a2a2a',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              maxWidth: '500px',
              width: '100%',
              padding: '30px',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setInfoModal(null)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid #2a2a2a',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'rgba(156, 163, 175, 0.7)',
                fontSize: '18px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.color = 'rgba(209, 216, 224, 0.9)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                e.target.style.color = 'rgba(156, 163, 175, 0.7)';
              }}
            >
              ×
            </button>

            {/* Title */}
            <h3 style={{
              color: 'rgba(209, 216, 224, 0.95)',
              fontSize: '1.3em',
              fontWeight: '700',
              marginBottom: '20px',
              paddingRight: '40px'
            }}>
              {infoModal.title}
            </h3>

            {/* Description */}
            <div style={{
              color: 'rgba(156, 163, 175, 0.9)',
              fontSize: '0.95em',
              lineHeight: '1.6',
              marginBottom: '20px'
            }}>
              {infoModal.description}
            </div>

            {/* Formula */}
            <div style={{
              background: '#222222',
              border: '1px solid #2a2a2a',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <div style={{
                color: 'rgba(156, 163, 175, 0.7)',
                fontSize: '0.8em',
                fontWeight: '600',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Calcul
              </div>
              <div style={{
                color: '#60a5fa',
                fontSize: '0.95em',
                fontFamily: 'monospace',
                wordBreak: 'break-word'
              }}>
                {infoModal.formula}
              </div>
            </div>

            {/* Interpretation */}
            <div style={{
              background: 'rgba(74, 222, 128, 0.1)',
              border: '1px solid rgba(74, 222, 128, 0.2)',
              borderRadius: '8px',
              padding: '15px'
            }}>
              <div style={{
                color: 'rgba(156, 163, 175, 0.7)',
                fontSize: '0.8em',
                fontWeight: '600',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Interprétation
              </div>
              <div style={{
                color: 'rgba(209, 216, 224, 0.9)',
                fontSize: '0.9em',
                lineHeight: '1.5'
              }}>
                {infoModal.interpretation}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Portfolio Add Modal - Ask for investment start date */}
      {portfolioAddModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#1a1a1a',
            borderRadius: '16px',
            padding: '32px',
            border: '1px solid rgba(58, 58, 58, 0.5)',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
          }}>
            <h3 style={{
              fontSize: '1.2em',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '8px'
            }}>
              Ajouter au Portefeuille
            </h3>
            <p style={{
              fontSize: '0.9em',
              color: 'rgba(156, 163, 175, 0.8)',
              marginBottom: '24px'
            }}>
              Indiquez la date de début de l'investissement pour ce projet.
            </p>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '0.85em',
                color: 'rgba(156, 163, 175, 0.9)',
                marginBottom: '8px'
              }}>
                Date de début de l'investissement
              </label>
              <input
                type="date"
                value={portfolioStartDate}
                onChange={(e) => setPortfolioStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(58, 58, 58, 0.5)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '1em',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setPortfolioAddModal({ show: false, projectKey: null });
                  setPortfolioStartDate('');
                }}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(58, 58, 58, 0.5)',
                  borderRadius: '8px',
                  color: 'rgba(156, 163, 175, 0.9)',
                  fontSize: '0.9em',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => { e.target.style.background = 'rgba(255, 255, 255, 0.1)'; }}
                onMouseLeave={(e) => { e.target.style.background = 'rgba(255, 255, 255, 0.05)'; }}
              >
                Annuler
              </button>
              <button
                onClick={confirmAddToPortfolio}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(16, 185, 129, 0.2)',
                  border: '1px solid rgba(16, 185, 129, 0.4)',
                  borderRadius: '8px',
                  color: '#10b981',
                  fontSize: '0.9em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => { e.target.style.background = 'rgba(16, 185, 129, 0.3)'; }}
                onMouseLeave={(e) => { e.target.style.background = 'rgba(16, 185, 129, 0.2)'; }}
              >
                Ajouter au Portefeuille
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewModal.show && previewModal.project && (() => {
        const project = previewModal.project;

        // Extract acquisition data
        const acquisitionData = project.acquisitionData || {};
        const prixAchat = parseFloat(acquisitionData.prixAchat) || 0;
        const fraisNotaire = parseFloat(acquisitionData.fraisNotaire) || 0;
        const fraisAgence = parseFloat(acquisitionData.fraisAgence) || 0;
        const travaux = parseFloat(acquisitionData.travaux) || 0;
        const autresFrais = parseFloat(acquisitionData.autresFrais) || 0;
        const ameublement = parseFloat(acquisitionData.ameublement) || 0;

        const totalBudget = prixAchat + fraisNotaire + fraisAgence + travaux + autresFrais + ameublement;

        // Extract financing data
        const financementData = project.financementData || {};
        const apport = parseFloat(financementData.apport) || 0;
        const montantEmprunt = totalBudget - apport;
        const tauxInteret = parseFloat(financementData.tauxInteret) || 0;
        const dureeEmprunt = parseFloat(financementData.dureeEmprunt) || 0;
        const tauxAssurance = parseFloat(financementData.tauxAssurance) || 0;

        // Calculate monthly payment
        let mensualite = 0;
        let mensualiteAssurance = 0;
        if (montantEmprunt > 0 && tauxInteret > 0 && dureeEmprunt > 0) {
          const tauxMensuel = tauxInteret / 100 / 12;
          const nbMensualites = dureeEmprunt * 12;
          mensualite = montantEmprunt * (tauxMensuel * Math.pow(1 + tauxMensuel, nbMensualites)) / (Math.pow(1 + tauxMensuel, nbMensualites) - 1);
          mensualiteAssurance = (montantEmprunt * tauxAssurance / 100) / 12;
        }

        const mensualiteTotale = mensualite + mensualiteAssurance;
        const coutTotalCredit = (mensualiteTotale * dureeEmprunt * 12) - montantEmprunt;
        const coutAssuranceTotal = mensualiteAssurance * dureeEmprunt * 12;

        // Extract property revenues
        const propertyRevenues = project.propertyRevenues || [];
        const loyerAnnuel = propertyRevenues.reduce((sum, rev) => sum + (parseFloat(rev.amount) || 0), 0);
        const loyerMensuel = loyerAnnuel / 12;

        // Extract charges
        const recurrentFees = project.recurrentFees || [];
        const chargesMensuelles = recurrentFees.reduce((sum, fee) => {
          const amount = parseFloat(fee.amount) || 0;
          if (fee.frequency === 'monthly') return sum + amount;
          if (fee.frequency === 'annual') return sum + (amount / 12);
          if (fee.frequency === 'quarterly') return sum + (amount / 3);
          return sum;
        }, 0);

        // Calculate cash flow
        const cashFlow = loyerMensuel - chargesMensuelles - mensualiteTotale;

        // Calculate net return
        const rendementNet = prixAchat > 0 ? ((loyerAnnuel - (chargesMensuelles * 12)) / prixAchat) * 100 : 0;

        // Get extracted data
        const extractedData = project.extractedData || {};
        const address = extractedData.address || 'Adresse non disponible';
        const surface = parseFloat(extractedData.surface) || 0;

        // Get investment start date
        const investmentStartDate = getPortfolioStartDate(project.key) ||
                                    acquisitionData.investmentStartDate ||
                                    new Date().toISOString().split('T')[0];

        // Get images
        const images = extractedData.images || [];
        const mainImage = extractedData.imageUrl;

        return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '40px'
          }}>
            <div style={{
              background: '#1a1a1a',
              borderRadius: '16px',
              maxWidth: '1200px',
              width: '100%',
              height: 'fit-content',
              maxHeight: '90vh',
              position: 'relative',
              border: '2px solid #3a3a3a',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Close Button */}
              <button
                onClick={() => setPreviewModal({ show: false, project: null })}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  fontSize: '1.5em',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => { e.target.style.background = 'rgba(255, 255, 255, 0.2)'; }}
                onMouseLeave={(e) => { e.target.style.background = 'rgba(255, 255, 255, 0.1)'; }}
              >
                ×
              </button>

              {/* Header with Address and Date */}
              <div style={{
                background: '#222222',
                padding: '24px 32px',
                borderTopLeftRadius: '14px',
                borderTopRightRadius: '14px',
                borderBottom: '1px solid #3a3a3a',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(156, 163, 175, 0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <span style={{ fontSize: '1.1em', fontWeight: '600', color: '#ffffff' }}>{address}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(156, 163, 175, 0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  <span style={{ fontSize: '1em', fontWeight: '500', color: 'rgba(156, 163, 175, 0.9)' }}>
                    {new Date(investmentStartDate).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div style={{ padding: '24px', flex: 1, overflow: 'auto' }}>
                {/* Top KPIs */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '24px' }}>
                  <div style={{
                    background: 'rgba(42, 42, 42, 0.6)',
                    padding: '16px 20px',
                    borderRadius: '12px',
                    border: '1px solid rgba(75, 75, 75, 0.8)'
                  }}>
                    <div style={{ fontSize: '0.75em', color: 'rgba(156, 163, 175, 0.6)', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Budget</div>
                    <div style={{ fontSize: '1.6em', fontWeight: '700', color: '#fbbf24' }}>{formatNumber(totalBudget, 0)} €</div>
                  </div>
                  <div style={{
                    background: 'rgba(42, 42, 42, 0.6)',
                    padding: '16px 20px',
                    borderRadius: '12px',
                    border: '1px solid rgba(75, 75, 75, 0.8)'
                  }}>
                    <div style={{ fontSize: '0.75em', color: 'rgba(156, 163, 175, 0.6)', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cash-Flow</div>
                    <div style={{ fontSize: '1.6em', fontWeight: '700', color: cashFlow >= 0 ? '#10b981' : '#ef4444' }}>
                      {formatNumber(cashFlow, 0)} €/mois
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(42, 42, 42, 0.6)',
                    padding: '16px 20px',
                    borderRadius: '12px',
                    border: '1px solid rgba(75, 75, 75, 0.8)'
                  }}>
                    <div style={{ fontSize: '0.75em', color: 'rgba(156, 163, 175, 0.6)', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Rendement net</div>
                    <div style={{ fontSize: '1.6em', fontWeight: '700', color: rendementNet >= 0 ? '#10b981' : '#ef4444' }}>
                      {formatNumber(rendementNet, 2)}%
                    </div>
                  </div>
                </div>

                {/* Endettement & Frais Grid - Side by Side */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  {/* Endettement Section */}
                  <div style={{
                    background: '#222222',
                    padding: '16px',
                    borderRadius: '12px',
                    border: '1px solid #3a3a3a'
                  }}>
                    <div style={{ fontSize: '1.1em', fontWeight: '700', color: '#ffffff', marginBottom: '16px' }}>Endettement</div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      {/* Situation Crédit */}
                      <div>
                        <div style={{ fontSize: '0.85em', fontWeight: '600', color: 'rgba(156, 163, 175, 0.9)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Situation Crédit <span style={{ fontSize: '0.75em', cursor: 'help' }}>ⓘ</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.8em' }}>Durée du Crédit</span>
                            <span style={{ fontWeight: '600', color: '#ffffff', fontSize: '0.8em' }}>{creditData.durationYears || 20} ans</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.8em' }}>Taux d'Intérêt</span>
                            <span style={{ fontWeight: '600', color: '#ffffff', fontSize: '0.8em' }}>{creditData.interestRate || 0}%</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.8em' }}>Type de Crédit</span>
                            <span style={{ fontWeight: '600', color: '#ffffff', fontSize: '0.8em' }}>
                              {creditData.type === 'amortizing' ? 'Amortissable' :
                               creditData.type === 'bullet' ? 'In Fine' :
                               creditData.type === 'interestOnly' ? 'Intérêts Seuls' : 'Crescendo'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Financement */}
                      <div>
                        <div style={{ fontSize: '0.85em', fontWeight: '600', color: 'rgba(156, 163, 175, 0.9)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Financement <span style={{ fontSize: '0.75em', cursor: 'help' }}>ⓘ</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.8em' }}>Apport</span>
                            <span style={{ fontWeight: '600', color: '#ffffff', fontSize: '0.8em' }}>{formatNumber(financementData.apport || 0, 1)} €</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.8em' }}>Dette (Emprunt)</span>
                            <span style={{ fontWeight: '600', color: '#ffffff', fontSize: '0.8em' }}>{formatNumber(creditData.amount || financementData.emprunt || 0, 1)} €</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Frais Section */}
                  <div style={{
                    background: '#222222',
                    padding: '16px',
                    borderRadius: '12px',
                    border: '1px solid #3a3a3a'
                  }}>
                    <div style={{ fontSize: '1.1em', fontWeight: '700', color: '#ffffff', marginBottom: '16px' }}>Frais</div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      {/* Acquisition */}
                      <div>
                        <div style={{ fontSize: '0.85em', fontWeight: '600', color: 'rgba(156, 163, 175, 0.9)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Acquisition <span style={{ fontSize: '0.75em', cursor: 'help' }}>ⓘ</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.8em' }}>Prix d'Achat</span>
                            <span style={{ fontWeight: '600', color: '#ffffff', fontSize: '0.8em' }}>{formatNumber(prixAchat, 1)} €</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.8em' }}>Frais Notaire</span>
                            <span style={{ fontWeight: '600', color: '#ffffff', fontSize: '0.8em' }}>{formatNumber(fraisNotaire, 1)} €</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.8em' }}>Travaux</span>
                            <span style={{ fontWeight: '600', color: '#ffffff', fontSize: '0.8em' }}>{formatNumber(travaux, 1)} €</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #3a3a3a' }}>
                            <span style={{ color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.85em', fontWeight: '600' }}>Coût Total</span>
                            <span style={{ fontWeight: '700', color: '#fbbf24', fontSize: '0.85em' }}>{formatNumber(totalBudget, 1)} €</span>
                          </div>
                        </div>
                      </div>

                      {/* Charges Récurrentes */}
                      <div>
                        <div style={{ fontSize: '0.85em', fontWeight: '600', color: 'rgba(156, 163, 175, 0.9)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Charges Récurrentes <span style={{ fontSize: '0.75em', cursor: 'help' }}>ⓘ</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.8em' }}>Précompte</span>
                            <span style={{ fontWeight: '600', color: '#ffffff', fontSize: '0.8em' }}>
                              {formatNumber((recurrentFees.find(f => f.name === 'propertyTax')?.amount || 0), 1)} €/a
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '0.8em' }}>Copropriété</span>
                            <span style={{ fontWeight: '600', color: '#ffffff', fontSize: '0.8em' }}>
                              {formatNumber((recurrentFees.find(f => f.name === 'coOwnershipFees')?.amount || 0), 2)} €/t
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Images Grid */}
                {(mainImage || images.length > 0) && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{ fontSize: '1em', fontWeight: '700', color: '#ffffff', marginBottom: '12px' }}>Photos</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                      {mainImage && (
                        <div style={{ aspectRatio: '4/3', overflow: 'hidden', borderRadius: '8px', border: '1px solid #3a3a3a' }}>
                          <img src={mainImage} alt="Main" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      )}
                      {images.slice(0, 3).map((img, idx) => (
                        <div key={idx} style={{ aspectRatio: '4/3', overflow: 'hidden', borderRadius: '8px', border: '1px solid #3a3a3a' }}>
                          <img src={img} alt={`Image ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default App;
